// src/translations.js
const translations = {
    en: {
      app: {
        loading: "Loading...",
        skipToMain: "Skip to main content",
      },
      common: {
        unsavedChangesTitle: "Unsaved Changes",
        unsavedChangesMessage: "You have unsaved changes. Do you want to leave without saving?",
        stay: "Stay",
        leave: "Leave",
        actions: {
          create: "Create",
          update: "Update",
          delete: "Delete",
          login: "Login",
          logout: "Logout",
          dataImport: "Data Import",
          dataExport: "Data Export"
        },
        levels: {
          info: "Info",
          warning: "Warning",
          error: "Error"
        },
        modules: {
          GeoRisk: "Geographical Risk",
          CustomerRisk: "Customer Risk",
          BWRA: "BWRA",
          TransactionMonitoring: "Transaction Monitoring",
          PeriodicReporting: "Periodic Reporting",
          Admin: "Administration",
          Auth: "Authentication",
          Dashboard: "Dashboard",
          DataSources: "Data Sources",
          FieldMappings: "Field Mappings"
        },
        riskLevels: {
          low: "Low",
          medium: "Medium",
          high: "High",
          veryHigh: "Very High",
          unacceptable: "Unacceptable"
        },
        indexStatus: {
          ACTIVE: "Active",
          DRAFT: "Draft"
        },
        unknown: "Unknown",
        user: "User",
        deletedUserSuffix: "(deleted)",
        confirm: "Confirm",
        processing: "Processing…",
        cancel: "Cancel",
        close: "Close",
        undo: "Undo",
        openGlossary: "Open Glossary",
        notAvailable: "N/A",
        articlePrefix: "Art.",
        dataTable: {
          noResults: "No results found",
          previous: "Previous",
          next: "Next",
          page: "Page",
          rowsPerPage: "Rows per page",
          showing: "Showing {start}–{end} of {total}",
        },
        toast: {
          saved: "Saved successfully",
          deleted: "Deleted successfully",
          exported: "Export completed",
          imported: "Import completed",
          error: "An error occurred",
          licenseSaved: "License updated",
          settingsSaved: "Settings saved",
          userSaved: "User saved",
          userDeleted: "User deleted",
          entitySaved: "Entity saved",
          entityDeleted: "Entity deleted",
          parameterAdded: "Parameter added",
          parameterUpdated: "Parameter updated",
          parameterDeleted: "Parameter deleted",
          modelConfigSaved: "Model configuration saved",
          previewGenerated: "Preview generated — scroll down to see results",
          bwraRiskSaved: "Risk saved",
          bwraRiskArchived: "Risk archived",
          bwraRiskRestored: "Risk restored",
          bwraRiskDeleted: "Risk deleted",
          bwraControlSaved: "Control saved",
          bwraControlDeleted: "Control deleted",
          bwraActionSaved: "Action saved",
          bwraActionDeleted: "Action deleted",
          bwraAcceptanceSaved: "Risk accepted",
          bwraAcceptanceRevoked: "Risk acceptance revoked",
          bwraReportGenerated: "Report generated successfully",
          bwraReportExported: "Report exported",
          bwraReportError: "Report generation failed",
        },
        errorBoundary: {
          title: "Something went wrong",
          message: "An unexpected error occurred. Please try again.",
          retry: "Try again",
          details: "Error details",
        },
        breadcrumb: {
          home: "Home",
          ariaLabel: "Breadcrumb navigation",
        },
        commandPalette: {
          placeholder: "Search modules and actions...",
          openHint: "Ctrl+K",
          noResults: "No results found",
          categoryModules: "Modules",
          categoryActions: "Quick Actions",
          actionGoTo: "Go to",
          ariaLabel: "Command palette",
          pressEsc: "Press Esc to close",
        },
        form: {
          required: "Required field",
          errorRequired: "This field is required",
          errorEmail: "Enter a valid email address",
          errorMinLength: "At least {{min}} characters required",
          errorMaxLength: "Maximum {{max}} characters allowed",
        },
        concurrency: {
          conflictTitle: "Edit Conflict Detected",
          conflictDescription: "This record was modified by {{user}} on {{date}} at {{time}} while you were editing it.",
          keepMine: "Keep my version",
          keepMineHint: "Save your changes and overwrite the other user's changes.",
          keepTheirs: "Discard my changes",
          keepTheirsHint: "Dismiss your changes and reload the latest saved version.",
          mergeManually: "Merge manually",
          mergeManuallyHint: "Close this dialog so you can compare both versions yourself.",
          editingLabel: "Currently editing:",
        },
      },
      import: {
        wizard: {
          defaultTitle: "Import Data",
          stepSelect: "Select File",
          stepPreview: "Preview",
          stepMap: "Map Fields",
          stepConfirm: "Import",
          selectPrompt: "Click or drag a file here",
          selectFormats: "Supported formats: CSV, Excel (.xlsx), JSON",
          parsing: "Parsing file…",
          emptyFile: "The file appears to be empty",
          parseError: "Could not read file: {{error}}",
          formatHintsTitle: "Supported Formats",
          formatExcelHint: "First row = headers, following rows = data",
          previewRowCount: "{{count}} rows detected",
          previewShowing: "(showing first {{count}})",
          mapDescription: "Match the columns in your file to the expected data fields.",
          mapTargetField: "Target field",
          mapSourceColumn: "Source column",
          mapNotMapped: "(skip / not mapped)",
          requiredNote: "Required fields",
          validationOk: "{{count}} rows ready to import",
          validationErrors: "{{count}} rows skipped due to errors",
          noValidRows: "No valid rows",
          andMore: "…and {{count}} more",
          confirmPreviewTitle: "Rows to import (first 5 shown):",
          importBtn: "Import {{count}} rows",
          back: "Back",
          next: "Next",
          validateBtn: "Validate & Import",
          rowError: "Row {{n}}: \"{{field}}\" is required",
          buttonLabel: "Import Data",
        },
        fields: {
          name: "Name",
          category: "Category",
          description: "Description",
          dbField: "DB Field",
        },
      },
      sidebar: {
        dashboard: "Dashboard",
        customerRisk: "Customer Risk",
        geographicalRisk: "Geographical Risk",
        bwra: "BWRA",
        transactionMonitoring: "Transaction Monitoring",
        periodicReporting: "Periodic Reporting",
        admin: "Administration",
        roleLabel: "Role",
        expand: "Expand sidebar",
        collapse: "Collapse sidebar",
      },
      permissions: {
        requiresEdit: "Edit permission required",
        requiresEditDesc: "You have read-only access to this module. Contact your administrator to request edit permissions.",
        requiresAdmin: "Admin permission required",
        requiresAdminDesc: "This section requires administrator access.",
        readOnlyBadge: "Read-only",
        roles: {
          admin: "Admin",
          analyst: "Analyst",
          viewer: "Viewer",
        },
      },
      topMenu: {
        logout: "Log out",
        sessionLeft: "Session: {{time}} left",
        inactive: "Inactive: {{time}}",
        language: "Language",
        sessionExpiring: "Session expires in {{time}}",
        inactivityWarning: "Logout due to inactivity in {{time}}",
        days: "{{count}} days",
        day: "{{count}} day",
        hours: "{{count}} hours",
        hour: "{{count}} hour",
        minutes: "{{count}} minutes",
        minute: "{{count}} minute",
        zeroMinutes: "0 minutes",
        switchEntity: "Switch entity",
        noEntity: "No entity",
        preferences: "Preferences",
        theme: "Theme",
        themeDefault: "Default",
        themeDark: "Dark",
        themeLight: "Light",
        sessionSection: "Session",
        brandTagline: "Risk Management System",
        glossary: "AML Glossary",
        search: "Search",
      },
      notifications: {
        label: "Notifications",
        panelTitle: "Notifications",
        empty: "All clear — no pending actions",
        markAllRead: "Mark all as read",
        unread: "Unread",
        urgentCount: "{{count}} urgent",
        warningCount: "{{count}} warning",
        annualReport: "Annual Report {{year}}",
        bwraReview: "BWRA Annual Review",
        scheduledValidation: "Validation: {{model}}",
        staleData: "{{module}}: Data overdue",
        overdueDesc: "{{count}} days overdue — action required",
        dueSoonDesc: "Due in {{count}} days",
        staleDataDesc: "Data has not been updated within the expected interval",
        scheduledReportCreated: "Scheduled report created: {{year}}",
        scheduledReportCreatedDesc: "A draft report for {{year}} was auto-created — please review and complete it",
        reportDistributed: "Report {{year}} approved and distributed",
        reportDistributedDesc: "The {{year}} report was approved and distributed to {{count}} recipient(s)",
        kpiThreshold: "KPI Warning: {{kpi}}",
        kpiThresholdDesc: "Current value {{value}} meets or exceeds threshold of {{threshold}}",
      },
      globalSearch: {
        title: "Global Search",
        placeholder: "Search risks, rules, countries, models…",
        hint: "Start typing to search all modules",
        typeMore: "Type at least 2 characters",
        noResults: "No results found",
      },
      helpTerms: {
        keyTerms: "Key terms",
      },
      modules: {
        dashboard: {
            welcome: "Welcome to Risk Management Studio",
            chooseModule: "Choose a module from the sidebar to get started.",
            moduleStatus: "Module Status",
            recentActivity: "Recent Activity",
            riskSummary: "Risk Summary",
            geoRiskModels: "GeoRisk Models",
            customerRiskModels: "CustomerRisk Models",
            configured: "configured",
            countries: "countries",
            indices: "indices",
            models: "models",
            parameters: "parameters",
            noData: "No data yet",
            noRecentActivity: "No recent activity recorded.",
            viewAll: "View All",
            goToModule: "Go to module",
            totalCountries: "Total Countries",
            totalIndices: "Total Indices",
            highRiskCountries: "High Risk Countries",
            avgRiskScore: "Avg Risk Score",
            totalModels: "Total Models",
            totalParameters: "Total Parameters",
            quickActions: "Quick Actions",
            openGeoRisk: "Open GeoRisk",
            openCustomerRisk: "Open Customer Risk",
            openBwra: "Open BWRA",
            openTxMon: "Open Transaction Monitoring",
            openPrpt: "Open Periodic Reporting",
            openAdmin: "Open Admin",
            quickActionsSubtitle: "Shortcuts to specific tasks in each module",
            quickActionImportData: "Import Risk Data",
            quickActionManageCountries: "Manage Countries",
            quickActionManageParameters: "Manage Parameters",
            quickActionRiskRegister: "Risk Register",
            quickActionActionPlan: "Action Plan",
            quickActionMonitoringRules: "Monitoring Rules",
            quickActionExportReport: "Export Report",
            quickActionCreateRisk: "Create Risk",
            quickActionRunValidation: "Run Validation",
            quickActionNewReport: "New Report",
            systemOverview: "System Overview",
            riskAreas: "Risk Areas",
            controls: "Controls",
            unmitigated: "Unmitigated",
            countryRiskDistribution: "Country Risk Distribution",
            bwraRiskPosture: "BWRA Risk Posture",
            grossRisk: "Avg Gross Risk",
            residualRisk: "Avg Residual Risk",
            mitigatedRisks: "{{count}} mitigated",
            unmitigatedRisks: "{{count}} unmitigated",
            highRiskAlert: "{{count}} countries flagged as high risk or above",
            viewHighRiskCountries: "View high-risk countries",
            viewUnmitigatedRisks: "View unmitigated risks",
            geoRiskTitle: "Geographical Risk",
            geoRiskSubtitle: "Covers risks in BWRA",
            customerRiskTitle: "Customer Risk",
            customerRiskSubtitle: "Covers risks in BWRA",
            bwraTitle: "BWRA",
            bwraSubtitle: "Starting point – identify business-wide risks",
            txMonTitle: "Transaction Monitoring",
            txMonSubtitle: "Covers risks in BWRA",
            prptTitle: "Periodic Reporting",
            prptSubtitle: "FI reporting — annual supervisory data",
            prptReports: "Reports",
            prptAwaitingReview: "Awaiting Review",
            prptApproved: "Approved",
            prptViewReview: "View reports awaiting review",
            adminTitle: "Administration",
            highRisk: "High Risk",
            highRiskOrAbove: "High Risk or Above",
            auditEvents: "Audit Events",
            statusActive: "Active",
            statusSetupNeeded: "Setup needed",
            riskLevelLow: "Low",
            riskLevelMedium: "Medium",
            riskLevelHigh: "High",
            riskLevelVeryHigh: "Very High",
            riskLevelUnacceptable: "Unacceptable",
            riskLevelUnscored: "Unscored",
            customizeLayout: "Customize Layout",
            layoutReset: "Reset Layout",
            layoutDone: "Done",
            layoutEditHint: "Drag cards to reorder",
            dragToReorder: "Drag to reorder",
            complianceChainLabel: "End-to-end compliance chain",
            complianceChainLastRun: "Last run: {{time}}",
            runComplianceChain: "Run compliance chain validation",
            timeJustNow: "just now",
            timeMinutesAgo: "{{count}}m ago",
            timeHoursAgo: "{{count}}h ago",
            timeDaysAgo: "{{count}}d ago",
            activityLoggedIn: "User logged in",
            activityLoggedOut: "User logged out",
            activityCreated: "Created",
            activityUpdated: "Updated",
            activityDeleted: "Deleted",
            activityImported: "Imported data to",
            activityExported: "Exported data from",
            activityIn: "in",
            entityType: {
                user: "user",
                role: "role",
                entity: "entity",
                settings: "settings",
                riskmodel: "risk model",
                riskindex: "risk index",
                modelconfiguration: "model configuration",
                parameter: "parameter",
            },
            complianceCoverage: "AMLR Compliance",
            complianceArticlesCovered: "Covered",
            complianceArticlesPartial: "Partial",
            complianceArticlesGap: "Gaps",
            compliancePct: "{{pct}}% covered",
            complianceViewMap: "View Compliance Map",
            // Tabs
            tabOverview: "Overview",
            tabKRI: "Key Risk Indicators",
            // KRI Dashboard
            kriTitle: "Key Risk Indicators",
            kriSubtitle: "Management summary with traffic light status for key metrics",
            kriHighRiskCountries: "High-Risk Countries",
            kriHighRiskCountriesDesc: "Countries flagged as high risk or above",
            kriModelValidation: "Model Validation",
            kriModelValidationDesc: "Latest validation accuracy across models",
            kriScheduledValidation: "Scheduled Validation",
            kriScheduledValidationDesc: "Last automated model validation run",
            kriValidationAccuracy: "{{accuracy}}% accuracy",
            kriValidationError: "Last run failed",
            kriBwraCoverage: "BWRA Coverage",
            kriBwraCoverageDesc: "Percentage of risks mitigated by controls",
            kriComplianceCoverage: "Compliance Coverage",
            kriComplianceCoverageDesc: "Percentage of AMLR articles covered",
            kriLastBwraUpdate: "Latest BWRA Update",
            kriLastBwraUpdateDesc: "Time since last BWRA version was saved",
            kriStatusGreen: "On target",
            kriStatusYellow: "Needs attention",
            kriStatusRed: "Action required",
            kriNoValidation: "No validation",
            kriNever: "Never",
            kriDaysAgo: "{{count}} days ago",
            kriToday: "Today",
            kriCountries: "{{count}} countries",
            kriAccuracy: "{{pct}}% accuracy",
            kriCoverage: "{{pct}}% covered",
            kriMitigated: "{{pct}}% mitigated",
            kriConfigureTitle: "Configure Indicators",
            kriConfigureDesc: "Select which Key Risk Indicators to display on your dashboard",
            kriConfigureBtn: "Configure",
            kriConfigureDone: "Done",
            kriConfigureShowAll: "Show all",
            kriTrendVsWeek: "vs. last week",
            kriTrendStable: "No change",
            kriTrendPeriodLabel: "Trend period",
            kriTrendVs7: "vs. last 7 days",
            kriTrendVs30: "vs. last 30 days",
            kriTrendVs90: "vs. last 90 days",
            kriPeriodDays: "{{n}} days",
            // Data freshness indicators
            freshness: {
                green: "Up to date",
                yellow: "Needs review",
                red: "Data overdue",
                grey: "No data",
                today: "Updated today",
                yesterday: "Updated yesterday",
                daysAgo: "Updated {{days}} days ago",
                never: "Never updated",
            },
            // Module health panel
            health: {
                title: "System Health Overview",
                subtitle: "At-a-glance status for all active modules — data freshness and key metrics.",
                colModule: "Module",
                colStatus: "Health",
                colMetric1: "Metric 1",
                colMetric2: "Metric 2",
                colLastUpdated: "Last Updated",
                colLastValidation: "Last Validation",
                never: "Never",
                noValidation: "Not run",
                validationError: "Error",
                status: {
                    green: "Healthy",
                    yellow: "Needs attention",
                    red: "Overdue",
                    grey: "No data",
                },
                overallStatus: {
                    green: "All modules healthy",
                    yellow: "Some modules need attention",
                    red: "Action required",
                    grey: "No data yet",
                },
            },
            // Onboarding guide
            onboardingTitle: "Welcome — here's how to get started",
            onboardingSubtitle: "All modules currently show \"Setup needed\". Follow these steps to configure the system:",
            onboardingStep1Title: "1. BWRA",
            onboardingStep1Desc: "Start here. Identify your business-wide risks and define risk appetite. BWRA is the foundation — all models below exist to cover these risks.",
            onboardingStep2Title: "2. Geographical Risk",
            onboardingStep2Desc: "Configure country risk indices to cover the geographic risks identified in BWRA. Scores feed back into residual risk calculations.",
            onboardingStep3Title: "3. Customer Risk",
            onboardingStep3Desc: "Build a customer risk model to cover customer-related risks from BWRA. Parameters and weights determine individual customer risk scores.",
            onboardingStep4Title: "4. Transaction Monitoring",
            onboardingStep4Desc: "Define monitoring rules to cover transaction risk scenarios identified in BWRA. Rules require configuration before becoming active.",
            onboardingDismiss: "Got it, let me start",
            onboardingShowAgain: "Show guide again",
            noLicenseHeading: "No License",
            noLicenseContact: "Contact your administrator",
            noLicenseCardTooltip: "This module is not licensed — click to manage licenses",
            // Risk Progression View
            riskProgression: {
                title: "Risk Progression",
                subtitle: "Inherent risk → controls → residual risk across all modules",
                inherentTitle: "Inherent Risk",
                inherentSubBwra: "Avg gross risk: {{score}}/25",
                inherentSubGeo: "{{count}} high-risk countries",
                inherentSourceBwra: "Source: BWRA",
                inherentSourceGeo: "Source: GeoRisk",
                controlTitle: "Control Effectiveness",
                controlSub: "{{mitigated}} of {{total}} risks mitigated",
                controlHigh: "Effective",
                controlMedium: "Partial",
                controlLow: "Insufficient",
                residualTitle: "Residual Risk",
                residualSub: "Avg residual risk: {{score}}/25",
                arrowControls: "controls applied",
                arrowResult: "net result",
                noData: "No data",
                trendNote: "Trend based on last {{days}} days",
                trendNoData: "Not enough data for trend",
                trendLabel: "7-day trend",
                emptyInherentNoBwra: "No BWRA risks registered yet",
                emptyInherentNoBwraHint: "Add risks in BWRA to calculate inherent risk",
                emptyInherentNoGeo: "No active GeoRisk indices",
                emptyInherentNoGeoHint: "Configure your GeoRisk model to see country risk data",
                emptyControl: "No BWRA risks registered",
                emptyControlHint: "Add risks in BWRA to track control effectiveness",
                emptyResidual: "No BWRA data yet",
                emptyResidualHint: "Configure BWRA to calculate residual risk",
            },
            // Risk Trend Chart
            riskTrend: {
                title: "Risk Trend",
                subtitle: "Inherent risk, control effectiveness and residual risk over time",
                periodLabel: "Select time period",
                emptyTitle: "No historical data available yet",
                emptyHint: "Risk snapshots are saved daily. Come back tomorrow to see your first trend.",
                dataNote: "Based on {{count}} data points",
            },
            // Compliance Deadline Tracker
            deadline: {
                title: "Compliance Deadlines",
                subtitle: "Upcoming reporting and review deadlines",
                empty: "No upcoming deadlines in the next {{days}} days",
                dueInDays: "Due in {{count}} days",
                dueToday: "Due today",
                overdueDays: "{{count}} days overdue",
                annualReport: "Annual Report {{year}}",
                annualReportDesc: "FI periodic reporting",
                bwraReview: "BWRA Annual Review",
                bwraReviewDesc: "Annual review required",
                scheduledValidation: "Validation: {{model}}",
                scheduledValidationDesc: "Scheduled model validation",
                statusOverdue: "Overdue",
                statusWarning: "Due soon",
                statusOk: "On track",
            },
            // Activity Feed
            activityFeed: {
                title: "Activity Feed",
                allModules: "All Modules",
                filterUserPlaceholder: "Filter by user…",
                period_all: "All time",
                period_today: "Today",
                period_week: "Last 7 days",
                period_month: "Last 30 days",
                showing: "Showing {{shown}} of {{total}} events",
            },
            // KPI Threshold warnings
            kpiThreshold: {
                title: "KPI Threshold Warnings",
                subtitle: "Configure thresholds for Dashboard KPI cards. A visual warning is shown when a value meets or exceeds the threshold.",
                geo_highRisk: "High-Risk Countries",
                geo_highRiskDesc: "Warn when high-risk countries ≥ threshold",
                bwra_unmitigated: "Unmitigated Risks",
                bwra_unmitigatedDesc: "Warn when unmitigated BWRA risks ≥ threshold",
                prpt_review: "Reports Awaiting Review",
                prpt_reviewDesc: "Warn when reports awaiting review ≥ threshold",
                threshold: "Threshold",
                enabled: "Active",
                warningBadge: "KPI threshold exceeded",
                reset: "Reset to defaults",
            },
            // Group / consolidated report tab
            tabKoncern: "Group View",
            group: {
                title: "Consolidated Risk Report",
                subtitle: "Aggregated risk data across all entities in the group",
                totalEntities: "Entities",
                totalRisks: "Total Risks",
                totalHighRisks: "High-Risk Risks",
                totalControls: "Total Controls",
                entityColumn: "Entity",
                typeColumn: "Type",
                riskCountColumn: "Risks",
                highColumn: "High / Very High",
                avgResidualColumn: "Avg. Residual",
                controlsColumn: "Controls",
                viewEntityBwra: "View in BWRA",
                topRisks: "Highest residual risks",
                riskName: "Risk",
                category: "Category",
                residualScore: "Residual",
                riskLevel: "Level",
                noRisks: "No risks registered for this entity.",
                noEntities: "No entities configured. Add entities in Administration to use this view.",
                type_parent: "Parent",
                type_subsidiary: "Subsidiary",
                type_branch: "Branch",
                type_joint_venture: "JV",
            },
        },
        customerRisk: {
            welcome: "Customer Risk model module",
            chooseModule: "Create great customer risk models",
            modelbuilder: "Model Builder",
            modelbuilderDesc: "Create and edit risk models.",
            simulation: "Simulation",
            simulationDesc: "Test the model with different scenarios.",
            stressTest: "Stress Test",
            stressTestDesc: "Test model robustness using extreme parameter values.",
            stressTestPreset_worst: "Worst Case",
            stressTestPreset_best: "Best Case",
            stressTestPreset_booleans: "All Booleans Active",
            stressCurrentScenario: "Current Scenario",
            stressCurrentValue: "Current Value",
            stressExtremeValue: "Extreme Value",
            stressCurrentScore: "Current Score",
            stressExtremeScore: "Stress Score",
            stressScoreDiff: "Change",
            parameter: "Parameter Management",
            parameterDesc: "Add or modify risk parameters.",
            // Common
            cancel: "Cancel",
            save: "Save",
            add: "Add",
            actions: "Actions",
            description: "Description",
            // Risk levels
            riskLow: "Low",
            riskMedium: "Medium",
            riskHigh: "High",
            riskVeryHigh: "Very High",
            riskUnacceptable: "Unacceptable",
            // ModelBuilder
            noModels: "There are currently no existing models",
            addModel: "Add Model",
            options: "Options",
            models: "Models",
            addNewModel: "Add New Model",
            selectModelHeader: "Select Model",
            modelManagement: "Model Management",
            noParameters: "There are no parameters in this model",
            addParameter: "Add Parameter",
            riskClassThresholds: "Risk Class Thresholds",
            enterModelName: "Enter model name",
            modelNameRequired: "Model name is required.",
            modelNameExists: "A model with this name already exists.",
            selectOrCreateModel: "Please select or create a model first",
            saveModel: "Save Model",
            saveModelVersion: "Save Model Version",
            selectVersionType: "Select version update type:",
            firstVersion: "First Version",
            firstVersionDescription: "This will create the first saved version of the model.",
            minorUpdate: "Minor Update",
            majorUpdate: "Major Update",
            savedAs: "Model saved as {{version}}",
            confirmDeleteParameter: "Are you sure you want to remove this parameter?",
            confirmDeleteParameterInUse: "Warning: this parameter is used in the following models: {{models}}. Removing it may break those models. Are you sure you want to continue?",
            deleteModel: "Delete Model",
            cloneModel: "Clone Model",
            exportModel: "Export Model (JSON)",
            exportModelDocPDF: "Export Model Documentation (PDF)",
            importModel: "Import Model (JSON)",
            importModelSuccess: "Model \"{{name}}\" imported successfully",
            importModelError: "Failed to import model: invalid format",
            importModelDuplicate: "A model named \"{{name}}\" already exists. Import cancelled.",
            copyOf: "Copy of {{name}}",
            confirmDeleteModel: "Are you sure you want to delete the model \"{{name}}\"? This action cannot be undone.",
            confirmDeleteModelTitle: "Delete Model",
            newVersion: "New version",
            newVersionTitle: "Create new version",
            newVersionDescription: "Create a new version of this approved model. Changes will only take effect once the new version is approved through the governance workflow.",
            newVersionConfirm: "Create new version",
            newVersionApprovalReset: "Approval is reset to 'draft'. The current approved version remains in production until the new version is approved.",
            newVersionPreview: "New version will be: {{version}}",
            minorUpdateHint: "Small adjustments to parameters, thresholds or documentation.",
            majorUpdateHint: "Significant structural changes (new parameters, altered risk logic).",
            viewVersionHistory: "Version history",
            versionHistoryTitle: "Version history – {{name}}",
            versionHistoryDescription: "All versions of this model, including archived approvals. The production version is marked below.",
            versionHistoryVersion: "Version",
            versionHistoryStatus: "Status",
            versionHistoryApprovedBy: "Approved by",
            versionHistoryApprovedAt: "Approved at",
            versionHistoryActive: "Production",
            versionHistoryCurrent: "current",
            versionHistoryProduction: "In production",
            activeProductionVersion: "Version {{version}} is active in production.",
            activeVersionWhileEditing: "Version {{active}} is active in production. You are editing draft {{draft}}.",
            versionHistory: "Version History",
            version: "Version",
            date: "Date",
            updatedBy: "Updated By",
            draft: "draft",
            // Save interaction
            unsavedChanges: "Unsaved changes",
            changesPreview: "Changes to be saved:",
            parametersAdded: "{{count}} parameter(s) added",
            parametersRemoved: "{{count}} parameter(s) removed",
            parametersModified: "{{count}} parameter(s) modified",
            configurationChanged: "Model configuration changed",
            noChangesDetected: "No changes detected",
            changes: "Changes",
            viewDiff: "Click to view changes",
            // Version diff
            versionDiffTitle: "Changes in {{version}}",
            diffNoData: "No change data available for this version (saved before change tracking was enabled).",
            diffInitialVersion: "Initial version",
            diffInitialBadge: "Initial",
            diffNoChangeBadge: "No changes",
            diffChangeBadge: "{{count}} change(s)",
            diffNoChanges: "No changes recorded for this version.",
            diffParametersAdded: "Parameters added",
            diffParametersRemoved: "Parameters removed",
            diffParametersModified: "Parameters modified",
            diffConfigChanged: "Configuration changes",
            diffFieldType: "Type",
            diffFieldRisks: "Risks",
            diffFieldRiskType: "Risk type",
            diffFieldWeight: "Weight",
            diffFieldThresholds: "Thresholds",
            diffFieldBaseScore: "Base score",
            diffFieldLowestRiskCategory: "Lowest risk category",
            diffFieldRiskThresholds: "Risk thresholds",
            diffFieldCddConfig: "CDD configuration",
            diffFieldEddTriggers: "EDD triggers",
            diffFieldRiskLevelConfig: "Risk level names",
            close: "Close",
            // Simulation
            noSavedModels: "No saved models available. Create and save a model in the Model Builder tab first.",
            selectModelPrompt: "Select a model from the dropdown above to start the simulation.",
            parameterCol: "Parameter",
            type: "Type",
            value: "Value",
            score: "Score",
            riskType: "Risk Type",
            riskLevelIndicator: "Risk Level Indicator",
            dynamic: "Dynamic",
            notApplicable: "N/A",
            // Scenarios
            scenarios: "Scenarios",
            saveScenario: "Save Scenario",
            scenarioName: "Scenario name",
            scenarioNameRequired: "Scenario name is required.",
            savedScenarios: "Saved Scenarios",
            noScenarios: "No saved scenarios yet.",
            loadScenario: "Load",
            deleteScenario: "Delete",
            confirmDeleteScenario: "Are you sure you want to delete the scenario \"{{name}}\"?",
            scenarioSaved: "Scenario \"{{name}}\" saved.",
            saveAsTestCase: "Save as Test Case",
            savedTestCases: "Saved Test Cases",
            testCaseSaved: "Test case saved.",
            confirmDeleteTestCase: "Are you sure you want to delete this test case?",
            testCaseDate: "Date",
            modelVersion: "Model Version",
            totalScore: "Total Score",
            // ModelConfiguration
            modelConfiguration: "Model Configuration",
            lowestRiskCategory: "Lowest Risk Category",
            baseScore: "Base Score",
            lowToNormal: "Low → Normal Risk",
            normalToHigh: "Normal → High Risk",
            highToVeryHigh: "High → Very High Risk",
            veryHighToUnacceptable: "Very High → Unacceptable",
            // ModelSelector
            selectRiskModel: "Select Risk Model",
            chooseAModel: "Choose a model",
            // ParameterForm
            addNewParameter: "Add New Parameter",
            editParameter: "Edit Parameter",
            parameterNameRequired: "Parameter name is required.",
            typeRequired: "Please select at least one parameter type.",
            parameterName: "Parameter Name:",
            parameterType: "Type:",
            parameterFormBasicInfo: "Basic Information",
            parameterFormTypeSection: "Parameter Type",
            parameterNamePlaceholder: "e.g. Customer Category",
            parameterDescriptionPlaceholder: "e.g. Classifies the customer by risk category",
            bwraCategoriesLabel: "BWRA Risk Coverage:",
            bwraCategoriesHelp: "Which BWRA risk categories this parameter contributes to",
            bwraCategoriesNone: "No BWRA risk coverage selected",
            declaredRisksLabel: "Addressed Risks:",
            declaredRisksHelp: "Which specific BWRA risks this parameter contributes to addressing",
            noRisksRegistered: "No risks registered in BWRA",
            dbFieldColumn: "DB Field",
            boolean: "Boolean",
            booleanDescription: "Mapped from a Yes/No or True/False column in the database",
            numeric: "Numeric",
            numericDescription: "Mapped from a numeric value column in the database",
            dbValueTrue: "Database value TRUE",
            dbValueFalse: "Database value FALSE",
            dbValue: "Database value",
            dbValueTruePlaceholder: "e.g. 1, Y, true",
            dbValueFalsePlaceholder: "e.g. 0, N, false",
            dbValueNumericPlaceholder: "e.g. score, 100",
            dbValueTrueHelp: "The value stored in the database column when this is TRUE",
            dbValueFalseHelp: "The value stored in the database column when this is FALSE",
            dbValueNumericHelp: "The database column name or reference value for this numeric parameter",
            importParametersTitle: "Import Parameters",
            // ParameterManagement page header
            parameterLibraryTitle: "Parameter Library",
            parameterLibraryDescription: "Define parameters here that can then be used in the Model Builder to construct customer risk scoring models.",
            searchParametersPlaceholder: "Search by name or description...",
            noParametersMatchSearch: "No parameters match your search.",
            // Parameter tags (CUST-059)
            parameterTagsLabel: "Tags",
            parameterTagsHelp: "Click a tag to select it. Selected tags (shown with ✓) appear in the parameter list and enable filtering.",
            filterAll: "All",
            tagsColumn: "Tags",
            parameterTagKYC: "KYC",
            parameterTagTransaction: "Transaction",
            parameterTagJurisdiction: "Jurisdiction",
            parameterTagBehavior: "Behavior",
            parameterTagPEP: "PEP",
            parameterTagScreening: "Screening",
            // ParameterList
            label: "Label",
            noExistingParameters: "There are currently no existing parameters",
            // ParameterPopup
            selectParameter: "Select parameter",
            selectParameterType: "Select Parameter Type",
            selectType: "Select type",
            selectRiskType: "Select risk type",
            parameterTypeRequired: "Please select a parameter type",
            riskTypeRequired: "Please select a risk type for Boolean parameters",
            weightInvalid: "Weight must be a positive number (1–100)",
            normalRisk: "Normal risk",
            highRisk: "High Risk",
            veryHighRisk: "Very High Risk",
            weight: "Weight",
            enterWeight: "Enter weight",
            normalThreshold: "Normal threshold",
            highThreshold: "High threshold",
            veryHighThreshold: "Very High threshold",
            unacceptableThreshold: "Unacceptable threshold",
            associatedRisks: "Associated Risks",
            parameterAlreadyExists: "This parameter already exists in the model",
            parameterAlreadyAdded: "Added",
            // Risk categories (stored as enum, displayed translated)
            regulatoryRisk: "Regulatory Risk",
            financialCrimeRisk: "Financial Crime Risk",
            reputationalRisk: "Reputational Risk",
            operationalRisk: "Operational Risk",
            creditRisk: "Credit Risk",
            // Type display
            numerical: "Numerical",
            // RiskGauge
            scoreLabel: "Score: ",
            riskClass: "Risk Class: ",
            cddLevel: "CDD Level: ",
            cddSDD: "SDD",
            cddCDD: "CDD",
            cddEDD: "EDD",
            cddSDDFull: "Simplified Due Diligence",
            cddCDDFull: "Customer Due Diligence",
            cddEDDFull: "Enhanced Due Diligence",
            // CDD Configuration
            cddClassification: "CDD Classification",
            cddEnabled: "Enable CDD classification",
            eddStartsAt: "EDD starts at risk class",
            // EDD Triggers
            eddTriggers: "EDD Triggers",
            eddTriggersDescription: "Mandatory triggers that automatically escalate CDD level to EDD regardless of score",
            eddTriggerPep: "PEP (Politically Exposed Person)",
            eddTriggerPepShort: "PEP",
            eddTriggerHighRiskCountry: "High-risk country",
            eddTriggerHighRiskCountryShort: "High-risk country",
            eddTriggerComplexOwnership: "Complex ownership structure",
            eddTriggerComplexOwnershipShort: "Complex ownership",
            eddTriggerActive: "Active",
            eddOverrideWarning: "EDD triggered by: {{triggers}}",
            eddOverrideLabel: "EDD (Override)",
            // Risk Level Configuration
            riskLevelConfig: "Risk Level Configuration",
            includeUnacceptable: "Include 'Unacceptable' as a separate level",
            riskLevelNames: "Custom Level Names",
            riskLevelNamePlaceholder: "Custom name (optional)",
            // Model Documentation Fields
            modelDocumentation: "Model Documentation",
            businessPurpose: "Business Purpose",
            businessPurposePlaceholder: "Describe the business rationale and regulatory purpose of this model...",
            lastReviewedBy: "Last Reviewed By",
            lastReviewedByPlaceholder: "Name of reviewer",
            modelReviewDate: "Review Date",
            // ModelList
            existingModels: "Existing Models",
            // Risks column
            risks: "Risks",
            // Validation
            validation: "Validation",
            validationDesc: "Validate your model against production data.",
            dataValidation: {
                title: "Data Validation",
                description: "Upload anonymized production data to validate how well your customer risk model captures reality.",
                selectModel: "Select Model",
                selectModelHint: "Choose a saved model to validate against uploaded data.",
                uploadCSV: "Upload CSV",
                uploadHint: "CSV, TSV, or TXT file with customer data and expected risk levels",
                datasets: "Uploaded Datasets",
                rowCount: "{{count}} rows",
                colCount: "{{count}} columns",
                deleteDataset: "Delete dataset",
                confirmDeleteDataset: "Are you sure you want to delete the dataset \"{{name}}\"? This action cannot be undone.",
                columnMapping: "Column Mapping",
                columnMappingHint: "Map your CSV columns to the model's parameters and expected risk level.",
                expectedRiskColumn: "Expected risk level",
                expectedRiskScoreColumn: "Expected risk score (optional)",
                noScoreColumn: "-- None (category only) --",
                parameterColumns: "Parameter Columns",
                selectColumn: "-- Select column --",
                columnPreviewLabel: "Sample values",
                runValidation: "Run Validation",
                running: "Running...",
                results: "Validation Results",
                ranAt: "Validation ran at {{time}}",
                accuracy: "Accuracy",
                totalRecords: "Records",
                falsePositives: "False Positives",
                falseNegatives: "False Negatives",
                scoreDiscrepancies: "Score discrepancies",
                scoreDiscrepancyTooltip: "Customers in the correct risk category but with external score differing from the model by more than {{pct}}% (min {{abs}}).",
                externalScore: "External Score",
                scoreDiff: "Diff",
                recommendationGood: "The model captures {{pct}}% of cases correctly. Good alignment with production data.",
                recommendationModerate: "The model captures {{pct}}% of cases correctly. Consider reviewing misclassified cases and adjusting thresholds.",
                recommendationPoor: "The model captures only {{pct}}% of cases correctly. Significant calibration needed — review risk thresholds and parameter weights.",
                distributionComparison: "Risk Level Distribution",
                riskLevel: "Risk Level",
                expected: "Expected (Data)",
                predicted: "Predicted (Model)",
                difference: "Difference",
                confusionMatrix: "Confusion Matrix",
                confusionLegend: "Rows = actual risk level from data. Columns = predicted by model. Diagonal = correct predictions.",
                showDetails: "Show details ({{count}} records)",
                score: "Score",
                match: "Match",
                showingFirst: "Showing first {{count}} of {{total}} records",
                showingXofY: "Showing {{shown}} of {{total}} records",
                showMoreRows: "Show more ({{count}} remaining)",
                exportPDF: "Export PDF",
                exportExcel: "Export Excel",
                exportingReport: "Generating report...",
                export: {
                    reportTitle: "CustomerRisk Validation Report",
                    modelName: "Model",
                    sheetParamMapping: "Parameter Mapping",
                    paramName: "Parameter",
                    mappedColumn: "Mapped Column",
                },
                inputMode: "Validation Source",
                inputModeCSV: "CSV Upload",
                inputModeDataSource: "Data Source",
                selectDataSource: "Select data source",
                noDataSources: "No data sources configured. Add one in Admin → Data Sources.",
                selectTable: "Select table",
                loadingTables: "Loading tables...",
                loadingColumns: "Loading columns...",
                filterClause: "Filter (optional)",
                filterClauseHint: "Filter rows: key=value pairs separated by comma (e.g. status=active,country=SE)",
                dataSourceError: "Error loading data source: {{error}}",
                noRowsFromSource: "No data returned from source. Try a different table or adjust the filter.",
            },
            // Weight Distribution Chart (CUST-062)
            weightDistributionTitle: "Parameter Weight Distribution",
            weightDistributionSubtitle: "Relative share of each parameter's weight in the model",
            noWeightedParams: "No parameters with weight defined",
            outlierHigh: "High outlier – this parameter has significantly more weight than average",
            outlierLow: "Low outlier – this parameter has significantly less weight than average",
            // Risk Score Histogram (CUST-063)
            riskScoreHistogramTitle: "Risk Score Distribution",
            riskScoreHistogramSubtitle: "Distribution of saved scenarios by risk level",
            riskScoreHistogramNoScenarios: "Save at least one scenario to see the score distribution",
            riskScoreHistogramInsightBalanced: "Distribution appears balanced",
            riskScoreHistogramInsightOverLabel: "Potential over-classification",
            riskScoreHistogramInsightOverDesc: "More than 60% of scenarios fall in elevated risk levels. Consider whether the model thresholds are correctly calibrated.",
            riskScoreHistogramInsightUnderLabel: "Potential under-classification",
            riskScoreHistogramInsightUnderDesc: "More than 60% of scenarios fall in lower risk levels. Consider whether the model captures all relevant risk indicators.",
            // Segment Analysis (CUST-066)
            segmentAnalysisTitle: "Segment Analysis",
            segmentAnalysisSubtitle: "Saved scenarios grouped by risk segment, showing average scores and parameter values per group.",
            segmentAnalysisNoScenarios: "Save at least one scenario to see the segment analysis",
            segmentAnalysisScenarios: "scenarios",
            segmentAnalysisAvgScore: "Avg. score",
            segmentAnalysisAvgValue: "Average value",
            // Threshold Optimization (CUST-068)
            thresholdOptimTitle: "Threshold Optimization",
            thresholdOptimDesc: "Analyze saved scenarios to find threshold values that create the clearest separation between risk groups, based on natural breaks in the score distribution.",
            thresholdOptimMinData: "At least {{min}} saved scenarios are required to run threshold optimization.",
            thresholdOptimRun: "Optimize Thresholds",
            thresholdOptimResults: "Optimization Results",
            thresholdOptimColLevel: "Risk Level",
            thresholdOptimColCurrent: "Current (≥)",
            thresholdOptimColSuggested: "Suggested (≥)",
            thresholdOptimColDiff: "Change",
            thresholdOptimDistTitle: "Scenario Distribution: Current vs. Suggested",
            thresholdOptimDistCurrent: "Current",
            thresholdOptimDistSuggested: "Suggested",
            thresholdOptimInsightReclassified: "{{count}} of {{total}} scenarios would be reclassified with the suggested thresholds.",
            thresholdOptimInsightNoChange: "Current thresholds already align with natural score breaks — no reclassification needed.",
            thresholdOptimInsightBoundary: "Scenarios near a boundary: {{current}} → {{suggested}}",
            // Parameter Dependency View (CUST-070)
            paramDep: {
                title: "Dependency View",
                subtitle: "Visualizes how each model parameter influences the final risk score. Line thickness reflects parameter weight.",
                networkTitle: "Parameter Network",
                centerLabel: "Risk Score",
                sensitivityTitle: "Sensitivity Analysis",
                sensitivityDesc: "Shows the score impact for each Boolean parameter when active. Higher weight = greater influence on the final score.",
                colParameter: "Parameter",
                colRiskType: "Risk Type",
                colWeight: "Weight",
                colScoreImpact: "Score (active)",
                colPctOfBase: "% of base score",
                legendLabel: "Type",
            },
            // Approval Workflow (CUST-061)
            approval: {
                panelTitle: "Model Approval",
                statusDraft: "Draft",
                statusSubmitted: "In Review",
                statusApproved: "Approved",
                statusRejected: "Rejected",
                submitForReview: "Submit for Review",
                submitForReviewHint: "Mark this model as ready for review. The model will be locked for editing until a decision is made.",
                approveModel: "Approve Model",
                rejectModel: "Reject",
                approveComment: "Approval comment (optional)",
                rejectComment: "Reason for rejection (required)",
                rejectCommentRequired: "Please provide a reason for rejection.",
                approvedBy: "Approved by",
                approvedAt: "Approved on",
                approvalHistory: "Approval History",
                noHistory: "No approval actions yet.",
                historyFrom: "From",
                historyTo: "To",
                historyBy: "By",
                historyAt: "Date",
                historyComment: "Comment",
                lockedMessage: "This model is locked for editing while under review.",
                approvedLockedMessage: "This model is approved and locked. Clone it to make changes.",
                approvedLockedMessageVersioned: "This model is approved and locked. Create a new version to make changes — the current version stays in production until the new one is approved.",
                toastSubmitted: "Model submitted for review.",
                toastApproved: "Model approved.",
                toastRejected: "Model returned to draft.",
                noApprovedModels: "No approved models available. A model must be approved before it can be used here.",
                onlyApprovedInfo: "Only approved models can be used in Simulation and Validation.",
                confirmSubmit: "Submit \"{{name}}\" for review? The model will be locked until approved or rejected.",
            },
            // Model Comparison
            comparison: {
                title: "Model Comparison",
                comparisonTab: "Compare Models",
                modelA: "Model A",
                modelB: "Model B",
                vs: "vs.",
                selectBothModels: "Select two models above to compare their simulation results side by side.",
                diff: "Diff (A − B)",
            },
            // Field Mapping tab
            fieldMapping: {
                tab: "Mapping",
                title: "Field Mapping",
                subtitle: "Map model parameters to columns in an external data source.",
                selectModelPrompt: "Select a model to configure its data source mapping.",
                dataSource: "Data Source",
                selectDataSource: "— Select data source —",
                table: "Table",
                selectTable: "— Select table —",
                tableNamePlaceholder: "Enter table name",
                rowIdColumn: "Row ID Column",
                selectRowIdColumn: "— Select column —",
                rowIdColumnPlaceholder: "Enter column name",
                parameterMappings: "Parameter Mappings",
                parameterCol: "Parameter",
                columnCol: "Source Column",
                transformCol: "Transform",
                selectColumn: "— Select column —",
                columnPlaceholder: "Enter column name",
                transform_none: "None",
                transform_uppercase: "Uppercase",
                transform_lowercase: "Lowercase",
                transform_trim: "Trim",
                transform_number: "To Number",
                transform_boolean: "To Boolean",
                transform_date: "To Date",
                previewData: "Preview Data (5 rows)",
                saveMapping: "Save Mapping",
                saving: "Saving…",
                statusComplete: "Mapping complete — all parameters mapped",
                statusIncomplete: "{{count}} parameter(s) without column mapping",
                savedSuccess: "Mapping saved successfully",
                saveError: "Failed to save mapping: {{error}}",
                errorNoDataSource: "Please select a data source",
                errorNoTable: "Please enter a table name",
                errorNoRowId: "Please specify the row ID column",
                previewTitle: "Data Preview",
                previewSubtitle: "Sample rows from {{table}}",
                previewNoData: "No preview available — connect to a live data source to fetch rows.",
                noDataSources: "No data sources configured. Add one in Admin → Data Sources.",
                incompleteBanner: "Mapping incomplete — {{count}} parameter(s) missing",
                newParamBadge: "Missing mapping",
                markIntentionallyPartial: "Mark as complete despite missing mappings",
                markIntentionallyPartialDone: "Marked as intentionally partial",
                intentionallyPartialNote: "This mapping has been marked as intentionally partial.",
                validationBlockedByMapping: "Field mapping is incomplete for this model. Go to the Mapping tab and map all parameters before running validation against a data source.",
                validationBlockedLink: "Go to Mapping tab",
            },
            // Score History (CUST-080)
            scoreHistoryTitle: "Risk Score History",
            scoreHistorySubtitle: "Score snapshots over time for the selected model and scenario.",
            scoreHistoryRecord: "Record Snapshot",
            scoreHistoryReasonPlaceholder: "Reason for snapshot (optional)",
            scoreHistoryReasonManual: "Manual snapshot",
            scoreHistoryReasonTestCase: "Test case saved",
            scoreHistoryEmpty: "No snapshots recorded yet. Use 'Record Snapshot' to capture the current score.",
            scoreHistoryClear: "Clear History",
            scoreHistoryConfirmClear: "Clear all score history for this model? This cannot be undone.",
            scoreHistorySnapshotSaved: "Score snapshot recorded.",
            scoreHistoryColDate: "Date",
            scoreHistoryColRiskClass: "Risk Class",
            scoreHistoryColReason: "Reason",
            scoreHistoryMore: "… and {{count}} older entries",
            correlationAnalysis: {
                tab: "Correlation",
                title: "Parameter Correlation Analysis",
                subtitle: "Pearson correlation between parameters based on saved scenarios and test cases. Parameters with correlation > 0.8 are potentially redundant.",
                computeBtn: "Compute Correlations",
                computing: "Computing...",
                noNumericalParams: "No numerical or boolean parameters found in this model.",
                insufficientDataPoints: "Insufficient data — at least 3 scenarios or test cases are required to compute correlations.",
                dataPoints: "{{count}} data points available (scenarios + test cases)",
                matrixTitle: "Correlation Matrix",
                warningHigh: "High correlation",
                legendTitle: "Correlation scale",
                legendNone: "No correlation",
                warningList: "Highly Correlated Parameter Pairs (> 0.8)",
                warningListEmpty: "No pairs with correlation above 0.8 found.",
                warningPairLabel: "Correlation",
                warningPairDataPoints: "shared data points",
                paramA: "Parameter A",
                paramB: "Parameter B",
                insufficientData: "Insufficient data (< 3 shared data points)",
            },
        },
        bwra: {
            overview: "Overview",
            riskRegister: "Risk Register",
            controlsTab: "Controls",
            overviewTitle: "Business-Wide Risk Assessment Overview",
            overviewSubtitle: "Summary of the organization's AML/CFT risk posture based on identified risks, controls, and model connections.",
            introBannerTitle: "Start here: BWRA is the foundation of your AML work",
            introBannerBody: "The Business-Wide Risk Assessment (BWRA) is where all AML compliance work begins. You identify your organization's gross risks, define controls, and set your risk appetite here. The other modules — GeoRisk, Customer Risk, and Transaction Monitoring — are analytical models that provide data to support the risks you manage in BWRA.",
            introBannerStep1: "Identify and assess risks in the Risk Register",
            introBannerStep2: "Map risks to analytical models (GeoRisk, Customer Risk, TM)",
            introBannerStep3: "Track residual risk after controls are applied",
            introBannerDismiss: "Got it",
            identifiedRisks: "Identified Risks",
            controls: "Controls",
            mitigated: "Mitigated",
            linkedToModels: "Linked to Models",
            riskPosture: "Risk Posture",
            avgGrossRisk: "Avg Gross Risk",
            avgResidualRisk: "Avg Residual Risk",
            byCategory: "Risk by Category",
            statusBreakdown: "Status Breakdown",
            statusIdentified: "Identified",
            statusAssessed: "Assessed",
            statusMitigated: "Mitigated",
            riskRegisterTitle: "Risk Register",
            importRisksTitle: "Import Risks",
            category: "Category",
            allCategories: "All Categories",
            status: "Status",
            allStatuses: "All Statuses",
            filterUnmitigated: "Unmitigated only",
            filterMyRisks: "My Risks",
            ownerLabel: "Owner",
            ownerNone: "(No owner)",
            riskOwner: "Risk Owner",
            searchRisksPlaceholder: "Search risks...",
            riskId: "ID",
            riskName: "Risk",
            grossRisk: "Gross Risk",
            residualRisk: "Residual Risk",
            reduction: "Reduction",
            description: "Description",
            likelihood: "Likelihood",
            impact: "Impact",
            controlsMeasures: "Controls & Measures",
            noControls: "No controls defined. This risk is unmitigated.",
            noRisksFound: "No risks match the current filters.",
            controlsTitle: "Controls & Model Connections",
            controlsSubtitle: "Overview of all controls, their effectiveness, and connections to risk models in GeoRisk and CustomerRisk modules.",
            controlsListTab: "Controls List",
            effectivenessOverviewTab: "Effectiveness Overview",
            effDashboard: {
                title: "Control Effectiveness Dashboard",
                subtitle: "Distribution of control evaluation results by risk category.",
                summaryByCategory: "Summary by Category",
                allControlsDetail: "All Controls",
                category: "Risk Category",
                notEvaluated: "Evaluation missing",
                totalControls: "Total",
                controlId: "ID",
                controlName: "Control",
                riskName: "Risk",
                evalStatus: "Evaluation",
                reviewPeriod: "Review Period",
                distribution: "Distribution",
                noControls: "No controls defined. Add controls to risks in the Risk Register.",
            },
            modelConnections: "Model Connections",
            modelConnectionsDesc: "Controls linked to risk models in other modules provide automated, data-driven risk mitigation.",
            noModelConnections: "No model connections configured yet.",
            autoModelControls: "Auto-generated Model Controls",
            autoModelControlsDesc: "Controls derived automatically from GeoRisk indexes and CustomerRisk parameters that declare coverage of specific risks. These are read-only and update when model configuration changes.",
            autoControlBadge: "Auto: Model Control",
            noAutoControls: "No auto-generated controls. Configure which BWRA risks GeoRisk indexes and CustomerRisk parameters address (in the Risk Management configuration).",
            allControls: "All Controls",
            controlId: "ID",
            controlName: "Control",
            type: "Type",
            addressesRisk: "Addresses Risk",
            effectiveness: "Effectiveness",
            linkedModule: "Linked Module",
            unmitigatedRisks: "Unmitigated Risks",
            noControlsLinked: "No controls linked",
            goToModule: "Go to module",
            modelMapping: "Model Mapping",
            mapping: {
                title: "Model-Risk Mapping",
                subtitle: "The red thread: how risk models and controls connect to identified risks, showing the flow from gross risk through mitigating controls to residual risk.",
                geoRiskDesc: "Country risk scoring model with multiple indices and 4 calculation types.",
                customerRiskDesc: "Customer risk classification based on configurable parameters and scoring models.",
                txMonDesc: "Transaction monitoring rules that declare coverage of BWRA risks.",
                activeIndices: "active indices",
                configuredModels: "configured models",
                parameters: "parameters",
                activeRules: "active rules",
                mappedRules: "mapped to risks",
                traceabilityMatrix: "Traceability Matrix",
                traceabilityDesc: "Shows which risk categories are covered by which control types. Gaps indicate risks without any mitigating controls.",
                risks: "Risks",
                gaps: "Gaps",
                riskFlows: "Risk Flow Diagrams",
                riskFlowsDesc: "Visual flow for each risk: Gross Risk → Controls/Models → Residual Risk. The 'red thread' showing how each risk is assessed and mitigated.",
                modelLinked: "Model-linked risks",
                gapsIdentified: "Coverage gaps",
                noControls: "No controls",
                moduleHealth: "Module Validation Status",
                moduleHealthDesc: "Shows the validation status of each linked module. Higher scores mean more complete model data backing the risk controls.",
                moduleLevel: "Module-level (all)",
                notModeled: "Not model-validated",
                notModeledStatus: "Manually controlled",
                notModeledDetail: "No RMS module linked — effectiveness based on manual assessment",
                selfAssessed: "Self-Assessed Controls",
                selfAssessedDesc: "These controls (PROD/TX/CHAN risk categories) are managed via system or manual controls without a linked RMS model. Residual risk is based on control effectiveness only.",
                modelCoverage: "Risk Coverage Declared by Models",
                modelCoverageDesc: "GeoRisk indexes, CustomerRisk parameters and TxMon rules that declare coverage of BWRA risk categories — the two-way connection showing which models cover which risks.",
                coverageGeoRisk: "GeoRisk Indexes Covering This Risk",
                coverageCustomerRisk: "CustomerRisk Parameters Covering This Risk",
                coverageTxMon: "TxMon Rules Covering This Risk",
                noCoverageConfigured: "No coverage declared in this module yet.",
                coverageIndexes: "Indexes",
                coverageParameters: "Parameters",
                coverageRules: "Rules",
                declareCoverage: "Configure in",
                autoGenerated: "auto",
            },
            calculatedResidual: {
                title: "Calculated Residual Risk",
                basedOn: "Based on control effectiveness and model validation",
                calculatedScore: "Calculated Score",
                manualScore: "Manual Score",
                vs: "vs",
                difference: "Difference",
                lowerThanManual: "lower than manual",
                higherThanManual: "higher than manual",
                sameAsManual: "same as manual",
                controlBreakdown: "Control Breakdown",
                baseReduction: "Base reduction",
                actualReduction: "Actual reduction",
                modelStatus: "Model status",
                noControls: "No controls to calculate from",
                validated: "Validated",
                partial: "Partial data",
                noData: "No data",
            },
            addRisk: "Add Risk",
            editRisk: "Edit Risk",
            deleteRisk: "Archive Risk",
            deleteRiskConfirm: "Archive this risk? It will be hidden from the active register but preserved for audit purposes.",
            archiveRisk: "Archive Risk",
            archiveRiskConfirm: "Archive this risk? It will be hidden from the active register but preserved for audit purposes.",
            restoreRisk: "Restore Risk",
            restoreRiskConfirm: "Restore this risk to the active register?",
            permanentDeleteRisk: "Delete Permanently",
            permanentDeleteRiskConfirm: "PERMANENTLY DELETE this risk and all its controls? This action cannot be undone and violates AMLR audit trail requirements. Type 'DELETE' to confirm.",
            showArchived: "Show archived",
            hideArchived: "Hide archived",
            archivedBadge: "Archived",
            archivedAt: "Archived",
            acceptRisk: "Accept Risk",
            revokeAcceptance: "Revoke Acceptance",
            acceptedBadge: "ACCEPTED",
            riskAcceptanceModal: {
                title: "Accept Risk",
                subtitle: "This risk will be formally accepted. Document the justification below.",
                motivationLabel: "Justification",
                motivationPlaceholder: "Describe why this risk is accepted and what compensating controls exist...",
                acceptedByLabel: "Accepted by",
                acceptedByPlaceholder: "Name of responsible person",
                acceptedAtLabel: "Date",
                save: "Accept Risk",
                cancel: "Cancel",
                revokeConfirm: "Are you sure you want to revoke the acceptance of this risk?",
                motivationRequired: "Justification is required.",
                acceptedByRequired: "Accepted by is required.",
                expiresAtLabel: "Expires on",
            },
            expirationAlert: {
                sectionTitle: "Expiring Risk Acceptances",
                expiresSoon: "Expires in {days} day(s)",
                expiresInDays: "{days}d left",
                expired: "EXPIRED",
                reassessmentRequired: "Reassessment required",
                viewInRegister: "View in Risk Register",
                dashboardAlert: "{count} acceptance(s) expiring soon",
            },
            addControl: "Add Control",
            editControl: "Edit Control",
            deleteControl: "Delete Control",
            deleteControlConfirm: "Are you sure you want to delete this control?",
            importTestResults: "Import Test Results",
            viewTestResults: "View Test Results ({count})",
            testResultHistory: {
                title: "Test Result History",
                date: "Date",
                status: "Status",
                comment: "Comment",
                pass: "Pass",
                fail: "Fail",
                noResults: "No test results imported yet.",
                importedAt: "Imported",
            },
            importTestResultsModal: {
                title: "Import Test Results",
                description: "Upload a CSV or JSON file with automated control test results.",
                fileLabel: "File (CSV or JSON)",
                csvFormat: "CSV format: date, status (pass/fail), comment",
                jsonFormat: "JSON format: [{\"date\": \"...\", \"status\": \"pass\", \"comment\": \"...\"}]",
                preview: "Preview ({count} records)",
                date: "Date",
                status: "Status",
                comment: "Comment",
                import: "Import",
                cancel: "Cancel",
                parseError: "Could not parse file. Check the format.",
                noResults: "No records found in file.",
                appendMode: "Append to existing results",
                replaceMode: "Replace existing results",
                previewMore: "+{{count}} more",
            },
            addAction: "Add Action",
            editAction: "Edit Action",
            deleteAction: "Delete Action",
            deleteActionConfirm: "Are you sure you want to delete this action?",
            actionSection: {
                title: "Actions",
                noActions: "No actions defined for this risk.",
                description: "Description",
                descriptionPlaceholder: "Describe the action to be taken...",
                responsible: "Responsible",
                responsiblePlaceholder: "Name of responsible person",
                deadline: "Deadline",
                priority: "Priority",
                linkedControl: "Linked Control",
                noControlLink: "No specific control",
                count: "{{count}} actions",
                priorities: {
                    HIGH: "High",
                    MEDIUM: "Medium",
                    LOW: "Low",
                },
                statuses: {
                    NOT_STARTED: "Not Started",
                    IN_PROGRESS: "In Progress",
                    COMPLETED: "Completed",
                    VERIFIED: "Verified",
                },
            },
            selectRisk: "Select Risk",
            actions: "Actions",
            save: "Save",
            cancel: "Cancel",
            delete: "Delete",
            riskNameLabel: "Risk Name",
            residualExceedsGross: "Residual risk cannot exceed gross risk",
            controlLinkedModelRequired: "A specific model must be selected when a module is linked",
            categoryLabel: "Category",
            descriptionLabel: "Description",
            likelihoodLabel: "Likelihood",
            impactLabel: "Impact",
            statusLabel: "Status",
            ownerSelectLabel: "Risk Owner",
            ownerSelectPlaceholder: "Select owner...",
            controlNameLabel: "Control Name",
            controlType: "Control Type",
            controlDescription: "Description",
            controlEffectiveness: "Effectiveness",
            effectivenessFormat: "{{level}}/5",
            controlLinkedModule: "Linked Module",
            controlLinkedModel: "Linked Model",
            controlLinkedIndex: "Linked Index",
            allModels: "All models (module-level)",
            allIndexes: "All indexes (module-level)",
            allRulesAndModels: "All rules & models (module-level)",
            controlLinkedRuleOrModel: "Linked Rule or Model",
            txmonRulesGroup: "Rules",
            txmonModelsGroup: "Models",
            none: "None",
            saved: "Changes saved",
            demo: {
                title: "Demo Data",
                description: "This risk register contains sample data for demonstration purposes. You can keep it to explore or start fresh with an empty register.",
                keepData: "Keep Data",
                startFresh: "Start Fresh",
                confirmStartFresh: "Are you sure you want to start fresh? This will permanently delete ALL data and cannot be undone.",
            },
            export: {
                button: "Export to Excel",
                generateReport: "Generate BWRA Report",
                score: "Score",
                level: "Level",
                controlCount: "Controls",
                controlId: "Control ID",
                summaryTitle: "BWRA - Business-Wide Risk Assessment",
                reportTitle: "BWRA Report - Business-Wide Risk Assessment",
                exportDate: "Export Date",
                generatedBy: "Generated By",
                systemName: "RMS",
                riskSummary: "Risk Summary",
                sheetSummary: "Summary",
                sheetRisks: "Risks",
                sheetControls: "Controls",
                sheetScope: "Scope",
                sheetAppetite: "Risk Appetite",
                sheetPolicies: "Policies",
                sheetModelMapping: "Model Mapping",
                sheetAnnex: "Annex I-II-III",
                linkedModel: "Linked Model",
                validationScore: "Validation Score",
                validationStatus: "Validation Status",
                validationDetail: "Validation Detail",
                validationAccuracy: "Validation Accuracy",
                highRiskCapture: "High-Risk Capture",
                noValidationRun: "No validation run",
                validationSummary: "Validation Summary",
                records: "records",
                moduleLevel: "(Module level)",
                noModelLinks: "No controls are linked to risk models.",
                modelMappingTitle: "Model Mapping & Validation Status",
                annexTitle: "AMLR Annex I/II/III per Risk",
                toc: "Table of Contents",
                appetiteStatus: "Status",
                reportConfig: {
                    title: "Report Configuration",
                    subtitle: "Choose format and sections to include in the report",
                    format: "Format",
                    formatPDF: "PDF Document",
                    formatExcel: "Excel Workbook",
                    sections: "Sections",
                    sectionSummary: "Summary",
                    sectionScope: "Scope",
                    sectionAppetite: "Risk Appetite",
                    sectionRisks: "Risk Register",
                    sectionControls: "Controls",
                    sectionModelMapping: "Model Mapping",
                    sectionPolicies: "Policies",
                    sectionAnnex: "Annexes (I/II/III)",
                    sectionCompliance: "Compliance Map",
                    sectionCategoryAnalysis: "Category Deep Analysis",
                    selectAll: "Select all",
                    deselectAll: "Deselect all",
                    generate: "Generate Report",
                    cancel: "Cancel",
                    generating: "Generating...",
                    generateError: "Report generation failed. Please check your data and try again.",
                    pdfPageOf: "Page {{page}} of {{total}}",
                    toc: "Table of Contents",
                    language: "Report Language",
                    languageSwedish: "Swedish",
                    languageEnglish: "English",
                },
            },
            versions: {
                title: "Version History",
                subtitle: "Versions are automatically saved when generating a BWRA report. Compare versions to track changes over time.",
                count: "{{count}} versions",
                noVersions: "No versions saved yet. Generate a BWRA report to create the first version.",
                viewDetails: "View details",
                compareWithPrevious: "Compare with previous version",
                delete: "Delete version",
                risksShort: "risks",
                controlsShort: "controls",
                policiesShort: "policies",
                avgGross: "Avg gross risk",
                avgResidual: "Avg residual risk",
                mitigatedCount: "Mitigated",
                comparisonTitle: "Changes: {{v1}} → {{v2}}",
                noChanges: "No changes detected between these versions.",
                added: "Added",
                removed: "Removed",
                confirmDelete: "Are you sure you want to delete this version? This action cannot be undone.",
                controlAddedTo: "Control added to '{{risk}}': {{name}}",
                controlRemovedFrom: "Control removed from '{{risk}}': {{name}}",
                riskScoreFormat: "L{{l}}/I{{i}}",
                descriptionUpdated: "(text updated)",
                scopeChanged: "Scope — {{field}} changed",
                appetiteChanged: "Risk appetite — {{field}} changed",
                complianceChanged: "Compliance — Article {{articleId}} {{field}}: {{from}} → {{to}}",
                complianceNotesChanged: "Compliance — Article {{articleId}} notes updated",
                fields: {
                    name: "Name",
                    category: "Category",
                    status: "Status",
                    description: "Description",
                    grossRisk: "Gross risk",
                    residualRisk: "Residual risk",
                    effectiveness: "Effectiveness",
                    type: "Type",
                    businessDescription: "Business description",
                    products: "Products",
                    customerCategories: "Customer categories",
                    distributionChannels: "Distribution channels",
                    statement: "Risk appetite statement",
                    thresholds: "Appetite thresholds",
                },
            },
            categories: {
                GEO: "Geographical Risk",
                CUST: "Customer Risk",
                PROD: "Product/Service Risk",
                TX: "Transaction Risk",
                CHAN: "Channel Risk",
            },
            likelihoodLevel1: "Very Low",
            likelihoodLevel2: "Low",
            likelihoodLevel3: "Medium",
            likelihoodLevel4: "High",
            likelihoodLevel5: "Very High",
            impactLevel1: "Negligible",
            impactLevel2: "Minor",
            impactLevel3: "Moderate",
            impactLevel4: "Major",
            impactLevel5: "Severe",
            riskLevels: {
                low: "Low",
                medium: "Medium",
                high: "High",
                veryHigh: "Very High",
            },
            controlTypes: {
                MODEL: "Risk Model",
                SYSTEM: "System Control",
                MANUAL: "Manual Process",
            },
            statusValues: {
                IDENTIFIED: "Identified",
                ASSESSED: "Assessed",
                MITIGATED: "Mitigated",
                ARCHIVED: "Archived",
            },
            score: "Score",
            noControlsShort: "None",
            addresses: "Addresses",
            grossMax: "Gross max",
            residualMax: "Residual max",
            linked: "Linked",
            risksCount: "{{count}} risks",
            riskFilterCount: "{{filtered}} of {{total}} risks",
            moduleNames: {
                GeoRisk: "Geographical Risk",
                CustomerRisk: "Customer Risk",
                TransactionMonitoring: "Transaction Monitoring",
            },
            indexTypes: {
                BOOLEAN: "Boolean",
                SCORING: "Scoring",
                UNKNOWN: "Unknown",
            },
            appetite: {
                tab: "Risk Appetite",
                title: "Risk Appetite Statement",
                subtitle: "Define the organization's risk appetite per risk category. Thresholds determine acceptable, elevated and unacceptable risk levels as required by AMLR.",
                statementTitle: "Risk Appetite Statement",
                statementHelp: "Document the organization's overall risk appetite and tolerance for ML/TF risk. This statement guides the risk assessment process.",
                statementPlaceholder: "Describe the organization's overall risk appetite, risk tolerance levels, and guiding principles for ML/TF risk management...",
                acceptableThreshold: "Acceptable (≤)",
                elevatedThreshold: "Elevated (≤)",
                acceptable: "Acceptable",
                elevated: "Elevated",
                unacceptable: "Unacceptable",
                actualRisk: "Actual risk (max residual)",
                deviationWarning: "{{count}} risk category/categories exceed the defined risk appetite",
                edit: "Edit",
                thresholdRule: "Elevated threshold must exceed Acceptable threshold",
                validationOutOfRange: "Value must be between 1 and 25",
                validationElevatedMustExceedAcceptable: "Elevated must exceed Acceptable",
            },
            radar: {
                title: "Risk Appetite vs Actual Risk",
                subtitle: "Visual comparison of defined risk appetite thresholds and actual residual risk per category.",
                appetiteLabel: "Appetite threshold",
                actualLabel: "Actual residual risk",
                acceptableZone: "Acceptable",
                elevatedZone: "Elevated",
                unacceptableZone: "Unacceptable",
                noDeviations: "All categories within appetite",
                deviations: "{{count}} categories exceed appetite",
                trendTitle: "Trend since last version",
                improved: "Improved",
                worsened: "Worsened",
                unchanged: "Unchanged",
                noData: "No risk data to display",
            },
            annexI: {
                title: "AMLR Annex I – Risk Variables",
                description: "Each risk must confirm that all Annex I risk variable categories have been considered, or provide a motivation for exclusion.",
                indicator: "Annex I: {{count}}/{{total}} addressed",
                indicatorTooltipComplete: "All {{total}} Annex I categories addressed",
                indicatorTooltipMissing: "Missing: {{categories}}",
                considered: "Considered",
                notAddressed: "Not addressed",
                motivationPlaceholder: "Motivation for exclusion...",
                categories: {
                    CUSTOMER: "Customer Risk Factors",
                    PRODUCT: "Product/Service Risk Factors",
                    GEO: "Geographical Risk Factors",
                    CHANNEL: "Channel Risk Factors",
                    TRANSACTION: "Transaction Risk Factors",
                },
                factors: {
                    customerType: "Customer type (natural/legal person, trust)",
                    ownershipStructure: "Ownership structure and beneficial ownership",
                    businessActivity: "Nature and complexity of business activity",
                    assetLevel: "Level of assets or transaction volumes",
                    purposeRelationship: "Purpose and intended nature of relationship",
                    productType: "Type of product or service",
                    productComplexity: "Complexity of product or service",
                    transactionValue: "Value or size of transactions",
                    anonymousTransactions: "Anonymous or pseudonymous transactions",
                    countryResidence: "Country of residence or establishment",
                    destinationCountries: "Transaction destination countries",
                    highRiskJurisdictions: "FATF-listed or high-risk jurisdictions",
                    nonFaceToFace: "Non-face-to-face business relationships",
                    paymentMethods: "Payment methods used",
                    thirdPartyIntroducers: "Third-party introducers or intermediaries",
                    transactionPatterns: "Unusual or complex transaction patterns",
                    transactionSizes: "Transaction sizes relative to expected activity",
                    cashTransactions: "Cash-intensive transactions",
                    crossBorderTransactions: "Cross-border wire transfers",
                },
            },
            annexII: {
                title: "AMLR Annex II – Low Risk Factors",
                description: "Factors indicating potentially lower ML/TF risk. When applicable, simplified due diligence (SDD) may be justified.",
                selected: "selected",
                categories: {
                    CUSTOMER_LOW: "Customer factors (low risk)",
                    PRODUCT_LOW: "Product/service factors (low risk)",
                    GEO_LOW: "Geographical factors (low risk)",
                    TRANSACTION_LOW: "Transaction factors (low risk)",
                },
                factors: {
                    publicEntity: "Public administration or public enterprise",
                    regulatedInstitution: "Credit institution or financial institution subject to AML requirements",
                    listedCompany: "Company listed on a regulated market with disclosure requirements",
                    knownCustomer: "Resident customer with well-known and verified profile",
                    limitedValueInsurance: "Life insurance policy with low annual premium",
                    pensionScheme: "Pension, superannuation or similar scheme with employer contributions",
                    definedBenefitProduct: "Financial product with no early redemption clause and limited value",
                    electronicMoneyLowValue: "Electronic money product with limited reload/transaction value",
                    euEeaMember: "EU/EEA member state",
                    effectiveAmlSystem: "Third country with effective AML/CFT system (FATF compliant)",
                    lowCorruptionCountry: "Country with low corruption levels per recognized indices",
                    lowValueTransaction: "Low-value or occasional transaction below threshold",
                    regularPaymentKnownSource: "Regular payments from verified, known sources",
                    transparentOwnership: "Transparent ownership structure with identified beneficial owners",
                },
            },
            annexIII: {
                title: "AMLR Annex III – High Risk Factors",
                description: "Factors indicating potentially higher ML/TF risk. When applicable, enhanced due diligence (EDD) is required.",
                selected: "selected",
                categories: {
                    CUSTOMER_HIGH: "Customer factors (high risk)",
                    PRODUCT_HIGH: "Product/service factors (high risk)",
                    GEO_HIGH: "Geographical factors (high risk)",
                    TRANSACTION_HIGH: "Transaction factors (high risk)",
                },
                factors: {
                    unusualPatterns: "Unusual circumstances or transaction patterns without clear economic purpose",
                    complexOwnership: "Complex or opaque beneficial ownership structure",
                    cashIntensiveBusiness: "Cash-intensive business",
                    noPhysicalPresence: "Non-resident or no physical presence in the country",
                    pepStatus: "Politically Exposed Person (PEP) or family member/close associate",
                    thirdCountryNational: "Third-country national applying for residence rights or citizenship",
                    anonymousInstruments: "Anonymous transactions or products allowing anonymity",
                    privateBanking: "Private banking services",
                    cryptoAssets: "Crypto-asset products or services",
                    newProductNovelDelivery: "New products with novel delivery mechanisms or technology",
                    fatfHighRisk: "Country identified by FATF as high-risk or under increased monitoring",
                    sanctionedCountry: "Country or territory subject to EU or international sanctions",
                    weakAmlFramework: "Country with inadequate AML/CFT framework or supervision",
                    highCorruption: "Country with significant levels of corruption or organized crime",
                    unusuallyLarge: "Unusually large transactions relative to customer profile",
                    noEconomicRationale: "Transactions with no apparent economic rationale",
                    complexTransaction: "Unusually complex transaction patterns",
                    crossBorderHighRisk: "Cross-border transactions involving high-risk jurisdictions",
                },
            },
            cddRecommendation: {
                title: "CDD Recommendation",
                levels: {
                    SDD: "Simplified Due Diligence (SDD)",
                    CDD: "Standard Due Diligence (CDD)",
                    EDD: "Enhanced Due Diligence (EDD)",
                },
                descriptions: {
                    SDD: "Low-risk factors identified – simplified measures may be applied",
                    CDD: "Standard due diligence measures apply",
                    EDD: "High-risk factors identified – enhanced measures are required",
                },
            },
            scope: {
                tab: "Scope",
                title: "Business Scope & Context",
                subtitle: "Document the organization's business scope, products, customer categories, distribution channels and geographical exposure as required by AMLR Art. 10.",
                edit: "Edit",
                lastUpdated: "Last updated",
                businessDescription: "Business Description & Scope",
                businessDescriptionHelp: "Describe the organization's business activities, size, complexity, and the scope of the BWRA.",
                businessDescriptionPlaceholder: "Describe the organization's core business activities, legal structure, size and complexity...",
                productsServices: "Products & Services",
                productsServicesHelp: "List all products and services offered that are relevant for ML/TF risk assessment.",
                productPlaceholder: "e.g. Current accounts, Lending, Private banking...",
                addProduct: "Add product/service",
                addGroup: "Add group / business area",
                groupNamePlaceholder: "Group name, e.g. Retail, Corporate, Investment Banking...",
                addItem: "Add item",
                itemPlaceholder: "Item name...",
                customerCategories: "Customer Categories",
                customerCategoriesHelp: "List the customer categories and segments served by the organization.",
                customerCategoryPlaceholder: "e.g. Retail customers, Corporate clients, PEPs...",
                addCustomerCategory: "Add customer category",
                distributionChannels: "Distribution Channels",
                distributionChannelsHelp: "List the channels through which products and services are delivered.",
                channelPlaceholder: "e.g. Branch offices, Online banking, Mobile app...",
                addChannel: "Add channel",
                geoExposure: "Geographical Exposure",
                geoExposureHelp: "Describe the organization's geographical footprint and link to relevant GeoRisk indexes for automated risk assessment.",
                geoDescriptionPlaceholder: "Describe geographical markets, cross-border activities, correspondent banking relationships...",
                linkedGeoIndexes: "Linked GeoRisk Indexes",
                notDocumented: "Not yet documented",
                riskAssessmentReferences: "Risk Assessment References",
                riskAssessmentReferencesHelp: "Document how the EU's supranational risk assessment (SNRA) and national risk assessments have been considered in this BWRA, as required by AMLR Art. 10.",
                snra: "EU Supranational Risk Assessment (SNRA)",
                snraHelp: "The European Commission's supranational risk assessment identifies ML/TF risks at EU level.",
                nra: "National Risk Assessment (NRA)",
                nraHelp: "The national supervisory authority's risk assessment for your jurisdiction.",
                considered: "Considered in BWRA",
                notConsidered: "Not yet considered",
                reference: "Reference / Version",
                referencePlaceholder: "e.g. SNRA 2024, COM(2024) 365 final",
                nraReferencePlaceholder: "e.g. FI Dnr 22-123, NRA 2024",
                authority: "Supervisory Authority",
                authorityPlaceholder: "e.g. Finansinspektionen (FI)",
                howConsidered: "How has this been considered?",
                howConsideredPlaceholder: "Describe how the findings from this risk assessment have been incorporated into the organization's BWRA...",
                affectedCategories: "Affected Risk Categories",
                noCategoriesSelected: "No categories selected",
                linkedRisks: "Linked Risks",
                noLinkedRisks: "No risks linked",
                manageRiskLinks: "Link to risks",
                riskLinksTitle: "Linked risks",
                riskLinksHelp: "Select which risks in the risk register are related to this scope item.",
                riskLinksCount: "{{count}} risk(s) linked",
                scopeCoverage: "Scope Coverage",
                scopeCoverageHelp: "Scope items (products, services, distribution channels) linked to this risk.",
                noScopeLinks: "No scope items linked to this risk.",
                scopeFieldNames: {
                    products: "Products & Services",
                    customerCategories: "Customer Categories",
                    distributionChannels: "Distribution Channels",
                },
                saveScopeLinks: "Save links",
                cancelScopeLinks: "Cancel",
            },
            policies: {
                tab: "Policies",
                title: "Policies & Procedures",
                subtitle: "Create and link internal policies to risk categories as required by AMLR Art. 11. Identify gaps where risk categories lack associated policies.",
                addPolicy: "Add Policy",
                editPolicy: "Edit Policy",
                deletePolicy: "Delete Policy",
                deletePolicyConfirm: "Are you sure you want to delete this policy and all its procedures?",
                policyName: "Policy Name",
                policyNamePlaceholder: "e.g. KYC Policy, Transaction Monitoring Policy...",
                policyDescription: "Description",
                policyDescriptionPlaceholder: "Describe the policy's purpose, scope, and key requirements...",
                linkedCategories: "Linked Risk Categories",
                linkedCategoriesHelp: "Select which risk categories this policy addresses.",
                procedures: "Procedures",
                addProcedure: "Add Procedure",
                procedureTitle: "Procedure Title",
                procedureTitlePlaceholder: "e.g. Customer onboarding procedure...",
                procedureDescription: "Description",
                procedureDescriptionPlaceholder: "Describe the procedure steps...",
                removeProcedure: "Remove",
                status: "Status",
                version: "Version",
                createdAt: "Created",
                updatedAt: "Last Updated",
                noPolicies: "No policies defined yet. Create policies to document how identified risks are addressed through internal procedures and controls.",
                fromComplianceBanner: "From Compliance Map — Article {{article}}",
                fromComplianceDesc: "This article has no linked controls. The form below has pre-selected the relevant risk categories. Fill in a name and save to address this gap.",
                clearFilter: "Clear",
                gapIndicator: "Gap Indicator",
                gapDescription: "Risk categories without any linked active policy.",
                noGaps: "All risk categories are covered by at least one policy.",
                categoriesWithGap: "Categories without policy coverage",
                policyCount: "{{count}} policies",
                procedureCount: "{{count}} procedures",
                statusValues: {
                    DRAFT: "Draft",
                    ACTIVE: "Active",
                    ARCHIVED: "Archived",
                },
            },
            compliance: {
                tab: "Compliance",
                title: "AMLR Compliance Map",
                subtitle: "Map AMLR articles to controls and track compliance status. Articles are grouped by regulatory theme.",
                totalArticles: "Total Articles",
                coverageSummary: "{{covered}} of {{total}} articles covered ({{pct}}%)",
                article: "Article",
                requirement: "Requirement",
                statusHeader: "Status",
                linkedControls: "Controls",
                actions: "Actions",
                noControls: "No controls linked",
                editStatus: "Edit status",
                notesPlaceholder: "Add notes about compliance status...",
                save: "Save",
                cancel: "Cancel",
                status: {
                    NOT_ASSESSED: "Not Assessed",
                    COMPLIANT: "Compliant",
                    PARTIAL: "Partial",
                    NON_COMPLIANT: "Non-Compliant",
                },
                gap: {
                    title: "Gap Analysis",
                    header: "Gap",
                    GREEN: "Covered & validated",
                    YELLOW: "Covered, not validated",
                    RED: "No controls linked",
                    coverageSummary: "{{covered}} of {{total}} articles covered ({{pct}}%)",
                    uncoveredTitle: "Articles without linked controls",
                    navigateToPolicy: "Click to go to Policies and address this gap",
                },
                export: {
                    button: "Export Compliance Report",
                    reportTitle: "AMLR Compliance Report",
                    sheetCompliance: "Compliance Status",
                    sheetGaps: "Identified Gaps",
                    theme: "Theme",
                    trafficLight: "Traffic Light",
                    notes: "Notes",
                    recommendation: "Recommendation",
                    recRed: "Create and link controls for this article. No controls currently address this requirement.",
                    recYellow: "Validate existing controls. Controls are linked but compliance status is not confirmed.",
                    recGreen: "No action required. Article is covered by validated controls.",
                    gapsTitle: "Identified Compliance Gaps",
                    noGaps: "No compliance gaps identified. All articles are covered.",
                    criticalGaps: "Critical Gaps (No Controls Linked)",
                    partialGaps: "Partial Gaps (Controls Not Validated)",
                },
                themes: {
                    riskAssessment: "Risk Assessment (Art. 10–15)",
                    cdd: "Customer Due Diligence (Art. 16–21)",
                    sddEdd: "SDD & EDD Measures (Art. 22–26)",
                    recordKeeping: "Record Keeping & Reliance (Art. 27–29)",
                    beneficialOwnership: "Beneficial Ownership (Art. 42–47)",
                    reporting: "Reporting & FIU (Art. 50–54)",
                },
                articles: {
                    art10: "Obligation for obliged entities to carry out a business-wide risk assessment (BWRA)",
                    art11: "Internal policies, controls and procedures based on BWRA outcomes",
                    art12: "Individual risk assessment for each business relationship and transaction",
                    art13: "Approval of BWRA and risk management framework by senior management",
                    art14: "Documentation requirements for risk assessments and methodologies",
                    art15: "Consideration of supervisory risk assessment in BWRA process",
                    art16: "General obligation to apply customer due diligence measures",
                    art17: "Timing of customer due diligence – when CDD must be applied",
                    art18: "Identification and verification of the customer's identity",
                    art19: "Identification of beneficial owners and verification measures",
                    art20: "Assessment of the purpose and intended nature of business relationship",
                    art21: "Ongoing monitoring of business relationships and transactions",
                    art22: "Conditions and measures for simplified due diligence (SDD)",
                    art23: "Conditions and measures for enhanced due diligence (EDD)",
                    art24: "EDD measures for high-risk third countries identified by EU",
                    art25: "EDD measures for politically exposed persons (PEPs)",
                    art26: "EDD for cross-border correspondent banking relationships",
                    art27: "Record keeping obligations – minimum 5 years retention",
                    art28: "Prohibition of anonymous accounts, passbooks and safe-deposit boxes",
                    art29: "Conditions for reliance on third parties for CDD",
                    art42: "Obligation for legal entities to hold beneficial ownership information",
                    art43: "Establishment and maintenance of beneficial ownership registers",
                    art44: "Access to beneficial ownership registers by competent authorities",
                    art45: "Beneficial ownership of trusts and similar legal arrangements",
                    art46: "Central registers for trusts and similar legal arrangements",
                    art47: "Accuracy and timeliness of beneficial ownership information",
                    art50: "Obligation to file suspicious transaction reports (STRs) with FIU",
                    art51: "Powers and responsibilities of Financial Intelligence Units",
                    art52: "FIU analysis and dissemination of STR information",
                    art53: "Feedback mechanism from FIU to reporting entities",
                    art54: "Prohibition of disclosure (tipping-off) of STR filings",
                },
            },
            categoryAnalysis: {
                title: "Category Deep Analysis",
                subtitle: "Document a detailed risk analysis per category. Captures inherent risk level, trend, key findings, and mitigation strategy as required for a complete BWRA.",
                editAnalysis: "Edit Analysis",
                noAnalysis: "No deep analysis documented for this category yet. Click 'Edit Analysis' to add one.",
                inherentRiskLevel: "Inherent Risk Level",
                trendDirection: "Risk Trend",
                keyFindings: "Key Findings",
                mitigationStrategy: "Mitigation Strategy",
                regulatoryContext: "Regulatory Context",
                reviewer: "Reviewer",
                lastReviewed: "Last Reviewed",
                saveAnalysis: "Save Analysis",
                riskCount: "{{count}} risk(s) in this category",
                inherentLevels: {
                    "": "Not assessed",
                    HIGH: "High",
                    MEDIUM: "Medium",
                    LOW: "Low",
                },
                trendDirections: {
                    "": "Not assessed",
                    INCREASING: "Increasing",
                    STABLE: "Stable",
                    DECREASING: "Decreasing",
                },
                keyFindingsPlaceholder: "Describe the main risk drivers, key observations, and significant exposures for this category...",
                mitigationStrategyPlaceholder: "Describe how the organization mitigates risks in this category, including key controls and model-based tools...",
                regulatoryContextPlaceholder: "Summarize relevant regulatory requirements, supervisory guidance, or SNRA/NRA findings related to this category...",
                reviewerPlaceholder: "Name of the person who reviewed this analysis",
                sheetName: "Category Analysis",
                exportTitle: "Detailed Risk Analysis per Category",
            },
            orgReport: {
                tab: "Org Report",
                title: "Aggregated Risk Report – Organizational Level",
                subtitle: "Consolidated risk summary across all entities in the organization hierarchy. Shows gross and residual risk per entity.",
                export: "Export to Excel",
                sheetName: "Org Risk Report",
                entity: "Entity",
                type: "Type",
                country: "Country",
                totalRisks: "Risks",
                avgGross: "Avg Gross",
                avgResidual: "Avg Residual",
                mitigated: "Mitigated",
                identified: "Identified",
                worstGross: "Max Gross Level",
                worstResidual: "Max Residual Level",
                entitiesCount: "Entities",
                totalRisksAll: "Total Risks",
                mitigatedAll: "Mitigated",
                unassessedAll: "Unassessed",
                noEntities: "No entities configured.",
                categoryAggTitle: "Aggregated Risk by Category (all entities)",
                hierarchyTitle: "Risk Summary per Entity",
                risksShort: "risks",
            },
            actionPlan: {
                tab: "Action Plan",
                title: "Action Plan",
                subtitle: "Aggregated list of all actions across all BWRA risks. Use for board reporting and follow-up.",
                noActions: "No actions defined. Add actions from the Risk Register.",
                filterStatus: "Status",
                filterPriority: "Priority",
                filterRisk: "Risk",
                filterResponsible: "Responsible",
                responsibleFilterPlaceholder: "Search by name...",
                filterDeadline: "Deadline",
                allStatuses: "All Statuses",
                allPriorities: "All Priorities",
                allRisks: "All Risks",
                allDeadlines: "All",
                deadlineOverdue: "Overdue",
                deadlineThisMonth: "This Month",
                deadlineNext3Months: "Next 3 Months",
                riskLabel: "Risk",
                overdueLabel: "OVERDUE",
                goToRisk: "Go to Risk",
                export: "Export",
                exportExcel: "Export to Excel",
                exportPDF: "Export as PDF",
                exportSheetName: "Action Plan",
                exportTitle: "BWRA Action Plan",
                count: "{{count}} actions",
                actionId: "Action ID",
                description: "Description",
                responsible: "Responsible",
                deadline: "Deadline",
                priority: "Priority",
                status: "Status",
                linkedRisk: "Risk",
                linkedControl: "Control",
                pdfPageOf: "Page {{page}} of {{total}}",
                pdfGeneratedBy: "Generated by",
                pdfExportDate: "Date",
            },
            riskTimeline: {
                title: "Risk Lifecycle",
                empty: "No lifecycle events recorded yet.",
                noDate: "Date unknown",
                events: {
                    riskIdentified: "Risk identified",
                    riskStatus: "Status: {{status}}",
                    actionCreated: "Action {{id}} created",
                    actionCompleted: "Action {{id}} completed",
                    actionVerified: "Action {{id}} verified",
                    controlEvaluated: "Control evaluated: {{control}} ({{period}})",
                    responsible: "Responsible: {{name}}",
                    evaluatedBy: "Evaluated by: {{name}}",
                    notConducted: "Evaluation not conducted",
                    effectiveness: "Effectiveness: {{level}}",
                },
            },
            riskScoreTrend: {
                title: "Risk Score Trend",
                grossLabel: "Gross risk",
                residualLabel: "Residual risk",
                noHistory: "Score history will appear here as you update this risk over time.",
                chartAriaLabel: "Risk score trend chart showing gross and residual risk scores over time",
                trend: {
                    up: "Increasing",
                    down: "Decreasing",
                    stable: "Stable",
                },
            },
            annualReview: {
                tab: "Annual Review (ÅRKU)",
                title: "Annual Control Review (ÅRKU)",
                subtitle: "Evaluate the effectiveness of all controls once per year. For controls rated partially or non-effective, create remediation actions.",
                newReview: "New Review",
                createReview: "Create Review",
                cancel: "Cancel",
                noReviews: "No annual reviews conducted yet.",
                noReviewsDesc: "Click 'New Review' to start the first annual control evaluation.",
                noControls: "No controls to evaluate in this review.",
                noControlsInfo: "Add controls to risks in the Risk Register before creating an annual review.",
                willIncludeControls: "This review will include {{count}} control(s) from the current risk register.",
                period: "Period / Label",
                periodPlaceholder: "e.g. 2025 or 2025–2026",
                periodHelp: "Enter a year (e.g. 2025) or a range (e.g. 2025–2026) to identify this review period.",
                evaluationDate: "Evaluation Date",
                evaluatedBy: "Evaluated By",
                evaluatedByPlaceholder: "Name or role",
                linkVersion: "Linked BWRA Version",
                noVersion: "No version linked",
                conducted: "Conducted",
                yes: "Yes",
                no: "No",
                effectiveness: "Effectiveness",
                observations: "Observations / Comments",
                observationsPlaceholder: "Enter observations, findings, or comments...",
                eff: {
                    YES: "Effective",
                    PARTIAL: "Partially Effective",
                    NO: "Not Effective",
                },
                status: {
                    DRAFT: "Draft",
                    COMPLETED: "Completed",
                },
                totalControls: "Total Controls",
                evaluatedShort: "evaluated",
                pendingActions: "Pending Actions",
                saveDraft: "Save Draft",
                completeReview: "Complete Review",
                completeReviewDisabledTooltip: "At least one control must be evaluated before completing the review.",
                confirmCompleteTitle: "Complete Review?",
                confirmCompleteMessage: "This action cannot be undone. The review will be marked as completed and locked for editing.",
                viewActionPlan: "View Action Plan",
                delete: "Delete review",
                confirmDelete: "Delete review {{id}} ({{period}})? This cannot be undone.",
                actionPrompt: "This control is rated non-effective or partially effective. Create a remediation action.",
                createAction: "Create Action",
                cancelAction: "Cancel",
                saveAction: "Save Action",
                actionCreated: "Remediation action created",
                actionDescription: "Action Description",
                actionDescriptionPlaceholder: "Describe the remediation action...",
                actionResponsible: "Responsible",
                actionResponsiblePlaceholder: "Name or role",
                actionDeadline: "Deadline",
                arkuPrefix: "ÅRKU",
                periodicReportLink: "Periodic Report {{year}}",
                periodicReportTooltip: "Go to the Periodic Reporting report for {{year}}",
                editPeriod: "Edit period label",
                savePeriod: "Save period",
            },
            kriTitle: "Key Risk Indicators",
            kriBwraCoverage: "BWRA Coverage",
            kriBwraCoverageDesc: "Percentage of risks mitigated by controls",
            kriComplianceCoverage: "Compliance Coverage",
            kriComplianceCoverageDesc: "Percentage of AMLR articles covered",
            kriModelValidation: "Model Validation",
            kriModelValidationDesc: "Latest validation accuracy across models",
            kriScheduledValidation: "Scheduled Validation",
            kriScheduledValidationDesc: "Last automated model validation run",
            kriValidationAccuracy: "{{accuracy}}% accuracy",
            kriValidationError: "Last run failed",
            kriStatusGreen: "On target",
            kriStatusYellow: "Needs attention",
            kriStatusRed: "Action required",
            kriNoValidation: "No validation",
            heatmapTab: "Heatmap",
            heatmap: {
                title: "Risk Heat Map",
                subtitle: "5×5 probability/consequence matrix. Each dot represents a risk — click to navigate to the Risk Register.",
                toggleLabel: "Risk type",
                legend: "Legend:",
                noRisks: "No risks to display. Add risks in the Risk Register.",
            },
            gapAnalysis: {
                tab: "Gap Analysis",
                title: "Gap Analysis Report",
                subtitle: "Identifies risks without adequate controls — risks with no controls or only ineffective controls, sorted by residual risk exposure.",
                generateBtn: "Generate Gap Analysis",
                exportBtn: "Export to Excel",
                placeholder: "Click 'Generate Gap Analysis' to identify risks without adequate controls.",
                generatedAt: "Generated",
                totalRisks: "Total Risks",
                totalGaps: "Gaps Identified",
                noControlsCount: "No Controls",
                ineffectiveOnlyCount: "Ineffective Controls Only",
                gapPercent: "Gap Rate",
                sectionNoControls: "Risks Without Controls",
                sectionIneffective: "Risks With Only Ineffective Controls",
                noControlsNote: "No controls defined — this risk is fully unmitigated.",
                ineffectiveNote: "{{count}} control(s) defined, but max effectiveness is {{max}}/5 (ineffective).",
                recommendedActions: "Recommended Actions",
                allClearTitle: "No gaps identified",
                allClearBody: "All active risks have at least one effective control. Well done.",
                controlCount: "Controls",
                gapType: "Gap Type",
                gapTypes: {
                    NO_CONTROLS: "No controls",
                    INEFFECTIVE_ONLY: "Ineffective controls only",
                },
                exportTitle: "BWRA Gap Analysis Report",
                sheetSummary: "Summary",
                sheetDetail: "Gaps",
                recommendations: {
                    LINK_MODEL: "Link to {{module}} model — add a model control to provide data-driven mitigation.",
                    ADD_MANUAL_CONTROL: "Add a manual or system control to mitigate this risk.",
                    ADD_ACTION: "Create an action plan item to remediate this gap.",
                    IMPROVE_MODEL_VALIDATION: "Run model validation to improve the effectiveness score of linked models.",
                    INCREASE_EFFECTIVENESS: "Review and strengthen existing controls to increase their effectiveness rating.",
                    VERIFY_MODEL_COVERAGE: "Verify that {{module}} covers this risk category in its configuration.",
                },
            },
            boardReport: {
                tab: "Board Report",
                title: "Board-Level Risk Report",
                subtitle: "Executive summary of the AML risk landscape, top exposures, and control environment — formatted for board-level review.",
                generateBtn: "Generate Board Report",
                printBtn: "Print to PDF",
                placeholder: "Click 'Generate Board Report' to create a board-ready summary of the current risk landscape.",
                generatedAt: "Generated",
                sections: {
                    executiveSummary: "Executive Summary",
                    topRisks: "Top 10 Risk Exposures",
                    controls: "Control Environment",
                    controlStatus: "Control Environment",
                    gapAnalysis: "Gap Analysis",
                    signature: "Review & Approval",
                    attestation: "Review & Approval",
                },
                top10Title: "Top 10 risks by residual exposure",
                riskColumn: "Risk",
                coverage: "Control coverage",
                attestationText: "I confirm that the above risk assessment has been reviewed in accordance with AMLR and FFFS 2017:11 requirements.",
                totalRisks: "Total Risks",
                activeRisks: "Active Risks",
                mitigated: "Mitigated",
                avgGross: "Avg. Gross Risk",
                avgResidual: "Avg. Residual Risk",
                riskReduction: "Risk Reduction",
                appetiteStatement: "Risk Appetite Statement",
                noAppetiteStatement: "No risk appetite statement has been defined.",
                rank: "#",
                grossScore: "Gross",
                residualScore: "Residual",
                level: "Level",
                noRisks: "No active risks registered.",
                totalControls: "Total Controls",
                effectiveControls: "Effective Controls (≥3)",
                controlCoverage: "Coverage Rate",
                avgEffectiveness: "Avg. Effectiveness",
                risksWithoutControls: "Risks Without Controls",
                totalGaps: "Total Gaps",
                noControlsCount: "No Controls",
                ineffectiveOnlyCount: "Ineffective Controls",
                gapRate: "Gap Rate",
                allControlsAdequate: "All active risks have adequate controls.",
                reviewDate: "Review Date",
                reviewerName: "Reviewer Name",
                reviewerTitle: "Title / Function",
                reviewerNamePlaceholder: "Enter name",
                reviewerTitlePlaceholder: "Enter title",
                signatureLabel: "Signature",
                approvalLabel: "Board Approval",
                printHint: "Use your browser's print function (Ctrl+P / Cmd+P) to save as PDF.",
                fffsNote: "Prepared in accordance with FFFS 2017:11 and AMLR requirements.",
                preparedBy: "Prepared by",
                confidential: "CONFIDENTIAL — Internal use only",
            },
            trendAnalysis: {
                tab: "Trends",
                title: "Risk Trend Analysis",
                subtitle: "Track how risk levels evolve across annual review periods. Identify risks that are improving, worsening, or stable over time.",
                improving: "Improving",
                worsening: "Worsening",
                stable: "Stable",
                noData: "No Data",
                noHistory: "No Risk History Available",
                noHistoryDesc: "Risk score history will appear here once risks have been assessed over time.",
                residualScore: "Residual Risk",
                grossScore: "Gross Risk",
                selectHint: "Select risks to display in the chart",
                selectRisksHint: "Select one or more risks above to view the trend chart.",
                chartAriaLabel: "Risk score trend chart",
                summaryTitle: "Risk Trend Summary",
                reviewsTitle: "Annual Review Reference",
                trend: {
                    improving: "Improving",
                    worsening: "Worsening",
                    stable: "Stable",
                },
                col: {
                    risk: "Risk",
                    category: "Category",
                    grossScore: "Gross",
                    residualScore: "Residual",
                    delta: "Change",
                    trend: "Trend",
                },
            },
            calendar: {
                tab: "Calendar",
                title: "Compliance Calendar",
                subtitle: "Overview of all action plan deadlines. Click a deadline to navigate to the related risk.",
                prevMonth: "Previous month",
                nextMonth: "Next month",
                today: "Today",
                thisMonth: "This Month's Deadlines",
                noDeadlines: "No deadlines this month.",
                overdueAlert: "{{count}} overdue action(s) require attention.",
                months: {
                    jan: "January",
                    feb: "February",
                    mar: "March",
                    apr: "April",
                    may: "May",
                    jun: "June",
                    jul: "July",
                    aug: "August",
                    sep: "September",
                    oct: "October",
                    nov: "November",
                    dec: "December",
                },
                weekdays: {
                    mon: "Mon",
                    tue: "Tue",
                    wed: "Wed",
                    thu: "Thu",
                    fri: "Fri",
                    sat: "Sat",
                    sun: "Sun",
                },
            },
            fieldMapping: {
                tab: "Mapping",
                title: "Field Mapping",
                subtitle: "Map BWRA risk fields (gross risk score, category, status) to columns in an external data source.",
                dataSource: "Data Source",
                selectDataSource: "— Select data source —",
                table: "Table",
                selectTable: "— Select table —",
                tableNamePlaceholder: "Enter table name",
                rowIdColumn: "Risk ID Column",
                selectRowIdColumn: "— Select column —",
                rowIdColumnPlaceholder: "Enter column name (e.g. risk_id)",
                riskMappings: "Risk Field Mappings",
                riskFieldCol: "Risk Field",
                columnCol: "Source Column",
                transformCol: "Transform",
                selectColumn: "— Select column —",
                columnPlaceholder: "Enter column name",
                transform_none: "None",
                transform_uppercase: "Uppercase",
                transform_lowercase: "Lowercase",
                transform_trim: "Trim",
                transform_number: "To Number",
                transform_boolean: "To Boolean",
                transform_date: "To Date",
                previewData: "Preview Data (5 rows)",
                saveMapping: "Save Mapping",
                saving: "Saving…",
                statusComplete: "Mapping complete — all fields mapped",
                statusIncomplete: "{{count}} field(s) without column mapping",
                savedSuccess: "Mapping saved successfully",
                saveError: "Failed to save mapping: {{error}}",
                errorNoDataSource: "Please select a data source",
                errorNoTable: "Please enter a table name",
                errorNoRowId: "Please specify the risk ID column",
                previewTitle: "Data Preview",
                previewSubtitle: "Sample rows from {{table}}",
                previewNoData: "No preview available — connect to a live data source to fetch rows.",
                noDataSources: "No data sources configured. Add one in Admin → Data Sources.",
                noRisks: "No risks registered. Add risks in the Risk Register before configuring field mapping.",
            },
            backToSection: "Back to {{section}}",
            sections: {
                overview: {
                    label: "Overview",
                    description: "Module overview, business scope, and risk appetite statement.",
                },
                riskanalysis: {
                    label: "Risk Analysis",
                    description: "Risk register, heatmap, trend analysis, and gap analysis.",
                },
                controlsActions: {
                    label: "Controls & Actions",
                    description: "Controls, action plans, annual reviews, and compliance calendar.",
                },
                compliancePolicies: {
                    label: "Compliance & Policies",
                    description: "AMLR compliance map, policies, model mapping, and field mapping.",
                },
                reports: {
                    label: "Reports",
                    description: "Organizational and board-level risk reports.",
                },
            },
            toolLabels: {
                overview:      "Overview",
                scope:         "Business Scope",
                appetite:      "Risk Appetite",
                register:      "Risk Register",
                heatmap:       "Risk Heatmap",
                trendanalysis: "Trend Analysis",
                gapanalysis:   "Gap Analysis",
                controls:      "Controls",
                actionplan:    "Action Plan",
                annualreview:  "Annual Review",
                calendar:      "Calendar",
                compliance:    "Compliance Map",
                policies:      "Policies",
                mapping:       "Model Mapping",
                fieldmapping:  "Field Mapping",
                orgreport:     "Org Report",
                boardreport:   "Board Report",
                compliancechain: "Compliance Chain Validation",
            },
            toolDescriptions: {
                overview:      "Dashboard summary of risks, controls, and risk posture.",
                scope:         "Define the organization's business scope and context.",
                appetite:      "Set and document the risk appetite statement.",
                register:      "Manage and assess all identified AML/CFT risks.",
                heatmap:       "Visualize risks by likelihood and impact.",
                trendanalysis: "Track risk score trends over time.",
                gapanalysis:   "Identify gaps between required and implemented controls.",
                controls:      "Document and evaluate control measures.",
                actionplan:    "Track remediation actions and deadlines.",
                annualreview:  "Conduct the annual AML/CFT control review (ÅRKU).",
                calendar:      "View compliance deadlines and upcoming reviews.",
                compliance:    "Map risks to AMLR articles and requirements.",
                policies:      "Link policies and procedures to risk categories.",
                mapping:       "Map risks to models and analytical tools.",
                fieldmapping:  "Configure field mapping between BWRA and data sources.",
                orgreport:     "Aggregated risk report at the organizational level.",
                boardreport:   "Board-level risk summary and attestation.",
                compliancechain: "End-to-end compliance chain validation — verifies BWRA risks are handled by models and that models are validated against data.",
            },
            complianceChain: {
                title: "Compliance Chain Validation",
                subtitle: "End-to-end validation that BWRA risks are handled by models/controls AND that those models are validated against current data.",
                runNow: "Run validation now",
                running: "Running…",
                lastRun: "Last run: {{time}}",
                gapsHeading: "Gaps requiring attention",
                coverageHeading: "Risk coverage",
                modelsHeading: "Model validation status",
                scheduleHeading: "Automated schedule",
                historyHeading: "Run history",
                scheduleEnable: "Run automatically on schedule",
                scheduleFrequency: "Frequency",
                scheduleHour: "Hour (0–23)",
                scheduleNote: "Scheduled runs are executed by the system when a user opens the app on or after the next scheduled time. For production deployments, wire this into a server-side job runner.",
                coveredYes: "Covered",
                coveredNo: "Uncovered",
                status: {
                    green: "Compliance chain OK",
                    yellow: "Attention needed",
                    red: "Critical gaps",
                    grey: "No data",
                },
                statusDesc: {
                    green: "Every BWRA risk is handled and every referenced model is validated against current data.",
                    yellow: "Some models have stale validation data or lack validation entirely. Review before the next board meeting.",
                    red: "BWRA risks are uncovered or models have failed validation. Immediate action required.",
                    grey: "No data available. Define BWRA risks and run a validation.",
                },
                counts: {
                    risksCovered: "Risks covered",
                    modelsOk: "Models OK",
                    failedModels: "Failed validations",
                    staleModels: "Stale validations",
                },
                modelStatus: {
                    green: "OK",
                    yellow: "Stale",
                    red: "Failed",
                    grey: "No validation",
                },
                issueTypes: {
                    uncovered_risk: "Uncovered BWRA risk",
                    failed_model: "Model failed validation",
                    stale_validation: "Stale model validation",
                    missing_validation: "Model never validated",
                },
                issueMeta: {
                    accuracy: "Accuracy: {{value}}%",
                    ageDays: "Age: {{days}} days",
                },
                frequency: {
                    daily: "Daily",
                    weekly: "Weekly",
                    monthly: "Monthly",
                },
                table: {
                    risk: "Risk",
                    category: "Category",
                    coverage: "Coverage",
                    models: "Models/Controls",
                    module: "Module",
                    model: "Model",
                    accuracy: "Accuracy",
                    validationDate: "Validation date",
                    status: "Status",
                    timestamp: "Timestamp",
                    triggeredBy: "Triggered by",
                    gaps: "Gaps",
                },
            },
        },
        transactionMonitoring: {
            title: "Transaction Monitoring",
            subtitle: "Document monitoring rules, map them to BWRA risks, and record alarm performance data.",
            tabOverview: "Overview",
            tabRules: "Rules",
            tabMapping: "Rule Mapping",
            tabPerformance: "Alert Performance",
            tabModelOverview: "Model Overview",
            tabValidation: "Validation",
            tabThresholds: "Thresholds",
            tabAlerts: "Alert Workflow",
            tabTemplates: "Rule Templates",
            tabScenarioTest: "Scenario Test",
            overview: {
                title: "Transaction Monitoring Overview",
                description: "Document and assess the transaction monitoring function. Map monitoring rules to BWRA risks and record alert performance data from the operational system.",
                totalRules: "Rules Documented",
                activeRules: "Active Rules",
                mappedRules: "Rules Mapped to Risks",
                rulesWithPerformance: "Rules with Performance Data",
                totalThresholds: "Thresholds Defined",
                totalModels: "Models Documented",
                noData: "No rules documented yet. Go to the Rules tab to add monitoring rules.",
            },
            rules: {
                title: "Monitoring Rules",
                subtitle: "Document the transaction monitoring rules in the operational system. Register which rules exist, their type, threshold, and current status.",
                addRule: "Add Rule",
                newRuleFromTemplate: "New Rule from Template",
                editRule: "Edit Rule",
                saveRule: "Save Rule",
                cancel: "Cancel",
                deleteRule: "Delete",
                confirmDelete: "Are you sure you want to delete rule {{name}}?",
                noRules: "No rules documented. Add a rule to start.",
                ruleId: "Rule ID",
                ruleName: "Rule Name",
                ruleNamePlaceholder: "e.g. High-Value Transfer",
                systemName: "Operational System",
                systemNamePlaceholder: "e.g. Actimize, NICE Actimize, Temenos",
                description: "Description",
                descriptionPlaceholder: "Describe what this rule monitors and its purpose...",
                ruleType: "Rule Type",
                threshold: "Threshold (Amount)",
                thresholdPlaceholder: "e.g. 10000",
                currency: "Currency",
                enabled: "Active in System",
                actions: "Actions",
                ruleType_AMOUNT: "Amount",
                ruleType_FREQUENCY: "Frequency",
                ruleType_PATTERN: "Pattern",
                nameRequired: "Rule name is required.",
                thresholdInvalid: "Threshold must be a positive number.",
                patternRegex: "Search pattern (regex)",
                patternRegexPlaceholder: "e.g. cash|withdrawal|ATM",
                patternRegexFallback: "(keyword match from rule name — no pattern set)",
                patternInvalidRegex: "Invalid regular expression. Please check the pattern syntax.",
                patternExplanation: "Pattern rules match transaction fields (description, type, reference) using a regex. If no pattern is set, keywords from the rule name are used as fallback.",
                ruleAction: "Operational Action",
                ruleAction_ALERT: "Alert",
                ruleAction_BLOCK: "Block",
                ruleAction_REVIEW: "Review",
                ruleAction_SAR: "SAR",
                ruleAction_LOG: "Log Only",
                simulate: "Simulate",
                simulatorTitle: "Rule Simulator",
                simulatorDescription: "Test how this rule would behave without activating it in production.",
                simulatorRule: "Simulating rule",
                simulatorThresholdOverride: "Test threshold",
                simulatorThresholdHint: "Temporary override — not saved. Change to see how different thresholds affect hit rate.",
                simulatorAddScenario: "Add test scenario",
                simulatorInputAmount: "Test amount",
                simulatorInputCount: "Test count",
                simulatorInputPattern: "Test description / text",
                simulatorInputPlaceholder_AMOUNT: "e.g. 15000",
                simulatorInputPlaceholder_FREQUENCY: "e.g. 5",
                simulatorInputPlaceholder_PATTERN: "e.g. cash withdrawal international",
                simulatorAdd: "Add",
                simulatorNoScenarios: "No test scenarios added yet. Enter a value above and click Add.",
                simulatorHit: "HIT",
                simulatorMiss: "MISS",
                simulatorSummary: "{{hits}} of {{total}} scenarios would trigger this rule.",
                simulatorClear: "Clear scenarios",
                simulatorClose: "Close simulator",
                simulatorRemove: "Remove",
                simulatorNoThreshold: "No threshold defined — all scenarios will miss.",
                triggerCount30Days: "Hits (30 days)",
                triggerCountZeroBadge: "No hits",
                triggerCountZeroTooltip: "Possible ineffective rule — no triggers recorded in the last 30 days",
                thresholdMismatchBadge: "Exceeds regulatory threshold",
                thresholdMismatchDetail: "Rule amount exceeds {{name}}: {{amount}} {{currency}}",
                searchPlaceholder: "Search rules by name, description or type…",
                filterStatus: "Status",
                filterStatusAll: "All statuses",
                filterStatusActive: "Active",
                filterStatusInactive: "Inactive",
                filterType: "Type",
                filterTypeAll: "All types",
                noFilterResults: "No rules match the current filters.",
                filterCount: "Showing {{shown}} of {{total}} rules",
                batchSelectAll: "Select all visible",
                batchDeselectAll: "Deselect all",
                batchSelected: "{{count}} selected",
                batchActivate: "Activate selected",
                batchDeactivate: "Deactivate selected",
                batchConfirmActivate: "Activate {{count}} rules?",
                batchConfirmDeactivate: "Deactivate {{count}} rules?",
                batchActivated: "{{count}} rules activated",
                batchDeactivated: "{{count}} rules deactivated",
                history: "History",
                historyButtonTitle: "View change history for this rule",
                historyTitle: "Change History",
                historyVersions: "Versions",
                historyNoVersions: "No previous versions recorded yet. History is created when a rule is edited.",
                historyChangedBy: "Changed by",
                historySelectHint: "Select a version from the list to see what changed.",
                historyVersion: "Version",
                historyNoDiff: "No field changes detected compared to current version.",
                historyFieldLabel: "Field",
                historyOldValue: "Previous value",
                historyNewValue: "Current value",
                historySnapshotValue: "Value at this version",
                historyAllFields: "Show all fields",
                historyRestore: "Restore to",
                historyRestored: "Rule restored to previous version.",
                historyConfirmRestore: "Restore rule to {{version}}? The current values will become the new version in history.",
                historyBatchOperation: "Batch operation",
                exportRules: "Export Rules",
                exportSelected: "Export Selected ({{count}})",
                exportAll: "Export All ({{count}})",
                exportSuccess: "{{count}} rules exported",
                importRules: "Import Rules",
                importTitle: "Import Rules from JSON",
                importDescription: "Upload a previously exported rules file (.json). Review conflicts before importing.",
                importDrop: "Choose file or drop here",
                importFileError: "Could not read file. Make sure it is a valid JSON export.",
                importFormatError: "Invalid file format. Expected an RMS rules export.",
                importPreviewTitle: "Import Preview",
                importPreviewNew: "{{count}} new rules",
                importPreviewConflicts: "{{count}} conflicts",
                importConflictId: "ID conflict",
                importConflictName: "Name conflict",
                importStrategyLabel: "When conflicts are found:",
                importStrategySkip: "Skip conflicts (only import new rules)",
                importStrategyOverwrite: "Overwrite existing rules",
                importStrategyNew: "Import as new rules (new IDs assigned)",
                importConfirm: "Import",
                importCancel: "Cancel",
                importSuccess: "Import complete: {{imported}} imported, {{overwritten}} overwritten, {{skipped}} skipped",
                importNoFile: "No file selected",
                importClose: "Close",
                depViewBtn: "Dependency View",
                depTitle: "Rule Dependency & Overlap Analysis",
                depSubtitle: "Shows which rules overlap and may trigger on the same transactions. Redundant rules should be reviewed and possibly consolidated.",
                depClose: "Close dependency view",
                depNoOverlaps: "No overlapping rules detected. Each rule covers a unique scenario.",
                depMatrixTitle: "Overlap Matrix",
                depPairsTitle: "{{count}} overlapping rule pairs",
                depFilterAll: "All overlap types",
                depNoFilteredPairs: "No pairs match the selected filter.",
                depNoOverlap: "No overlap",
                depRedundantAlert: "{{count}} potentially redundant rule pair(s) detected — same type, threshold, and currency.",
                depRedundantDetail: "Identical threshold: {{threshold}}",
                depAmountDetail: "Both trigger above {{threshold}}",
                depPatternDetail: "Shared keywords: {{keywords}}",
                depRiskDetail: "{{count}} shared BWRA risk(s)",
                depOverlap_REDUNDANT: "Potentially redundant",
                depOverlap_AMOUNT_OVERLAP: "Amount overlap",
                depOverlap_FREQUENCY_OVERLAP: "Frequency overlap",
                depOverlap_PATTERN_OVERLAP: "Pattern overlap",
                depOverlap_RISK_OVERLAP: "Risk overlap",
            },
            mapping: {
                title: "Rule-to-Risk Mapping",
                description: "Document which BWRA risks each monitoring rule addresses and which products/services it covers.",
                noRules: "No rules documented yet. Go to the Rules tab to add monitoring rules.",
                editMapping: "Edit Mapping",
                saveMapping: "Save Mapping",
                cancel: "Cancel",
                confirmTitle: "Confirm changes",
                confirmMessage: "You are about to change risk mappings for rule {{ruleName}}. Do you want to continue?",
                saveSuccess: "Mappings saved",
                saveError: "Failed to save mappings",
                bwraRisks: "BWRA Risks Addressed",
                noRisksInBWRA: "No risks in BWRA. Add risks in the BWRA module first.",
                linkedProducts: "Covered Products / Services",
                noProductsInBWRA: "No products in BWRA Scope. Add products in the BWRA module.",
                risksLinked: "risks",
                productsLinked: "products",
                noMapping: "Not mapped",
                unmappedActiveWarning: "Active rule without risk mapping — flagged for auditor review",
            },
            performance: {
                title: "Alert Performance Data",
                description: "Document the performance of monitoring rules in the operational system per period. Record how many alerts were created, investigated, and reported.",
                noRules: "No rules documented yet. Go to the Rules tab to add monitoring rules.",
                noData: "No performance data recorded for this rule.",
                entries: "entries",
                expand: "View / Add",
                collapse: "Close",
                addEntry: "Add Period Data",
                editEntry: "Edit",
                deleteEntry: "Delete",
                saveEntry: "Save",
                confirmDelete: "Delete this performance entry?",
                period: "Period",
                periodPlaceholder: "e.g. 2025-Q1 or 2025-01",
                periodRequired: "Period is required",
                alertsCreated: "Alerts Created",
                alertsInvestigated: "Investigated",
                alertsReported: "Reported (STR/SAR)",
                sarFiled: "SAR Filed",
                notes: "Notes",
            },
            modelOverview: {
                title: "Transaction Monitoring Model Overview",
                subtitle: "Define transaction monitoring models. Each model groups monitoring rules and declares which BWRA risks it addresses. These declarations are used by BWRA for automatic control derivation.",
                addModel: "Add Model",
                editModel: "Edit Model",
                saveModel: "Save Model",
                cancel: "Cancel",
                deleteModel: "Delete",
                confirmDelete: "Are you sure you want to delete model \"{{name}}\"?",
                noModels: "No models defined. Add a model to start.",
                modelId: "Model ID",
                modelName: "Model Name",
                modelNamePlaceholder: "e.g. High-Value Transfer Detection",
                description: "Description",
                descriptionPlaceholder: "Describe the purpose and scope of this monitoring model...",
                status: "Status",
                status_active: "Active",
                status_draft: "Draft",
                status_deprecated: "Deprecated",
                linkedRules: "Linked Rules",
                linkedRulesCount: "{{count}} rules",
                declaredRisks: "Declared BWRA Risks",
                declaredRisksCount: "{{count}} risks",
                lastModified: "Last Modified",
                nameRequired: "Model name is required.",
                selectRules: "Link Monitoring Rules",
                noRulesAvailable: "No rules documented. Go to the Rules tab to add monitoring rules.",
                selectRisks: "Declare BWRA Risks Addressed",
                noRisksAvailable: "No risks in BWRA. Add risks in the BWRA module first.",
                actions: "Actions",
            },
            thresholds: {
                title: "Regulatory Thresholds",
                subtitle: "Document regulatory threshold values that govern mandatory reporting and escalation. Examples: STR/SAR cash thresholds, wire transfer reporting limits.",
                addThreshold: "Add Threshold",
                editThreshold: "Edit Threshold",
                saveThreshold: "Save Threshold",
                cancel: "Cancel",
                deleteThreshold: "Delete",
                confirmDelete: "Are you sure you want to delete threshold \"{{name}}\"?",
                noThresholds: "No thresholds defined. Add a threshold to start.",
                thresholdId: "ID",
                name: "Name",
                namePlaceholder: "e.g. STR Cash Threshold",
                regulation: "Regulation / Basis",
                regulationPlaceholder: "e.g. AMLD5 Art. 11(b)",
                amount: "Amount",
                amountPlaceholder: "e.g. 10000",
                currency: "Currency",
                transactionType: "Transaction Type",
                description: "Description",
                descriptionPlaceholder: "Describe the purpose of this threshold...",
                enabled: "Active",
                actions: "Actions",
                nameRequired: "Threshold name is required.",
                transactionType_CASH: "Cash",
                transactionType_TRANSFER: "Transfer",
                transactionType_ALL: "All Types",
                transactionType_WIRE: "Wire Transfer",
                transactionType_CRYPTO: "Crypto",
            },
            validation: {
                title: "Model Validation",
                description: "Test monitoring rules against sample transaction data to verify they behave as expected. Run rules on test data and see which rules hit or miss.",
                noRules: "No rules documented yet. Go to the Rules tab to add monitoring rules before running validation.",
                selectRules: "Select Rules to Test",
                testData: "Test Transaction Data (JSON)",
                testDataHint: "Enter transactions as a JSON array. Use 'amount' for AMOUNT rules, 'count'/'frequency' for FREQUENCY rules, 'description'/'type' for PATTERN rules.",
                runValidation: "Run Validation",
                loadExample: "Load Example",
                jsonInvalid: "Invalid JSON. Please enter a valid JSON array of transactions.",
                noTransactions: "No transactions provided.",
                results: "Validation Results",
                saveResult: "Save Result",
                totalTransactions: "Test Transactions",
                totalHits: "Total Hits",
                totalMisses: "Total Misses",
                rulesWithHits: "Rules with Hits",
                hits: "Hits",
                misses: "Misses",
                hitRate: "Hit Rate",
                skipped: "Skipped (disabled)",
                disabled: "disabled",
                hit: "hit",
                miss: "miss",
                hitsDetail: "Transactions that triggered this rule",
                missesDetail: "Transactions that did not trigger this rule",
                expand: "Details",
                collapse: "Hide",
                history: "Validation History",
                noHistory: "No validation results saved yet.",
                showHistory: "Show History",
                hideHistory: "Hide History",
                transactions: "transactions",
                delete: "Delete",
                confirmDelete: "Are you sure you want to delete this validation result? This action cannot be undone.",
                noThresholdDefined: "No threshold defined",
                patternMatchFound: "Pattern match found",
                noPatternMatch: "No pattern match",
                unknownRuleType: "Unknown rule type",
                historyLimitWarning: "History is near the retention limit ({{count}}/{{limit}} runs). Older runs will be deleted automatically when the limit is reached. Configure the limit in Admin → Settings → Audit.",
                historyLimitReached: "History retention limit reached ({{limit}} runs). Oldest runs are deleted automatically when new results are saved. Configure the limit in Admin → Settings → Audit.",
                exportCsv: "Export CSV",
                exportCsvTitle: "Export validation result as CSV",
            },
            alerts: {
                title: "Alert Workflow",
                subtitle: "Case management for triggered alerts. Track each alert from initial detection through investigation to SAR report or closure.",
                newAlert: "New Alert",
                filterAll: "All",
                colId: "ID",
                colSubject: "Subject",
                colRule: "Rule",
                colStatus: "Status",
                colCreated: "Created",
                statusNew: "New",
                statusInvestigating: "Under Investigation",
                statusSar: "SAR Report",
                statusClosed: "Closed",
                createAlert: "Create Alert",
                subject: "Subject",
                subjectPlaceholder: "Brief description of the alert",
                subjectRequired: "Subject is required",
                description: "Description",
                ruleLabel: "Associated Rule",
                noRule: "— No rule —",
                amount: "Amount",
                currency: "Currency",
                updateStatus: "Update Status",
                comment: "Comment",
                commentRequired: "A comment is required for status changes",
                commentPlaceholder: "Add a note explaining this status change",
                statusRequired: "Select a target status",
                update: "Update",
                cancel: "Cancel",
                auditTrail: "Audit Trail",
                noActivity: "No activity recorded",
                deleteAlert: "Delete Alert",
                confirmDelete: "Are you sure you want to delete alert {{id}}?",
                noAlerts: "No alerts found.",
                alertCreated: "Alert created",
                statusUpdated: "Status updated",
                alertDeleted: "Alert deleted",
                actionStartInvestigation: "Start Investigation",
                actionFileSar: "File SAR Report",
                actionClose: "Close (False Positive)",
            },
            ruleTemplates: {
                title: "Rule Templates",
                description: "Pre-built monitoring rules for common AML scenarios. Use a template as a starting point and customize to match your operational system.",
                useTemplate: "Use Template",
                cancelTemplate: "Cancel",
                customizeRule: "Customize Rule",
                saveAsRule: "Save as New Rule",
                ruleSaved: "Rule created from template.",
                category_ctr: "CTR",
                category_structuring: "Structuring",
                category_highrisk: "High-Risk Jurisdiction",
                category_pep: "PEP",
                category_largeamount: "Large Amount",
                tpl_ctr_name: "CTR — Cash Transactions >15,000 EUR",
                tpl_ctr_description: "Identifies cash transactions exceeding the regulatory reporting threshold of 15,000 EUR (CTR requirement per AML Directive 6AMLD).",
                tpl_structuring_name: "Structuring — Fragmented Deposits",
                tpl_structuring_description: "Detects potential structuring (smurfing) by identifying high frequency of transactions below the reporting threshold.",
                tpl_highrisk_name: "High-Risk Jurisdictions — FATF Countries",
                tpl_highrisk_description: "Flags transactions linked to FATF grey-listed or blacklisted jurisdictions.",
                tpl_pep_name: "PEP — Politically Exposed Persons",
                tpl_pep_description: "Identifies transactions potentially linked to politically exposed persons (PEP).",
                tpl_large_name: "Unusually Large Transactions >50,000 EUR",
                tpl_large_description: "Flags transactions significantly exceeding normal transaction patterns for private individuals.",
            },
            rulePerformance: {
                title: "Rule Performance Analysis",
                buttonTitle: "Performance Analysis",
                close: "Close",
                fpRate: "False Positive Rate",
                detectionRate: "Detection Rate",
                triggersPerDay: "Triggers / Day (30d)",
                triggersPerWeek: "Triggers / Week",
                totalCases: "Total Alert Cases",
                sarCases: "SAR Filed",
                closedCases: "Closed (False Positive)",
                triggersLast30Days: "Triggers (last 30 days)",
                documentedAlerts: "Documented Alerts (all periods)",
                documentedSar: "SAR",
                highFpWarning: "False positive rate exceeds 80% — consider threshold optimization",
                sourceDocumented: "Metrics based on documented performance data (Alert Performance tab).",
                sourceCases: "Metrics based on alert cases in the system. No documented performance data available.",
                sourceNone: "No data available. Add alert cases or document performance data to see metrics.",
            },
            thresholdTuning: {
                title: "Threshold Tuning Assistant",
                buttonTitle: "Optimise Thresholds",
                close: "Close",
                currentThreshold: "Current threshold",
                suggestedThreshold: "Suggested threshold",
                expectedImpact: "Expected impact",
                fpChange: "False positive rate",
                detectionChange: "Detection rate",
                applyThreshold: "Apply suggested threshold",
                keepCurrent: "Keep current threshold",
                disclaimer: "Suggestions are estimates based on historical performance data. Review with your compliance team before applying.",
                reason_noRule: "Rule not found.",
                reason_patternRule: "Threshold optimisation is not applicable to pattern-based rules.",
                reason_noThreshold: "This rule has no numeric threshold configured.",
                reason_noData: "Not enough performance data to generate a suggestion. Add alert cases or document performance data first.",
                reason_highFpCritical: "False positive rate exceeds 80%. Raising the threshold is strongly recommended to reduce analyst workload.",
                reason_highFpModerate: "False positive rate exceeds 60%. A moderate threshold increase should reduce noise without impacting detection.",
                reason_highTriggerRate: "Trigger volume exceeds 20 alerts per day. Raising the threshold will reduce analyst workload.",
                reason_lowDetection: "Detection rate is below 30% despite a reasonable trigger volume. Lowering the threshold may capture more true positives.",
                reason_lowTriggerRate: "Fewer than 0.5 triggers per day — the threshold may be set too high. Lowering it could surface relevant transactions.",
                reason_performanceGood: "Current threshold performance is within acceptable range. No adjustment is recommended at this time.",
            },
            scenarioTest: {
                title: "Scenario-Based Testing",
                description: "Test your rules against realistic predefined scenarios to verify they catch known money laundering patterns such as structuring, smurfing, and trade-based money laundering.",
                noRules: "No monitoring rules defined. Add rules in the Rules tab before running scenario tests.",
                selectScenario: "Select a Scenario",
                selectScenarioHint: "Click a scenario to run it against all your monitoring rules. Results show immediately which rules trigger.",
                resultsFor: "Results for",
                totalTransactions: "Transactions",
                rulesTriggered: "Rules Triggered",
                rulesNotTriggered: "Rules Not Triggered",
                totalHits: "Total Hits",
                triggeredRules: "Triggered rules",
                notTriggeredRules: "Not triggered",
                hitsOf: "hits",
                transactions: "transactions",
                skippedRules: "{{count}} disabled rule(s) skipped.",
                showTransactions: "Show scenario transactions",
                txAmount: "Amount",
                txType: "Type",
                txCount: "Count",
                txDescription: "Description",
                risk_high: "High Risk",
                risk_medium: "Medium Risk",
                scenarios: {
                    structuring: {
                        name: "Structuring",
                        description: "5 cash deposits just below the €10,000 reporting threshold — a classic structuring (layering) pattern.",
                        expectedTriggers: "Expected to trigger: AMOUNT rules with threshold ≤ 9,750 EUR and FREQUENCY rules with threshold ≤ 5.",
                    },
                    smurfing: {
                        name: "Smurfing",
                        description: "8 small transfers from different accounts plus an aggregated transaction totalling €25,500. Typical smurfing network behaviour.",
                        expectedTriggers: "Expected to trigger: FREQUENCY rules with threshold ≤ 8, AMOUNT rules with threshold ≤ 3,000 EUR (individual), and AMOUNT rules ≤ 25,500 EUR (aggregate).",
                    },
                    tbml: {
                        name: "Trade-Based Money Laundering",
                        description: "3 import/export transactions involving high-risk jurisdictions (Iran, Myanmar, North Korea) with elevated amounts.",
                        expectedTriggers: "Expected to trigger: PATTERN rules matching high-risk jurisdiction keywords (iran, myanmar, nordkorea) and AMOUNT rules with threshold ≤ 12,000 EUR.",
                    },
                    pep: {
                        name: "PEP Transactions",
                        description: "3 transactions with descriptions suggesting politically exposed persons — politician payments, ministerial consulting fees, and diplomatic honoraria.",
                        expectedTriggers: "Expected to trigger: PATTERN rules for PEP keywords (pep, politiker, minister, diplomat) and AMOUNT rules with threshold ≤ 30,000 EUR.",
                    },
                    large_cash: {
                        name: "Large Cash Transactions",
                        description: "2 unusually large cash deposits of €75,000 and €50,000, significantly exceeding normal transaction patterns.",
                        expectedTriggers: "Expected to trigger: AMOUNT rules with threshold ≤ 50,000 EUR.",
                    },
                },
            },
        },
        periodicReporting: {
            title: "Periodic Reporting",
            subtitle: "Generate, manage, and export periodic AML compliance reports.",
            tabAnnual: "Annual Reports",
            tabComparison: "Comparison View",
            tabExport: "Export",
            tabSchedule: "Schedule",
            annual: {
                title: "Annual Reports",
                description: "Create and manage annual AML risk assessment reports. Track draft, finalized, and submitted reports.",
                draftReports: "Draft Reports",
                finalizedReports: "Finalized Reports",
                submittedReports: "Submitted Reports",
                noData: "No reports available. Create an annual report to get started.",
                createReport: "Create Annual Report",
                year: "Year",
                selectYear: "Select year...",
                invalidYear: "Enter a valid year (2000–2100)",
                status: "Status",
                status_draft: "Draft",
                status_review: "Under Review",
                status_approved: "Approved",
                filterByStatus: "Filter by status",
                allStatuses: "All",
                actions: "Actions",
                completion: "Completion",
                completionEmpty: "Empty",
                completionFull: "Complete",
                view: "View",
                deleteReport: "Delete",
                confirmDelete: "Are you sure you want to delete the report for {{year}}?",
                reportYear: "Report year",
                yearAlreadyExists: "A report for {{year}} already exists.",
                form: {
                    title: "Report {{year}}",
                    backToList: "Back to list",
                    save: "Save",
                    saved: "Saved",
                    unsavedChanges: "Unsaved changes",
                    submitForReview: "Submit for review",
                    approve: "Approve",
                    rejectWithComment: "Reject (return to draft)",
                    rejectionComment: "Rejection comment",
                    rejectionCommentPlaceholder: "Explain why the report is being returned to draft...",
                    rejectConfirm: "Reject report",
                    rejectionCommentRequired: "A comment is required when rejecting a report.",
                    approvalHistory: "Approval History",
                    noHistory: "No status transitions yet.",
                    lockedMessage: "This report is approved and locked. No further changes can be made.",
                    lockedBadge: "Locked",
                    historyFrom: "from",
                    historyTo: "to",
                    historyBy: "by",
                    historyDate: "Date",
                    historyComment: "Comment",
                    allApprovalStepsCompleted: "All {{count}} approval step(s) completed",
                    approvalFlowTitle: "Approval Flow",
                    multiStepFlowTitle: "Multi-Step Approval Workflow",
                    toastSubmitted: "Report submitted for review.",
                    toastApproved: "Report approved.",
                    toastRejected: "Report returned to draft.",
                    toastStepApproved: "Step approved. Moving to the next step.",
                    toastAllStepsApproved: "All steps approved. Report is now locked.",
                    distributionLogTitle: "Distribution",
                    distributionLogSentAt: "Distributed",
                    distributionLogSentBy: "Sent by",
                    distributionLogRecipients: "Recipients",
                    distributionLogNoRecipients: "No distribution list configured. Configure recipients in Admin → Distribution List.",
                    distributionLogStatusSent: "Sent",
                    distributionLogRole: "Role",
                    stepLabel: "Step",
                    stepSubmitted: "Submitted",
                    stepStatus_pending: "Pending",
                    stepStatus_approved: "Approved",
                    stepStatus_rejected: "Rejected",
                    currentStep: "Current step",
                    approveStep: "Approve this step",
                    approveComment: "Comment (optional)",
                    approveCommentPlaceholder: "Add a note for this approval step...",
                    stepApprovedBy: "Approved by",
                    sectionGeneral: "General Information",
                    responsiblePerson: "Responsible person",
                    responsiblePersonPlaceholder: "Full name",
                    reportingDate: "Reporting date",
                    notes: "Notes",
                    notesPlaceholder: "General notes for this reporting period...",
                    sectionCustomers: "Customer Statistics",
                    totalCustomers: "Total customers",
                    highRiskCustomers: "High-risk customers",
                    pepCustomers: "PEP customers",
                    newCustomers: "New customers",
                    terminatedCustomers: "Terminated customer relationships",
                    sectionTransactions: "Transaction Monitoring",
                    reviewedTransactions: "Reviewed transactions",
                    flaggedTransactions: "Flagged transactions",
                    flaggedTransactionsValue: "Value of flagged transactions (SEK)",
                    sectionReporting: "Reporting",
                    strFiled: "Suspicious transaction reports (STR) filed",
                    largeAmountReports: "Large amount reports filed",
                    sectionTraining: "Training",
                    trainedEmployees: "Trained employees",
                    avgTrainingHours: "Average training hours per employee",
                    sectionRiskAssessment: "Risk Assessment",
                    overallRiskLevel: "Overall risk level",
                    deficienciesFound: "Identified deficiencies",
                    deficienciesFoundPlaceholder: "Describe any deficiencies found...",
                    actionsTaken: "Actions taken",
                    actionsTakenPlaceholder: "Describe actions taken to address deficiencies...",
                    plannedActions: "Planned actions",
                    plannedActionsPlaceholder: "Describe planned future actions...",
                    sectionAttachments: "Internal Support Material",
                    attachmentsInternalNote: "Internal reference material — not included in the report export.",
                    addAttachment: "Add attachment",
                    attachmentName: "Filename",
                    attachmentSize: "Size",
                    attachmentDate: "Uploaded",
                    attachmentUploader: "Uploaded by",
                    attachmentUploadedBy: "By: {{user}}",
                    attachmentVersionCount: "{{count}} versions",
                    attachmentShowHistory: "Version history",
                    attachmentHideHistory: "Hide history",
                    attachmentAddVersion: "Upload new version",
                    attachmentVersionHistory: "Version history",
                    attachmentVersionNum: "Version",
                    attachmentLatest: "Latest",
                    noAttachments: "No attachments",
                    deleteAttachment: "Remove",
                    sectionMeta: "Report Metadata",
                    financialEntityId: "FI Entity ID",
                    consolidationScope: "Consolidation Scope",
                    consolidationConsolidated: "Consolidated",
                    consolidationIndividual: "Individual",
                    expandAll: "Expand All",
                    collapseAll: "Collapse All",
                    sectionCount: "{{count}} sections",
                    autoFillBtn: "Auto-fill from modules",
                    autoFillTitle: "Data from other modules",
                    autoFillClose: "Close",
                    autoFillApply: "Apply",
                    autoFillApplyAll: "Apply all",
                    autoFillApplied: "Applied",
                    autoFillAllApplied: "All suggestions applied.",
                    autoFillSource: "Source: {{source}} • {{date}}",
                    autoFillGeoRiskTitle: "Geographic Risk (GeoRisk)",
                    autoFillBwraTitle: "Business-Wide Risk Assessment (BWRA)",
                    autoFillCustomerRiskTitle: "Customer Risk",
                    autoFillHighRiskCountries: "{{count}} high-risk countries identified",
                    autoFillNoHighRiskCountries: "No high-risk countries identified",
                    autoFillRiskSummary: "{{total}} active risks, {{high}} rated high/very-high",
                    autoFillNoRisks: "No risks registered in BWRA",
                    autoFillModelStatus: "{{count}} saved models, {{params}} parameters",
                    autoFillNoModels: "No customer risk models saved",
                    autoFillFieldB13: "B13: Countries rated high risk",
                    autoFillFieldB14: "B14: Third-country high-risk consideration → Yes",
                    autoFillFieldB1: "B1: General risk assessment conducted → Yes",
                    autoFillFieldB2: "B2: Date of latest risk assessment update",
                    autoFillSourceBadge: "Auto-filled from {{source}}",
                    autoFillActiveIndexes: "{{count}} active indexes",
                    autoFillLastUpdated: "Last updated: {{date}}",
                    autoFillNoFieldMappings: "No direct field suggestions available",
                    autoFillOverrideNote: "Values can be edited after applying.",
                },
            },
            comparison: {
                title: "Comparison View",
                description: "Compare risk metrics across reporting periods to identify trends and changes.",
                comingSoon: "Period comparison will be available in a future release.",
                needMoreReports: "Create at least 2 annual reports to enable multi-year comparison.",
                selectYears: "Select years to compare:",
                filterSection: "Filter by section:",
                allSections: "All sections",
                field: "Field",
                selectAtLeastTwo: "Select at least two years to compare.",
                legendUp: "Increase vs previous year",
                legendDown: "Decrease vs previous year",
                legendSame: "No change",
                legendChanged: "Value changed",
                deltaNote: "Numeric fields show absolute and % change vs previous year",
                dynRowsCount: "Number of entries",
                dynRowsBadge: "Dynamic rows",
                exportCsv: "Export CSV",
                csvSection: "Section",
                exportCsvFilename: "comparison_{{years}}.csv",
                showChangedOnly: "Show changed rows only",
                showAll: "Show all rows",
                changedRowsSummary: "{{changed}} of {{total}} fields changed",
                noChangedRows: "No differences found between the selected years.",
                unchangedIndicator: "= No change",
            },
            export: {
                title: "Export Reports",
                description: "Export approved reports to JSON for regulatory submission and internal review.",
                noReports: "No reports available to export. Create an annual report first.",
                tableYear: "Year",
                tableStatus: "Status",
                tableUpdated: "Last Updated",
                tableActions: "Actions",
                exportReport: "Export JSON",
                exportAll: "Export All Approved",
                noApproved: "No approved reports to export.",
                exportedAt: "Exported",
                successMessage: "Report {{year}} exported successfully.",
                exportAllSuccess: "{{count}} approved report(s) exported successfully.",
                fiExportBtn: "Export to FI JSON (v4.0)",
                fiExportSuccess: "FI JSON export for {{year}} completed.",
                fiValidationTitle: "Validation warning",
                fiValidationWarning: "The following required fields are empty. The file will still be generated but may be rejected by Finansinspektionen.",
                fiMissingFieldsLabel: "Missing fields:",
                fiExportAnyway: "Export anyway",
                fiSchemaFieldsMissing: "{{count}} required data field(s) in the report schema are empty.",
                fiSchemaFieldsLabel: "Missing schema fields:",
                fiAndMore: "...and {{count}} more",
                fiFieldLabel_reportingDate: "Reporting date",
                fiFieldLabel_financialEntityId: "Financial entity ID",
                fiFieldLabel_consolidationScope: "Consolidation scope",
            },
            schema: {
                sectionA0101: "A. General Company Information",
                sectionA0102: "A. Company Operations per Country",
                sectionA0201: "B. Risk Assessment and Procedures",
                sectionA0301: "C. Customer Stock",
                sectionA0401: "D. Transaction Monitoring",
                sectionA0501: "E. Sanctions",
                sectionA0601: "F. Training",
                sectionA0701: "G. Cross-border Transactions",
                sectionA0702: "G. Cross-border Transactions per Country",
                sectionA0801: "H. Frozen Assets",
                groupHeadingA0301_010: "Number of customers with tax residence in Sweden",
                groupHeadingA0301_030: "Number of customers with tax residence in EU/EEA (excl. Sweden)",
                groupHeadingA0301_050: "Number of customers with tax residence outside EU/EEA",
                groupHeadingA0301_070: "How many customers are assessed as high risk?",
                groupHeadingA0301_090: "Number of customers identified as PEP, family member of PEP, or known associate of PEP",
                groupHeadingA0301_150: "Established business relationships",
                groupHeadingA0301_160: "For how many of the company's established business relationships are current and sufficient customer due diligence data missing?",
                groupHeadingA0301_180: "For banks, if applicable: correspondent relationships you provide, with tax residence in:",
                groupHeadingA0301_210: "For banks, if applicable: correspondent relationships you hold, with tax residence in:",
                addRow: "Add Row",
                deleteRow: "Remove",
                noRows: "No rows yet. Click 'Add Row' to add.",
                schemaVersion: "Schema: AML v{{version}}",
                searchPlaceholder: "Search...",
                selected: "selected",
                showMore: "... and {{count}} more. Search to filter.",
                selectedItems: "Selected:",
                removeItem: "Remove",
            },
            validation: {
                required: "Required field",
                invalidNumber: "Must be a number",
                minValue: "Minimum value: {{min}}",
                maxValue: "Maximum value: {{max}}",
                crossFieldLte: "{{fiId}} must not exceed {{refFiId}} (entered: {{value}}, limit: {{max}})",
                crossFieldRequiredIf: "{{fiId}} is required when {{condFiId}} is filled",
            },
            comments: {
                toggleTitle: "Comments",
                noComments: "No comments yet.",
                placeholder: "Write a comment... (Ctrl+Enter to save)",
                save: "Save",
                close: "Close",
                delete: "Delete comment",
            },
            interpretationComment: {
                label: "Interpretation comment",
                toggle: "Interpretation comment",
                placeholder: "Document how your organization interprets this question (for internal use)...",
                hint: "Saved together with the report. Inherited by new reports.",
            },
            regulatory: {
                toggleTitle: "Show regulatory requirements",
                showReqs: "Regulatory basis",
                coverageTitle: "Regulatory coverage check",
                coverageSuffix: "regulations fully covered",
                coverageDesc: "Shows whether mandatory fields for each regulatory requirement are complete.",
                fieldsFilledSuffix: "required fields filled",
                reqs: {
                    FFFS_kap2: "Customer identification and risk assessment",
                    FFFS_kap3: "Customer due diligence (KYC)",
                    FFFS_kap4: "Enhanced due diligence",
                    FFFS_kap5: "Sanctions screening",
                    FFFS_kap6: "Geographic risk and cross-border activities",
                    FFFS_kap7: "Transaction monitoring and STR reporting",
                    FFFS_kap8: "Staff training and competence",
                    AMLD4_Art6: "National risk assessment (NRA)",
                    AMLD4_Art13: "Customer due diligence measures",
                    AMLD4_Art17: "Enhanced measures for high-risk third countries",
                    AMLD4_Art30: "Beneficial ownership information (corporates)",
                    AMLD4_Art33: "Suspicious transaction reporting",
                    AMLD4_Art46: "Staff training",
                    AMLD4_Art47: "Sanctions compliance",
                },
            },
            schedule: {
                title: "Report Schedule",
                description: "Configure automatic creation of annual report drafts. When enabled, a new draft will be created automatically on the scheduled date each year.",
                enabledLabel: "Enable automatic report creation",
                enabledDescription: "Automatically creates a new draft report on the scheduled date each year.",
                monthLabel: "Creation month",
                dayLabel: "Day of month",
                targetYearLabel: "Report covers",
                targetYearCurrentYear: "Current year",
                targetYearPreviousYear: "Previous year",
                assigneesLabel: "Notify users (comma-separated)",
                assigneesPlaceholder: "e.g. user1, user2",
                save: "Save Schedule",
                saved: "Schedule saved",
                nextRunLabel: "Next scheduled run",
                lastAutoCreatedLabel: "Last auto-created report",
                lastAutoCreatedValue: "Report {{year}} created on {{date}}",
                noLastAutoCreated: "No reports have been auto-created yet.",
                autoCreatedBanner: "Automatically created a draft report for {{year}}.",
                months: {
                    "1": "January", "2": "February", "3": "March", "4": "April",
                    "5": "May", "6": "June", "7": "July", "8": "August",
                    "9": "September", "10": "October", "11": "November", "12": "December",
                },
            },
        },
        administration: {
            welcome: "Administration module",
            chooseModule: "Choose a module from the sidebar to get started.",
            tabs: {
                overview: "Overview",
                audit: "Audit Logs",
                users: "Users",
                entities: "Entities",
                settings: "Settings",
                licenses: "Licenses",
                validation: "Validation Scheduler",
                activity: "Activity Dashboard",
                export: "Regulatory Export",
                approval: "Approval Workflow",
                distribution: "Distribution List",
                manuals: "User Manuals",
                backup: "Backup & Restore",
                systemHealth: "System Health",
                reportTemplates: "Report Templates",
                retention: "Data Retention",
                dataIsolation: "Data Isolation",
                userImport: "User Import",
                apiKeys: "API Keys",
                dataSources: "Data Sources",
                dataMigration: "Data Migration",
                configTemplates: "Config Templates",
                notificationSettings: "Notifications",
                entityMigration: "Entity Migration",
                ssoConfig: "SSO / Identity"
            },
            sections: {
                overview: { label: "Overview", description: "Module status, audit logs, and system activity" },
                usersAccess: { label: "Users & Access", description: "User management, roles, imports, and workflows" },
                entities: { label: "Entities", description: "Entity management, hierarchy, and licenses" },
                dataIntegration: { label: "Data & Integration", description: "Data sources, migration, isolation, and retention" },
                configuration: { label: "Configuration", description: "Settings, templates, validation, and notifications" },
                help: { label: "Help", description: "User manuals and documentation" },
            },
            toolDescriptions: {
                overview: "Module statistics and system status at a glance",
                audit: "Browse and filter all system audit log entries",
                activity: "User activity trends and login statistics",
                systemHealth: "Monitor services, storage, and performance",
                users: "Create, edit, and manage user accounts and roles",
                userImport: "Bulk-import users from CSV or directory",
                approval: "Configure multi-step approval chains for reports",
                distribution: "Manage report recipient distribution lists",
                entities: "Add and edit legal entities and hierarchy",
                entityMigration: "Move data between entities safely",
                licenses: "Assign and track module licenses per entity",
                dataSources: "Register external database and API connections",
                dataMigration: "Migrate and transform data between sources",
                dataIsolation: "Verify that entity data boundaries are correct",
                backup: "Create, schedule, and restore system backups",
                retention: "Configure how long data is stored per module",
                settings: "System-wide configuration and module toggles",
                configTemplates: "Reusable configuration templates",
                notificationSettings: "Configure alerts and notification channels",
                reportTemplates: "Edit report layout and branding templates",
                validation: "Schedule automated model validation runs",
                export: "Export data for regulatory submissions",
                apiKeys: "Manage API keys for external integrations",
                manuals: "Download user manuals and quick reference guides",
                ssoConfig: "Configure SSO with Azure AD, Okta, ADFS or other identity providers",
            },
            backToSection: "Back to {{section}}",
            ssoConfig: {
                title: "SSO & Identity Provider",
                subtitle: "Configure single sign-on (SSO) for your organisation using OIDC or SAML 2.0",
                mockNotice: "Architecture mode: This configuration is saved locally. A real IdP integration requires a backend SSO proxy. Contact your deployment team to connect to Azure AD, Okta, or ADFS.",
                providerSelection: "Authentication Provider",
                active: "Active",
                testConnection: "Test connection",
                testing: "Testing...",
                reset: "Reset",
                save: "Save",
                saving: "Saving...",
                savedSuccess: "SSO configuration saved",
                testSuccess: "Connection test successful",
                testFailed: "Connection test failed",
                testResultOk: "Connection successful — IdP responded as expected",
                testResultFail: "Connection failed — check your configuration",
                providers: {
                    local: {
                        label: "Local accounts",
                        desc: "Users authenticate with username and password stored in the system",
                        activeTitle: "Local authentication is active",
                        activeDesc: "Users sign in with their local credentials. To enable SSO, select OIDC or SAML 2.0 above and save the configuration."
                    },
                    oidc: {
                        label: "OIDC / OAuth 2.0",
                        desc: "OpenID Connect — Azure AD, Okta, Google, Auth0"
                    },
                    saml: {
                        label: "SAML 2.0",
                        desc: "SAML federation — ADFS, Shibboleth, PingFederate"
                    }
                },
                oidc: {
                    title: "OpenID Connect Configuration",
                    desc: "Enter the discovery URL and client credentials provided by your identity provider.",
                    discoveryUrl: "Discovery URL",
                    discoveryUrlHint: "The /.well-known/openid-configuration endpoint of your IdP",
                    clientId: "Client ID",
                    clientIdPlaceholder: "e.g. a1b2c3d4-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
                    clientSecret: "Client Secret",
                    redirectUri: "Redirect URI",
                    scope: "Scopes",
                    scopeHint: "Space-separated list of OAuth scopes"
                },
                saml: {
                    title: "SAML 2.0 Configuration",
                    desc: "Enter the entity ID and IdP metadata URL provided by your identity provider.",
                    entityId: "Service Provider Entity ID",
                    entityIdHint: "The unique identifier for this application (usually a URL)",
                    idpMetadataUrl: "IdP Metadata URL",
                    idpMetadataUrlHint: "The URL where your IdP publishes its SAML metadata",
                    acsUrl: "Assertion Consumer Service URL",
                    nameIdFormat: "NameID Format",
                    nameIdEmail: "Email Address",
                    nameIdPersistent: "Persistent",
                    nameIdTransient: "Transient",
                    nameIdUnspecified: "Unspecified"
                },
                errors: {
                    missing_required_fields: "Required fields are missing",
                    invalid_discovery_url: "Discovery URL must start with https://",
                    invalid_metadata_url: "Metadata URL must start with https://",
                    unsupported_provider: "Unsupported provider type"
                },
                docs: {
                    title: "Supported identity providers",
                    azureAD: "Azure Active Directory (Entra ID) — OIDC and SAML 2.0",
                    okta: "Okta — OIDC and SAML 2.0",
                    adfs: "Active Directory Federation Services (ADFS) — SAML 2.0"
                }
            },
            dataSources: {
                title: "Data Sources",
                subtitle: "Register and manage external data source connections. Test connectivity and configure credentials for each source.",
                addBtn: "Add Data Source",
                addTitle: "Add Data Source",
                editTitle: "Edit Data Source",
                labelName: "Name",
                namePlaceholder: "e.g. Production Supabase, Core Banking REST API...",
                labelType: "Type",
                type_supabase: "Supabase",
                type_postgres: "PostgreSQL",
                type_mssql: "SQL Server (MSSQL)",
                type_rest: "REST API",
                type_csv: "CSV File",
                type_mock: "Mock (Demo)",
                field_url: "Project URL",
                field_url_hint: "https://xxxx.supabase.co",
                field_anonKey: "Anon / Service Key",
                field_anonKey_hint: "Supabase anon or service role key",
                field_host: "Host",
                field_host_hint: "e.g. db.example.com or 10.0.0.1",
                field_port: "Port",
                field_port_hint: "e.g. 5432",
                field_database: "Database",
                field_database_hint: "Database name",
                field_user: "Username",
                field_user_hint: "Database user",
                field_password: "Password",
                field_password_hint: "Database password",
                field_baseUrl: "Base URL",
                field_baseUrl_hint: "https://api.example.com",
                field_token: "Bearer Token",
                field_token_hint: "Optional authorization token",
                colName: "Name",
                colType: "Type",
                colStatus: "Status",
                colLastTested: "Last Tested",
                colActions: "Actions",
                statusOk: "OK",
                statusError: "Error",
                statusUnknown: "Not tested",
                testBtn: "Test Connection",
                testing: "Testing…",
                editBtn: "Edit",
                deleteBtn: "Delete",
                cancel: "Cancel",
                saveBtn: "Save",
                emptyTitle: "No data sources yet",
                emptyDesc: "Add your first data source to connect Prime RMS to external databases or APIs.",
                loading: "Loading…",
                createdToast: "Data source \"{{name}}\" created.",
                updatedToast: "Data source \"{{name}}\" updated.",
                deletedToast: "Data source \"{{name}}\" deleted.",
                testOk: "Connected successfully ({{ms}} ms)",
                testFail: "Connection failed: {{error}}",
                testOkToast: "\"{{name}}\" — connected ({{ms}} ms)",
                testFailToast: "\"{{name}}\" — connection failed: {{error}}",
                confirmDeleteMsg: "Delete data source \"{{name}}\"? This cannot be undone.",
                errorNameRequired: "Name is required.",
                testSaveFirst: "Save the data source first, then test the connection.",
                saveError: "Failed to save data source."
            },
            distribution: {
                title: "Report Distribution List",
                subtitle: "Configure who receives approved reports. When a report is fully approved, all active recipients are notified and delivery is tracked.",
                addRecipient: "Add Recipient",
                nameLabel: "Name",
                namePlaceholder: "e.g. Chief Compliance Officer",
                emailLabel: "Email",
                emailPlaceholder: "e.g. cco@example.com",
                roleLabel: "Role",
                role_board: "Board Member",
                role_auditor: "External Auditor",
                role_compliance: "Compliance Officer",
                role_regulator: "Regulator",
                role_other: "Other",
                removeRecipient: "Remove",
                noRecipients: "No recipients configured. Add recipients to enable distribution when reports are approved.",
                savedToast: "Distribution list saved.",
                errorNameRequired: "Recipient name is required.",
                errorEmailRequired: "A valid email address is required.",
                tableHeaderName: "Name",
                tableHeaderEmail: "Email",
                tableHeaderRole: "Role",
                tableHeaderActions: "Actions",
                addSectionHeading: "Add recipient",
                recipientsSectionHeading: "Recipients ({{count}})",
            },
            approval: {
                title: "Approval Workflow Configuration",
                subtitle: "Configure multi-step approval requirements for periodic reports.",
                enabled: "Multi-step approval enabled",
                disabled: "Multi-step approval disabled",
                toggleHint: "When enabled, each report must pass all configured steps before being approved.",
                disabledInfo: "Multi-step approval is currently disabled. Reports use the standard single-step review process.",
                stepsHeading: "Approval Steps",
                stepsHint: "Define the steps a report must pass. Order matters — steps are processed top to bottom.",
                noSteps: "No steps configured yet. Click \"Add step\" to define the first approval step.",
                stepNamePlaceholder: "Step name (e.g. Compliance Officer Review)",
                addStep: "Add step",
                removeStep: "Remove step",
                moveUp: "Move up",
                moveDown: "Move down",
                savedToast: "Approval workflow configuration saved.",
                errorNoSteps: "Enable requires at least one non-empty step.",
            },
            entities: {
                title: "Entity Management",
                addEntity: "Add Entity",
                editEntity: "Edit Entity",
                deleteEntity: "Delete Entity",
                createEntity: "Create Entity",
                saveChanges: "Save Changes",
                cancel: "Cancel",
                totalEntities: "Total Entities",
                parentCompanies: "Parent Companies",
                subsidiaries: "Child Entities",
                companyStructure: "Company Structure",
                noEntities: "No entities configured. Add a parent company to get started.",
                entityName: "Entity Name",
                namePlaceholder: "Enter company name",
                orgNumber: "Organization Number",
                orgNumberPlaceholder: "e.g. 556000-0001",
                entityType: "Entity Type",
                country: "Country",
                parentEntity: "Parent Entity",
                noParent: "None (root entity)",
                description: "Description",
                descriptionPlaceholder: "Brief description of the entity...",
                addChild: "Add subsidiary",
                confirmDelete: "Are you sure you want to delete \"{{name}}\"?",
                confirmDeleteWithChildren: "\"{{name}}\" has {{count}} direct subsidiaries. They will be moved up one level. Continue?",
                type_parent: "Parent Company",
                type_subsidiary: "Subsidiary",
                type_branch: "Branch",
                type_joint_venture: "Joint Venture",
                countryCodePlaceholder: "SE",
                countrySelectPlaceholder: "Search country...",
                entityLabel: "Entity",
                reportColorsHeading: "Report Colors (Override)",
                reportColorsDesc: "Optional per-entity color scheme. Leave blank to use the global branding colors.",
                reportColorPrimary: "Primary Color",
                reportColorSecondary: "Secondary Color",
                reportColorAccent: "Accent Color",
                reportColorsUseGlobal: "Use global branding colors",
                errorCircularParent: "Cannot set a descendant as parent — this would create a circular reference.",
                errorInvalidCountryCode: "Enter an ISO 3166-1 country code, e.g. SE, DE, US",
                activeEntity: "Active Entity",
                setAsActive: "Set as active entity",
                searchPlaceholder: "Search by name or org. number…",
                noSearchResults: "No entities match your search.",
                viewList: "List",
                viewHierarchy: "Hierarchy",
                hierarchy: {
                    expandAll: "Expand All",
                    collapseAll: "Collapse All",
                    expand: "Expand",
                    collapse: "Collapse",
                    usersTitle: "Users with access to this entity",
                    usersHint: "users with access",
                    licensesTitle: "{{active}} of {{total}} modules licensed",
                    licensesHint: "active / no licenses"
                },
                wizard: {
                    title: "New Entity Setup",
                    stepInfo: "Step {{current}} of {{total}}",
                    step_grunddata: "Basic Info",
                    step_moduler: "Modules",
                    step_access: "User Access",
                    step_colors: "Report Colors",
                    step_summary: "Summary",
                    back: "Back",
                    next: "Next",
                    finish: "Create Entity",
                    successToast: "Entity \"{{name}}\" created successfully",
                    modulesDesc: "Select which modules to license for this entity.",
                    accessDesc: "Configure which users have access to this entity.",
                    accessAutoGroup: "Global Access",
                    accessSpecificGroup: "Restricted Access",
                    accessAutomatic: "All entities",
                    colorsDesc: "Optionally configure entity-specific report colors.",
                    summaryDesc: "Review the configuration before creating the entity.",
                    summaryModules: "Modules",
                    summaryAccess: "User Access",
                    summaryNoModules: "No modules selected",
                    summaryNoSpecificUsers: "No specific users configured",
                    summaryUsersAutomatic: "{{count}} user(s) with global access (automatic)",
                    summaryColors: "Report Colors",
                    summaryColorsGlobal: "Using global branding colors",
                    summaryColorsCustom: "Custom colors configured",
                    noUsers: "No users found"
                }
            },
            overview: {
                title: "Administration Overview",
                subtitle: "System status, user statistics, and recent activity.",
                systemStatus: "System Status",
                allSystemsOperational: "All systems operational",
                activeUsers: "Active Users",
                activeUsersCount: "{{count}} active of {{total}} total",
                totalUsers: "Total Users",
                activeUsersLabel: "Active",
                inactiveUsersLabel: "Inactive",
                modules: "Modules",
                modulesOperational: "{{count}} modules operational",
                userStats: "User Statistics",
                roleAdmins: "Admins",
                roleAnalysts: "Analysts",
                roleViewers: "Viewers",
                moduleSummary: "Module Summary",
                indexCount: "{{count}} indexes",
                countryCount: "{{count}} countries",
                modelCount: "{{count}} models",
                parameterCount: "{{count}} parameters",
                riskCount: "{{count}} risks",
                controlCount: "{{count}} controls",
                reportCount: "{{count}} reports",
                ruleCount: "{{count}} rules",
                prApprovedCount: "{{count}} approved",
                txActiveCount: "{{count}} active",
                noModulesEnabled: "No risk modules are currently enabled.",
                recentActivity: "Recent Activity",
                noRecentActivity: "No recent activity recorded.",
                showingXofY: "Showing {{shown}} of {{total}} events",
                viewAll: "View All",
                colTime: "Time",
                colUser: "User",
                colModule: "Module",
                colAction: "Action",
                action_CREATE: "Created",
                action_UPDATE: "Updated",
                action_DELETE: "Deleted",
                action_LOGIN: "Login",
                action_LOGOUT: "Logout",
                action_DATA_IMPORT: "Data Import",
                action_DATA_EXPORT: "Data Export",
                action_STATUS_TRANSITION: "Status Change",
                action_COMMENT: "Comment",
                action_EXPORT: "Export",
                module_Auth: "Authentication",
                module_GeoRisk: "Geographical Risk",
                module_CustomerRisk: "Customer Risk",
                module_BWRA: "BWRA",
                module_Admin: "Administration",
                module_Settings: "Settings"
            },
            audit: {
                title: "Audit Logs",
                filters: "Filters",
                export: "Export CSV",
                clearAll: "Clear All",
                showingResults: "Showing {{filtered}} of {{total}} log entries",
                noLogs: "No audit logs found matching the current filters.",
                allModules: "All Modules",
                allActions: "All Actions",
                allLevels: "All Levels",
                fromDate: "From Date",
                toDate: "To Date",
                filterByUser: "Filter by user...",
                searchPlaceholder: "Search module, action, user, details…",
                searchLabel: "Free-text search",
                clearSearch: "Clear search",
                clearFilters: "Clear Filters",
                viewDetails: "View details",
                confirmClearAll: "Are you sure you want to clear all audit logs? This action cannot be undone.",
                clearAllSuccess: "Cleared {{count}} audit log entries.",
                clearAllAdminOnly: "Only administrators can clear the audit log.",
                logDetails: "Log Details",
                timestamp: "Timestamp",
                user: "User",
                module: "Module",
                action: "Action",
                level: "Level",
                details: "Details",
                sessionInfo: "Session Info",
                time: "Time",
                actions: "Actions",
                levelDefault: "INFO",
                chainIntegrityBroken: "WARNING: Audit trail integrity is compromised. Possible manipulation detected. Contact your administrator.",
                chainIntegrityOk: "Audit trail integrity verified.",
                paginationPrevious: "Previous",
                paginationNext: "Next",
                paginationPageInfo: "Page {{current}} of {{total}}",
                paginationEntries: "{{from}}–{{to}} of {{filtered}} entries",
                exportSuccess: "Exported {{count}} log entries to CSV",
                exportMenu: "Export",
                exportCsv: "Export CSV",
                exportExcel: "Export Excel",
                exportPdf: "Export PDF",
                exportExcelSuccess: "Exported {{count}} log entries to Excel",
                exportPdfSuccess: "Exported {{count}} log entries to PDF"
            },
            users: {
                title: "User Management",
                addUser: "Add User",
                editUser: "Edit User",
                deleteUser: "Delete User",
                createUser: "Create User",
                createRole: "Create Role",
                saveChanges: "Save Changes",
                cancel: "Cancel",
                totalUsers: "Total Users",
                activeUsers: "Active",
                admins: "Admins",
                searchPlaceholder: "Search users...",
                allRoles: "All Roles",
                allStatuses: "All Statuses",
                role_admin: "Admin",
                role_analyst: "Analyst",
                role_viewer: "Viewer",
                statusActive: "Active",
                statusInactive: "Inactive",
                toggleStatus: "Toggle status",
                confirmToggleStatus: "Change status of \"{{name}}\" to {{status}}?",
                colUser: "User",
                colRole: "Role",
                colStatus: "Status",
                colModules: "Module Access",
                colLastLogin: "Last Login",
                colActions: "Actions",
                noUsersFound: "No users found matching the current filters.",
                showingCount: "Showing {{filtered}} of {{total}} users",
                confirmDelete: "Are you sure you want to delete",
                cannotDeleteSelf: "You cannot delete your own account.",
                cannotDeleteLastAdmin: "Cannot delete the last administrator account.",
                firstName: "First Name",
                firstNamePlaceholder: "Enter first name",
                lastName: "Last Name",
                lastNamePlaceholder: "Enter last name",
                email: "Email (login)",
                emailPlaceholder: "Enter email address",
                password: "Password",
                passwordPlaceholder: "Enter password",
                confirmPassword: "Confirm Password",
                confirmPasswordPlaceholder: "Re-enter password",
                emailInvalid: "Please enter a valid email address",
                passwordMismatch: "Passwords do not match",
                passwordRequired: "Password is required",
                passwordHint: "The new user will log in with this password",
                changePassword: "Change Password",
                changePasswordHint: "Leave blank to keep the existing password",
                newPassword: "New Password",
                newPasswordPlaceholder: "Enter new password",
                confirmNewPasswordPlaceholder: "Re-enter new password",
                passwordTooWeak: "Password does not meet security requirements",
                passwordReqLength: "At least 8 characters",
                passwordReqUppercase: "At least 1 uppercase letter (A–Z)",
                passwordReqLowercase: "At least 1 lowercase letter (a–z)",
                passwordReqNumber: "At least 1 number (0–9)",
                passwordReqSpecial: "At least 1 special character (!@#$...)",
                module_Dashboard: "Dashboard",
                module_GeoRisk: "Geographical Risk",
                module_CustomerRisk: "Customer Risk",
                module_BWRA: "BWRA",
                module_Admin: "Administration",
                module_TransactionMonitoring: "Transaction Monitoring",
                module_PeriodicReporting: "Periodic Reporting",
                moduleShort_Dashboard: "Dash",
                moduleShort_GeoRisk: "Geo",
                moduleShort_CustomerRisk: "Cust",
                moduleShort_BWRA: "BWRA",
                moduleShort_Admin: "Adm",
                moduleShort_TransactionMonitoring: "TxMon",
                moduleShort_PeriodicReporting: "Rpt",
                permissions: "Permissions",
                permissionLevel: "Permission Level",
                accessEnabled: "Access",
                noAccess: "No access",
                rolePresetHint: "Changing role applies default permissions for that role",
                perm_view: "View",
                perm_edit: "Edit",
                perm_admin: "Administer",
                permShort_view: "V",
                permShort_edit: "E",
                permShort_admin: "A",
                entityAccess: "Entity Access",
                entityAccessHint: "Select which entities this user can access",
                allEntities: "All entities",
                allEntitiesHint: "User can access all current and future entities",
                specificEntities: "Specific entities",
                noEntitiesSelected: "No entities selected",
                noEntityAccessWarning: "No entities selected — user will not be able to see any data",
                entityCount: "{{count}} entity/entities",
                perEntityRoles: "Per-entity Roles",
                perEntityRolesHint: "Assign different roles per entity. Leave empty to use the global role.",
                sameAsGlobal: "Same as global role",
                entityRoleOverrides: "{{count}} role override(s)",
                manageRoles: "Manage Roles",
                hideRoles: "Hide Roles",
                rolesSection: "Role Definitions",
                addRole: "Add Role",
                editRole: "Edit Role",
                deleteRole: "Delete Role",
                roleName: "Role Name",
                roleNamePlaceholder: "Enter role name",
                builtIn: "Built-in",
                customRoleLabel: "Custom",
                confirmDeleteRole: "Are you sure you want to delete the role",
                roleInUse: "Cannot delete: role is assigned to one or more users",
                roleBuiltInNoDelete: "Built-in roles cannot be deleted",
                defaultPermissions: "Default Permissions",
                roleNameRequired: "Role name is required",
                noCustomRoles: "No custom roles defined yet",
                roleUsersCount: "{{count}} user(s)",
                createCustomRole: "Create Custom Role",
                roleBuilderTitle: "Role Builder",
                roleBuilderSubtitle: "Define granular permissions per module",
                roleBuilderStep1: "Role Details",
                roleBuilderStep2: "Permissions",
                roleBuilderStep3: "Assign Users",
                roleBuilderPreset: "Start from preset",
                roleBuilderPresetHint: "Optionally select a base role to start from",
                action_read: "Read",
                action_create: "Create",
                action_edit: "Edit",
                action_delete: "Delete",
                action_approve: "Approve",
                actionsMatrixHint: "Select which actions are allowed per module",
                actionsSelectedLabel: "actions",
                assignUsersStep: "Assign to Users",
                assignUsersHint: "Optionally assign the new role to existing users",
                roleCreatedSuccess: "Role created and saved",
                roleBuilderBack: "Back",
                roleBuilderNext: "Next",
                selectAll: "Select all",
                selectNone: "Deselect all"
            },
            settings: {
                title: "System Settings",
                sessionManagement: "Session Management",
                sessionTimeout: "Session Timeout",
                hours: "hours",
                minutes: "minutes",
                sessionTimeoutDesc: "Automatic logout after this time regardless of activity",
                inactivityTimeout: "Inactivity Timeout",
                inactivityTimeoutDesc: "Logout after this time of inactivity",
                warningTime: "Warning Time",
                warningTimeDesc: "Show warning this many minutes before session expires",
                resetSessionSettings: "Reset Session Settings",
                auditLogSettings: "Audit Log Settings",
                retentionDays: "Retention Days",
                retentionDaysDesc: "How many days to keep audit logs",
                maximumLogs: "Maximum Logs",
                maximumLogsDesc: "Maximum number of logs to keep in storage",
                resetAuditSettings: "Reset Audit Settings",
                validationHistorySettings: "Validation History Retention",
                validationHistoryLimit: "Maximum Saved Validation Runs",
                validationHistoryLimitDesc: "Maximum number of validation runs to keep. Older runs are removed automatically. Recommended: at least 100 for 7-year regulatory retention.",
                tabSession: "Session",
                tabAudit: "Audit",
                import: "Import",
                export: "Export",
                saveChanges: "Save Changes",
                resetAllSettings: "Reset All Settings",
                currentRole: "Current role",
                confirmReset: "Reset {{category}} settings to default values?",
                confirmResetAll: "Reset all settings to default values?",
                savedSuccess: "Settings saved successfully - Changes active immediately",
                savedError: "Error saving settings",
                resetSuccess: "Settings reset to defaults - Changes active immediately",
                importedSuccess: "Settings imported successfully - Changes active immediately",
                importError: "Error importing settings",
                importPreviewTitle: "Preview Import Changes",
                importPreviewDesc: "Review the changes below before confirming the import.",
                importPreviewNoChanges: "No changes detected — the imported settings are identical to the current settings.",
                importPreviewChanged: "{{count}} change(s) detected",
                importPreviewCurrent: "Current",
                importPreviewNew: "New",
                importPreviewConfirm: "Confirm Import",
                importPreviewCategorySession: "Session",
                importPreviewCategoryAudit: "Audit Log",
                importPreviewCategoryUi: "Interface",
                importPreviewCategoryBranding: "Branding",
                importPreviewCategoryModules: "Modules",
                importPreviewInvalidFile: "Invalid settings file — missing 'settings' property.",
                tabModules: "Modules",
                modulesSettings: "Module Configuration",
                modulesDesc: "Choose which modules are active and their order in the sidebar.",
                alwaysActive: "Always active",
                moduleEnabled: "Active",
                moduleDisabled: "Inactive",
                moveUp: "Move up",
                moveDown: "Move down",
                resetModulesSettings: "Reset Module Settings",
                tabBranding: "Branding",
                tabThresholds: "KPI Thresholds",
                thresholds: {
                    title: "KPI Threshold Warnings",
                    subtitle: "Configure thresholds for Dashboard KPI cards. Cards show a red warning border and icon when a KPI value meets or exceeds its threshold.",
                    geo_highRisk: "High-Risk Countries",
                    geo_highRiskDesc: "Warn when the number of high-risk (or above) countries reaches this value",
                    bwra_unmitigated: "Unmitigated BWRA Risks",
                    bwra_unmitigatedDesc: "Warn when the number of unmitigated BWRA risks reaches this value",
                    prpt_review: "Reports Awaiting Review",
                    prpt_reviewDesc: "Warn when the number of periodic reports awaiting review reaches this value",
                    threshold: "Threshold",
                    enabled: "Active",
                    reset: "Reset KPI thresholds to defaults",
                },
                brandingSettings: "Report Branding",
                brandingDesc: "Configure company identity shown in PDF reports.",
                companyName: "Company Name",
                companyNameDesc: "Shown in PDF report header. Replaces the default system name.",
                companySubtitle: "Report Subtitle",
                companySubtitleDesc: "Optional secondary line in the PDF report header (e.g. department or document type).",
                companyOrgNumber: "Organization Number",
                companyOrgNumberDesc: "Optional registration/org number shown on the PDF cover page.",
                companyLogo: "Company Logo",
                companyLogoDesc: "Upload a PNG or JPEG logo. It will appear in the top-left of PDF reports.",
                logoUpload: "Upload Logo",
                logoRemove: "Remove Logo",
                logoPreview: "Logo preview",
                resetBrandingSettings: "Reset Branding Settings",
                reportColorsHeading: "Report Colors",
                reportColorsDesc: "Configure the color scheme used in PDF reports. Each entity can have its own colors.",
                reportColorPrimary: "Primary Color",
                reportColorPrimaryDesc: "Main brand color. Used for headings, section dividers, and the cover background.",
                reportColorSecondary: "Secondary Color",
                reportColorSecondaryDesc: "Accent bar color on the cover page.",
                reportColorAccent: "Accent Color",
                reportColorAccentDesc: "Decorative bar and visual highlights throughout the report.",
                reportColorsReset: "Reset to Default Colors"
            },
            licenses: {
                title: "License Management",
                subtitle: "Manage which modules are licensed per entity.",
                entityLabel: "Entity",
                selectEntity: "Select entity",
                noEntities: "No entities configured. Add entities in the Entities tab.",
                moduleColumn: "Module",
                statusColumn: "Status",
                expiryColumn: "Expiry",
                actionsColumn: "Actions",
                licensed: "Licensed",
                unlicensed: "Unlicensed",
                expired: "Expired",
                noExpiry: "No expiry",
                expiresOn: "Expires {{date}}",
                activate: "Activate",
                deactivate: "Deactivate",
                editExpiry: "Edit expiry",
                saveExpiry: "Save",
                cancelExpiry: "Cancel",
                expiryLabel: "Expiry date (optional)",
                activateSuccess: "License activated for {{module}}",
                deactivateSuccess: "License deactivated for {{module}}",
                expiryUpdated: "Expiry date updated",
                noLicenseHeading: "No License",
                noLicenseMessage: "This module is not licensed for your organization. Contact your administrator.",
                module_customer_risk: "Customer Risk",
                module_geographical_risk: "Geographical Risk",
                module_bwra: "BWRA",
                module_transaction_monitoring: "Transaction Monitoring",
                module_periodic_reporting: "Periodic Reporting",
                expiryWarningTitle: "Licenses expiring soon",
                expiryWarningItem: "{{module}} — {{days}} day(s) remaining",
                expiryWarningItemToday: "{{module}} — expires today",
                showMatrix: "Matrix view",
                showList: "List view"
            },
            validationScheduler: {
                title: "Validation Scheduler",
                subtitle: "Configure automated model validation schedules, run validations manually, and view history.",
                schedulesTitle: "Model Schedules",
                noModels: "No models available for scheduling. Add datasets in the GeoRisk or CustomerRisk modules first.",
                colModel: "Model",
                colEnabled: "Enabled",
                colFrequency: "Frequency",
                colDataset: "Dataset",
                colLastRun: "Last Run",
                colNextRun: "Next Run",
                colActions: "Actions",
                colTimestamp: "Timestamp",
                colTriggeredBy: "Triggered By",
                colAccuracy: "Accuracy",
                colStatus: "Status",
                enable: "Enable schedule",
                disable: "Disable schedule",
                daily: "Daily",
                weekly: "Weekly",
                frequency: "Frequency",
                dataset: "Dataset",
                noDataset: "— select dataset —",
                runNow: "Run now",
                running: "Running…",
                neverRun: "Never run",
                statusSuccess: "Success",
                statusError: "Error",
                statusPending: "Pending",
                triggeredScheduled: "Scheduled",
                triggeredManual: "Manual",
                historyTitle: "Validation History",
                noHistory: "No validation runs recorded yet.",
                runSuccess: "Validation completed — accuracy: {{accuracy}}%",
                runError: "Validation failed: {{error}}",
                errorNoDataset: "Select a dataset before running validation.",
                clearOlderThan: "Clear history older than",
                days: "days",
                clearBtn: "Clear history",
                clearConfirmMessage: "This will permanently delete all validation history entries older than {{days}} days. This action cannot be undone.",
                clearSuccess: "Cleared {{count}} history entries older than {{days}} days.",
                clearNone: "No history entries found older than {{days}} days.",
                weekdayLabel: "Day of week",
                mon: "Monday",
                tue: "Tuesday",
                wed: "Wednesday",
                thu: "Thursday",
                fri: "Friday",
                sat: "Saturday",
                sun: "Sunday"
            },
            activity: {
                title: "User Activity Dashboard",
                period7d: "7 days",
                period30d: "30 days",
                period90d: "90 days",
                totalEvents: "Total Events",
                uniqueUsers: "Unique Users",
                activeModules: "Active Modules",
                noData: "No activity data for the last {{days}} days.",
                moduleActivity: "Activity per Module",
                mostActiveUsers: "Most Active Users",
                topActions: "Most Common Actions",
                rank: "#",
                user: "User",
                events: "Events",
                share: "Share",
                action: "Action"
            },
            regulatoryExport: {
                title: "Regulatory Export",
                subtitle: "Generate and download AML risk data in formats required by supervisory authorities (EBA, Finansinspektionen).",
                formatTitle: "Export Format",
                formatHelp: "Choose the format required by your supervisory authority. EBA XML is used for European Banking Authority reporting. Finansinspektionen CSV is used for Swedish FSA reporting.",
                formatEbaDesc: "European Banking Authority — XML schema for AML risk assessments",
                formatFiDesc: "Finansinspektionen — CSV format for Swedish FSA submissions",
                periodTitle: "Reporting Period",
                periodHelp: "Enter the period this export covers, e.g. 2025-Q4 for the fourth quarter of 2025.",
                periodLabel: "Period",
                periodPlaceholder: "e.g. 2025-Q4",
                periodHint: "Format: YYYY-Q1/Q2/Q3/Q4 or YYYY-MM",
                scopeTitle: "Data Scope",
                scopeHelp: "Select which modules to include in the export. At least one module must be selected.",
                scopeBwra: "BWRA — Business-Wide Risk Assessment",
                scopeGeoRisk: "Geographical Risk",
                scopeCustomerRisk: "Customer Risk",
                validationTitle: "Pre-Export Validation",
                validationDesc: "Validate that all required data is present and complete before generating the export file.",
                validateBtn: "Validate Data",
                validationPassed: "Validation passed — data is ready for export",
                validationFailed: "Validation failed — correct errors before exporting",
                exportBtn: "Generate & Download",
                exportHint: "Validation runs automatically before the file is generated.",
                exportSuccess: "Export generated: {{filename}}",
                exportError: "Export failed: {{error}}",
                historyTitle: "Export History",
                clearHistory: "Clear History",
                clearHistoryConfirm: "This will permanently delete all export history records. Continue?",
                historyClearedSuccess: "Export history cleared.",
                colTimestamp: "Timestamp",
                colFormat: "Format",
                colPeriod: "Period",
                colFile: "Filename",
                validation_no_entity: "No active entity configured",
                validation_entity_name_missing: "Active entity has no name",
                validation_entity_country_missing: "Entity country is not set",
                validation_entity_orgnumber_missing: "Entity organization number is not set",
                validation_bwra_no_risks: "BWRA module has no risks registered",
                validation_georisk_no_indexes: "GeoRisk module has no indexes configured",
                validation_georisk_no_active_indexes: "GeoRisk module has no active indexes",
                validation_customerrisk_no_models: "CustomerRisk module has no models",
                validation_no_scope_selected: "Select at least one module to include",
                validationBwraMissingResidual: "{{count}} risks are missing residual risk assessment",
                validationBwraMissingCategory: "{{count}} risks have no category assigned"
            },
            manuals: {
                title: "User Manuals",
                subtitle: "Download and view documentation for all modules and system functions.",
                searchPlaceholder: "Search manuals…",
                filterCategory: "Filter by category",
                allCategories: "All categories",
                download: "Download",
                downloadTitle: "Download PDF",
                downloadStarted: "Downloading: {{name}}",
                version: "v",
                docCount: "{{count}} documents",
                noResults: "No manuals match your search.",
                cat: {
                    user: "User Guide",
                    technical: "Technical Guide",
                    admin: "Administrator Guide",
                    compliance: "Compliance Guide"
                },
                doc: {
                    "ug-overview": {
                        title: "System Overview",
                        desc: "Introduction to Prime RMS — modules, navigation, and key concepts."
                    },
                    "ug-georisk": {
                        title: "Geographical Risk Module",
                        desc: "How to configure country risk indexes, scoring models, and risk levels."
                    },
                    "ug-custrisk": {
                        title: "Customer Risk Module",
                        desc: "Building customer risk models, scoring parameters, and CDD/EDD classification."
                    },
                    "ug-bwra": {
                        title: "BWRA Module",
                        desc: "Business-Wide Risk Assessment: risks, controls, residual risk, and traceability."
                    },
                    "ug-txmon": {
                        title: "Transaction Monitoring",
                        desc: "Setting up monitoring rules, alert management, and case handling."
                    },
                    "ug-reporting": {
                        title: "Periodic Reporting",
                        desc: "Generating, approving, and exporting compliance reports."
                    },
                    "tg-install": {
                        title: "Installation Guide",
                        desc: "System requirements, installation steps, and on-premises deployment."
                    },
                    "tg-config": {
                        title: "Configuration Reference",
                        desc: "Environment variables, storage backends, and advanced configuration."
                    },
                    "tg-api": {
                        title: "API Reference",
                        desc: "REST API endpoints, authentication, and integration examples."
                    },
                    "tg-storage": {
                        title: "Database & Storage Guide",
                        desc: "StorageAdapter, migration paths from localStorage to production databases."
                    },
                    "ag-users": {
                        title: "User & Role Management",
                        desc: "Creating users, assigning roles, configuring permissions and entity access."
                    },
                    "ag-licenses": {
                        title: "License Management",
                        desc: "Activating modules, setting expiry dates, and managing multi-entity licenses."
                    },
                    "ag-settings": {
                        title: "System Settings",
                        desc: "Session timeouts, audit log retention, branding, and module configuration."
                    },
                    "cg-aml": {
                        title: "AML Compliance Framework",
                        desc: "Regulatory background, FATF guidelines, and how Prime RMS supports AML compliance."
                    },
                    "cg-validation": {
                        title: "Model Validation Guide",
                        desc: "Validation methodology, scheduling, accuracy metrics, and approval workflows."
                    },
                    "cg-audit": {
                        title: "Audit Trail & Evidence",
                        desc: "Using the audit log for regulatory evidence, export formats, and retention rules."
                    }
                }
            },
            dataMigration: {
                title: "Data Migration",
                subtitle: "Migrate existing localStorage data to the configured Supabase cloud database.",
                migrateHeading: "Migrate Local Data to Cloud",
                migrateDesc: "{{count}} data entries found in local storage. Click the button below to copy them to the Supabase database. This operation is idempotent — safe to run multiple times.",
                startBtn: "Start Migration",
                runAgainBtn: "Run Again",
                notAvailable: "Data migration is only available when a Supabase backend is configured. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY to enable.",
                noLocalData: "No local data found in storage. Nothing to migrate.",
                progressLabel: "Migrating key {{current}} of {{total}} ({{pct}}%)…",
                status_migrated: "Migrated",
                status_skipped: "Skipped",
                status_error: "Error",
                doneBanner: "Migration complete — {{migrated}} entries migrated, {{errors}} errors.",
                errorBanner: "Migration encountered an error. Check results and try again.",
                doneToast: "Migration complete — {{count}} entries migrated to Supabase.",
                errorToast: "Migration failed. Please check the connection and try again.",
                historyHeading: "Migration History",
                historyStats: "{{migrated}} / {{total}} migrated",
                historyErrors: "({{errors}} errors)"
            },
            entityMigration: {
                title: "Entity Data Migration",
                subtitle: "Move or copy risk assessment data between entities during organisational restructuring. A full audit trail is created for every operation.",
                stepEntitiesHeading: "Select Source and Target Entities",
                labelSource: "Source entity (from)",
                labelTarget: "Target entity (to)",
                selectEntity: "— Select entity —",
                labelMode: "Operation:",
                modeCopy: "Copy",
                modeMove: "Move",
                modeCopyDesc: "Data is copied — original remains intact.",
                modeMoveDesc: "Data is moved — original is deleted after transfer.",
                sameEntityWarning: "Source and target must be different entities.",
                previewBtn: "Preview data",
                stepPreviewHeading: "Select Data to Migrate",
                previewDesc: "Select which modules to include in the migration. Deselect any that should be left in the source entity.",
                noDataFound: "No entity-scoped data found for the selected source entity.",
                module_bwra: "BWRA (Business-Wide Risk Assessment)",
                module_georisk: "GeoRisk",
                module_customerrisk: "Customer Risk",
                module_txmon: "Transaction Monitoring",
                module_other: "Other / Uncategorised",
                keyCount: "{{count}} storage keys",
                itemCount: "{{count}} items",
                moveWarning: "Move mode: source data will be permanently deleted after transfer. This cannot be undone.",
                migrateBtn: "Migrate {{count}} key(s)",
                runningBtn: "Migrating…",
                resultsHeading: "Migration Complete",
                resultMigrated: "{{count}} key(s) migrated",
                resultSkipped: "{{count}} skipped (empty)",
                statusMigrated: "Migrated",
                statusSkipped: "Skipped",
                doneToast: "Migration complete — {{count}} storage key(s) transferred.",
                errorToast: "Migration failed. Please try again."
            },
            backup: {
                title: "Backup & Restore",
                subtitle: "Export all system data to a file and restore from a previous backup.",
                createHeading: "Create Backup",
                createDesc: "Download a complete snapshot of all system data. The backup file includes configurations, risk assessments, models, reports, and user settings.",
                createBtn: "Download Backup",
                downloadedToast: "Backup created — {{count}} data entries exported.",
                downloadError: "Failed to create backup. Please try again.",
                restoreHeading: "Restore from Backup",
                restoreDesc: "Upload a backup file to restore system data. Only files created by this system are accepted.",
                restoreWarning: "Warning: restoring a backup will overwrite all current data. This action cannot be undone.",
                restoreBtn: "Upload Backup File",
                confirmRestore: "Restore backup from {{date}} ({{count}} entries)? All current data will be overwritten.",
                restoredToast: "Backup restored — {{count}} data entries loaded.",
                restoreError: "Failed to restore backup. Please check the file and try again.",
                validationFailed: "Invalid backup file",
                error_parseError: "File could not be parsed as JSON",
                error_invalidFormat: "Unrecognised file format",
                error_unknownVersion: "Unsupported backup version",
                error_missingData: "Backup file contains no data"
            },
            systemHealth: {
                title: "System Health",
                subtitle: "Monitor storage usage and data volumes per module. Helps identify oversized data sets and plan capacity.",
                totalUsed: "Storage Used",
                ofLimit: "of {{limit}} limit",
                totalKeys: "Data Entries",
                storageEntries: "keys in storage",
                usagePercent: "Usage",
                moduleBreakdown: "Data by Module",
                refresh: "Refresh",
                lastRefreshed: "Last refreshed at {{time}}",
                colModule: "Module",
                colKeys: "Entries",
                colSize: "Size",
                colShare: "Share",
                alertWarning: "Storage is at {{pct}}% — consider clearing unused data.",
                alertCritical: "Storage is at {{pct}}% — critical! Exports or saves may fail.",
                module_core: "Core / Infrastructure",
                module_admin: "Admin",
                module_bwra: "BWRA",
                module_georisk: "GeoRisk",
                module_customerrisk: "Customer Risk",
                module_validation: "Data Validation",
                module_periodicreporting: "Periodic Reporting",
                module_txmon: "Transaction Monitoring",
                module_scheduledvalidation: "Scheduled Validation",
                module_regulatoryexport: "Regulatory Export",
                module_other: "Other"
            },
            reportTemplates: {
                title: "Report Template Editor",
                subtitle: "Define the sections and fields that appear in generated reports. Each template can be assigned to specific entities.",
                templatesHeading: "Templates",
                newTemplate: "New Template",
                templateNamePlaceholder: "Template name",
                templateDescPlaceholder: "Short description (optional)",
                create: "Create",
                selectTemplate: "Select a template from the list to start editing.",
                templateName: "Template Name",
                templateDescription: "Description",
                sectionsHeading: "Sections",
                addSection: "Add Section",
                sectionTitlePlaceholder: "Section title",
                noSections: "No sections yet. Click \"Add Section\" to get started.",
                fieldCount: "{{count}} field(s)",
                sectionCount: "{{count}} section(s)",
                addField: "Add Field",
                noFields: "No fields. Click \"Add Field\" to add the first field.",
                fieldLabelPlaceholder: "Field label",
                fieldDescription: "Help text",
                fieldDescriptionPlaceholder: "Optional description shown to the report author.",
                required: "Required",
                moreOptions: "More options",
                moveUp: "Move up",
                moveDown: "Move down",
                removeField: "Remove field",
                removeSection: "Remove section",
                expand: "Expand",
                collapse: "Collapse",
                selectOptions: "Options",
                selectOptionsPlaceholder: "Option 1, Option 2, Option 3",
                selectOptionsHint: "Comma-separated list of options.",
                deleteTemplate: "Delete Template",
                confirmDelete: "Are you sure you want to delete template \"{{name}}\"?",
                cannotDeleteDefault: "The default template cannot be deleted.",
                savedToast: "Template saved.",
                deletedToast: "Template deleted.",
                errorNameRequired: "Template name is required.",
                fieldType_text: "Text",
                fieldType_textarea: "Text area",
                fieldType_number: "Number",
                fieldType_date: "Date",
                fieldType_boolean: "Yes / No",
                fieldType_select: "Dropdown"
            },
            retention: {
                title: "Data Retention",
                subtitle: "Configure how long each type of data is retained before archiving. AML regulations require a minimum retention period of 5 years.",
                policiesHeading: "Retention Policies",
                policiesDesc: "Define the retention period per data type. When you run the archive, records older than the configured period are moved to the archive.",
                runArchiveBtn: "Run Archive Now",
                colDataType: "Data Type",
                colRetentionPeriod: "Retention Period",
                colEnabled: "Enabled",
                yearSingular: "year",
                yearPlural: "years",
                enabled: "Enabled",
                disabled: "Disabled",
                enablePolicy: "Enable this retention policy",
                disablePolicy: "Disable this retention policy",
                policyEnabled: "Retention policy enabled.",
                policyDisabled: "Retention policy disabled.",
                archiveRunToast: "Archive completed — {{count}} record(s) moved to archive.",
                lastRunTitle: "Last archive run:",
                summaryArchived: "{{archived}} archived, {{remaining}} remaining",
                archiveHeading: "Archived Records",
                archiveDesc: "Browse and restore records that have been moved to the archive. Restoring a record returns it to its original data store.",
                allDataTypes: "All data types",
                searchPlaceholder: "Search archived records...",
                colArchivedType: "Data Type",
                colArchivedAt: "Archived",
                colOriginalDate: "Original Date",
                colActions: "Actions",
                restoreBtn: "Restore",
                purgeBtn: "Purge Archive",
                emptyArchiveTitle: "No archived records",
                emptyArchiveDesc: "Run the archive to move old records here based on the configured retention policies.",
                confirmRestore: "Restore this record to the active data store?",
                confirmPurge: "Permanently delete all archived records for \"{{dataType}}\"? This cannot be undone.",
                restoreSuccess: "Record restored successfully.",
                restoreError: "Failed to restore record.",
                purgeSuccess: "{{count}} archived record(s) permanently deleted.",
                dataType_audit_logs: "Audit Logs",
                dataType_validation_history: "Validation Run History",
                exportBtn: "Export CSV",
                exportToast: "{{count}} archived record(s) exported to CSV.",
                exportColRecord: "Record",
                pagePrev: "Previous",
                pageNext: "Next",
                pageLabel: "Page {{current}} of {{total}}",
                pageRecordCount: "{{count}} total"
            },
            dataIsolation: {
                title: "Data Isolation Verifier",
                subtitle: "Verify that each entity's data is correctly scoped and separated in storage.",
                description: "This tool scans all storage keys and confirms that entity-specific data is properly isolated per entity. Run this check before audits or when onboarding new subsidiary entities.",
                runBtn: "Verify Data Isolation",
                running: "Running...",
                verifySuccess: "Data isolation verified — no issues found.",
                verifyIssues: "Verification complete — {{count}} potential issue(s) found.",
                verifyError: "An error occurred during verification.",
                statusOk: "All data correctly isolated",
                statusIssues: "{{count}} potential issue(s) detected",
                verifiedAt: "Verified at {{time}}",
                cardEntities: "Entities",
                cardTotalKeys: "Total Keys",
                cardSystemKeys: "Global Keys",
                cardIssues: "Orphan Keys",
                entityBreakdownTitle: "Entity Data Breakdown",
                colEntity: "Entity",
                colType: "Type",
                colScopedKeys: "Scoped Storage Keys",
                colStatus: "Isolation Status",
                statusKeyOk: "Isolated",
                noData: "No entity-scoped data found",
                globalKeysTitle: "Global (System) Keys",
                globalKeysDesc: "These keys are shared across all entities by design — users, settings, audit log, licenses, and other system-wide data.",
                orphanKeysTitle: "Orphan Keys",
                orphanKeysDesc: "These storage keys appear to belong to a deleted entity. They are harmless but can be cleaned up via a full data reset or manual removal.",
                issuesTitle: "Detected Issues",
                noOrphans: "No orphan keys detected.",
                type_parent: "Parent Company",
                type_subsidiary: "Subsidiary",
                type_branch: "Branch"
            },
            userImport: {
                title: "Import Users",
                subtitle: "Bulk-onboard users by uploading a CSV file. Preview and validate before importing.",
                instructions: "Upload a CSV file with one user per row. Required columns: firstName, lastName, email, role. Optional: entityAccess (semicolon-separated entity IDs or 'all').",
                requiredColumns: "Required columns:",
                columnList: "firstName, lastName, email, role",
                downloadTemplate: "Download Template",
                uploadLabel: "Click to select a CSV file",
                uploadHint: "Supported format: .csv (UTF-8)",
                validRows: "{{count}} valid",
                invalidRows: "{{count}} invalid",
                colFirstName: "First Name",
                colLastName: "Last Name",
                colEmail: "Email",
                colRole: "Role",
                colEntityAccess: "Entity Access",
                colStatus: "Status",
                statusOk: "OK",
                changeFile: "Change file",
                cancel: "Cancel",
                importBtn: "Import {{count}} users",
                noValidRows: "No valid rows to import.",
                importSuccess: "{{count}} users imported successfully.",
                importDone: "{{count}} users were imported.",
                skippedRows: "{{count}} row(s) were skipped due to errors.",
                importAnother: "Import more users",
                error_missingEmail: "Email is required",
                error_invalidEmail: "Invalid email format",
                error_duplicateEmail: "Email already exists",
                error_missingRole: "Role is required",
                error_invalidRole: "Invalid role (use: admin, analyst, or viewer)"
            },
            apiKeys: {
                title: "API Key Management",
                subtitle: "Create and manage API keys for external integrations. Each key is granted specific permission scopes.",
                statTotal: "Total Keys",
                statActive: "Active Keys",
                statRequests: "Total Requests",
                keysHeading: "API Keys",
                createKeyBtn: "Create API Key",
                createTitle: "Create New API Key",
                labelName: "Key Name",
                namePlaceholder: "e.g. Reporting Integration, BI Connector...",
                labelScopes: "Permission Scopes",
                scopesHint: "Select the permissions this key will have.",
                labelExpiry: "Expiry Date",
                expiryHint: "Leave blank for a key that never expires.",
                scopeGroup_read: "Read Access",
                scopeGroup_export: "Export",
                scopeGroup_admin: "Admin",
                scope_read_georisk: "Read GeoRisk Data",
                scope_read_customers: "Read Customer Risk Data",
                scope_read_bwra: "Read BWRA Data",
                scope_read_compliance: "Read Compliance Data",
                scope_export_data: "Export Data",
                scope_admin_reports: "Manage Reports",
                cancel: "Cancel",
                createBtn: "Generate Key",
                colName: "Name",
                colKey: "Key (masked)",
                colScopes: "Scopes",
                colCreated: "Created",
                colExpiry: "Expiry",
                colUsage: "Requests",
                colLastUsed: "Last Used",
                colStatus: "Status",
                colActions: "Actions",
                statusActive: "Active",
                statusRevoked: "Revoked",
                statusExpired: "Expired",
                revokeBtn: "Revoke",
                activateBtn: "Activate",
                deleteBtn: "Delete",
                confirmDelete: "Delete this key?",
                deleteConfirmYes: "Yes, delete",
                noExpiry: "No expiry",
                expired: "(expired)",
                emptyTitle: "No API keys yet",
                emptyDesc: "Create your first API key to allow external systems to integrate with Prime RMS.",
                keyCreated: "API key \"{{name}}\" created successfully.",
                keyOnceWarning: "Copy this key now — it will not be shown again.",
                copy: "Copy",
                copied: "Copied!",
                understood: "I've saved the key",
                infoNote: "API keys provide programmatic access to Prime RMS. Keep them secret — treat them like passwords. Revoke any key that may have been compromised.",
                keyCreatedToast: "API key \"{{name}}\" created.",
                keyRevokedToast: "API key \"{{name}}\" revoked.",
                keyActivatedToast: "API key \"{{name}}\" activated.",
                keyDeletedToast: "API key \"{{name}}\" deleted.",
                errorNameRequired: "Key name is required.",
                errorScopeRequired: "Select at least one permission scope.",
                close: "Close"
            },
            configTemplates: {
                title: "Configuration Templates",
                subtitle: "Save the current settings as a named template and apply it to other entities.",
                saveCurrentBtn: "Save as Template",
                saveFormTitle: "Save Current Settings as Template",
                templateName: "Template Name",
                templateNamePlaceholder: "e.g. Standard Bank Branch, High-Risk Entity...",
                templateDescription: "Description (optional)",
                templateDescriptionPlaceholder: "Brief description of this configuration",
                saveBtn: "Save Template",
                saving: "Saving...",
                cancelBtn: "Cancel",
                saveNote: "A snapshot of all current settings (session, audit, modules, branding) will be saved.",
                savedSuccess: "Template saved successfully.",
                applyBtn: "Apply",
                deleteBtn: "Delete",
                confirmApply: "Apply template \"{{name}}\" to current settings? This will overwrite your current configuration.",
                confirmDelete: "Delete template \"{{name}}\"? This cannot be undone.",
                appliedSuccess: "Template \"{{name}}\" applied successfully.",
                applyError: "Failed to apply template.",
                deletedSuccess: "Template deleted.",
                createdAt: "Created",
                emptyTitle: "No templates saved",
                emptyDesc: "Save the current settings as a template to reuse them across entities."
            },
            notificationSettings: {
                title: "Notification Settings",
                subtitle: "Configure which notification types are active for each user role, and how frequently they are delivered.",
                selectRole: "Role:",
                builtIn: "Built-in",
                configFor: "Configuration for: {{role}}",
                colType: "Notification Type",
                colEnabled: "Enabled",
                colFrequency: "Frequency",
                enableBtn: "Enable",
                disableBtn: "Disable",
                savedToast: "Notification settings saved.",
                resetToast: "Settings reset to defaults.",
                resetDefaults: "Reset to Defaults",
                type_deadlines: "Deadlines & Annual Reviews",
                type_deadlines_desc: "Alerts for upcoming report deadlines and BWRA annual review dates.",
                type_staleData: "Stale Data Warnings",
                type_staleData_desc: "Notifications when module data has not been updated within the required timeframe.",
                type_scheduledValidations: "Scheduled Model Validations",
                type_scheduledValidations_desc: "Reminders for upcoming or overdue scheduled model validation runs.",
                type_assigneeNotifications: "Task Assignments",
                type_assigneeNotifications_desc: "Notifications for reports or tasks assigned directly to the user.",
                type_kpiThresholds: "KPI Threshold Warnings",
                type_kpiThresholds_desc: "Notifications when Dashboard KPI values exceed configured thresholds.",
                freq_immediate: "Immediate",
                freq_daily: "Daily digest",
                freq_weekly: "Weekly digest",
                webhookTitle: "Webhook Integration",
                webhookDesc: "Send event data to an external URL when key events occur in the system. The payload is a JSON object with event, payload, timestamp, and source fields.",
                webhookUrl: "Endpoint URL",
                webhookTest: "Send Test",
                webhookTesting: "Sending…",
                webhookTestOk: "Test payload delivered successfully.",
                webhookTestFail: "Could not reach the webhook endpoint. Check the URL and try again.",
                webhookSavedToast: "Webhook settings saved.",
                webhookEvents: "Events that trigger the webhook",
                webhookEvent_approvalGranted: "Approval granted",
                webhookEvent_approvalGranted_desc: "Fired when a model or report is approved.",
                webhookEvent_ruleChanged: "Rule changed",
                webhookEvent_ruleChanged_desc: "Fired when a transaction monitoring rule is created, edited, or deleted.",
                webhookEvent_newRiskAdded: "New risk added",
                webhookEvent_newRiskAdded_desc: "Fired when a new BWRA risk entry is created.",
                webhookEvent_reportCreated: "Report created",
                webhookEvent_reportCreated_desc: "Fired when a periodic report is auto-created or published.",
                webhookEvent_validationCompleted: "Validation completed",
                webhookEvent_validationCompleted_desc: "Fired when a scheduled model validation run finishes."
            }
        },
        geoRisk: {
            tabs: {
                overview: "Overview",
                riskManagement: "Risk Management",
                countryManager: "Country Manager",
                modelConfiguration: "Model Configuration",
                validation: "Validation",
                mapView: "Map View",
                abTest: "A/B Test",
                countryHistory: "Country History",
                mapping: "Mapping",
                correlationAnalysis: "Index Correlation"
            },
            correlationAnalysis: {
                title: "Index Correlation Analysis",
                subtitle: "Pearson correlation between index scores across all countries. Indices with correlation > 0.9 are potentially redundant.",
                computeBtn: "Compute Correlations",
                computing: "Computing...",
                noScoringIndexes: "No scoring indices with data available. Add scoring indices and upload country data to use this analysis.",
                matrixTitle: "Correlation Matrix",
                warningThreshold: "Warning: correlation > 0.9",
                warningHigh: "High correlation",
                warningLow: "Low data overlap",
                countriesOverlap: "countries",
                legendTitle: "Correlation scale",
                legendNeg: "Strong negative",
                legendNone: "No correlation",
                legendPos: "Strong positive",
                warningList: "Highly Correlated Index Pairs",
                warningListEmpty: "No pairs with correlation above 0.9 found.",
                warningPairLabel: "Correlation",
                warningPairCountries: "shared countries",
                indexA: "Index A",
                indexB: "Index B",
                indexType: "Type",
                indexTypeBoolean: "Boolean",
                indexTypeScoring: "Scoring",
                insufficientData: "Insufficient data (< 3 shared countries)"
            },
            countryHistory: {
                pageTitle: "Country Risk History",
                pageSubtitle: "Track how a country's risk score has evolved over time and which index changes drove it",
                selectCountry: "Select a country to view its risk history",
                searchPlaceholder: "Search country...",
                noData: "No historical data yet. Snapshots are captured automatically when you visit this page.",
                singleSnapshot: "Only one snapshot available. Return here after more data has been collected.",
                currentLevel: "Current risk level",
                chartTitle: "Risk level over time",
                snapshotsCount: "snapshots recorded",
                captureSnapshot: "Capture snapshot now",
                snapshotCaptured: "Snapshot captured",
                indexChanges: "Recent index events",
                noIndexChanges: "No index change events recorded.",
                eventLabel: "updated",
                affectedCountry: "This country was affected"
            },
            abTest: {
                title: "Model A/B Test",
                subtitle: "Compare two model configurations side by side on all countries",
                modelA: "Model A",
                modelB: "Model B",
                currentModel: "Current model",
                vs: "VS",
                selectByType: "Different model type",
                selectByVersion: "Saved version",
                noVersions: "no saved versions",
                modelType: "Model type",
                savedVersion: "Version",
                runComparison: "Run Comparison",
                running: "Running...",
                summaryTitle: "Comparison Summary",
                totalCountries: "Countries evaluated",
                changed: "Different classification",
                higherRisk: "Higher risk in B",
                lowerRisk: "Lower risk in B",
                distributionComparison: "Risk level distribution",
                riskLevel: "Risk Level",
                delta: "Change",
                resultsTitle: "Country-by-country results",
                showOnlyDiff: "Show only differences",
                noDifferences: "No differences — both models produce identical results.",
                country: "Country",
                change: "Change",
                increased: "Increased",
                decreased: "Decreased"
            },
            overview: {
                pageSubtitle: "Country risk scores and distribution across all active indexes",
                country: "Country",
                riskLevel: "Risk Level",
                searchCountry: "Search country...",
                all: "All",
                veryHigh: "Very High",
                high: "High",
                medium: "Medium",
                low: "Low",
                unacceptable: "Unacceptable",
                fatfStatus: "FATF Status",
                listed: "Listed",
                nonListed: "Non-listed",
                euStatus: "EU Status",
                highRisk: "High Risk",
                normal: "Normal",
                exportData: "Export data",
                noResults: "No countries match the current filters.",
                summaryLabel: "Risk Distribution",
                summaryTotal: "Total",
                summaryHighPlus: "HIGH or above",
                clearFilters: "Clear filters",
                notCalculated: "Not calculated",
                attributionTitle: "Risk drivers",
                attributionTopContributors: "Top contributing indices",
                attributionWeight: "Weight",
                attributionValue: "Value",
                attributionContribution: "Contribution",
                attributionNoData: "No attribution data available",
                attributionDeterministicLabel: "Triggered by",
                attributionGroupLabel: "Rule group",
                attributionDeductionLabel: "Score deduction",
                attributionShowWhy: "Why?",
                attributionHideWhy: "Hide"
            },
            override: {
                modalTitle: "Set Manual Risk Override",
                modalTitleEdit: "Edit Manual Risk Override",
                info: "A manual override replaces the model-calculated risk level for this country. An auditable reason is required.",
                riskLevelLabel: "Override Risk Level",
                reasonLabel: "Reason (mandatory)",
                reasonPlaceholder: "Describe why this country's risk level is being manually set...",
                setBy: "Set by",
                date: "Override date",
                overrideBadge: "Manual",
                overrideTooltip: "This risk level has been manually overridden",
                removeButton: "Remove Override",
                saveButton: "Save Override",
                cancelButton: "Cancel",
                confirmRemoveTitle: "Remove Override",
                confirmRemoveMessage: "Are you sure you want to remove the manual override for this country? The model-calculated level will be restored.",
                reasonRequired: "A reason is required to save the override.",
                currentOverride: "Current override",
                modelLevel: "Model level",
                renewButton: "Renew Override",
                renewedConfirm: "Override renewed successfully.",
                staleWarning: "This override was set {days} days ago and has not been reviewed. Please confirm it is still valid.",
                staleBadge: "Review needed",
                staleAlertTitle: "{count} manual override(s) need review",
                staleAlertItem: "{country} — {days} days since last review",
                staleAlertDismiss: "Dismiss",
                lastReviewed: "Last reviewed",
                reviewedBy: "Reviewed by",
                reviewHistory: "Review history",
                reviewPeriodLabel: "Review period (days)",
                reviewPeriodSaved: "Review period saved"
            },
            comparison: {
                title: "Country Comparison",
                back: "← Back to overview",
                attribute: "Attribute",
                legendHighest: "Highest value",
                legendLowest: "Lowest value",
                legendDiff: "Values differ between countries",
                compareButton: "Compare",
                selectHint: "Select 2–4 countries to compare",
                clearSelection: "Clear selection"
            },
            riskExplanation: {
                title: "Plain-Language Risk Explanation",
                generateBtn: "Explain Risk",
                copyBtn: "Copy Text",
                copiedFeedback: "Copied!",
                exportBtn: "Export (.txt)",
                close: "Close",
                noActiveIndexes: "No active risk indices \u2014 risk cannot be assessed.",
                generatedNote: "Automatically generated from current model configuration on",
                assessedToHave: " is assessed to have ",
                riskSuffix: " risk.",
                basisPrefix: "The assessment is based on ",
                basisIndexCount: " active risk index.",
                basisIndexCountPlural: " active risk indices.",
                noTriggersNote: "No risk factors have been triggered above their thresholds. The country meets the baseline risk level.",
                detDriversLabel: "Risk is driven by the following factors:",
                weightedScoreIntro: "Based on a weighted analysis, the overall risk score is",
                weightedTopLabel: "Main contributing factors:",
                weightedRowWeight: "weight",
                weightedRowContrib: "contribution",
                invertedScoreIntro: "Starting from a baseline of",
                invertedScoreEnd: ", the final risk score is",
                invertedDeductionsLabel: "Score-reducing factors:",
                invertedDeductionOf: "reduction:",
                ruleGroupsLabel: "Triggered risk rule groups:",
                ruleGroupTriggeredBy: "triggered by:",
                footerNote: "This explanation is intended for board-level reporting and compliance documentation."
            },
            export: {
                country: "Country",
                riskClass: "Risk Class",
                fatfStatus: "FATF Status",
                euStatus: "EU Status",
                cpiScore: "CPI Score",
                lastUpdated: "Last Updated",
                dataSources: "Data Sources"
            },
            mapView: {
                subtitle: "Geographic distribution of country risk levels. Hover for details, click to filter the country table.",
                legendLabel: "Risk level filter",
                filterHint: "Click to filter by this risk level",
                clearFilter: "Show all",
                mapAriaLabel: "World map showing country risk levels as colored bubbles",
                equator: "Equator",
                fatfListed: "FATF listed",
                euHighRisk: "EU high-risk",
                clickHint: "Click to filter country table",
                showing: "Showing {{count}} of {{total}} countries"
            },
            stalenessAlerts: {
                sectionLabel: "Data staleness alarms",
                title: "{{count}} index(es) need updating",
                lastUpdated: "Last updated",
                daysStale: "{{days}} days overdue",
                affectedCountries: "{{count}} countries",
                dismiss: "Dismiss alarm",
                dashboardBadge: "{{count}} stale index(es)",
                dashboardHint: "View staleness alarms in GeoRisk overview"
            },
            riskMgmt: {
                pageSubtitle: "Manage risk indexes, configure scoring rules, and update country data",
                addNewIndex: "Add New Index",
                riskIndex: "Risk Index",
                indexName: "Index Name",
                type: "Type",
                lastUpdated: "Last updated",
                status: "Status",
                actions: "Actions",
                edit: "Edit",
                name: "Name",
                description: "Description",
                source: "Source",
                updateFrequency: "Update Frequency",
                cancel: "Cancel",
                createIndex: "Create Index",
                editIndex: "Edit Index",
                save: "Save",
                delete: "Delete",
                confirmDeleteTitle: "Delete Index",
                confirmDeleteMessage: "Are you sure you want to delete the index \"{{name}}\"? This action cannot be undone.",
                confirm: "Delete",
                indexDeleted: "Index deleted",
                // IndexDetailView
                countryScores: "Country Scores",
                countriesWithValues: "Countries with values",
                countriesWithoutValues: "Countries without values",
                allCountries: "All countries",
                uploadNewData: "Upload New Data",
                countryCode: "Country Code",
                country: "Country",
                sourceValue: "Source Value",
                calculatedValue: "Calculated Value",
                statusCol: "Status",
                lastUpdatedCol: "Last Updated",
                updateSourceCol: "Update Source",
                manualChange: "Manual change",
                sourceRef: "Source Reference",
                sourceRefPlaceholder: "e.g. FATF MER 2025, Transparency International CPI 2024",
                yes: "Yes",
                no: "No",
                invalidType: "Invalid Type",
                close: "Close",
                indexType: "Index Type",
                associatedRisks: "Associated Risks",
                scaleMaximum: "Scale Maximum",
                invertedScale: "Inverted Scale",
                normalizeValues: "Normalize Values",
                displayValueMissing: "Display Value for Missing Data",
                newCountryDefault: "New Country Default",
                changesSaved: "Changes saved successfully",
                // ScoreEditor
                editScoreFor: "Edit Score for {{name}}",
                enterScore: "Enter score",
                invalidScore: "Please enter a valid score between {{min}} and {{max}}.",
                // ChangesSummary
                reviewChanges: "Review Changes",
                scoreChanges: "Score Changes",
                configChanges: "Configuration Changes",
                previousValue: "Previous Value",
                newValue: "New Value",
                setting: "Setting",
                statusActive: "Active",
                statusInactive: "Inactive",
                commentRequired: "Comment is required",
                commentLabel: "Comment (required)",
                commentPlaceholder: "Describe the changes made...",
                confirmBtn: "Confirm",
                // DataUpload
                uploadTitle: "Upload New Data",
                validationErrors: "Validation Errors",
                preview: "Preview",
                value: "Value",
                importData: "Import Data",
                errorParsingFile: "Error parsing file: {{message}}",
                invalidCountry: "Invalid country: {{name}}",
                invalidValue: "Invalid value for {{country}}: {{value}}",
                valueOutOfRange: "Value for {{country}} must be between {{min}} and {{max}}",
                formatInstructionsTitle: "Expected File Format",
                formatInstructionsIntro: "Supported formats:",
                formatCsvTitle: "CSV (comma or semicolon separated)",
                formatCsvExample: "Column 1: country code (alpha-2), Column 2: numeric value",
                formatJsonTitle: "JSON object",
                formatXlsxTitle: "Excel (XLSX / XLS)",
                formatXlsxExample: "Column A: country code (alpha-2), Column B: numeric value",
                formatNote: "Headers are optional and will be detected automatically.",
                // DataUpload diff preview
                importDiffExistingValue: "Existing",
                importDiffNewValue: "New",
                importDiffChange: "Change",
                importDiffStatusNew: "New",
                importDiffStatusChanged: "Changed",
                importDiffStatusUnchanged: "Unchanged",
                importDiffSummary: "{{changed}} changed, {{added}} new, {{unchanged}} unchanged",
                importDiffPreviewTitle: "Preview — changes vs. existing data",
                importDiffNoChanges: "No changes detected — all imported values match existing data.",
                // VersionHistory
                versionHistory: "Version history",
                version: "Version {{number}}",
                updatedBy: "Updated by: {{name}}",
                versionComment: "Comment: {{comment}}",
                noVersions: "No versions saved yet. Save changes to create the first version.",
                file: "File: {{name}}",
                changes: "Changes:",
                // IndexDetails tabs
                indexInfo: "Index info",
                configuration: "Configuration",
                updatedByLabel: "Updated by",
                // Status translations
                status_active: "Active",
                status_pendingUpdate: "Pending Update",
                status_outdated: "Outdated",
                // Type translations
                type_booleanType: "Boolean List",
                type_scoringType: "Scoring Index",
                type_dominantType: "Dominant",
                type_weightedType: "Weighted",
                // Update frequency translations
                freq_quarterly: "Quarterly",
                freq_yearly: "Yearly",
                freq_semiAnnual: "Semi-Annual",
                freq_monthly: "Monthly",
                freq_daily: "Daily",
                freq_weekly: "Weekly",
                stalenessThresholdInfo: "Staleness threshold: {{days}} days",
                // IndexList — declared risks column
                coversRisks: "Covers Risks",
                coversRisksNone: "None",
                coversRisksCount: "{{count}} risk",
                coversRisksCountPlural: "{{count}} risks",
                coversRisksModalTitle: "Covered BWRA Risks",
                coversRisksModalDesc: "The following BWRA risks are addressed by this index:",
                // TypeConfig
                indexTypeConfig: "Index Type Configuration",
                selectIndexType: "Select Index Type:",
                scoringIndex: "Scoring Index",
                booleanList: "Boolean List",
                associatedRisksLabel: "Associated Risks:",
                bwraCategoriesLabel: "BWRA Risk Coverage:",
                bwraCategoriesHelp: "Which BWRA risk categories this index helps mitigate",
                declaredRisksLabel: "Addressed Risks:",
                declaredRisksHelp: "Which specific BWRA risks this index helps address",
                noRisksRegistered: "No risks registered in BWRA",
                impactType: "Impact Type:",
                impactScore: "Score",
                impactRiskLevel: "Risk Level",
                riskScore: "Risk Score:",
                riskLevelLabel: "Risk Level:",
                veryHigh: "Very High",
                high: "High",
                mediumLevel: "Medium",
                lowLevel: "Low",
                allowNullValues: "Allow Null Values:",
                // ScaleConfig
                scaleConfiguration: "Scale Configuration",
                scaleRange: "Scale Range:",
                to: "to",
                invertedScaleLabel: "Inverted Scale:",
                invertedScaleHelp: "Check if higher values indicate lower risk",
                normalizeValuesLabel: "Normalize Values:",
                normalizeValuesHelp: "Automatically normalize values to 0-100 scale",
                // DefaultValues
                defaultValues: "Default Values",
                displayValueLabel: "Display Value for Missing Data:",
                newCountryDefaultLabel: "New Country Default:",
                noDefaultPlaceholder: "Leave empty for no default",
                // Risks
                moneyLaundering: "Money Laundering Risk",
                terroristFinancing: "Terrorist Financing Risk",
                sanctions: "Sanctions Risk",
                corruption: "Corruption Risk",
                taxEvasion: "Tax Evasion Risk",
                proliferationFinancing: "Proliferation Financing Risk",
                regulatory: "Regulatory Risk",
                minValuePlaceholder: "Min value",
                maxValuePlaceholder: "Max value",
                // Empty state
                emptyStateTitle: "No indexes defined",
                emptyStateDesc: "Get started by adding your first risk index.",
                emptyStateAddBtn: "Add Index"
            },
            dataPanel: {
                currentValues: "Current Values",
                uploadNewData: "Upload New Data",
                noData: "No data available for this index. Upload data to get started.",
                countryCode: "Country Code",
                country: "Country",
                value: "Value"
            },
            countryManager: {
                title: "Manage Countries",
                addCountry: "Add Country",
                addNewCountry: "Add New Country",
                alpha2: "Alpha2",
                alpha3: "Alpha3",
                nameEn: "Name (EN)",
                nameSv: "Name (SV)",
                options: "Options",
                edit: "Edit",
                editCountry: "Edit Country",
                delete: "Delete",
                deleteConfirm: "Are you sure you want to delete this country? This action cannot be undone.",
                allFieldsRequired: "All fields are required.",
                cancel: "Cancel",
                save: "Save",
                search: "Search countries (name, alpha2, alpha3)...",
                countries: "countries",
                noResults: "No countries match your search.",
                previous: "Previous",
                next: "Next",
                page: "Page",
                alpha2Format: "Alpha-2 code must be exactly 2 uppercase letters (e.g. SE).",
                alpha3Format: "Alpha-3 code must be exactly 3 uppercase letters (e.g. SWE).",
                duplicateAlpha2: "A country with this Alpha-2 code already exists.",
                riskLevel: "Risk Level"
            },
            modelConfig: {
                title: "Configure Risk Model",
                generatePreview: "Generate Preview",
                exportConfig: "Export Configuration",
                previewResults: "Model Preview Results",
                noRiskIndicators: "No risk indicators",
                score: "Score",
                base: "Base",
                boosts: "Boosts",
                weightedAvg: "Weighted Avg",
                topContributors: "Top contributors",
                started: "Started",
                deductions: "Deductions",
                mainDeductions: "Main deductions",
                previewNoData: "No country data available. Add data to indexes in Risk Management to generate a preview.",
                previewNoActiveIndexes: "No active indexes configured — add indexes in Risk Management.",
                unsavedChanges: "unsaved changes",
                saveChanges: "Save Changes",
                discardChanges: "Discard Changes",
                failedToSave: "Failed to save changes: ",
                draftFoundTitle: "Unsaved draft found",
                draftFoundMessage: "Unsaved changes from {timestamp} were found. Do you want to restore the draft or ignore it?",
                draftRestore: "Restore draft",
                draftIgnore: "Ignore",
                generalSettings: "General Model Settings",
                modelType: "Model Type:",
                dominantModel: "Dominant Model",
                weightedModel: "Weighted Model",
                ruleGroupsModel: "Rule Groups Model",
                invertedModel: "Inverted Model",
                dominantHelp: "Highest risk from any index wins (traditional dominant model)",
                weightedHelp: "All indexes are weighted and combined into a final score (0-100)",
                ruleGroupsHelp: "Indexes are grouped with max boosts applied per group",
                invertedHelp: "Countries start at high risk (100) and get deductions for good scores",
                lowestRiskLevel: "Lowest Risk Level:",
                low: "Low",
                weightedConfig: "Weighted Model Configuration",
                weightedConfigHelp: "Each index is weighted and combined into a final risk score (0-100). Higher weights make an index more important in the final calculation.",
                weight: "Weight:",
                ruleGroupsConfig: "Rule Groups Configuration",
                ruleGroupsConfigHelp: "Configure how different risk factors are combined. Each group has a maximum boost that can be applied even if multiple conditions in the group are met.",
                groupName: "Group name",
                maxBoost: "Max Boost:",
                remove: "Remove",
                assignIndexes: "Assign indexes to this group:",
                alreadyAssigned: "Already assigned",
                addNewRuleGroup: "Add New Rule Group",
                newRuleGroup: "New Rule Group",
                baseRiskLabel: "Base Risk:",
                baseRiskLow: "Countries start at 0 (Low Risk).",
                baseRiskMedium: "Countries start at 1 (Medium Risk).",
                invertedConfigTitle: "Inverted Model Configuration",
                invertedConfigHelp: "Countries start at 100 (High Risk) and get deductions for good index scores. Missing data keeps the high risk - countries must prove they are low risk.",
                deductionWeight: "Deduction Weight:",
                riskLevelsTitle: "Risk Levels & Thresholds (0-4 Scale)",
                riskLevelsHelp: "Configure score-to-risk-level mapping. Risk levels are filtered based on the Lowest Risk Level setting.",
                invertedSettings: "Inverted Model Settings",
                startingScore: "Starting Score:",
                booleanDeduction: "Boolean Deduction:",
                maxScoringDeduction: "Max Scoring Deduction:",
                minimumScore: "Minimum Score:",
                howItWorks: "How it works:",
                viewVersionHistory: "View Version History",
                riskConfiguration: "Risk Configuration",
                booleanScale: "Boolean (Present/Not Present)",
                unknownScale: "Unknown scale",
                whenCountryPresent: "When country is present on this list:",
                presentEqualsRisk: "Present = Risk",
                absentEqualsRisk: "Absent = Risk",
                riskBoostValue: "Risk boost value:",
                riskTriggerConfig: "Risk Trigger Configuration",
                triggerThresholdAbove: "Trigger Threshold (scores ABOVE this trigger boost):",
                triggerThresholdBelow: "Trigger Threshold (scores BELOW this trigger boost):",
                invertedWarning: "This index is inverted - lower scores indicate higher risk",
                weightConfig: "Weight Configuration",
                weightImportance: "Weight (importance factor):",
                weightHelp: "Higher weights make this index more important in weighted calculations",
                closeSettings: "Close settings",
                indexesPanel: "Risk Indexes",
                indexesPanelHint: "Click a row to configure per-index settings",
                settingsPanel: "Model Configuration",
                step1: "Configure Indexes",
                step2: "Set Model Type",
                step3: "Preview & Save",
                typeLabel: "Type:",
                sourceLabel: "Source:",
                scaleLabel: "Scale:",
                lastUpdatedLabel: "Last Updated:",
                // ModelSnapshot
                noModelData: "No model data available",
                yes: "Yes",
                no: "No",
                indexConfiguration: "Index Configuration",
                noIndexConfigDeterministic: "No index configuration for deterministic model",
                activationThreshold: "Activation Threshold:",
                notSet: "Not set",
                activation: "Activation:",
                presenceTriggers: "Presence triggers",
                listBasedActivation: "List-based activation",
                riskLevelsLabel: "Risk Levels",
                ruleGroupsLabel: "Rule Groups",
                maxBoostLabel: "Max Boost:",
                noIndexesAssigned: "No indexes assigned",
                invertedModelConfig: "Inverted Model Configuration",
                modelTypeLabel: "Model Type",
                lowestRiskLevelLabel: "Lowest Risk Level",
                // ModelChangesSummary
                reviewModelChanges: "Review Model Changes",
                reviewChangesDescription: "Please review the changes before saving. A comment and your name are required.",
                changeTypeModelType: "Model Type",
                changeTypeIndexWeights: "Index Weights",
                changeTypeDeductionWeights: "Deduction Weights",
                changeTypeRiskLevel: "Risk Level Configuration",
                changeTypeRuleGroups: "Rule Groups",
                changeTypeGeneralSetting: "General Settings",
                changeTypeHybridSetting: "Hybrid Settings",
                changeTypeInvertedConfig: "Inverted Model Configuration",
                changes: "changes",
                fieldColumn: "Field",
                previousValue: "Previous Value",
                newValue: "New Value",
                descriptionColumn: "Description",
                changesSavedBy: "Changes will be saved by:",
                currentUser: "Current User",
                commentRequired: "Comment (required)",
                commentRequiredAlert: "Comment is required",
                userRequiredAlert: "Unable to determine current user. Please log in again.",
                commentPlaceholder: "Describe the changes made...",
                saveChangesCount: "Save Changes ({{count}} total)",
                cancel: "Cancel",
                // VersionHistoryModal
                versionHistory: "Version History",
                versionLabel: "Version",
                noVersionsYet: "No saved versions yet. Save a model configuration to start tracking history.",
                restoreVersion: "Restore",
                restoreVersionConfirm: "Restore version {{version}} from {{date}}? The configuration will be loaded as unsaved changes — you can review and save it.",
                restoreVersionSuccess: "Version {{version}} restored — review and save to apply.",
                // VersionDetailModal
                byUser: "by",
                commentLabel: "Comment:",
                modelConfiguration: "Model Configuration",
                changesMade: "Changes Made ({{count}})",
                // IndexSettings help-text
                scaleRange: "Scale: 1-{{max}}",
                normalizedSuffix: " (normalized to 1-100)",
                invertedSuffix: " (inverted - lower values = higher risk)",
                booleanHelpText: "+{{boost}} will be added to base risk when condition is met",
                scoringHelpText: "When country score is {{direction}} {{threshold}}, it will trigger +{{boost}} risk boost",
                scoringHelpAbove: "above",
                scoringHelpBelow: "below",
                scoringBoostHelpText: "+{{boost}} will be added to base risk when threshold is triggered",
                // GeneralSettings inverted explanation
                baseRiskDeterminedBy: "Determined by \"Lowest Risk Level\" setting above.",
                invertedExplCountries: "Countries start with {{score}} points (high risk)",
                invertedExplBoolean: "Boolean indexes: -{{deduction}} points when false/0",
                invertedExplScoring: "Scoring indexes: up to -{{deduction}} points based on score",
                invertedExplMinimum: "Final score cannot go below {{min}}",
                minPlaceholder: "Min",
                maxPlaceholder: "Max",
                chooseColor: "Choose color",
                // Change descriptions (useModelChangeTracker)
                changeDesc: {
                    changedModelType: 'Changed model type from "{{from}}" to "{{to}}"',
                    setWeight: 'Set weight for {{name}} to {{value}} (new parameter)',
                    changedWeight: 'Changed weight for {{name}} from {{from}} to {{to}}',
                    setDeductionWeight: 'Set deduction weight for {{name}} to {{value}} (new parameter)',
                    changedDeductionWeight: 'Changed deduction weight for {{name}} from {{from}} to {{to}}',
                    setMinThreshold: 'Set minimum threshold for {{level}} risk level to {{value}} (new parameter)',
                    changedMinThreshold: 'Changed minimum threshold for {{level}} risk level from {{from}} to {{to}}',
                    setMaxThreshold: 'Set maximum threshold for {{level}} risk level to {{value}} (new parameter)',
                    changedMaxThreshold: 'Changed maximum threshold for {{level}} risk level from {{from}} to {{to}}',
                    setColor: 'Set color for {{level}} risk level to {{value}} (new parameter)',
                    changedColor: 'Changed color for {{level}} risk level from {{from}} to {{to}}',
                    setRiskLevelSetting: 'Set risk level setting: {{field}}',
                    changedRiskLevelSetting: 'Changed risk level setting: {{field}}',
                    addedIndexesToGroup: 'Added indexes to rule group "{{group}}": {{indexes}}',
                    createdEmptyGroup: 'Created rule group "{{group}}" with no indexes',
                    modifiedGroupAddedRemoved: 'Modified rule group "{{group}}": added {{added}}, removed {{removed}}',
                    addedToGroup: 'Added to rule group "{{group}}": {{indexes}}',
                    removedFromGroup: 'Removed from rule group "{{group}}": {{indexes}}',
                    modifiedGroupIndexes: 'Modified index assignments for rule group "{{group}}"',
                    setGroupName: 'Set name for rule group "{{group}}" to "{{value}}" (new parameter)',
                    changedGroupName: 'Changed rule group name from "{{from}}" to "{{to}}"',
                    setGroupMaxBoost: 'Set max boost for rule group "{{group}}" to {{value}} (new parameter)',
                    changedGroupMaxBoost: 'Changed max boost for rule group "{{group}}" from {{from}} to {{to}}',
                    createdRuleGroups: 'Created rule groups: {{groups}} (new parameter)',
                    createdEmptyRuleGroups: 'Created empty rule groups configuration (new parameter)',
                    addedRuleGroup: 'Added new rule group: "{{name}}"',
                    removedRuleGroup: 'Removed rule group',
                    modifiedRuleGroupsConfig: 'Modified rule groups configuration',
                    setRuleGroupSetting: 'Set rule group setting: {{field}}',
                    changedRuleGroupSetting: 'Changed rule group setting: {{field}}',
                    setLowestRiskLevel: 'Set lowest risk level to "{{value}}" (new parameter)',
                    changedLowestRiskLevel: 'Changed lowest risk level from "{{from}}" to "{{to}}"',
                    setGeneralSetting: 'Set general setting: {{field}}',
                    changedGeneralSetting: 'Changed general setting: {{field}}',
                    setHybridStrategy: 'Set hybrid strategy to "{{value}}" (new parameter)',
                    changedHybridStrategy: 'Changed hybrid strategy from "{{from}}" to "{{to}}"',
                    setAdditiveThreshold: 'Set additive threshold to {{value}} (new parameter)',
                    changedAdditiveThreshold: 'Changed additive threshold from {{from}} to {{to}}',
                    setWeightedThreshold: 'Set weighted threshold to {{value}} (new parameter)',
                    changedWeightedThreshold: 'Changed weighted threshold from {{from}} to {{to}}',
                    setHybridSetting: 'Set hybrid setting: {{field}}',
                    changedHybridSetting: 'Changed hybrid setting: {{field}}',
                    setStartingScore: 'Set starting score to {{value}} (new parameter)',
                    changedStartingScore: 'Changed starting score from {{from}} to {{to}}',
                    setBooleanDeduction: 'Set boolean deduction to {{value}} (new parameter)',
                    changedBooleanDeduction: 'Changed boolean deduction from {{from}} to {{to}}',
                    setMaxScoringDeduction: 'Set max scoring deduction to {{value}} (new parameter)',
                    changedMaxScoringDeduction: 'Changed max scoring deduction from {{from}} to {{to}}',
                    setMinimumScore: 'Set minimum score to {{value}} (new parameter)',
                    changedMinimumScore: 'Changed minimum score from {{from}} to {{to}}',
                    setInvertedConfig: 'Set inverted config: {{field}}',
                    changedInvertedConfig: 'Changed inverted config: {{field}}',
                    setField: 'Set {{field}} to {{value}} (new parameter)',
                    changedField: 'Changed {{field}} from {{from}} to {{to}}'
                },
                // Model Governance Approval Workflow (COMP-038)
                approval: {
                    panelTitle: "Model Governance",
                    statusDraft: "Draft",
                    statusSubmitted: "In Review",
                    statusApproved: "Approved",
                    submitForReview: "Submit for Approval",
                    approveModel: "Approve Configuration",
                    rejectModel: "Reject",
                    approveComment: "Approval comment (optional)",
                    rejectComment: "Reason for rejection (required)",
                    rejectCommentRequired: "Please provide a reason for rejection.",
                    approvedBy: "Approved by",
                    approvedAt: "Approved on",
                    approvalHistory: "Approval History",
                    noHistory: "No approval actions yet.",
                    historyFrom: "From",
                    historyTo: "To",
                    historyBy: "By",
                    historyAt: "Date",
                    historyComment: "Comment",
                    lockedMessage: "This configuration is locked while under review. Reject to unlock for editing.",
                    approvedLockedMessage: "This configuration is approved and active. Save new changes to start a new review cycle.",
                    toastSubmitted: "Configuration submitted for approval.",
                    toastApproved: "Configuration approved and active.",
                    toastRejected: "Configuration returned to draft.",
                    lockedChangesHint: "Configuration is locked — reject the review to make changes.",
                }
            },
            preview: {
                title: "Configuration Preview",
                country: "Country",
                originalValue: "Original Value",
                processedValue: "Processed Value",
                displayValue: "Display Value"
            },
            validation: {
                title: "Model & Data Validation",
                lastRun: "Last run",
                rerunValidation: "Re-run Validation",
                rerunComplete: "Validation complete",
                expandAll: "Expand all",
                collapseAll: "Collapse all",
                all: "All",
                errors: "Errors",
                warnings: "Warnings",
                passed: "Passed",
                totalChecks: "Total Checks",
                noChecksMatch: "No checks match the current filter.",
                clearFilter: "Show all",
                navigateToIndex: "Open {{name}} in Risk Management",
                modelConfiguration: "Model Configuration",
                dataFreshness: "Data Freshness",
                dataCompleteness: "Data Completeness",
                countryData: "Country Data",
                manualOverrides: "Manual Risk Overrides",
                checksCount: "{{count}} check(s)",
                checks: {
                    indexAvailability: "Index availability",
                    indexTypeValidity: "Index type validity",
                    indexStatus: "Index status",
                    scoringScale: "Scoring scale configuration",
                    riskModelInstantiation: "RiskModel instantiation",
                    updateStatus: "{{name}} – update status",
                    requiredFields: "{{name}} – required fields",
                    riskCategorization: "{{name}} – risk categorization",
                    countryList: "Country list",
                    countryDataIntegrity: "Country data integrity",
                    uniqueCountryCodes: "Unique country codes",
                    overrideCount: "Active manual overrides",
                    staleOverrides: "Overrides needing review"
                },
                messages: {
                    indexesConfigured: "{{count}} index(es) configured",
                    noIndexesConfigured: "No indexes configured – model cannot calculate risk",
                    allIndexesValidType: "All indexes have valid type (BOOLEAN or SCORING)",
                    invalidTypeCount: "{{count}} index(es) have invalid type",
                    allIndexesActive: "All indexes are ACTIVE",
                    inactiveCount: "{{count}} index(es) are not ACTIVE",
                    allScoringScaleConfigured: "All {{count}} scoring index(es) have scale configured",
                    missingScoringScale: "{{count}} scoring index(es) missing scale.maxValue",
                    riskModelOk: "RiskModel instantiates and validates correctly",
                    riskModelFailed: "RiskModel failed to instantiate or validate",
                    noUpdateDate: "No update date recorded",
                    overdue: "Overdue: last updated {{days}} days ago (threshold: {{threshold}} days for {{frequency}})",
                    approachingDeadline: "Approaching deadline: last updated {{days}} days ago (threshold: {{threshold}} days)",
                    upToDate: "Up to date: last updated {{days}} days ago",
                    allRequiredFieldsPresent: "All required fields present",
                    missingFields: "Missing fields: {{fields}}",
                    coversRiskCategories: "Covers {{count}} risk category(ies): {{categories}}",
                    noRiskCategoriesDefined: "No risk categories defined",
                    countriesInSystem: "{{count}} countries in system",
                    expectedCountryCoverage: "Expected at least 100 countries for comprehensive coverage",
                    allCountriesHaveFields: "All countries have alpha2 code and English name",
                    countriesMissingFields: "{{count}} countries missing required fields",
                    allCountryCodesUnique: "All country codes are unique",
                    duplicateCodesFound: "{{count}} duplicate code(s) found",
                    noOverrides: "No active manual overrides",
                    activeOverrides: "{{count}} active manual override(s) — model results replaced for these countries",
                    noStaleOverrides: "All overrides are within the review period",
                    staleOverrideCount: "{{count}} override(s) need review — review period has expired",
                    daysAgo: "days since last review"
                },
                details: {
                    lastUpdated: "Last updated: {{date}}",
                    updatedBy: "Updated by: {{user}}",
                    frequency: "Frequency: {{frequency}}",
                    source: "Source: {{source}}",
                    countriesInSystem: "Countries in system: {{count}}"
                },
                modelChecks: "Model Checks",
                dataValidation: "Data Validation",
                exportReport: "Export Report",
                export: {
                    sheetChecks: "Model Checks",
                    sheetSummary: "Summary",
                    colCategory: "Category",
                    colCheck: "Check",
                    colStatus: "Status",
                    colMessage: "Message",
                    colDetails: "Details",
                    statusPass: "PASS",
                    statusWarning: "WARNING",
                    statusError: "ERROR",
                    summaryTitle: "GeoRisk Model Validation Report",
                    totalChecks: "Total Checks",
                    errors: "Errors",
                    warnings: "Warnings",
                    passed: "Passed",
                    generatedBy: "Generated by Prime RMS",
                    entity: "Entity",
                    runAt: "Run at"
                }
            },
            dataValidation: {
                title: "Data Validation",
                description: "Upload anonymized production data to validate how well your model captures reality.",
                uploadCSV: "Upload CSV",
                uploadHint: "CSV, TSV, or TXT file with country codes and risk levels",
                noDatasets: "No datasets yet",
                noDatasetsDesc: "Upload a CSV file with country codes and expected risk levels to validate your model against real data.",
                datasets: "Uploaded Datasets",
                rowCount: "{{count}} rows",
                colCount: "{{count}} columns",
                deleteDataset: "Delete dataset",
                confirmDeleteDataset: "Are you sure you want to delete the dataset \"{{name}}\"? This action cannot be undone.",
                columnMapping: "Column Mapping",
                columnMappingHint: "Map your CSV columns to the model's data fields.",
                countryCodeColumn: "Country code (ISO Alpha-2)",
                expectedRiskColumn: "Expected risk level",
                indexDataColumns: "Index Data Columns (optional)",
                useSystemData: "— Use system data —",
                selectColumn: "— Select column —",
                runValidation: "Run Validation",
                running: "Running...",
                results: "Validation Results",
                ranAt: "Validation ran at {{time}}",
                accuracy: "Accuracy",
                totalRecords: "Records",
                falsePositives: "False Positives",
                falseNegatives: "False Negatives",
                recommendationGood: "The model captures {{pct}}% of cases correctly. Good alignment with production data.",
                recommendationModerate: "The model captures {{pct}}% of cases correctly. Consider reviewing misclassified cases and adjusting thresholds.",
                recommendationPoor: "The model captures only {{pct}}% of cases correctly. Significant calibration needed — review risk thresholds and index configuration.",
                distributionComparison: "Risk Level Distribution",
                riskLevel: "Risk Level",
                expected: "Expected (Data)",
                predicted: "Predicted (Model)",
                difference: "Difference",
                confusionMatrix: "Confusion Matrix",
                confusionLegend: "Rows = actual risk level from data. Columns = predicted by model. Diagonal = correct predictions.",
                showDetails: "Show details ({{count}} records)",
                countryCode: "Code",
                countryName: "Country",
                score: "Score",
                match: "Match",
                showingFirst: "Showing first {{count}} of {{total}} records",
                showMoreRows: "Show more ({{count}} remaining)",
                showingXofY: "Showing {{shown}} of {{total}} records",
                exportPDF: "Export PDF",
                exportExcel: "Export Excel",
                exportingReport: "Generating report...",
                exportError: "Export failed. Please try again."
            },
            // Field Mapping tab (GEO-069)
            fieldMapping: {
                tab: "Mapping",
                title: "Field Mapping",
                subtitle: "Map active GeoRisk indexes to columns in an external data source.",
                dataSource: "Data Source",
                selectDataSource: "— Select data source —",
                table: "Table",
                selectTable: "— Select table —",
                tableNamePlaceholder: "Enter table name",
                countryCodeColumn: "Country Code Column",
                selectCountryCodeColumn: "— Select column —",
                countryCodeColumnPlaceholder: "Enter column name (e.g. country_code)",
                indexMappings: "Index Mappings",
                indexCol: "Index",
                columnCol: "Source Column",
                transformCol: "Transform",
                selectColumn: "— Select column —",
                columnPlaceholder: "Enter column name",
                transform_none: "None",
                transform_uppercase: "Uppercase",
                transform_lowercase: "Lowercase",
                transform_trim: "Trim",
                transform_number: "To Number",
                transform_boolean: "To Boolean",
                transform_date: "To Date",
                previewData: "Preview Data (5 rows)",
                saveMapping: "Save Mapping",
                saving: "Saving…",
                statusComplete: "Mapping complete — all indexes mapped",
                statusIncomplete: "{{count}} index(es) without column mapping",
                savedSuccess: "Mapping saved successfully",
                saveError: "Failed to save mapping: {{error}}",
                errorNoDataSource: "Please select a data source",
                errorNoTable: "Please enter a table name",
                errorNoCountryColumn: "Please specify the country code column",
                previewTitle: "Data Preview",
                previewSubtitle: "Sample rows from {{table}}",
                previewNoData: "No preview available — connect to a live data source to fetch rows.",
                noDataSources: "No data sources configured. Add one in Admin → Data Sources.",
                noIndexes: "No active indexes configured. Add indexes in the Risk Management tab.",
            }
        },
      },
      validationReport: {
        title: "Validation Report",
        accuracy: "Overall Accuracy",
        highRiskCapture: "High-Risk Capture Rate",
        falseAlarmRate: "False Alarm Rate",
        underClassified: "Under-classified",
        overClassified: "Over-classified",
        perLevelAnalysis: "Per Risk Level Analysis",
        performanceMetrics: "Performance Metrics",
        riskLevel: "Risk Level",
        actual: "Actual",
        correct: "Correct",
        sensitivity: "Sensitivity (Recall)",
        precision: "Precision",
        recall: "Recall",
        f1Score: "F1-Score",
        fpr: "False Positive Rate",
        macroAverage: "Macro Average",
        under: "Under",
        over: "Over",
        recommendationCapture: "The model captures {{capture}}% of high-risk cases with an overall accuracy of {{pct}}%. This result can be linked to the BWRA as validation documentation.",
        recommendationGeneric: "The model achieves an overall accuracy of {{pct}}%. Run validation with production data to assess high-risk capture rate.",
        export: {
          reportTitle: "GeoRisk Validation Report",
          systemName: "RMS",
          exportDate: "Export Date",
          generatedBy: "Generated by",
          dataset: "Dataset",
          totalRecords: "Total Records",
          correctPredictions: "Correct Predictions",
          falsePositives: "False Positives",
          falseNegatives: "False Negatives",
          sheetSummary: "Summary",
          sheetPerLevel: "Per-Level Analysis",
          sheetDistribution: "Distribution",
          sheetConfusion: "Confusion Matrix",
          sheetDetails: "Details",
          confusionActual: "Actual / Predicted",
          noData: "No validation data available to export",
          pdfPageOf: "Page {{page}} of {{total}}",
        },
      },
      login: {
        title: "Log in",
        email: "Email address",
        emailLabel: "Email address",
        usernameLabel: "Username",
        password: "Password",
        passwordLabel: "Password",
        submit: "Log in",
        loading: "Signing in...",
        error: "Incorrect username or password!",
        jwtNotConfigured: "JWT authentication is not yet configured. Contact your administrator.",
        tagline: "AML Risk Management Platform",
        secureAccess: "Trusted by compliance teams across financial institutions",
        poweredBy: "Powered by Mertil",
        riskManagementSystem: "Risk Management System",
        feature1: "GeoRisk — Geographical Risk Modelling",
        feature2: "CustomerRisk — Customer-Based Risk Assessment",
        feature3: "BWRA — Business-Wide Risk Analysis",
        emailPlaceholder: "name@company.com",
        usernamePlaceholder: "Enter your username",
        version: "RMS v2.0",
      },
      glossary: {
        title: "AML Glossary",
        subtitle: "Definitions of key terms used in AML risk management.",
        searchPlaceholder: "Search terms...",
        noResults: "No matching terms found.",
        aml: {
          title: "AML — Anti-Money Laundering",
          definition: "A set of laws, regulations, and procedures intended to prevent criminals from disguising illegally obtained funds as legitimate income.",
        },
        bwra: {
          title: "BWRA — Business-Wide Risk Assessment",
          definition: "A comprehensive assessment of all money laundering and terrorist financing risks across an entire organisation. Required under AMLR.",
        },
        cdd: {
          title: "CDD — Customer Due Diligence",
          definition: "The process of verifying a customer's identity and assessing their risk level before and during a business relationship.",
        },
        edd: {
          title: "EDD — Enhanced Due Diligence",
          definition: "Additional checks applied to high-risk customers, such as PEPs or customers from high-risk jurisdictions, beyond standard CDD.",
        },
        fatf: {
          title: "FATF — Financial Action Task Force",
          definition: "The global standard-setter for anti-money laundering and counter-terrorism financing. Publishes the list of high-risk and monitored jurisdictions.",
        },
        grossRisk: {
          title: "Gross Risk",
          definition: "The inherent risk level before any controls or mitigation measures are applied. Calculated as likelihood × impact.",
        },
        kri: {
          title: "KRI — Key Risk Indicator",
          definition: "A metric used to signal increasing risk exposure. KRIs provide early warnings of potential breaches of risk appetite thresholds.",
        },
        kyc: {
          title: "KYC — Know Your Customer",
          definition: "The process of identifying and verifying the identity of clients. A core component of CDD and AML compliance.",
        },
        pep: {
          title: "PEP — Politically Exposed Person",
          definition: "An individual who holds or has held a prominent public function. PEPs are considered higher risk and require EDD measures.",
        },
        periodicReporting: {
          title: "Periodic Reporting",
          definition: "Regular reporting to regulators on AML/CFT activities, model performance, and compliance status, typically on an annual basis.",
        },
        residualRisk: {
          title: "Residual Risk",
          definition: "The remaining risk after controls and mitigation measures have been applied. The goal is to reduce residual risk below the organisation's risk appetite.",
        },
        riskAppetite: {
          title: "Risk Appetite",
          definition: "The level of risk an organisation is willing to accept in pursuit of its objectives. Defines acceptable thresholds for each risk category.",
        },
        riskIndex: {
          title: "Risk Index",
          definition: "A scored indicator used to assess risk in a specific dimension (e.g., a country corruption index). Multiple indices can be combined in a risk model.",
        },
        riskModel: {
          title: "Risk Model",
          definition: "A structured methodology for calculating a risk score from multiple inputs. RMS supports deterministic, weighted, and rule-based models.",
        },
        riskScore: {
          title: "Risk Score",
          definition: "A numerical value representing the level of risk for a specific entity (e.g., country or customer). Used for categorisation and reporting.",
        },
        str: {
          title: "STR — Suspicious Transaction Report",
          definition: "A report submitted to the financial intelligence unit (FIU) when a transaction is suspected to involve money laundering or terrorism financing.",
        },
        transactionMonitoring: {
          title: "Transaction Monitoring",
          definition: "Automated or manual review of customer transactions to detect unusual patterns that may indicate money laundering, fraud, or other financial crimes.",
        },
        auditLog: {
          title: "Audit Log",
          definition: "A chronological record of all user actions within the system, including who did what and when. Required for regulatory accountability and forensic investigations.",
        },
        rbac: {
          title: "RBAC — Role-Based Access Control",
          definition: "A method of restricting system access based on the roles assigned to individual users. In RMS, roles define which modules a user can view, edit, or administer.",
        },
        userRole: {
          title: "User Role",
          definition: "A predefined set of permissions assigned to a user. Common roles include Admin (full access), Analyst (read/write), and Viewer (read-only).",
        },
        entityAccess: {
          title: "Entity Access",
          definition: "Controls which legal entities or organisational units a user can access and manage. Useful in multi-entity deployments to enforce data segregation.",
        },
        licenseManagement: {
          title: "License Management",
          definition: "The process of activating, deactivating, and managing module licenses per entity. A module without an active license is inaccessible to users of that entity.",
        },
        reportingCycle: {
          title: "Reporting Cycle",
          definition: "The defined period covered by a compliance report — typically annual. Each reporting cycle must be documented, completed, and submitted to the regulator.",
        },
        approvalFlow: {
          title: "Approval Flow",
          definition: "The structured process by which a report moves from Draft → Under Review → Approved. Ensures adequate oversight before submission to the regulator.",
        },
        reportCompletion: {
          title: "Report Completion",
          definition: "The percentage of required sections filled in a periodic report. A fully completed report has all mandatory fields populated and is ready for approval.",
        },
        complianceReport: {
          title: "Compliance Report",
          definition: "A formal document summarising AML/CFT activities, model performance, and risk findings for a specific period. Submitted to regulators as part of ongoing compliance obligations.",
        },
        riskControl: {
          title: "Risk Control",
          definition: "A measure or action taken to mitigate a risk. Controls can be model-based (linked to a risk scoring model), system-based (automated), or manual. The effectiveness of controls determines the reduction in residual risk.",
        },
        unmitigatedRisk: {
          title: "Unmitigated Risk",
          definition: "A risk that has no controls linked to it — neither manual controls nor model-based auto-controls. Unmitigated risks have the full gross risk score as residual risk. They should be addressed by adding a control or by documenting an accepted risk appetite decision.",
        },
        traceabilityMatrix: {
          title: "Traceability Matrix",
          definition: "A table showing which risk categories are covered by which models or controls. Helps ensure that all identified risks have corresponding mitigation measures and that model coverage is complete.",
        },
        modelValidation: {
          title: "Model Validation",
          definition: "The process of verifying that a risk model performs as intended. Includes checking configuration, data quality, and comparing model predictions against known outcomes. Required for regulatory compliance.",
        },
        sdd: {
          title: "SDD — Simplified Due Diligence",
          definition: "A reduced level of customer due diligence applied to demonstrably low-risk customers. Fewer identity checks are required compared to standard CDD. Only applicable when all risk factors are consistently low.",
        },
        cddEddClassification: {
          title: "CDD/EDD Classification",
          definition: "Three-tier due diligence framework used to assign the appropriate level of customer checks:\n• SDD (Simplified) — low-risk customers, basic verification only\n• CDD (Standard) — default level for most customers\n• EDD (Enhanced) — triggered by high risk score, PEP status, or high-risk country",
        },
      },
      onboarding: {
        title: "Welcome to RMS",
        step: "Step",
        skip: "Skip",
        back: "Back",
        next: "Next",
        getStarted: "Get Started",
        steps: {
          welcome: {
            title: "Welcome to Risk Management Studio",
            body: "RMS is an AML model documentation and validation platform. It helps compliance teams manage risks, models, and regulatory requirements in one place.",
          },
          bwra: {
            title: "Business-Wide Risk Assessment",
            body: "Start here. Identify all business-wide risks and set risk appetite. BWRA is the starting point — GeoRisk, Customer Risk, and Transaction Monitoring are models that exist to cover the risks you define here.",
          },
          geoRisk: {
            title: "Geographical Risk",
            body: "A model that covers geographic risks identified in BWRA. Assess country-level AML risk using configurable indices (FATF, corruption, sanctions) and feed results back into residual risk.",
          },
          customerRisk: {
            title: "Customer Risk",
            body: "A model that covers customer-related risks identified in BWRA. Build scoring models with parameters such as KYC status, PEP flag, and transaction volume.",
          },
          txMon: {
            title: "Transaction Monitoring",
            body: "A model that covers transaction risk scenarios identified in BWRA. Define rules to detect suspicious patterns and manage alerts across your portfolio.",
          },
          complete: {
            title: "You're all set!",
            body: "Explore each module from the sidebar. Use the glossary icon in the top menu to look up AML terms at any time.",
          },
        },
      },
    },
    sv: {
      app: {
        loading: "Laddar...",
        skipToMain: "Hoppa till huvudinnehåll",
      },
      common: {
        unsavedChangesTitle: "Osparade ändringar",
        unsavedChangesMessage: "Du har osparade ändringar. Vill du lämna utan att spara?",
        stay: "Stanna",
        leave: "Lämna",
        actions: {
          create: "Skapa",
          update: "Uppdatera",
          delete: "Ta bort",
          login: "Logga in",
          logout: "Logga ut",
          dataImport: "Dataimport",
          dataExport: "Dataexport"
        },
        levels: {
          info: "Info",
          warning: "Varning",
          error: "Fel"
        },
        modules: {
          GeoRisk: "Geografisk risk",
          CustomerRisk: "Kundrisk",
          BWRA: "BWRA",
          TransactionMonitoring: "Transaktionsövervakning",
          PeriodicReporting: "Periodisk rapportering",
          Admin: "Administration",
          Auth: "Autentisering",
          Dashboard: "Instrumentpanel",
          DataSources: "Datakällor",
          FieldMappings: "Mappningar"
        },
        riskLevels: {
          low: "Låg",
          medium: "Medel",
          high: "Hög",
          veryHigh: "Mycket hög",
          unacceptable: "Oacceptabel"
        },
        indexStatus: {
          ACTIVE: "Aktiv",
          DRAFT: "Utkast"
        },
        unknown: "Okänd",
        user: "Användare",
        deletedUserSuffix: "(borttagen)",
        confirm: "Bekräfta",
        processing: "Bearbetar…",
        cancel: "Avbryt",
        close: "Stäng",
        undo: "Ångra",
        openGlossary: "Öppna ordlistan",
        notAvailable: "Ej tillämpligt",
        articlePrefix: "Art.",
        dataTable: {
          noResults: "Inga resultat hittades",
          previous: "Föregående",
          next: "Nästa",
          page: "Sida",
          rowsPerPage: "Rader per sida",
          showing: "Visar {start}–{end} av {total}",
        },
        toast: {
          saved: "Sparades",
          deleted: "Borttaget",
          exported: "Export klar",
          imported: "Import klar",
          error: "Ett fel uppstod",
          licenseSaved: "Licens uppdaterad",
          settingsSaved: "Inställningar sparade",
          userSaved: "Användare sparad",
          userDeleted: "Användare borttagen",
          entitySaved: "Entitet sparad",
          entityDeleted: "Entitet borttagen",
          parameterAdded: "Parameter tillagd",
          parameterUpdated: "Parameter uppdaterad",
          parameterDeleted: "Parameter borttagen",
          modelConfigSaved: "Modellkonfiguration sparad",
          previewGenerated: "Förhandsgranskning genererad",
          bwraRiskSaved: "Risk sparad",
          bwraRiskArchived: "Risk arkiverad",
          bwraRiskRestored: "Risk återställd",
          bwraRiskDeleted: "Risk raderad",
          bwraControlSaved: "Kontroll sparad",
          bwraControlDeleted: "Kontroll borttagen",
          bwraActionSaved: "Åtgärd sparad",
          bwraActionDeleted: "Åtgärd borttagen",
          bwraAcceptanceSaved: "Risk accepterad",
          bwraAcceptanceRevoked: "Riskacceptans återkallad",
          bwraReportGenerated: "Rapporten genererades",
          bwraReportExported: "Rapport exporterad",
          bwraReportError: "Rapportgenerering misslyckades",
        },
        errorBoundary: {
          title: "Något gick fel",
          message: "Ett oväntat fel inträffade. Vänligen försök igen.",
          retry: "Försök igen",
          details: "Felinformation",
        },
        breadcrumb: {
          home: "Hem",
          ariaLabel: "Brödsmulenavigation",
        },
        commandPalette: {
          placeholder: "Sök moduler och åtgärder...",
          openHint: "Ctrl+K",
          noResults: "Inga resultat hittades",
          categoryModules: "Moduler",
          categoryActions: "Snabbåtgärder",
          actionGoTo: "Gå till",
          ariaLabel: "Kommandopalett",
          pressEsc: "Tryck Esc för att stänga",
        },
        form: {
          required: "Obligatoriskt fält",
          errorRequired: "Det här fältet är obligatoriskt",
          errorEmail: "Ange en giltig e-postadress",
          errorMinLength: "Minst {{min}} tecken krävs",
          errorMaxLength: "Maximalt {{max}} tecken tillåts",
        },
        concurrency: {
          conflictTitle: "Redigeringskonflikt detekterad",
          conflictDescription: "Den här posten ändrades av {{user}} den {{date}} kl {{time}} medan du redigerade.",
          keepMine: "Behåll min version",
          keepMineHint: "Spara dina ändringar och skriv över den andra användarens ändringar.",
          keepTheirs: "Kasta mina ändringar",
          keepTheirsHint: "Kasta dina ändringar och ladda om den senast sparade versionen.",
          mergeManually: "Slå samman manuellt",
          mergeManuallyHint: "Stäng dialogen så att du kan jämföra båda versionerna själv.",
          editingLabel: "Redigeras just nu:",
        },
      },
      import: {
        wizard: {
          defaultTitle: "Importera data",
          stepSelect: "Välj fil",
          stepPreview: "Förhandsvisning",
          stepMap: "Mappa fält",
          stepConfirm: "Importera",
          selectPrompt: "Klicka eller dra en fil hit",
          selectFormats: "Stödda format: CSV, Excel (.xlsx), JSON",
          parsing: "Läser fil…",
          emptyFile: "Filen verkar vara tom",
          parseError: "Kunde inte läsa filen: {{error}}",
          formatHintsTitle: "Stödda format",
          formatExcelHint: "Första raden = rubriker, följande rader = data",
          previewRowCount: "{{count}} rader hittades",
          previewShowing: "(visar första {{count}})",
          mapDescription: "Matcha kolumnerna i din fil mot förväntade datafält.",
          mapTargetField: "Målfält",
          mapSourceColumn: "Källkolumn",
          mapNotMapped: "(hoppa över / ej mappad)",
          requiredNote: "Obligatoriska fält",
          validationOk: "{{count}} rader redo att importera",
          validationErrors: "{{count}} rader hoppas över på grund av fel",
          noValidRows: "Inga giltiga rader",
          andMore: "…och {{count}} till",
          confirmPreviewTitle: "Rader att importera (visar första 5):",
          importBtn: "Importera {{count}} rader",
          back: "Tillbaka",
          next: "Nästa",
          validateBtn: "Validera & importera",
          rowError: "Rad {{n}}: \"{{field}}\" är obligatoriskt",
          buttonLabel: "Importera data",
        },
        fields: {
          name: "Namn",
          category: "Kategori",
          description: "Beskrivning",
          dbField: "DB-fält",
        },
      },
      sidebar: {
        dashboard: "Instrumentpanel",
        customerRisk: "Kundrisk",
        geographicalRisk: "Geografisk risk",
        bwra: "BWRA",
        transactionMonitoring: "Transaktionsövervakning",
        periodicReporting: "Periodisk rapportering",
        admin: "Administration",
        roleLabel: "Roll",
        expand: "Expandera sidofältet",
        collapse: "Minimera sidofältet",
      },
      permissions: {
        requiresEdit: "Kräver redigeringsbehörighet",
        requiresEditDesc: "Du har läsbehörighet till denna modul. Kontakta din administratör för att begära redigeringsbehörighet.",
        requiresAdmin: "Kräver administratörsbehörighet",
        requiresAdminDesc: "Det här avsnittet kräver administratörsåtkomst.",
        readOnlyBadge: "Skrivskyddad",
        roles: {
          admin: "Administratör",
          analyst: "Analytiker",
          viewer: "Läsare",
        },
      },
      topMenu: {
        logout: "Logga ut",
        sessionLeft: "Session: {{time}} kvar",
        inactive: "Inaktiv: {{time}}",
        language: "Språk",
        sessionExpiring: "Sessionen löper ut om {{time}}",
        inactivityWarning: "Utloggning pga inaktivitet om {{time}}",
        days: "{{count}} dagar",
        day: "{{count}} dag",
        hours: "{{count}} timmar",
        hour: "{{count}} timme",
        minutes: "{{count}} minuter",
        minute: "{{count}} minut",
        zeroMinutes: "0 minuter",
        switchEntity: "Byt entitet",
        noEntity: "Ingen entitet",
        preferences: "Inställningar",
        theme: "Tema",
        themeDefault: "Standard",
        themeDark: "Mörkt",
        themeLight: "Ljust",
        sessionSection: "Session",
        brandTagline: "Risk Management System",
        glossary: "AML-ordlista",
        search: "Sök",
      },
      notifications: {
        label: "Notifikationer",
        panelTitle: "Notifikationer",
        empty: "Allt klart — inga väntande åtgärder",
        markAllRead: "Markera alla som lästa",
        unread: "Oläst",
        urgentCount: "{{count}} brådskande",
        warningCount: "{{count}} varning",
        annualReport: "Årsrapport {{year}}",
        bwraReview: "BWRA Årsöversyn",
        scheduledValidation: "Validering: {{model}}",
        staleData: "{{module}}: Data försenad",
        overdueDesc: "{{count}} dagar försenad — åtgärd krävs",
        dueSoonDesc: "Förfaller om {{count}} dagar",
        staleDataDesc: "Data har inte uppdaterats inom förväntat intervall",
        scheduledReportCreated: "Schemalagd rapport skapad: {{year}}",
        scheduledReportCreatedDesc: "Ett rapportutkast för {{year}} skapades automatiskt — granska och slutför det",
        reportDistributed: "Rapport {{year}} godkänd och distribuerad",
        reportDistributedDesc: "Rapporten för {{year}} godkändes och distribuerades till {{count}} mottagare",
        kpiThreshold: "KPI-varning: {{kpi}}",
        kpiThresholdDesc: "Aktuellt värde {{value}} uppnår eller överskrider tröskeln {{threshold}}",
      },
      globalSearch: {
        title: "Global sökning",
        placeholder: "Sök risker, regler, länder, modeller…",
        hint: "Börja skriva för att söka i alla moduler",
        typeMore: "Ange minst 2 tecken",
        noResults: "Inga resultat hittades",
      },
      helpTerms: {
        keyTerms: "Nyckelbegrepp",
      },
      modules: {
        dashboard: {
            welcome: "Välkommen till Risk Management Studio",
            chooseModule: "Välj en modul i sidomenyn för att börja.",
            moduleStatus: "Modulstatus",
            recentActivity: "Senaste aktivitet",
            riskSummary: "Risksammanfattning",
            geoRiskModels: "GeoRisk-modeller",
            customerRiskModels: "Kundrisk-modeller",
            configured: "konfigurerade",
            countries: "länder",
            indices: "index",
            models: "modeller",
            parameters: "parametrar",
            noData: "Ingen data ännu",
            noRecentActivity: "Ingen aktivitet registrerad.",
            viewAll: "Visa alla",
            goToModule: "Gå till modul",
            totalCountries: "Antal länder",
            totalIndices: "Antal index",
            highRiskCountries: "Högriskländer",
            avgRiskScore: "Genomsnittlig riskpoäng",
            totalModels: "Antal modeller",
            totalParameters: "Antal parametrar",
            quickActions: "Snabbåtgärder",
            openGeoRisk: "Öppna GeoRisk",
            openCustomerRisk: "Öppna Kundrisk",
            openBwra: "Öppna BWRA",
            openTxMon: "Öppna Transaktionsövervakning",
            openPrpt: "Öppna Periodisk rapportering",
            openAdmin: "Öppna Administration",
            quickActionsSubtitle: "Genvägar till specifika uppgifter i varje modul",
            quickActionImportData: "Importera riskdata",
            quickActionManageCountries: "Hantera länder",
            quickActionManageParameters: "Hantera parametrar",
            quickActionRiskRegister: "Riskregister",
            quickActionActionPlan: "Åtgärdsplan",
            quickActionMonitoringRules: "Övervakningsregler",
            quickActionExportReport: "Exportera rapport",
            quickActionCreateRisk: "Skapa risk",
            quickActionRunValidation: "Kör validering",
            quickActionNewReport: "Ny rapport",
            systemOverview: "Systemöversikt",
            riskAreas: "Riskområden",
            controls: "Kontroller",
            unmitigated: "Omitigerade",
            countryRiskDistribution: "Landriskfördelning",
            bwraRiskPosture: "BWRA Riskprofil",
            grossRisk: "Snitt bruttorisk",
            residualRisk: "Snitt residualrisk",
            mitigatedRisks: "{{count}} mitigerade",
            unmitigatedRisks: "{{count}} omitigerade",
            highRiskAlert: "{{count}} länder flaggade som högrisk eller högre",
            viewHighRiskCountries: "Visa högriskländer",
            viewUnmitigatedRisks: "Visa ej åtgärdade risker",
            geoRiskTitle: "Geografisk risk",
            geoRiskSubtitle: "Täcker risker i BWRA",
            customerRiskTitle: "Kundrisk",
            customerRiskSubtitle: "Täcker risker i BWRA",
            bwraTitle: "BWRA",
            bwraSubtitle: "Startpunkten – identifiera affärsövergripande risker",
            txMonTitle: "Transaktionsövervakning",
            txMonSubtitle: "Täcker risker i BWRA",
            prptTitle: "Periodisk rapportering",
            prptSubtitle: "FI-rapportering — årlig tillsynsdata",
            prptReports: "Rapporter",
            prptAwaitingReview: "Inväntar granskning",
            prptApproved: "Godkänd",
            prptViewReview: "Visa rapporter som inväntar granskning",
            adminTitle: "Administration",
            highRisk: "Högrisk",
            highRiskOrAbove: "Högrisk eller mer",
            auditEvents: "Granskningshändelser",
            statusActive: "Aktiv",
            statusSetupNeeded: "Konfiguration krävs",
            riskLevelLow: "Låg",
            riskLevelMedium: "Medel",
            riskLevelHigh: "Hög",
            riskLevelVeryHigh: "Mycket hög",
            riskLevelUnacceptable: "Oacceptabel",
            riskLevelUnscored: "Ej poängsatt",
            customizeLayout: "Anpassa layout",
            layoutReset: "Återställ layout",
            layoutDone: "Klar",
            layoutEditHint: "Dra kort för att ändra ordning",
            dragToReorder: "Dra för att flytta",
            complianceChainLabel: "Helhetsvalidering av compliance-kedjan",
            complianceChainLastRun: "Senast körd: {{time}}",
            runComplianceChain: "Kör helhetsvalidering",
            timeJustNow: "just nu",
            timeMinutesAgo: "{{count}} min sedan",
            timeHoursAgo: "{{count}}h sedan",
            timeDaysAgo: "{{count}}d sedan",
            activityLoggedIn: "Användare loggade in",
            activityLoggedOut: "Användare loggade ut",
            activityCreated: "Skapade",
            activityUpdated: "Uppdaterade",
            activityDeleted: "Tog bort",
            activityImported: "Importerade data till",
            activityExported: "Exporterade data från",
            activityIn: "i",
            entityType: {
                user: "användare",
                role: "roll",
                entity: "entitet",
                settings: "inställningar",
                riskmodel: "riskmodell",
                riskindex: "riskindex",
                modelconfiguration: "modellkonfiguration",
                parameter: "parameter",
            },
            complianceCoverage: "AMLR Compliance",
            complianceArticlesCovered: "Täckta",
            complianceArticlesPartial: "Delvis",
            complianceArticlesGap: "Gap",
            compliancePct: "{{pct}}% täckt",
            complianceViewMap: "Visa Compliance Map",
            // Tabs
            tabOverview: "Översikt",
            tabKRI: "Nyckelriskindikatorer",
            // KRI Dashboard
            kriTitle: "Nyckelriskindikatorer",
            kriSubtitle: "Ledningssammanfattning med trafikljusstatus för nyckeltal",
            kriHighRiskCountries: "Högriskländer",
            kriHighRiskCountriesDesc: "Länder flaggade som högrisk eller högre",
            kriModelValidation: "Modellvalidering",
            kriModelValidationDesc: "Senaste valideringsnoggrannhet för modeller",
            kriScheduledValidation: "Schemalagd validering",
            kriScheduledValidationDesc: "Senaste automatiserade modellvalideringskörning",
            kriValidationAccuracy: "{{accuracy}}% noggrannhet",
            kriValidationError: "Senaste körning misslyckades",
            kriBwraCoverage: "BWRA-täckning",
            kriBwraCoverageDesc: "Andel risker som mitigeras av kontroller",
            kriComplianceCoverage: "Compliance-täckning",
            kriComplianceCoverageDesc: "Andel AMLR-artiklar som täcks",
            kriLastBwraUpdate: "Senaste BWRA-uppdatering",
            kriLastBwraUpdateDesc: "Tid sedan senaste BWRA-version sparades",
            kriStatusGreen: "Inom mål",
            kriStatusYellow: "Kräver uppmärksamhet",
            kriStatusRed: "Åtgärd krävs",
            kriNoValidation: "Ingen validering",
            kriNever: "Aldrig",
            kriDaysAgo: "{{count}} dagar sedan",
            kriToday: "Idag",
            kriCountries: "{{count}} länder",
            kriAccuracy: "{{pct}}% noggrannhet",
            kriCoverage: "{{pct}}% täckt",
            kriMitigated: "{{pct}}% mitigerade",
            kriConfigureTitle: "Konfigurera indikatorer",
            kriConfigureDesc: "Välj vilka nyckelriskindikatorer som ska visas på din instrumentpanel",
            kriConfigureBtn: "Konfigurera",
            kriConfigureDone: "Klar",
            kriConfigureShowAll: "Visa alla",
            kriTrendVsWeek: "vs. förra veckan",
            kriTrendStable: "Ingen förändring",
            kriTrendPeriodLabel: "Trendperiod",
            kriTrendVs7: "vs. senaste 7 dagarna",
            kriTrendVs30: "vs. senaste 30 dagarna",
            kriTrendVs90: "vs. senaste 90 dagarna",
            kriPeriodDays: "{{n}} dagar",
            // Datakvalitetsindikatorer
            freshness: {
                green: "Aktuell data",
                yellow: "Behöver granskas",
                red: "Data försenad",
                grey: "Ingen data",
                today: "Uppdaterad idag",
                yesterday: "Uppdaterad igår",
                daysAgo: "Uppdaterad för {{days}} dagar sedan",
                never: "Aldrig uppdaterad",
            },
            // Modulhälsopanel
            health: {
                title: "Systemhälsoöversikt",
                subtitle: "En snabb statusöversikt för alla aktiva moduler — datakvalitet och nyckeltal.",
                colModule: "Modul",
                colStatus: "Hälsa",
                colMetric1: "Mätvärde 1",
                colMetric2: "Mätvärde 2",
                colLastUpdated: "Senast uppdaterad",
                colLastValidation: "Senaste validering",
                never: "Aldrig",
                noValidation: "Ej körts",
                validationError: "Fel",
                status: {
                    green: "OK",
                    yellow: "Kräver åtgärd",
                    red: "Försenad",
                    grey: "Ingen data",
                },
                overallStatus: {
                    green: "Alla moduler OK",
                    yellow: "Vissa moduler kräver åtgärd",
                    red: "Åtgärd krävs",
                    grey: "Ingen data ännu",
                },
            },
            // Onboarding guide
            onboardingTitle: "Välkommen — så här kommer du igång",
            onboardingSubtitle: "Alla moduler visar \"Konfiguration krävs\". Följ dessa steg för att konfigurera systemet:",
            onboardingStep1Title: "1. BWRA",
            onboardingStep1Desc: "Börja här. Identifiera dina affärsövergripande risker och definiera riskaptit. BWRA är grunden — alla modeller nedan finns för att täcka dessa risker.",
            onboardingStep2Title: "2. Geografisk risk",
            onboardingStep2Desc: "Konfigurera landriskindex för att täcka de geografiska riskerna identifierade i BWRA. Poängen matas tillbaka in i residualriskberäkningarna.",
            onboardingStep3Title: "3. Kundrisk",
            onboardingStep3Desc: "Bygg en kundriskmodell för att täcka kundrelaterade risker från BWRA. Parametrar och vikter bestämmer individuella kundpoäng.",
            onboardingStep4Title: "4. Transaktionsövervakning",
            onboardingStep4Desc: "Definiera övervakningsregler för att täcka transaktionsriskscenarier identifierade i BWRA. Regler kräver konfiguration innan de aktiveras.",
            onboardingDismiss: "Förstått, jag börjar nu",
            onboardingShowAgain: "Visa guiden igen",
            noLicenseHeading: "Ingen licens",
            noLicenseContact: "Kontakta din administratör",
            noLicenseCardTooltip: "Den här modulen är inte licensierad – klicka för att hantera licenser",
            // Risk Progression View
            riskProgression: {
                title: "Riskprogression",
                subtitle: "Inneboende risk → kontroller → residualrisk över alla moduler",
                inherentTitle: "Inneboende risk",
                inherentSubBwra: "Snitt bruttorisk: {{score}}/25",
                inherentSubGeo: "{{count}} högriskländer",
                inherentSourceBwra: "Källa: BWRA",
                inherentSourceGeo: "Källa: GeoRisk",
                controlTitle: "Kontrolleffektivitet",
                controlSub: "{{mitigated}} av {{total}} risker mitigerade",
                controlHigh: "Effektiv",
                controlMedium: "Delvis",
                controlLow: "Otillräcklig",
                residualTitle: "Residualrisk",
                residualSub: "Snitt residualrisk: {{score}}/25",
                arrowControls: "kontroller tillämpas",
                arrowResult: "nettoresultat",
                noData: "Ingen data",
                trendNote: "Trend baserad på senaste {{days}} dagarna",
                trendNoData: "Otillräcklig data för trend",
                trendLabel: "7-dagarstrend",
                emptyInherentNoBwra: "Inga BWRA-risker registrerade ännu",
                emptyInherentNoBwraHint: "Lägg till risker i BWRA för att beräkna inneboende risk",
                emptyInherentNoGeo: "Inga aktiva GeoRisk-index",
                emptyInherentNoGeoHint: "Konfigurera GeoRisk-modellen för att se landsriskdata",
                emptyControl: "Inga BWRA-risker registrerade",
                emptyControlHint: "Lägg till risker i BWRA för att följa kontrolleffektivitet",
                emptyResidual: "Ingen BWRA-data ännu",
                emptyResidualHint: "Konfigurera BWRA för att beräkna residualrisk",
            },
            // Risktrenddiagram
            riskTrend: {
                title: "Risktrenddiagram",
                subtitle: "Inneboende risk, kontrolleffektivitet och residualrisk över tid",
                periodLabel: "Välj tidsperiod",
                emptyTitle: "Ingen historisk data tillgänglig ännu",
                emptyHint: "Riskdata sparas dagligen. Kom tillbaka imorgon för att se din första trend.",
                dataNote: "Baserat på {{count}} datapunkter",
            },
            // Compliance-deadlinetracker
            deadline: {
                title: "Compliance-deadlines",
                subtitle: "Kommande rapporterings- och granskningsdeadlines",
                empty: "Inga kommande deadlines de nästa {{days}} dagarna",
                dueInDays: "Förfaller om {{count}} dagar",
                dueToday: "Förfaller idag",
                overdueDays: "{{count}} dagar försenad",
                annualReport: "Årsrapport {{year}}",
                annualReportDesc: "FI periodisk rapportering",
                bwraReview: "BWRA Årsöversyn",
                bwraReviewDesc: "Årsöversyn krävs",
                scheduledValidation: "Validering: {{model}}",
                scheduledValidationDesc: "Schemalagd modellvalidering",
                statusOverdue: "Försenad",
                statusWarning: "Förfaller snart",
                statusOk: "I tid",
            },
            // Aktivitetsfeed
            activityFeed: {
                title: "Aktivitetsfeed",
                allModules: "Alla moduler",
                filterUserPlaceholder: "Filtrera på användare…",
                period_all: "All tid",
                period_today: "Idag",
                period_week: "Senaste 7 dagarna",
                period_month: "Senaste 30 dagarna",
                showing: "Visar {{shown}} av {{total}} händelser",
            },
            // KPI-tröskelvarningar
            kpiThreshold: {
                title: "KPI-tröskelvarningar",
                subtitle: "Konfigurera tröskelvärden för Dashboard KPI-kort. En visuell varning visas när ett värde uppnår eller överskrider tröskeln.",
                geo_highRisk: "Högriskländer",
                geo_highRiskDesc: "Varna när högriskländer ≥ tröskel",
                bwra_unmitigated: "Omitigerade risker",
                bwra_unmitigatedDesc: "Varna när omitigerade BWRA-risker ≥ tröskel",
                prpt_review: "Rapporter under granskning",
                prpt_reviewDesc: "Varna när rapporter under granskning ≥ tröskel",
                threshold: "Tröskel",
                enabled: "Aktiv",
                warningBadge: "KPI-tröskel överskriden",
                reset: "Återställ standardvärden",
            },
            // Koncernvy / konsoliderad rapport
            tabKoncern: "Koncernvy",
            group: {
                title: "Konsoliderad riskrapport",
                subtitle: "Aggregerad riskdata för alla entiteter i koncernen",
                totalEntities: "Entiteter",
                totalRisks: "Totalt risker",
                totalHighRisks: "Högriskrisker",
                totalControls: "Totalt kontroller",
                entityColumn: "Entitet",
                typeColumn: "Typ",
                riskCountColumn: "Risker",
                highColumn: "Hög / Mycket hög",
                avgResidualColumn: "Snitt residual",
                controlsColumn: "Kontroller",
                viewEntityBwra: "Visa i BWRA",
                topRisks: "Högsta residualrisker",
                riskName: "Risk",
                category: "Kategori",
                residualScore: "Residual",
                riskLevel: "Nivå",
                noRisks: "Inga risker registrerade för denna entitet.",
                noEntities: "Inga entiteter konfigurerade. Lägg till entiteter i Administration för att använda denna vy.",
                type_parent: "Moderbolag",
                type_subsidiary: "Dotterbolag",
                type_branch: "Filial",
                type_joint_venture: "JV",
            },
        },
        customerRisk: {
            welcome: "Kundriskmodul",
            chooseModule: "Skapa kundrisklassificeringsmodeller",
            modelbuilder: "Modellbyggare",
            modelbuilderDesc: "Skapa och redigera riskmodeller.",
            simulation: "Simulering",
            simulationDesc: "Testa modellen med olika scenarier.",
            stressTest: "Stresstest",
            stressTestDesc: "Testa modellens robusthet med extrema parametervärden.",
            stressTestPreset_worst: "Värsta fall",
            stressTestPreset_best: "Bästa fall",
            stressTestPreset_booleans: "Alla booleaner aktiva",
            stressCurrentScenario: "Nuläge",
            stressCurrentValue: "Nulägesvärde",
            stressExtremeValue: "Extremvärde",
            stressCurrentScore: "Nulägespoäng",
            stressExtremeScore: "Stresspoäng",
            stressScoreDiff: "Förändring",
            parameter: "Parameterhantering",
            parameterDesc: "Lägg till eller ändra riskparametrar.",
            // Common
            cancel: "Avbryt",
            save: "Spara",
            add: "Lägg till",
            actions: "Åtgärder",
            description: "Beskrivning",
            // Risk levels
            riskLow: "Låg",
            riskMedium: "Medel",
            riskHigh: "Hög",
            riskVeryHigh: "Mycket hög",
            riskUnacceptable: "Oacceptabel",
            // ModelBuilder
            noModels: "Det finns inga befintliga modeller",
            addModel: "Lägg till modell",
            options: "Alternativ",
            models: "Modeller",
            addNewModel: "Lägg till ny modell",
            selectModelHeader: "Välj modell",
            modelManagement: "Modellhantering",
            noParameters: "Det finns inga parametrar i denna modell",
            addParameter: "Lägg till parameter",
            riskClassThresholds: "Riskklasströsklar",
            enterModelName: "Ange modellnamn",
            modelNameRequired: "Modellnamn krävs.",
            modelNameExists: "En modell med detta namn finns redan.",
            selectOrCreateModel: "Välj eller skapa en modell först",
            saveModel: "Spara modell",
            saveModelVersion: "Spara modellversion",
            selectVersionType: "Välj versionsuppdatering:",
            firstVersion: "Första versionen",
            firstVersionDescription: "Detta skapar den första sparade versionen av modellen.",
            minorUpdate: "Mindre uppdatering",
            majorUpdate: "Större uppdatering",
            savedAs: "Modell sparad som {{version}}",
            confirmDeleteParameter: "Är du säker på att du vill ta bort denna parameter?",
            confirmDeleteParameterInUse: "Varning: denna parameter används i följande modeller: {{models}}. Att ta bort den kan påverka dessa modeller. Är du säker på att du vill fortsätta?",
            deleteModel: "Ta bort modell",
            cloneModel: "Klona modell",
            exportModel: "Exportera modell (JSON)",
            exportModelDocPDF: "Exportera modelldokumentation (PDF)",
            importModel: "Importera modell (JSON)",
            importModelSuccess: "Modellen \"{{name}}\" importerades",
            importModelError: "Kunde inte importera modell: ogiltigt format",
            importModelDuplicate: "En modell med namnet \"{{name}}\" finns redan. Import avbröts.",
            copyOf: "Kopia av {{name}}",
            confirmDeleteModel: "Är du säker på att du vill ta bort modellen \"{{name}}\"? Denna åtgärd kan inte ångras.",
            confirmDeleteModelTitle: "Ta bort modell",
            newVersion: "Ny version",
            newVersionTitle: "Skapa ny version",
            newVersionDescription: "Skapa en ny version av denna godkända modell. Ändringar träder i kraft först när den nya versionen godkänts genom governance-flödet.",
            newVersionConfirm: "Skapa ny version",
            newVersionApprovalReset: "Godkännandet nollställs till 'utkast'. Den nuvarande godkända versionen förblir i produktion tills den nya versionen godkänts.",
            newVersionPreview: "Ny version blir: {{version}}",
            minorUpdateHint: "Små justeringar av parametrar, trösklar eller dokumentation.",
            majorUpdateHint: "Betydande strukturella förändringar (nya parametrar, ändrad risklogik).",
            viewVersionHistory: "Versionshistorik",
            versionHistoryTitle: "Versionshistorik – {{name}}",
            versionHistoryDescription: "Alla versioner av denna modell, inklusive arkiverade godkännanden. Produktionsversionen är markerad nedan.",
            versionHistoryVersion: "Version",
            versionHistoryStatus: "Status",
            versionHistoryApprovedBy: "Godkänd av",
            versionHistoryApprovedAt: "Godkänd den",
            versionHistoryActive: "Produktion",
            versionHistoryCurrent: "aktuell",
            versionHistoryProduction: "I produktion",
            activeProductionVersion: "Version {{version}} är aktiv i produktion.",
            activeVersionWhileEditing: "Version {{active}} är aktiv i produktion. Du redigerar utkast {{draft}}.",
            versionHistory: "Versionshistorik",
            version: "Version",
            date: "Datum",
            updatedBy: "Uppdaterad av",
            draft: "utkast",
            // Save interaction
            unsavedChanges: "Osparade ändringar",
            changesPreview: "Ändringar som sparas:",
            parametersAdded: "{{count}} parameter(ar) tillagd(a)",
            parametersRemoved: "{{count}} parameter(ar) borttagen(-na)",
            parametersModified: "{{count}} parameter(ar) ändrad(e)",
            configurationChanged: "Modellkonfiguration ändrad",
            noChangesDetected: "Inga ändringar hittades",
            changes: "Ändringar",
            viewDiff: "Klicka för att se ändringar",
            // Version diff
            versionDiffTitle: "Ändringar i {{version}}",
            diffNoData: "Ingen ändringsdata tillgänglig för denna version (sparad innan ändringsspårning aktiverades).",
            diffInitialVersion: "Ursprungsversion",
            diffInitialBadge: "Initial",
            diffNoChangeBadge: "Inga ändringar",
            diffChangeBadge: "{{count}} ändring(ar)",
            diffNoChanges: "Inga ändringar registrerade för denna version.",
            diffParametersAdded: "Parametrar tillagda",
            diffParametersRemoved: "Parametrar borttagna",
            diffParametersModified: "Parametrar ändrade",
            diffConfigChanged: "Konfigurationsändringar",
            diffFieldType: "Typ",
            diffFieldRisks: "Risker",
            diffFieldRiskType: "Risktyp",
            diffFieldWeight: "Vikt",
            diffFieldThresholds: "Tröskelvärden",
            diffFieldBaseScore: "Baspoäng",
            diffFieldLowestRiskCategory: "Lägsta riskklass",
            diffFieldRiskThresholds: "Risktröskelkvärden",
            diffFieldCddConfig: "CDD-konfiguration",
            diffFieldEddTriggers: "EDD-utlösare",
            diffFieldRiskLevelConfig: "Risknivånamn",
            close: "Stäng",
            // Simulation
            noSavedModels: "Inga sparade modeller tillgängliga. Skapa och spara en modell i Modellbyggare-fliken först.",
            selectModelPrompt: "Välj en modell i rullgardinsmenyn ovan för att starta simuleringen.",
            parameterCol: "Parameter",
            type: "Typ",
            value: "Värde",
            score: "Poäng",
            riskType: "Risktyp",
            riskLevelIndicator: "Risknivåindikator",
            dynamic: "Dynamisk",
            notApplicable: "Ej tillämpligt",
            // Scenarios
            scenarios: "Scenarier",
            saveScenario: "Spara scenario",
            scenarioName: "Scenarionamn",
            scenarioNameRequired: "Scenarionamn krävs.",
            savedScenarios: "Sparade scenarier",
            noScenarios: "Inga sparade scenarier ännu.",
            loadScenario: "Ladda",
            deleteScenario: "Ta bort",
            confirmDeleteScenario: "Är du säker på att du vill ta bort scenariot \"{{name}}\"?",
            scenarioSaved: "Scenario \"{{name}}\" sparat.",
            saveAsTestCase: "Spara som testfall",
            savedTestCases: "Sparade testfall",
            testCaseSaved: "Testfall sparat.",
            confirmDeleteTestCase: "Är du säker på att du vill ta bort detta testfall?",
            testCaseDate: "Datum",
            modelVersion: "Modellversion",
            totalScore: "Totalpoäng",
            // ModelConfiguration
            modelConfiguration: "Modellkonfiguration",
            lowestRiskCategory: "Lägsta riskkategori",
            baseScore: "Baspoäng",
            lowToNormal: "Låg → Normal risk",
            normalToHigh: "Normal → Hög risk",
            highToVeryHigh: "Hög → Mycket hög risk",
            veryHighToUnacceptable: "Mycket hög → Oacceptabel",
            // ModelSelector
            selectRiskModel: "Välj riskmodell",
            chooseAModel: "Välj en modell",
            // ParameterForm
            addNewParameter: "Lägg till ny parameter",
            editParameter: "Redigera parameter",
            parameterNameRequired: "Parameternamn krävs.",
            typeRequired: "Välj minst en parametertyp.",
            parameterName: "Parameternamn:",
            parameterType: "Typ:",
            parameterFormBasicInfo: "Grundläggande information",
            parameterFormTypeSection: "Parametertyp",
            parameterNamePlaceholder: "t.ex. Kundkategori",
            parameterDescriptionPlaceholder: "t.ex. Klassificerar kunden efter riskkategori",
            bwraCategoriesLabel: "BWRA-risktäckning:",
            bwraCategoriesHelp: "Vilka BWRA-riskkategorier denna parameter bidrar till",
            bwraCategoriesNone: "Ingen BWRA-risktäckning vald",
            declaredRisksLabel: "Adresserade risker:",
            declaredRisksHelp: "Vilka specifika BWRA-risker denna parameter bidrar till att adressera",
            noRisksRegistered: "Inga risker registrerade i BWRA",
            dbFieldColumn: "DB-fält",
            boolean: "Boolean",
            booleanDescription: "Hämtas från en Ja/Nej- eller Sant/Falskt-kolumn i databasen",
            numeric: "Numerisk",
            numericDescription: "Hämtas från en numerisk värdekolumn i databasen",
            dbValueTrue: "Databasvärde SANT",
            dbValueFalse: "Databasvärde FALSKT",
            dbValue: "Databasvärde",
            dbValueTruePlaceholder: "t.ex. 1, J, true",
            dbValueFalsePlaceholder: "t.ex. 0, N, false",
            dbValueNumericPlaceholder: "t.ex. poäng, 100",
            dbValueTrueHelp: "Värdet som lagras i databaskolumnen när detta är SANT",
            dbValueFalseHelp: "Värdet som lagras i databaskolumnen när detta är FALSKT",
            dbValueNumericHelp: "Databaskolumnens namn eller referensvärde för denna numeriska parameter",
            importParametersTitle: "Importera parametrar",
            // ParameterManagement page header
            parameterLibraryTitle: "Parameterbibliotek",
            parameterLibraryDescription: "Definiera parametrar här som sedan används i modellbyggaren för att konstruera kundriskscoringmodeller.",
            searchParametersPlaceholder: "Sök på namn eller beskrivning...",
            noParametersMatchSearch: "Inga parametrar matchar din sökning.",
            // Parameter tags (CUST-059)
            parameterTagsLabel: "Taggar",
            parameterTagsHelp: "Klicka på en tagg för att välja den. Valda taggar (märkta med ✓) visas i parameterlistan och möjliggör filtrering.",
            filterAll: "Alla",
            tagsColumn: "Taggar",
            parameterTagKYC: "KYC",
            parameterTagTransaction: "Transaktion",
            parameterTagJurisdiction: "Jurisdiktion",
            parameterTagBehavior: "Beteende",
            parameterTagPEP: "PEP",
            parameterTagScreening: "Screening",
            // ParameterList
            label: "Etikett",
            noExistingParameters: "Det finns inga befintliga parametrar",
            // ParameterPopup
            selectParameter: "Välj parameter",
            selectParameterType: "Välj parametertyp",
            selectType: "Välj typ",
            selectRiskType: "Välj risktyp",
            parameterTypeRequired: "Välj parametertyp",
            riskTypeRequired: "Välj risktyp för booleska parametrar",
            weightInvalid: "Svikt måste vara ett positivt tal (1–100)",
            normalRisk: "Normal risk",
            highRisk: "Hög risk",
            veryHighRisk: "Mycket hög risk",
            weight: "Vikt",
            enterWeight: "Ange vikt",
            normalThreshold: "Normal tröskel",
            highThreshold: "Hög tröskel",
            veryHighThreshold: "Mycket hög tröskel",
            unacceptableThreshold: "Oacceptabel tröskel",
            associatedRisks: "Associerade risker",
            parameterAlreadyExists: "Den här parametern finns redan i modellen",
            parameterAlreadyAdded: "Tillagd",
            // Risk categories (stored as enum, displayed translated)
            regulatoryRisk: "Regulatorisk risk",
            financialCrimeRisk: "Finansiell brottsrisk",
            reputationalRisk: "Reputationsrisk",
            operationalRisk: "Operationell risk",
            creditRisk: "Kreditrisk",
            // Type display
            numerical: "Numerisk",
            // RiskGauge
            scoreLabel: "Poäng: ",
            riskClass: "Riskklass: ",
            cddLevel: "CDD-nivå: ",
            cddSDD: "FDD",
            cddCDD: "CDD",
            cddEDD: "EDD",
            cddSDDFull: "Förenklad kundkännedom",
            cddCDDFull: "Kundkännedom",
            cddEDDFull: "Utökad kundkännedom",
            // CDD Configuration
            cddClassification: "CDD-klassificering",
            cddEnabled: "Aktivera CDD-klassificering",
            eddStartsAt: "EDD börjar vid riskklass",
            // EDD Triggers
            eddTriggers: "EDD-triggers",
            eddTriggersDescription: "Obligatoriska triggers som automatiskt höjer CDD-nivån till EDD oavsett poäng",
            eddTriggerPep: "PEP (Politiskt exponerad person)",
            eddTriggerPepShort: "PEP",
            eddTriggerHighRiskCountry: "Högriskland",
            eddTriggerHighRiskCountryShort: "Högriskland",
            eddTriggerComplexOwnership: "Komplex ägarstruktur",
            eddTriggerComplexOwnershipShort: "Komplex ägarstruktur",
            eddTriggerActive: "Aktiv",
            eddOverrideWarning: "EDD triggad av: {{triggers}}",
            eddOverrideLabel: "EDD (Överstyrning)",
            // Risk Level Configuration
            riskLevelConfig: "Konfiguration av risknivåer",
            includeUnacceptable: "Inkludera 'Oacceptabel' som separat nivå",
            riskLevelNames: "Anpassade nivånamn",
            riskLevelNamePlaceholder: "Eget namn (valfritt)",
            // Model Documentation Fields
            modelDocumentation: "Modelldokumentation",
            businessPurpose: "Affärsändamål",
            businessPurposePlaceholder: "Beskriv affärsrationalen och det regulatoriska syftet med denna modell...",
            lastReviewedBy: "Senast granskad av",
            lastReviewedByPlaceholder: "Granskarens namn",
            modelReviewDate: "Granskningsdatum",
            // ModelList
            existingModels: "Befintliga modeller",
            // Risks column
            risks: "Risker",
            // Validation
            validation: "Validering",
            validationDesc: "Validera din modell mot produktionsdata.",
            dataValidation: {
                title: "Datavalidering",
                description: "Ladda upp anonymiserad produktionsdata f\u00f6r att validera hur v\u00e4l din kundriskmodell f\u00e5ngar verkligheten.",
                selectModel: "V\u00e4lj modell",
                selectModelHint: "V\u00e4lj en sparad modell att validera mot uppladdad data.",
                uploadCSV: "Ladda upp CSV",
                uploadHint: "CSV, TSV eller TXT-fil med kunddata och f\u00f6rv\u00e4ntade riskniv\u00e5er",
                datasets: "Uppladdade dataset",
                rowCount: "{{count}} rader",
                colCount: "{{count}} kolumner",
                deleteDataset: "Ta bort dataset",
                confirmDeleteDataset: "Är du säker på att du vill ta bort datasetet \"{{name}}\"? Åtgärden kan inte ångras.",
                columnMapping: "Kolumnmappning",
                columnMappingHint: "Mappa dina CSV-kolumner till modellens parametrar och f\u00f6rv\u00e4ntad riskniv\u00e5.",
                expectedRiskColumn: "F\u00f6rv\u00e4ntad riskniv\u00e5",
                expectedRiskScoreColumn: "F\u00f6rv\u00e4ntat risk-score (valfritt)",
                noScoreColumn: "-- Ingen (endast kategori) --",
                parameterColumns: "Parameterkolumner",
                selectColumn: "-- V\u00e4lj kolumn --",
                columnPreviewLabel: "Exempelvärden",
                runValidation: "K\u00f6r validering",
                running: "K\u00f6r...",
                results: "Valideringsresultat",
                ranAt: "Validering k\u00f6rdes {{time}}",
                accuracy: "Tr\u00e4ffs\u00e4kerhet",
                totalRecords: "Poster",
                falsePositives: "Falska positiva",
                falseNegatives: "Falska negativa",
                scoreDiscrepancies: "Score-diskrepanser",
                scoreDiscrepancyTooltip: "Kunder med korrekt riskkategori men där externt score avviker mer än {{pct}}% (minst {{abs}}) från modellens beräkning.",
                externalScore: "Externt score",
                scoreDiff: "Diff",
                recommendationGood: "Modellen f\u00e5ngar {{pct}}% av fallen korrekt. God \u00f6verensst\u00e4mmelse med produktionsdata.",
                recommendationModerate: "Modellen f\u00e5ngar {{pct}}% av fallen korrekt. \u00d6verv\u00e4g att granska felklassificerade fall och justera tr\u00f6sklar.",
                recommendationPoor: "Modellen f\u00e5ngar bara {{pct}}% av fallen korrekt. Betydande kalibrering beh\u00f6vs \u2014 granska riskklassr\u00f6sklar och parametervikter.",
                distributionComparison: "Riskniv\u00e5f\u00f6rdelning",
                riskLevel: "Riskniv\u00e5",
                expected: "F\u00f6rv\u00e4ntad (Data)",
                predicted: "Predikterad (Modell)",
                difference: "Skillnad",
                confusionMatrix: "F\u00f6rv\u00e4xlingsmatris",
                confusionLegend: "Rader = faktisk riskniv\u00e5 fr\u00e5n data. Kolumner = predikterad av modell. Diagonal = korrekta prediktioner.",
                showDetails: "Visa detaljer ({{count}} poster)",
                score: "Po\u00e4ng",
                match: "Tr\u00e4ff",
                showingFirst: "Visar f\u00f6rsta {{count}} av {{total}} poster",
                showingXofY: "Visar {{shown}} av {{total}} poster",
                showMoreRows: "Visa fler ({{count}} kvar)",
                exportPDF: "Exportera PDF",
                exportExcel: "Exportera Excel",
                exportingReport: "Genererar rapport...",
                export: {
                    reportTitle: "Kundrisk-valideringsrapport",
                    modelName: "Modell",
                    sheetParamMapping: "Parametermappning",
                    paramName: "Parameter",
                    mappedColumn: "Mappad kolumn",
                },
                inputMode: "Valideringskälla",
                inputModeCSV: "CSV-uppladdning",
                inputModeDataSource: "Datakälla",
                selectDataSource: "Välj datakälla",
                noDataSources: "Inga datakällor konfigurerade. Lägg till en under Admin → Datakällor.",
                selectTable: "Välj tabell",
                loadingTables: "Laddar tabeller...",
                loadingColumns: "Laddar kolumner...",
                filterClause: "Filter (valfritt)",
                filterClauseHint: "Filtrera rader: key=value-par separerade med komma (t.ex. status=active,country=SE)",
                dataSourceError: "Fel vid laddning av datakälla: {{error}}",
                noRowsFromSource: "Inga data returnerades från källan. Prova en annan tabell eller justera filtret.",
            },
            // Viktfördelningsdiagram (CUST-062)
            weightDistributionTitle: "Parametrarnas viktfördelning",
            weightDistributionSubtitle: "Varje parameters relativa viktandel i modellen",
            noWeightedParams: "Inga parametrar med angiven vikt",
            outlierHigh: "Hög avvikare – denna parameter har betydligt högre vikt än genomsnittet",
            outlierLow: "Låg avvikare – denna parameter har betydligt lägre vikt än genomsnittet",
            // Riskpoängsfördelningshistogram (CUST-063)
            riskScoreHistogramTitle: "Riskpoängsfördelning",
            riskScoreHistogramSubtitle: "Fördelning av sparade scenarier per risknivå",
            riskScoreHistogramNoScenarios: "Spara minst ett scenario för att se poängsfördelningen",
            riskScoreHistogramInsightBalanced: "Fördelningen verkar balanserad",
            riskScoreHistogramInsightOverLabel: "Möjlig överklassificering",
            riskScoreHistogramInsightOverDesc: "Mer än 60% av scenarierna hamnar i förhöjda risknivåer. Överväg om modellens tröskelvärden är korrekt kalibrerade.",
            riskScoreHistogramInsightUnderLabel: "Möjlig underklassificering",
            riskScoreHistogramInsightUnderDesc: "Mer än 60% av scenarierna hamnar i lägre risknivåer. Överväg om modellen fångar alla relevanta riskindikatorer.",
            // Segmentanalys (CUST-066)
            segmentAnalysisTitle: "Segmentanalys",
            segmentAnalysisSubtitle: "Sparade scenarier grupperade per risksegment med genomsnittliga poäng och parametervärden per grupp.",
            segmentAnalysisNoScenarios: "Spara minst ett scenario för att se segmentanalysen",
            segmentAnalysisScenarios: "scenarier",
            segmentAnalysisAvgScore: "Genomsn. poäng",
            segmentAnalysisAvgValue: "Genomsnittvärde",
            // Tröskeloptimering (CUST-068)
            thresholdOptimTitle: "Tröskeloptimering",
            thresholdOptimDesc: "Analysera sparade scenarier för att hitta tröskelvärden som skapar tydligast separation mellan riskgrupper, baserat på naturliga brytpunkter i poängsfördelningen.",
            thresholdOptimMinData: "Minst {{min}} sparade scenarier krävs för att köra tröskeloptimering.",
            thresholdOptimRun: "Optimera trösklar",
            thresholdOptimResults: "Optimeringsresultat",
            thresholdOptimColLevel: "Risknivå",
            thresholdOptimColCurrent: "Nuläge (≥)",
            thresholdOptimColSuggested: "Förslag (≥)",
            thresholdOptimColDiff: "Förändring",
            thresholdOptimDistTitle: "Scenariofördelning: Nuläge vs. Förslag",
            thresholdOptimDistCurrent: "Nuläge",
            thresholdOptimDistSuggested: "Förslag",
            thresholdOptimInsightReclassified: "{{count}} av {{total}} scenarier skulle omklassificeras med de föreslagna trösklarna.",
            thresholdOptimInsightNoChange: "Nuvarande trösklar stämmer redan med naturliga poängbrytpunkter — ingen omklassificering behövs.",
            thresholdOptimInsightBoundary: "Scenarier nära en tröskelgräns: {{current}} → {{suggested}}",
            // Parameterberoende-visualisering (CUST-070)
            paramDep: {
                title: "Beroendevy",
                subtitle: "Visuellt visar hur varje modellparameter påverkar slutriskpoängen. Linjens tjocklek återspeglar parameterns vikt.",
                networkTitle: "Parameternätverk",
                centerLabel: "Riskpoäng",
                sensitivityTitle: "Känslighetsanalys",
                sensitivityDesc: "Visar poängpåverkan för varje boolesk parameter när den är aktiv. Högre vikt = större inflytande på slutpoängen.",
                colParameter: "Parameter",
                colRiskType: "Risktyp",
                colWeight: "Vikt",
                colScoreImpact: "Poäng (aktiv)",
                colPctOfBase: "% av baspoäng",
                legendLabel: "Typ",
            },
            // Godkännandeflöde (CUST-061)
            approval: {
                panelTitle: "Modellgodkännande",
                statusDraft: "Utkast",
                statusSubmitted: "Under granskning",
                statusApproved: "Godkänd",
                statusRejected: "Avvisad",
                submitForReview: "Skicka för granskning",
                submitForReviewHint: "Markera modellen som redo för granskning. Modellen låses för redigering tills ett beslut fattas.",
                approveModel: "Godkänn modell",
                rejectModel: "Avvisa",
                approveComment: "Godkännandekommentar (valfri)",
                rejectComment: "Skäl för avvisning (obligatorisk)",
                rejectCommentRequired: "Ange ett skäl för avvisningen.",
                approvedBy: "Godkänd av",
                approvedAt: "Godkänd datum",
                approvalHistory: "Godkännandehistorik",
                noHistory: "Inga godkännandeåtgärder ännu.",
                historyFrom: "Från",
                historyTo: "Till",
                historyBy: "Av",
                historyAt: "Datum",
                historyComment: "Kommentar",
                lockedMessage: "Modellen är låst för redigering under granskning.",
                approvedLockedMessage: "Modellen är godkänd och låst. Klona den för att göra ändringar.",
                approvedLockedMessageVersioned: "Modellen är godkänd och låst. Skapa en ny version för att göra ändringar — aktuell version förblir i produktion tills den nya versionen godkänts.",
                toastSubmitted: "Modellen har skickats för granskning.",
                toastApproved: "Modellen har godkänts.",
                toastRejected: "Modellen har återsänts till utkast.",
                noApprovedModels: "Inga godkända modeller tillgängliga. En modell måste godkännas innan den kan användas här.",
                onlyApprovedInfo: "Endast godkända modeller kan användas i Simulering och Validering.",
                confirmSubmit: "Skicka \"{{name}}\" för granskning? Modellen låses tills den godkänns eller avvisas.",
            },
            // Modelljämförelse
            comparison: {
                title: "Modelljämförelse",
                comparisonTab: "Jämför modeller",
                modelA: "Modell A",
                modelB: "Modell B",
                vs: "mot",
                selectBothModels: "Välj två modeller ovan för att jämföra deras simuleringsresultat sida vid sida.",
                diff: "Diff (A − B)",
            },
            // Fältmappning-tab
            fieldMapping: {
                tab: "Mappning",
                title: "Fältmappning",
                subtitle: "Koppla modellparametrar till kolumner i en extern datakälla.",
                selectModelPrompt: "Välj en modell för att konfigurera dess datakällsmappning.",
                dataSource: "Datakälla",
                selectDataSource: "— Välj datakälla —",
                table: "Tabell",
                selectTable: "— Välj tabell —",
                tableNamePlaceholder: "Ange tabellnamn",
                rowIdColumn: "Rad-id-kolumn",
                selectRowIdColumn: "— Välj kolumn —",
                rowIdColumnPlaceholder: "Ange kolumnnamn",
                parameterMappings: "Parametermappningar",
                parameterCol: "Parameter",
                columnCol: "Källkolumn",
                transformCol: "Transform",
                selectColumn: "— Välj kolumn —",
                columnPlaceholder: "Ange kolumnnamn",
                transform_none: "Ingen",
                transform_uppercase: "Versaler",
                transform_lowercase: "Gemener",
                transform_trim: "Trimma",
                transform_number: "Till tal",
                transform_boolean: "Till bool",
                transform_date: "Till datum",
                previewData: "Förhandsgranska data (5 rader)",
                saveMapping: "Spara mappning",
                saving: "Sparar…",
                statusComplete: "Mappning fullständig — alla parametrar mappade",
                statusIncomplete: "{{count}} parameter(ar) saknar kolumnmappning",
                savedSuccess: "Mappning sparad",
                saveError: "Kunde inte spara mappning: {{error}}",
                errorNoDataSource: "Välj en datakälla",
                errorNoTable: "Ange ett tabellnamn",
                errorNoRowId: "Ange rad-id-kolumn",
                previewTitle: "Dataförhandsgranskning",
                previewSubtitle: "Exempelrader från {{table}}",
                previewNoData: "Förhandsgranskning ej tillgänglig — anslut till en aktiv datakälla för att hämta rader.",
                noDataSources: "Inga datakällor konfigurerade. Lägg till en i Admin → Datakällor.",
                incompleteBanner: "Mappning ofullständig — {{count}} parameter(ar) saknas",
                newParamBadge: "Saknar mappning",
                markIntentionallyPartial: "Markera som klar trots ofullständighet",
                markIntentionallyPartialDone: "Markerad som medvetet partiell",
                intentionallyPartialNote: "Denna mappning är markerad som medvetet partiell.",
                validationBlockedByMapping: "Fältmappningen är ofullständig för denna modell. Gå till Mappning-tabben och mappa alla parametrar innan du kör validering mot en datakälla.",
                validationBlockedLink: "Gå till Mappning-tabben",
            },
            // Score History (CUST-080)
            scoreHistoryTitle: "Riskpoängshistorik",
            scoreHistorySubtitle: "Poängögonblicksbilder över tid för vald modell och scenario.",
            scoreHistoryRecord: "Registrera ögonblicksbild",
            scoreHistoryReasonPlaceholder: "Anledning till ögonblicksbild (valfritt)",
            scoreHistoryReasonManual: "Manuell ögonblicksbild",
            scoreHistoryReasonTestCase: "Testfall sparat",
            scoreHistoryEmpty: "Inga ögonblicksbilder registrerade ännu. Använd 'Registrera ögonblicksbild' för att fånga den aktuella poängen.",
            scoreHistoryClear: "Rensa historik",
            scoreHistoryConfirmClear: "Rensa all poängshistorik för denna modell? Det går inte att ångra.",
            scoreHistorySnapshotSaved: "Poängsögonblicksbild registrerad.",
            scoreHistoryColDate: "Datum",
            scoreHistoryColRiskClass: "Riskklass",
            scoreHistoryColReason: "Anledning",
            scoreHistoryMore: "… och {{count}} äldre poster",
            correlationAnalysis: {
                tab: "Korrelation",
                title: "Parameterkorrelationsanalys",
                subtitle: "Pearsonkorrelation mellan parametrar baserat på sparade scenarier och testfall. Parametrar med korrelation > 0,8 är potentiellt redundanta.",
                computeBtn: "Beräkna korrelationer",
                computing: "Beräknar...",
                noNumericalParams: "Inga numeriska eller booleska parametrar hittades i denna modell.",
                insufficientDataPoints: "Otillräcklig data — minst 3 scenarier eller testfall krävs för att beräkna korrelationer.",
                dataPoints: "{{count}} datapunkter tillgängliga (scenarier + testfall)",
                matrixTitle: "Korrelationsmatris",
                warningHigh: "Hög korrelation",
                legendTitle: "Korrelationsskala",
                legendNone: "Ingen korrelation",
                warningList: "Högt korrelerade parameterpar (> 0,8)",
                warningListEmpty: "Inga par med korrelation över 0,8 hittades.",
                warningPairLabel: "Korrelation",
                warningPairDataPoints: "gemensamma datapunkter",
                paramA: "Parameter A",
                paramB: "Parameter B",
                insufficientData: "Otillräcklig data (< 3 gemensamma datapunkter)",
            },
        },
        bwra: {
            overview: "Översikt",
            riskRegister: "Riskregister",
            controlsTab: "Kontroller",
            overviewTitle: "Verksamhetsomfattande riskbedömning – Översikt",
            overviewSubtitle: "Sammanfattning av organisationens AML/CFT-riskprofil baserat på identifierade risker, kontroller och modellkopplingar.",
            introBannerTitle: "Börja här: BWRA är grunden för allt AML-arbete",
            introBannerBody: "Den verksamhetsomfattande riskbedömningen (BWRA) är startpunkten för allt AML-compliance-arbete. Här identifierar du organisationens bruttorisker, definierar kontroller och sätter riskaptit. De övriga modulerna – GeoRisk, Kundrisk och Transaktionsövervakning – är analysmodeller som levererar underlag till de risker du hanterar i BWRA.",
            introBannerStep1: "Identifiera och bedöm risker i Riskregistret",
            introBannerStep2: "Koppla risker till analysmodeller (GeoRisk, Kundrisk, TM)",
            introBannerStep3: "Följ upp residualrisk efter att kontroller är på plats",
            introBannerDismiss: "Förstått",
            identifiedRisks: "Identifierade risker",
            controls: "Kontroller",
            mitigated: "Åtgärdade",
            linkedToModels: "Kopplade till modeller",
            riskPosture: "Riskprofil",
            avgGrossRisk: "Genomsnittlig bruttorisk",
            avgResidualRisk: "Genomsnittlig residualrisk",
            byCategory: "Risk per kategori",
            statusBreakdown: "Statusfördelning",
            statusIdentified: "Identifierad",
            statusAssessed: "Bedömd",
            statusMitigated: "Åtgärdad",
            riskRegisterTitle: "Riskregister",
            importRisksTitle: "Importera risker",
            category: "Kategori",
            allCategories: "Alla kategorier",
            status: "Status",
            allStatuses: "Alla statusar",
            filterUnmitigated: "Ej åtgärdade",
            filterMyRisks: "Mina risker",
            ownerLabel: "Ägare",
            ownerNone: "(Ingen ägare)",
            riskOwner: "Riskägare",
            searchRisksPlaceholder: "Sök risker...",
            riskId: "ID",
            riskName: "Risk",
            grossRisk: "Bruttorisk",
            residualRisk: "Residualrisk",
            reduction: "Reduktion",
            description: "Beskrivning",
            likelihood: "Sannolikhet",
            impact: "Konsekvens",
            controlsMeasures: "Kontroller och åtgärder",
            noControls: "Inga kontroller definierade. Risken är ej åtgärdad.",
            noRisksFound: "Inga risker matchar aktuella filter.",
            controlsTitle: "Kontroller och modellkopplingar",
            controlsSubtitle: "Översikt av alla kontroller, deras effektivitet och kopplingar till riskmodeller i GeoRisk- och Kundrisk-modulerna.",
            controlsListTab: "Kontrollista",
            effectivenessOverviewTab: "Effektivitetsöversikt",
            effDashboard: {
                title: "Dashboard för kontrolleffektivitet",
                subtitle: "Fördelning av kontrollernas utvärderingsresultat per riskkategori.",
                summaryByCategory: "Sammanfattning per kategori",
                allControlsDetail: "Alla kontroller",
                category: "Riskkategori",
                notEvaluated: "Utvärdering saknas",
                totalControls: "Totalt",
                controlId: "ID",
                controlName: "Kontroll",
                riskName: "Risk",
                evalStatus: "Utvärdering",
                reviewPeriod: "Granskningsperiod",
                distribution: "Fördelning",
                noControls: "Inga kontroller definierade. Lägg till kontroller till risker i riskregistret.",
            },
            modelConnections: "Modellkopplingar",
            modelConnectionsDesc: "Kontroller kopplade till riskmodeller i andra moduler ger automatiserad, datadriven riskhantering.",
            noModelConnections: "Inga modellkopplingar konfigurerade ännu.",
            autoModelControls: "Auto-genererade modellkontroller",
            autoModelControlsDesc: "Kontroller härledda automatiskt från GeoRisk-index och Kundrisk-parametrar som deklarerar täckning av specifika risker. Dessa är skrivskyddade och uppdateras när modellkonfigurationen ändras.",
            autoControlBadge: "Auto: Modellkontroll",
            noAutoControls: "Inga auto-genererade kontroller. Konfigurera vilka BWRA-risker GeoRisk-index och Kundrisk-parametrar adresserar (i Riskhanteringskonfigurationen).",
            allControls: "Alla kontroller",
            controlId: "ID",
            controlName: "Kontroll",
            type: "Typ",
            addressesRisk: "Hanterar risk",
            effectiveness: "Effektivitet",
            linkedModule: "Kopplad modul",
            unmitigatedRisks: "Ej åtgärdade risker",
            noControlsLinked: "Inga kontroller kopplade",
            goToModule: "Gå till modul",
            modelMapping: "Modellkoppling",
            mapping: {
                title: "Modell-risk-koppling",
                subtitle: "Den röda tråden: hur riskmodeller och kontroller kopplas till identifierade risker, från bruttorisk via åtgärder till residualrisk.",
                geoRiskDesc: "Landriskmodell med flera index och 4 beräkningstyper.",
                customerRiskDesc: "Kundrisklassificering baserad på konfigurerbara parametrar och poängmodeller.",
                txMonDesc: "Transaktionsövervakningsregler som deklarerar täckning av BWRA-risker.",
                activeIndices: "aktiva index",
                configuredModels: "konfigurerade modeller",
                parameters: "parametrar",
                activeRules: "aktiva regler",
                mappedRules: "kopplade till risker",
                traceabilityMatrix: "Spårbarhetsmatris",
                traceabilityDesc: "Visar vilka riskkategorier som täcks av vilka kontrolltyper. Gap indikerar risker utan åtgärder.",
                risks: "Risker",
                gaps: "Gap",
                riskFlows: "Riskflödesdiagram",
                riskFlowsDesc: "Visuellt flöde per risk: Bruttorisk → Kontroller/Modeller → Residualrisk. Den röda tråden som visar hur varje risk bedöms och åtgärdas.",
                modelLinked: "Modellkopplade risker",
                gapsIdentified: "Identifierade gap",
                noControls: "Inga kontroller",
                moduleHealth: "Modulvalideringsstatus",
                moduleHealthDesc: "Visar valideringsstatusen för varje kopplad modul. Högre poäng innebär mer komplett modelldata som stödjer riskkontrollerna.",
                moduleLevel: "Modulnivå (alla)",
                notModeled: "Ej modellvaliderad",
                notModeledStatus: "Manuellt kontrollerad",
                notModeledDetail: "Ingen RMS-modul kopplad — effektivitet baseras på manuell bedömning",
                selfAssessed: "Egenvärderade kontroller",
                selfAssessedDesc: "Dessa kontroller (PROD/TX/CHAN riskkategorier) hanteras via system- eller manuella kontroller utan kopplad RMS-modell. Residualrisk baseras enbart på kontrolleffektivitet.",
                modelCoverage: "Risktäckning deklarerad av modeller",
                modelCoverageDesc: "GeoRisk-index, Kundrisk-parametrar och TxMon-regler som deklarerar täckning av BWRA-riskkategorier — den tvåvägskoppling som visar vilka modeller som täcker vilka risker.",
                coverageGeoRisk: "GeoRisk-index som täcker denna risk",
                coverageCustomerRisk: "Kundrisk-parametrar som täcker denna risk",
                coverageTxMon: "TxMon-regler som täcker denna risk",
                noCoverageConfigured: "Ingen täckning deklarerad i denna modul ännu.",
                coverageIndexes: "Index",
                coverageParameters: "Parametrar",
                coverageRules: "Regler",
                declareCoverage: "Konfigurera i",
                autoGenerated: "auto",
            },
            calculatedResidual: {
                title: "Beräknad residualrisk",
                basedOn: "Baserat på kontrolleffektivitet och modellvalidering",
                calculatedScore: "Beräknad poäng",
                manualScore: "Manuell poäng",
                vs: "mot",
                difference: "Differens",
                lowerThanManual: "lägre än manuell",
                higherThanManual: "högre än manuell",
                sameAsManual: "samma som manuell",
                controlBreakdown: "Kontrolluppdelning",
                baseReduction: "Basreduktion",
                actualReduction: "Faktisk reduktion",
                modelStatus: "Modellstatus",
                noControls: "Inga kontroller att beräkna från",
                validated: "Validerad",
                partial: "Delvis data",
                noData: "Ingen data",
            },
            addRisk: "Lägg till risk",
            editRisk: "Redigera risk",
            deleteRisk: "Arkivera risk",
            deleteRiskConfirm: "Arkivera denna risk? Den döljs från aktivt register men bevaras för revisionsändamål.",
            archiveRisk: "Arkivera risk",
            archiveRiskConfirm: "Arkivera denna risk? Den döljs från aktivt register men bevaras för revisionsändamål.",
            restoreRisk: "Återställ risk",
            restoreRiskConfirm: "Återställ denna risk till det aktiva registret?",
            permanentDeleteRisk: "Radera permanent",
            permanentDeleteRiskConfirm: "PERMANENT RADERING av denna risk och alla kontroller? Åtgärden kan inte ångras och strider mot AMLR:s krav på revisionsspår. Skriv 'RADERA' för att bekräfta.",
            showArchived: "Visa arkiverade",
            hideArchived: "Dölj arkiverade",
            archivedBadge: "Arkiverad",
            archivedAt: "Arkiverades",
            acceptRisk: "Acceptera risk",
            revokeAcceptance: "Återkalla acceptans",
            acceptedBadge: "ACCEPTERAD",
            riskAcceptanceModal: {
                title: "Acceptera risk",
                subtitle: "Denna risk kommer att formellt accepteras. Dokumentera motiveringen nedan.",
                motivationLabel: "Motivering",
                motivationPlaceholder: "Beskriv varför denna risk accepteras och vilka kompenserande kontroller som finns...",
                acceptedByLabel: "Accepterad av",
                acceptedByPlaceholder: "Namn på ansvarig person",
                acceptedAtLabel: "Datum",
                save: "Acceptera risk",
                cancel: "Avbryt",
                revokeConfirm: "Är du säker på att du vill återkalla acceptansen för denna risk?",
                motivationRequired: "Motivering krävs.",
                acceptedByRequired: "Accepterad av krävs.",
                expiresAtLabel: "Utgår",
            },
            expirationAlert: {
                sectionTitle: "Utgående riskacceptanser",
                expiresSoon: "Utgår om {days} dag(ar)",
                expiresInDays: "{days}d kvar",
                expired: "UTGÅTT",
                reassessmentRequired: "Omprövning krävs",
                viewInRegister: "Visa i riskregister",
                dashboardAlert: "{count} acceptans(er) utgår snart",
            },
            addControl: "Lägg till kontroll",
            editControl: "Redigera kontroll",
            deleteControl: "Ta bort kontroll",
            deleteControlConfirm: "Är du säker på att du vill ta bort denna kontroll?",
            importTestResults: "Importera testresultat",
            viewTestResults: "Visa testresultat ({count})",
            testResultHistory: {
                title: "Testresultathistorik",
                date: "Datum",
                status: "Status",
                comment: "Kommentar",
                pass: "Godkänd",
                fail: "Underkänd",
                noResults: "Inga testresultat importerade ännu.",
                importedAt: "Importerades",
            },
            importTestResultsModal: {
                title: "Importera testresultat",
                description: "Ladda upp en CSV- eller JSON-fil med automatiserade kontrolltestresultat.",
                fileLabel: "Fil (CSV eller JSON)",
                csvFormat: "CSV-format: datum, status (pass/fail), kommentar",
                jsonFormat: "JSON-format: [{\"date\": \"...\", \"status\": \"pass\", \"comment\": \"...\"}]",
                preview: "Förhandsvisning ({count} poster)",
                date: "Datum",
                status: "Status",
                comment: "Kommentar",
                import: "Importera",
                cancel: "Avbryt",
                parseError: "Kunde inte tolka filen. Kontrollera formatet.",
                noResults: "Inga poster hittades i filen.",
                appendMode: "Lägg till i befintliga resultat",
                replaceMode: "Ersätt befintliga resultat",
                previewMore: "+{{count}} till",
            },
            addAction: "Lägg till åtgärd",
            editAction: "Redigera åtgärd",
            deleteAction: "Ta bort åtgärd",
            deleteActionConfirm: "Är du säker på att du vill ta bort denna åtgärd?",
            actionSection: {
                title: "Åtgärder",
                noActions: "Inga åtgärder definierade för denna risk.",
                description: "Beskrivning",
                descriptionPlaceholder: "Beskriv åtgärden som ska vidtas...",
                responsible: "Ansvarig",
                responsiblePlaceholder: "Namn på ansvarig person",
                deadline: "Deadline",
                priority: "Prioritet",
                linkedControl: "Kopplat kontrollmoment",
                noControlLink: "Inget specifikt kontrollmoment",
                count: "{{count}} åtgärder",
                priorities: {
                    HIGH: "Hög",
                    MEDIUM: "Medel",
                    LOW: "Låg",
                },
                statuses: {
                    NOT_STARTED: "Ej påbörjad",
                    IN_PROGRESS: "Pågående",
                    COMPLETED: "Genomförd",
                    VERIFIED: "Verifierad",
                },
            },
            selectRisk: "Välj risk",
            actions: "Åtgärder",
            save: "Spara",
            cancel: "Avbryt",
            delete: "Ta bort",
            riskNameLabel: "Risknamn",
            residualExceedsGross: "Residualrisk kan inte överstiga bruttorisk",
            controlLinkedModelRequired: "En specifik modell måste väljas när en modul är kopplad",
            categoryLabel: "Kategori",
            descriptionLabel: "Beskrivning",
            likelihoodLabel: "Sannolikhet",
            impactLabel: "Konsekvens",
            statusLabel: "Status",
            ownerSelectLabel: "Riskägare",
            ownerSelectPlaceholder: "Välj ägare...",
            controlNameLabel: "Kontrollnamn",
            controlType: "Kontrolltyp",
            controlDescription: "Beskrivning",
            controlEffectiveness: "Effektivitet",
            effectivenessFormat: "{{level}}/5",
            controlLinkedModule: "Kopplad modul",
            controlLinkedModel: "Kopplad modell",
            controlLinkedIndex: "Kopplat index",
            allModels: "Alla modeller (modulnivå)",
            allIndexes: "Alla index (modulnivå)",
            allRulesAndModels: "Alla regler & modeller (modulnivå)",
            controlLinkedRuleOrModel: "Kopplad regel eller modell",
            txmonRulesGroup: "Regler",
            txmonModelsGroup: "Modeller",
            none: "Ingen",
            saved: "Ändringar sparade",
            demo: {
                title: "Demodata",
                description: "Detta riskregister innehåller exempeldata för demonstration. Du kan behålla den för att utforska eller börja om med ett tomt register.",
                keepData: "Behåll data",
                startFresh: "Börja om",
                confirmStartFresh: "Är du säker på att du vill börja om? Detta raderar ALL data permanent och kan inte ångras.",
            },
            export: {
                button: "Exportera till Excel",
                generateReport: "Generera BWRA-rapport",
                score: "Poäng",
                level: "Nivå",
                controlCount: "Kontroller",
                controlId: "Kontroll-ID",
                summaryTitle: "BWRA - Verksamhetsomfattande Riskbedömning",
                reportTitle: "BWRA-rapport - Verksamhetsomfattande Riskbedömning",
                exportDate: "Exportdatum",
                generatedBy: "Genererad av",
                systemName: "RMS",
                riskSummary: "Risksammanfattning",
                sheetSummary: "Sammanfattning",
                sheetRisks: "Risker",
                sheetControls: "Kontroller",
                sheetScope: "Omfattning",
                sheetAppetite: "Riskaptit",
                sheetPolicies: "Policyer",
                sheetModelMapping: "Modellkopplingar",
                sheetAnnex: "Annex I-II-III",
                linkedModel: "Kopplad modell",
                validationScore: "Valideringspoäng",
                validationStatus: "Valideringsstatus",
                validationDetail: "Valideringsdetalj",
                validationAccuracy: "Valideringsnoggrannhet",
                highRiskCapture: "Högriskfångst",
                noValidationRun: "Ingen validering körd",
                validationSummary: "Valideringssammanfattning",
                records: "poster",
                moduleLevel: "(Modulnivå)",
                noModelLinks: "Inga kontroller är kopplade till riskmodeller.",
                modelMappingTitle: "Modellkopplingar och valideringsstatus",
                annexTitle: "AMLR Annex I/II/III per risk",
                toc: "Innehållsförteckning",
                appetiteStatus: "Status",
                reportConfig: {
                    title: "Rapportkonfiguration",
                    subtitle: "Välj format och sektioner som ska inkluderas i rapporten",
                    format: "Format",
                    formatPDF: "PDF-dokument",
                    formatExcel: "Excel-arbetsbok",
                    sections: "Sektioner",
                    sectionSummary: "Sammanfattning",
                    sectionScope: "Omfattning",
                    sectionAppetite: "Riskaptit",
                    sectionRisks: "Riskregister",
                    sectionControls: "Kontroller",
                    sectionModelMapping: "Modellkopplingar",
                    sectionPolicies: "Policyer",
                    sectionAnnex: "Annexar (I/II/III)",
                    sectionCompliance: "Compliance-karta",
                    sectionCategoryAnalysis: "Fördjupad kategorianalys",
                    selectAll: "Välj alla",
                    deselectAll: "Avmarkera alla",
                    generate: "Generera rapport",
                    cancel: "Avbryt",
                    generating: "Genererar...",
                    generateError: "Rapportgenerering misslyckades. Kontrollera dina data och försök igen.",
                    pdfPageOf: "Sida {{page}} av {{total}}",
                    toc: "Innehållsförteckning",
                    language: "Rapportspråk",
                    languageSwedish: "Svenska",
                    languageEnglish: "Engelska",
                },
            },
            versions: {
                title: "Versionshistorik",
                subtitle: "Versioner sparas automatiskt när en BWRA-rapport genereras. Jämför versioner för att följa förändringar över tid.",
                count: "{{count}} versioner",
                noVersions: "Inga versioner sparade ännu. Generera en BWRA-rapport för att skapa den första versionen.",
                viewDetails: "Visa detaljer",
                compareWithPrevious: "Jämför med föregående version",
                delete: "Ta bort version",
                risksShort: "risker",
                controlsShort: "kontroller",
                policiesShort: "policyer",
                avgGross: "Genomsnittlig bruttorisk",
                avgResidual: "Genomsnittlig residualrisk",
                mitigatedCount: "Åtgärdade",
                comparisonTitle: "Förändringar: {{v1}} → {{v2}}",
                noChanges: "Inga förändringar upptäckta mellan dessa versioner.",
                added: "Tillagd",
                removed: "Borttagen",
                confirmDelete: "Är du säker på att du vill ta bort denna version? Åtgärden kan inte ångras.",
                controlAddedTo: "Kontroll tillagd i '{{risk}}': {{name}}",
                controlRemovedFrom: "Kontroll borttagen från '{{risk}}': {{name}}",
                riskScoreFormat: "S{{l}}/K{{i}}",
                descriptionUpdated: "(text uppdaterad)",
                scopeChanged: "Scope — {{field}} ändrades",
                appetiteChanged: "Riskaptit — {{field}} ändrades",
                complianceChanged: "Efterlevnad — Artikel {{articleId}} {{field}}: {{from}} → {{to}}",
                complianceNotesChanged: "Efterlevnad — Artikel {{articleId}} anteckningar uppdaterades",
                fields: {
                    name: "Namn",
                    category: "Kategori",
                    status: "Status",
                    description: "Beskrivning",
                    grossRisk: "Bruttorisk",
                    residualRisk: "Residualrisk",
                    effectiveness: "Effektivitet",
                    type: "Typ",
                    businessDescription: "Verksamhetsbeskrivning",
                    products: "Produkter",
                    customerCategories: "Kundkategorier",
                    distributionChannels: "Distributionskanaler",
                    statement: "Riskaptitutlåtande",
                    thresholds: "Riskaptittröskelsvärden",
                },
            },
            categories: {
                GEO: "Geografisk risk",
                CUST: "Kundrisk",
                PROD: "Produkt-/Tjänsterisk",
                TX: "Transaktionsrisk",
                CHAN: "Kanalrisk",
            },
            likelihoodLevel1: "Mycket låg",
            likelihoodLevel2: "Låg",
            likelihoodLevel3: "Medel",
            likelihoodLevel4: "Hög",
            likelihoodLevel5: "Mycket hög",
            impactLevel1: "Försumbar",
            impactLevel2: "Mindre",
            impactLevel3: "Måttlig",
            impactLevel4: "Stor",
            impactLevel5: "Allvarlig",
            riskLevels: {
                low: "Låg",
                medium: "Medel",
                high: "Hög",
                veryHigh: "Mycket hög",
            },
            controlTypes: {
                MODEL: "Riskmodell",
                SYSTEM: "Systemkontroll",
                MANUAL: "Manuell process",
            },
            statusValues: {
                IDENTIFIED: "Identifierad",
                ASSESSED: "Bedömd",
                MITIGATED: "Åtgärdad",
                ARCHIVED: "Arkiverad",
            },
            score: "Poäng",
            noControlsShort: "Inga",
            addresses: "Hanterar",
            grossMax: "Brutto max",
            residualMax: "Residual max",
            linked: "Kopplad",
            risksCount: "{{count}} risker",
            riskFilterCount: "{{filtered}} av {{total}} risker",
            moduleNames: {
                GeoRisk: "Geografisk risk",
                CustomerRisk: "Kundrisk",
                TransactionMonitoring: "Transaktionsövervakning",
            },
            indexTypes: {
                BOOLEAN: "Boolean",
                SCORING: "Scoring",
                UNKNOWN: "Okänd",
            },
            appetite: {
                tab: "Riskaptit",
                title: "Riskaptitdokumentation",
                subtitle: "Definiera organisationens riskaptit per riskkategori. Tröskelvärden bestämmer acceptabla, förhöjda och oacceptabla risknivåer enligt AMLR.",
                statementTitle: "Riskaptituttalande",
                statementHelp: "Dokumentera organisationens övergripande riskaptit och tolerans för ML/TF-risk. Detta uttalande vägleder riskbedömningsprocessen.",
                statementPlaceholder: "Beskriv organisationens övergripande riskaptit, risktoleransnivåer och vägledande principer för ML/TF-riskhantering...",
                acceptableThreshold: "Acceptabel (≤)",
                elevatedThreshold: "Förhöjd (≤)",
                acceptable: "Acceptabel",
                elevated: "Förhöjd",
                unacceptable: "Oacceptabel",
                actualRisk: "Faktisk risk (max residual)",
                deviationWarning: "{{count}} riskkategori(er) överstiger den definierade riskaptiten",
                edit: "Redigera",
                thresholdRule: "Förhöjd tröskel måste överstiga Acceptabel tröskel",
                validationOutOfRange: "Värde måste vara mellan 1 och 25",
                validationElevatedMustExceedAcceptable: "Förhöjd måste överstiga Acceptabel",
            },
            radar: {
                title: "Riskaptit vs Faktisk Risk",
                subtitle: "Visuell jämförelse av definierade riskaptit-tröskelvärden och faktisk residualrisk per kategori.",
                appetiteLabel: "Aptit-tröskelvärde",
                actualLabel: "Faktisk residualrisk",
                acceptableZone: "Acceptabel",
                elevatedZone: "Förhöjd",
                unacceptableZone: "Oacceptabel",
                noDeviations: "Alla kategorier inom aptit",
                deviations: "{{count}} kategorier överstiger aptit",
                trendTitle: "Trend sedan senaste version",
                improved: "Förbättrat",
                worsened: "Försämrat",
                unchanged: "Oförändrat",
                noData: "Ingen riskdata att visa",
            },
            annexI: {
                title: "AMLR Bilaga I – Riskvariabler",
                description: "Varje risk måste bekräfta att alla riskvariabelkategorier i Bilaga I har beaktats, eller ge en motivering till undantag.",
                indicator: "Bilaga I: {{count}}/{{total}} beaktade",
                indicatorTooltipComplete: "Alla {{total}} Bilaga I-kategorier beaktade",
                indicatorTooltipMissing: "Saknas: {{categories}}",
                considered: "Beaktad",
                notAddressed: "Ej beaktad",
                motivationPlaceholder: "Motivering till undantag...",
                categories: {
                    CUSTOMER: "Kundriskfaktorer",
                    PRODUCT: "Produkt-/Tjänsteriskfaktorer",
                    GEO: "Geografiska riskfaktorer",
                    CHANNEL: "Kanalriskfaktorer",
                    TRANSACTION: "Transaktionsriskfaktorer",
                },
                factors: {
                    customerType: "Kundtyp (fysisk/juridisk person, trust)",
                    ownershipStructure: "Ägarstruktur och verkligt ägande",
                    businessActivity: "Verksamhetens art och komplexitet",
                    assetLevel: "Tillgångsnivå eller transaktionsvolymer",
                    purposeRelationship: "Syfte och avsedd karaktär på förbindelsen",
                    productType: "Typ av produkt eller tjänst",
                    productComplexity: "Produktens eller tjänstens komplexitet",
                    transactionValue: "Transaktionernas värde eller storlek",
                    anonymousTransactions: "Anonyma eller pseudonyma transaktioner",
                    countryResidence: "Bosättnings- eller etableringsland",
                    destinationCountries: "Destinationsländer för transaktioner",
                    highRiskJurisdictions: "FATF-listade eller högriskjurisdiktioner",
                    nonFaceToFace: "Affärsrelationer utan fysiskt möte",
                    paymentMethods: "Använda betalningsmetoder",
                    thirdPartyIntroducers: "Tredjepartsintroduktörer eller mellanhänder",
                    transactionPatterns: "Ovanliga eller komplexa transaktionsmönster",
                    transactionSizes: "Transaktionsstorlekar relativt förväntad aktivitet",
                    cashTransactions: "Kontantintensiva transaktioner",
                    crossBorderTransactions: "Gränsöverskridande överföringar",
                },
            },
            annexII: {
                title: "AMLR Bilaga II – Lågriskfaktorer",
                description: "Faktorer som indikerar potentiellt lägre ML/TF-risk. Vid tillämpning kan förenklade kundkännedomsåtgärder (SDD) vara motiverade.",
                selected: "valda",
                categories: {
                    CUSTOMER_LOW: "Kundfaktorer (låg risk)",
                    PRODUCT_LOW: "Produkt-/tjänstefaktorer (låg risk)",
                    GEO_LOW: "Geografiska faktorer (låg risk)",
                    TRANSACTION_LOW: "Transaktionsfaktorer (låg risk)",
                },
                factors: {
                    publicEntity: "Offentlig förvaltning eller offentligt företag",
                    regulatedInstitution: "Kreditinstitut eller finansiellt institut under AML-krav",
                    listedCompany: "Bolag noterat på en reglerad marknad med upplysningskrav",
                    knownCustomer: "Bosatt kund med väl känd och verifierad profil",
                    limitedValueInsurance: "Livförsäkring med låg årspremie",
                    pensionScheme: "Pensions- eller liknande system med arbetsgivarinbetalningar",
                    definedBenefitProduct: "Finansiell produkt utan förtida inlösen och med begränsat värde",
                    electronicMoneyLowValue: "E-penningprodukt med begränsad laddnings-/transaktionsvärde",
                    euEeaMember: "EU/EES-medlemsstat",
                    effectiveAmlSystem: "Tredjeland med effektivt AML/CFT-system (FATF-kompatibelt)",
                    lowCorruptionCountry: "Land med låga korruptionsnivåer enligt erkända index",
                    lowValueTransaction: "Lågvärdes- eller tillfällig transaktion under tröskelvärde",
                    regularPaymentKnownSource: "Regelbundna betalningar från verifierade, kända källor",
                    transparentOwnership: "Transparent ägarstruktur med identifierade verkliga ägare",
                },
            },
            annexIII: {
                title: "AMLR Bilaga III – Högriskfaktorer",
                description: "Faktorer som indikerar potentiellt högre ML/TF-risk. Vid tillämpning krävs skärpta kundkännedomsåtgärder (EDD).",
                selected: "valda",
                categories: {
                    CUSTOMER_HIGH: "Kundfaktorer (hög risk)",
                    PRODUCT_HIGH: "Produkt-/tjänstefaktorer (hög risk)",
                    GEO_HIGH: "Geografiska faktorer (hög risk)",
                    TRANSACTION_HIGH: "Transaktionsfaktorer (hög risk)",
                },
                factors: {
                    unusualPatterns: "Ovanliga omständigheter eller transaktionsmönster utan tydligt ekonomiskt syfte",
                    complexOwnership: "Komplex eller ogenomskinlig ägarstruktur",
                    cashIntensiveBusiness: "Kontantintensiv verksamhet",
                    noPhysicalPresence: "Ej bosatt eller saknar fysisk närvaro i landet",
                    pepStatus: "Politiskt exponerad person (PEP) eller familjemedlem/nära medarbetare",
                    thirdCountryNational: "Tredjelandsmedborgare som ansöker om uppehållsrätt eller medborgarskap",
                    anonymousInstruments: "Anonyma transaktioner eller produkter som möjliggör anonymitet",
                    privateBanking: "Private banking-tjänster",
                    cryptoAssets: "Kryptotillgångsprodukter eller -tjänster",
                    newProductNovelDelivery: "Nya produkter med innovativa leveransmekanismer eller teknik",
                    fatfHighRisk: "Land identifierat av FATF som högrisk eller under ökad övervakning",
                    sanctionedCountry: "Land eller territorium under EU- eller internationella sanktioner",
                    weakAmlFramework: "Land med bristfälligt AML/CFT-ramverk eller tillsyn",
                    highCorruption: "Land med betydande korruptions- eller organiserad brottslighet",
                    unusuallyLarge: "Ovanligt stora transaktioner relativt kundprofilen",
                    noEconomicRationale: "Transaktioner utan uppenbar ekonomisk motivering",
                    complexTransaction: "Ovanligt komplexa transaktionsmönster",
                    crossBorderHighRisk: "Gränsöverskridande transaktioner med högriskjurisdiktioner",
                },
            },
            cddRecommendation: {
                title: "CDD-rekommendation",
                levels: {
                    SDD: "Förenklad kundkännedom (SDD)",
                    CDD: "Normal kundkännedom (CDD)",
                    EDD: "Skärpt kundkännedom (EDD)",
                },
                descriptions: {
                    SDD: "Lågriskfaktorer identifierade – förenklade åtgärder kan tillämpas",
                    CDD: "Normala kundkännedomsåtgärder tillämpas",
                    EDD: "Högriskfaktorer identifierade – skärpta åtgärder krävs",
                },
            },
            scope: {
                tab: "Scope",
                title: "Verksamhetens omfattning och kontext",
                subtitle: "Dokumentera organisationens verksamhetsomfattning, produkter, kundkategorier, distributionskanaler och geografisk exponering enligt AMLR Art. 10.",
                edit: "Redigera",
                lastUpdated: "Senast uppdaterad",
                businessDescription: "Verksamhetsbeskrivning och omfattning",
                businessDescriptionHelp: "Beskriv organisationens verksamhet, storlek, komplexitet och BWRA:ns omfattning.",
                businessDescriptionPlaceholder: "Beskriv organisationens kärnverksamhet, juridisk struktur, storlek och komplexitet...",
                productsServices: "Produkter och tjänster",
                productsServicesHelp: "Lista alla produkter och tjänster som är relevanta för ML/TF-riskbedömningen.",
                productPlaceholder: "t.ex. Transaktionskonton, Utlåning, Private banking...",
                addProduct: "Lägg till produkt/tjänst",
                addGroup: "Lägg till grupp / affärsområde",
                groupNamePlaceholder: "Gruppnamn, t.ex. Privatpersoner, Företag, Investment Banking...",
                addItem: "Lägg till underpunkt",
                itemPlaceholder: "Underpunktens namn...",
                customerCategories: "Kundkategorier",
                customerCategoriesHelp: "Lista de kundkategorier och segment som organisationen betjänar.",
                customerCategoryPlaceholder: "t.ex. Privatkunder, Företagskunder, PEP:ar...",
                addCustomerCategory: "Lägg till kundkategori",
                distributionChannels: "Distributionskanaler",
                distributionChannelsHelp: "Lista de kanaler genom vilka produkter och tjänster levereras.",
                channelPlaceholder: "t.ex. Kontor, Internetbank, Mobilapp...",
                addChannel: "Lägg till kanal",
                geoExposure: "Geografisk exponering",
                geoExposureHelp: "Beskriv organisationens geografiska fotavtryck och koppla till relevanta GeoRisk-index för automatiserad riskbedömning.",
                geoDescriptionPlaceholder: "Beskriv geografiska marknader, gränsöverskridande verksamhet, korrespondentbanksrelationer...",
                linkedGeoIndexes: "Kopplade GeoRisk-index",
                notDocumented: "Inte dokumenterat ännu",
                riskAssessmentReferences: "Referenser till riskbedömningar",
                riskAssessmentReferencesHelp: "Dokumentera hur EU:s supranationella riskbedömning (SNRA) och nationella riskbedömningar har beaktats i denna BWRA, enligt AMLR Art. 10.",
                snra: "EU:s supranationella riskbedömning (SNRA)",
                snraHelp: "Europeiska kommissionens supranationella riskbedömning identifierar ML/TF-risker på EU-nivå.",
                nra: "Nationell riskbedömning (NRA)",
                nraHelp: "Den nationella tillsynsmyndighetens riskbedömning för din jurisdiktion.",
                considered: "Beaktad i BWRA",
                notConsidered: "Ännu ej beaktad",
                reference: "Referens / Version",
                referencePlaceholder: "t.ex. SNRA 2024, COM(2024) 365 final",
                nraReferencePlaceholder: "t.ex. FI Dnr 22-123, NRA 2024",
                authority: "Tillsynsmyndighet",
                authorityPlaceholder: "t.ex. Finansinspektionen (FI)",
                howConsidered: "Hur har denna beaktats?",
                howConsideredPlaceholder: "Beskriv hur resultaten från denna riskbedömning har inkorporerats i organisationens BWRA...",
                affectedCategories: "Påverkade riskkategorier",
                noCategoriesSelected: "Inga kategorier valda",
                linkedRisks: "Kopplade risker",
                noLinkedRisks: "Inga risker kopplade",
                manageRiskLinks: "Koppla risker",
                riskLinksTitle: "Kopplade risker",
                riskLinksHelp: "Välj vilka risker i riskregistret som är relaterade till denna scope-post.",
                riskLinksCount: "{{count}} risk(er) kopplad(e)",
                scopeCoverage: "Scope-täckning",
                scopeCoverageHelp: "Scope-poster (produkter, tjänster, distributionskanaler) kopplade till denna risk.",
                noScopeLinks: "Inga scope-poster kopplade till denna risk.",
                scopeFieldNames: {
                    products: "Produkter och tjänster",
                    customerCategories: "Kundkategorier",
                    distributionChannels: "Distributionskanaler",
                },
                saveScopeLinks: "Spara kopplingar",
                cancelScopeLinks: "Avbryt",
            },
            policies: {
                tab: "Policies",
                title: "Policies och procedurer",
                subtitle: "Skapa och koppla interna policies till riskkategorier enligt AMLR Art. 11. Identifiera gap där riskkategorier saknar kopplade policies.",
                addPolicy: "Lägg till policy",
                editPolicy: "Redigera policy",
                deletePolicy: "Ta bort policy",
                deletePolicyConfirm: "Är du säker på att du vill ta bort denna policy och alla dess procedurer?",
                policyName: "Policynamn",
                policyNamePlaceholder: "t.ex. KYC-policy, Transaktionsövervakningspolicy...",
                policyDescription: "Beskrivning",
                policyDescriptionPlaceholder: "Beskriv policyns syfte, omfattning och huvudkrav...",
                linkedCategories: "Kopplade riskkategorier",
                linkedCategoriesHelp: "Välj vilka riskkategorier denna policy adresserar.",
                procedures: "Procedurer",
                addProcedure: "Lägg till procedur",
                procedureTitle: "Procedurtitel",
                procedureTitlePlaceholder: "t.ex. Kundintroduktionsprocedur...",
                procedureDescription: "Beskrivning",
                procedureDescriptionPlaceholder: "Beskriv procedurstegen...",
                removeProcedure: "Ta bort",
                status: "Status",
                version: "Version",
                createdAt: "Skapad",
                updatedAt: "Senast uppdaterad",
                noPolicies: "Inga policies definierade ännu. Skapa policies för att dokumentera hur identifierade risker hanteras genom interna procedurer och kontroller.",
                fromComplianceBanner: "Från Compliance Map — Artikel {{article}}",
                fromComplianceDesc: "Denna artikel saknar kopplade kontroller. Formuläret nedan har förvalt relevanta riskkategorier. Fyll i ett namn och spara för att täcka detta gap.",
                clearFilter: "Rensa",
                gapIndicator: "Gapindikator",
                gapDescription: "Riskkategorier utan någon kopplad aktiv policy.",
                noGaps: "Alla riskkategorier täcks av minst en policy.",
                categoriesWithGap: "Kategorier utan policytäckning",
                policyCount: "{{count}} policies",
                procedureCount: "{{count}} procedurer",
                statusValues: {
                    DRAFT: "Utkast",
                    ACTIVE: "Aktiv",
                    ARCHIVED: "Arkiverad",
                },
            },
            compliance: {
                tab: "Compliance",
                title: "AMLR Compliance Map",
                subtitle: "Koppla AMLR-artiklar till kontroller och följ upp efterlevnadsstatus. Artiklarna grupperas per reglatoriskt tema.",
                totalArticles: "Totalt artiklar",
                coverageSummary: "{{covered}} av {{total}} artiklar täckta ({{pct}}%)",
                article: "Artikel",
                requirement: "Krav",
                statusHeader: "Status",
                linkedControls: "Kontroller",
                actions: "Åtgärder",
                noControls: "Inga kontroller kopplade",
                editStatus: "Redigera status",
                notesPlaceholder: "Lägg till noteringar om efterlevnadsstatus...",
                save: "Spara",
                cancel: "Avbryt",
                status: {
                    NOT_ASSESSED: "Ej bedömd",
                    COMPLIANT: "Uppfylld",
                    PARTIAL: "Delvis",
                    NON_COMPLIANT: "Ej uppfylld",
                },
                gap: {
                    title: "Gapanalys",
                    header: "Gap",
                    GREEN: "Täckt och validerad",
                    YELLOW: "Täckt, ej validerad",
                    RED: "Inga kontroller kopplade",
                    coverageSummary: "{{covered}} av {{total}} artiklar täckta ({{pct}}%)",
                    uncoveredTitle: "Artiklar utan kopplade kontroller",
                    navigateToPolicy: "Klicka för att gå till Policies och åtgärda detta gap",
                },
                export: {
                    button: "Exportera compliance-rapport",
                    reportTitle: "AMLR Compliance-rapport",
                    sheetCompliance: "Efterlevnadsstatus",
                    sheetGaps: "Identifierade gap",
                    theme: "Tema",
                    trafficLight: "Trafikljus",
                    notes: "Noteringar",
                    recommendation: "Rekommendation",
                    recRed: "Skapa och koppla kontroller för denna artikel. Inga kontroller adresserar detta krav.",
                    recYellow: "Validera befintliga kontroller. Kontroller är kopplade men efterlevnadsstatus är ej bekräftad.",
                    recGreen: "Ingen åtgärd krävs. Artikeln täcks av validerade kontroller.",
                    gapsTitle: "Identifierade compliance-gap",
                    noGaps: "Inga compliance-gap identifierade. Alla artiklar är täckta.",
                    criticalGaps: "Kritiska gap (inga kontroller kopplade)",
                    partialGaps: "Partiella gap (kontroller ej validerade)",
                },
                themes: {
                    riskAssessment: "Riskbedömning (Art. 10–15)",
                    cdd: "Kundkännedom (Art. 16–21)",
                    sddEdd: "FDD & EDD-åtgärder (Art. 22–26)",
                    recordKeeping: "Dokumentation & tredjeparter (Art. 27–29)",
                    beneficialOwnership: "Verkligt ägande (Art. 42–47)",
                    reporting: "Rapportering & FIU (Art. 50–54)",
                },
                articles: {
                    art10: "Skyldighet för verksamhetsutövare att genomföra en företagsövergripande riskbedömning (BWRA)",
                    art11: "Interna policies, kontroller och procedurer baserade på BWRA-resultat",
                    art12: "Individuell riskbedömning för varje affärsförbindelse och transaktion",
                    art13: "Godkännande av BWRA och riskhanteringsramverk av ledningen",
                    art14: "Dokumentationskrav för riskbedömningar och metodik",
                    art15: "Beaktande av tillsynsmyndighetens riskbedömning i BWRA-processen",
                    art16: "Allmän skyldighet att tillämpa kundkännedomsåtgärder",
                    art17: "Tidpunkt för kundkännedom – när CDD måste tillämpas",
                    art18: "Identifiering och verifiering av kundens identitet",
                    art19: "Identifiering av verkliga ägare och verifieringsåtgärder",
                    art20: "Bedömning av syftet med och den avsedda karaktären av affärsförbindelsen",
                    art21: "Fortlöpande övervakning av affärsförbindelser och transaktioner",
                    art22: "Villkor och åtgärder för förenklade kundkännedomsåtgärder (FDD)",
                    art23: "Villkor och åtgärder för skärpta kundkännedomsåtgärder (EDD)",
                    art24: "EDD-åtgärder för högrisk-tredjeländer identifierade av EU",
                    art25: "EDD-åtgärder för politiskt exponerade personer (PEP)",
                    art26: "EDD för gränsöverskridande korrespondentbankrelationer",
                    art27: "Krav på dokumentation – minst 5 års lagring",
                    art28: "Förbud mot anonyma konton, motböcker och bankfack",
                    art29: "Villkor för att förlita sig på tredje part för CDD",
                    art42: "Skyldighet för juridiska personer att inneha information om verkligt ägande",
                    art43: "Upprättande och upprätthållande av register för verkligt ägande",
                    art44: "Tillgång till register för verkligt ägande för behöriga myndigheter",
                    art45: "Verkligt ägande av truster och liknande juridiska arrangemang",
                    art46: "Centrala register för truster och liknande juridiska arrangemang",
                    art47: "Korrekthet och aktualitet av information om verkligt ägande",
                    art50: "Skyldighet att rapportera misstänkta transaktioner (STR) till FIU",
                    art51: "Befogenheter och ansvar för finansunderrättelseenheter",
                    art52: "FIU:s analys och spridning av STR-information",
                    art53: "Återkoppling från FIU till rapporterande enheter",
                    art54: "Förbud mot röjande av STR-anmälningar (tipping-off)",
                },
            },
            categoryAnalysis: {
                title: "Fördjupad kategorianalys",
                subtitle: "Dokumentera en detaljerad riskanalys per kategori. Fångar inneboende risknivå, trend, viktiga iakttagelser och åtgärdsstrategi som krävs för en fullständig BWRA.",
                editAnalysis: "Redigera analys",
                noAnalysis: "Ingen fördjupad analys dokumenterad för denna kategori ännu. Klicka 'Redigera analys' för att lägga till.",
                inherentRiskLevel: "Inneboende risknivå",
                trendDirection: "Risktrend",
                keyFindings: "Viktiga iakttagelser",
                mitigationStrategy: "Åtgärdsstrategi",
                regulatoryContext: "Regulatoriskt sammanhang",
                reviewer: "Granskare",
                lastReviewed: "Senast granskad",
                saveAnalysis: "Spara analys",
                riskCount: "{{count}} risk(er) i denna kategori",
                inherentLevels: {
                    "": "Ej bedömd",
                    HIGH: "Hög",
                    MEDIUM: "Medel",
                    LOW: "Låg",
                },
                trendDirections: {
                    "": "Ej bedömd",
                    INCREASING: "Ökande",
                    STABLE: "Stabil",
                    DECREASING: "Minskande",
                },
                keyFindingsPlaceholder: "Beskriv de viktigaste riskdrivarna, centrala observationer och betydande exponeringar för denna kategori...",
                mitigationStrategyPlaceholder: "Beskriv hur organisationen hanterar risker i denna kategori, inklusive nyckelkontroller och modellbaserade verktyg...",
                regulatoryContextPlaceholder: "Sammanfatta relevanta regulatoriska krav, tillsynsvägledning eller SNRA/NRA-iakttagelser relaterade till denna kategori...",
                reviewerPlaceholder: "Namn på den person som granskade denna analys",
                sheetName: "Kategorianalys",
                exportTitle: "Fördjupad riskanalys per kategori",
            },
            orgReport: {
                tab: "Org-rapport",
                title: "Aggregerad riskrapport – Organisationsnivå",
                subtitle: "Konsoliderad risksammanställning för alla entiteter i organisationshierarkin. Visar brutto- och residualrisk per entitet.",
                export: "Exportera till Excel",
                sheetName: "Org-riskrapport",
                entity: "Entitet",
                type: "Typ",
                country: "Land",
                totalRisks: "Risker",
                avgGross: "Avg brutto",
                avgResidual: "Avg residual",
                mitigated: "Åtgärdade",
                identified: "Identifierade",
                worstGross: "Max bruttonivå",
                worstResidual: "Max residualnivå",
                entitiesCount: "Entiteter",
                totalRisksAll: "Totala risker",
                mitigatedAll: "Åtgärdade",
                unassessedAll: "Ej bedömda",
                noEntities: "Inga entiteter konfigurerade.",
                categoryAggTitle: "Aggregerad risk per kategori (alla entiteter)",
                hierarchyTitle: "Risksammanfattning per entitet",
                risksShort: "risker",
            },
            actionPlan: {
                tab: "Åtgärdsplan",
                title: "Åtgärdsplan",
                subtitle: "Samlad lista med alla åtgärder från samtliga BWRA-risker. Används för styrelserapportering och uppföljning.",
                noActions: "Inga åtgärder definierade. Lägg till åtgärder från riskregistret.",
                filterStatus: "Status",
                filterPriority: "Prioritet",
                filterRisk: "Risk",
                filterResponsible: "Ansvarig",
                responsibleFilterPlaceholder: "Sök på namn...",
                filterDeadline: "Deadline",
                allStatuses: "Alla statusar",
                allPriorities: "Alla prioriteter",
                allRisks: "Alla risker",
                allDeadlines: "Alla",
                deadlineOverdue: "Försenade",
                deadlineThisMonth: "Denna månad",
                deadlineNext3Months: "Nästa 3 månader",
                riskLabel: "Risk",
                overdueLabel: "FÖRSENAD",
                goToRisk: "Gå till risk",
                export: "Exportera",
                exportExcel: "Exportera till Excel",
                exportPDF: "Exportera som PDF",
                exportSheetName: "Åtgärdsplan",
                exportTitle: "BWRA Åtgärdsplan",
                count: "{{count}} åtgärder",
                actionId: "Åtgärds-ID",
                description: "Beskrivning",
                responsible: "Ansvarig",
                deadline: "Deadline",
                priority: "Prioritet",
                status: "Status",
                linkedRisk: "Risk",
                linkedControl: "Kontroll",
                pdfPageOf: "Sida {{page}} av {{total}}",
                pdfGeneratedBy: "Genererad av",
                pdfExportDate: "Datum",
            },
            riskTimeline: {
                title: "Riskens livscykel",
                empty: "Inga livscykelhändelser registrerade ännu.",
                noDate: "Datum okänt",
                events: {
                    riskIdentified: "Risk identifierad",
                    riskStatus: "Status: {{status}}",
                    actionCreated: "Åtgärd {{id}} skapad",
                    actionCompleted: "Åtgärd {{id}} avslutad",
                    actionVerified: "Åtgärd {{id}} verifierad",
                    controlEvaluated: "Kontroll utvärderad: {{control}} ({{period}})",
                    responsible: "Ansvarig: {{name}}",
                    evaluatedBy: "Utvärderad av: {{name}}",
                    notConducted: "Utvärdering ej genomförd",
                    effectiveness: "Effektivitet: {{level}}",
                },
            },
            riskScoreTrend: {
                title: "Riskpoängens trend",
                grossLabel: "Bruttorisk",
                residualLabel: "Residualrisk",
                noHistory: "Poänghistorik visas här när du uppdaterar denna risk över tid.",
                chartAriaLabel: "Trenddiagram som visar brutto- och residualriskpoäng över tid",
                trend: {
                    up: "Ökande",
                    down: "Minskande",
                    stable: "Stabil",
                },
            },
            annualReview: {
                tab: "Årlig utvärdering (ÅRKU)",
                title: "Årlig kontrollutvärdering (ÅRKU)",
                subtitle: "Utvärdera effektiviteten hos alla kontroller en gång per år. För kontroller som bedöms delvis eller icke-effektiva skapas åtgärder.",
                newReview: "Ny utvärdering",
                createReview: "Skapa utvärdering",
                cancel: "Avbryt",
                noReviews: "Inga årliga utvärderingar genomförda ännu.",
                noReviewsDesc: "Klicka på 'Ny utvärdering' för att starta den första utvärderingen.",
                noControls: "Inga kontroller att utvärdera i denna genomgång.",
                noControlsInfo: "Lägg till kontroller till risker i riskregistret innan du skapar en årsuppföljning.",
                willIncludeControls: "Denna utvärdering inkluderar {{count}} kontroll(er) från aktuellt riskregister.",
                period: "Period / Beteckning",
                periodPlaceholder: "t.ex. 2025 eller 2025–2026",
                periodHelp: "Ange ett år (t.ex. 2025) eller ett intervall (t.ex. 2025–2026) för att identifiera denna granskningsperiod.",
                evaluationDate: "Utvärderingsdatum",
                evaluatedBy: "Utvärderad av",
                evaluatedByPlaceholder: "Namn eller roll",
                linkVersion: "Kopplad BWRA-version",
                noVersion: "Ingen version kopplad",
                conducted: "Genomförd",
                yes: "Ja",
                no: "Nej",
                effectiveness: "Effektivitet",
                observations: "Observationer / Kommentarer",
                observationsPlaceholder: "Ange observationer, fynd eller kommentarer...",
                eff: {
                    YES: "Effektiv",
                    PARTIAL: "Delvis effektiv",
                    NO: "Icke-effektiv",
                },
                status: {
                    DRAFT: "Utkast",
                    COMPLETED: "Avslutad",
                },
                totalControls: "Totalt kontroller",
                evaluatedShort: "utvärderade",
                pendingActions: "Öppna åtgärder",
                saveDraft: "Spara utkast",
                completeReview: "Slutför utvärdering",
                completeReviewDisabledTooltip: "Minst en kontroll måste vara utvärderad innan utvärderingen kan slutföras.",
                confirmCompleteTitle: "Slutför utvärdering?",
                confirmCompleteMessage: "Åtgärden kan inte ångras. Utvärderingen markeras som slutförd och låses för redigering.",
                viewActionPlan: "Visa åtgärdsplan",
                delete: "Ta bort utvärdering",
                confirmDelete: "Ta bort utvärdering {{id}} ({{period}})? Åtgärden kan inte ångras.",
                actionPrompt: "Denna kontroll är bedömd som delvis eller icke-effektiv. Skapa en åtgärd.",
                createAction: "Skapa åtgärd",
                cancelAction: "Avbryt",
                saveAction: "Spara åtgärd",
                actionCreated: "Åtgärd skapad",
                actionDescription: "Åtgärdsbeskrivning",
                actionDescriptionPlaceholder: "Beskriv åtgärden...",
                actionResponsible: "Ansvarig",
                actionResponsiblePlaceholder: "Namn eller roll",
                actionDeadline: "Deadline",
                arkuPrefix: "ÅRKU",
                periodicReportLink: "Periodisk rapport {{year}}",
                periodicReportTooltip: "Gå till periodicrapport för {{year}}",
                editPeriod: "Redigera periodbeteckning",
                savePeriod: "Spara period",
            },
            kriTitle: "Nyckelriskindikatorer",
            kriBwraCoverage: "BWRA-täckning",
            kriBwraCoverageDesc: "Andel risker som mitigeras av kontroller",
            kriComplianceCoverage: "Compliance-täckning",
            kriComplianceCoverageDesc: "Andel AMLR-artiklar som täcks",
            kriModelValidation: "Modellvalidering",
            kriModelValidationDesc: "Senaste valideringsnoggrannhet för modeller",
            kriScheduledValidation: "Schemalagd validering",
            kriScheduledValidationDesc: "Senaste automatiserade modellvalideringskörning",
            kriValidationAccuracy: "{{accuracy}}% noggrannhet",
            kriValidationError: "Senaste körning misslyckades",
            kriStatusGreen: "Inom mål",
            kriStatusYellow: "Kräver uppmärksamhet",
            kriStatusRed: "Åtgärd krävs",
            kriNoValidation: "Ingen validering",
            heatmapTab: "Riskmatris",
            heatmap: {
                title: "Riskmatris",
                subtitle: "5×5 sannolikhets-/konsekvenmatris. Varje punkt representerar en risk — klicka för att navigera till riskregistret.",
                toggleLabel: "Risktyp",
                legend: "Förklaring:",
                noRisks: "Inga risker att visa. Lägg till risker i riskregistret.",
            },
            gapAnalysis: {
                tab: "Gap-analys",
                title: "Gap-analysrapport",
                subtitle: "Identifierar risker utan adekvata kontroller — risker helt utan kontroller eller med enbart ineffektiva kontroller, sorterade efter residualriskexponering.",
                generateBtn: "Generera gap-analys",
                exportBtn: "Exportera till Excel",
                placeholder: "Klicka på 'Generera gap-analys' för att identifiera risker utan adekvata kontroller.",
                generatedAt: "Genererad",
                totalRisks: "Totalt antal risker",
                totalGaps: "Identifierade gap",
                noControlsCount: "Utan kontroller",
                ineffectiveOnlyCount: "Enbart ineffektiva kontroller",
                gapPercent: "Gapandel",
                sectionNoControls: "Risker utan kontroller",
                sectionIneffective: "Risker med enbart ineffektiva kontroller",
                noControlsNote: "Inga kontroller definierade — risken är helt omitigerad.",
                ineffectiveNote: "{{count}} kontroll(er) definierade, men max effektivitet är {{max}}/5 (ineffektivt).",
                recommendedActions: "Rekommenderade åtgärder",
                allClearTitle: "Inga gap identifierade",
                allClearBody: "Alla aktiva risker har minst en effektiv kontroll. Bra jobbat.",
                controlCount: "Kontroller",
                gapType: "Gaptyp",
                gapTypes: {
                    NO_CONTROLS: "Inga kontroller",
                    INEFFECTIVE_ONLY: "Enbart ineffektiva kontroller",
                },
                exportTitle: "BWRA Gap-analysrapport",
                sheetSummary: "Sammanfattning",
                sheetDetail: "Gap",
                recommendations: {
                    LINK_MODEL: "Koppla till {{module}}-modell — lägg till en modellkontroll för datadriven riskreducering.",
                    ADD_MANUAL_CONTROL: "Lägg till en manuell eller systembaserad kontroll för att hantera risken.",
                    ADD_ACTION: "Skapa en åtgärdsplan för att åtgärda detta gap.",
                    IMPROVE_MODEL_VALIDATION: "Kör modellvalidering för att förbättra effektivitetspoängen för kopplade modeller.",
                    INCREASE_EFFECTIVENESS: "Granska och stärk befintliga kontroller för att öka deras effektivitetsbetyg.",
                    VERIFY_MODEL_COVERAGE: "Verifiera att {{module}} täcker denna riskkategori i sin konfiguration.",
                },
            },
            boardReport: {
                tab: "Styrelserapport",
                title: "Styrelsenivårapport — AML-risk",
                subtitle: "Sammanfattning av AML-riskläget, topp-exponeringar och kontrollmiljö — formaterad för styrelsegenomgång.",
                generateBtn: "Generera styrelserapport",
                printBtn: "Skriv ut till PDF",
                placeholder: "Klicka på 'Generera styrelserapport' för att skapa en styrelseredo sammanfattning av det aktuella riskläget.",
                generatedAt: "Genererad",
                sections: {
                    executiveSummary: "Executive Summary",
                    topRisks: "Topp 10 riskexponeringar",
                    controls: "Kontrollmiljö",
                    controlStatus: "Kontrollmiljö",
                    gapAnalysis: "Gap-analys",
                    signature: "Granskning och godkännande",
                    attestation: "Granskning och godkännande",
                },
                top10Title: "Topp 10 risker efter residualexponering",
                riskColumn: "Risk",
                coverage: "Kontrolltäckning",
                attestationText: "Jag bekräftar att ovanstående riskbedömning har granskats i enlighet med AMLR och FFFS 2017:11.",
                totalRisks: "Totalt antal risker",
                activeRisks: "Aktiva risker",
                mitigated: "Åtgärdade",
                avgGross: "Medelbruttorisk",
                avgResidual: "Medelresidualrisk",
                riskReduction: "Riskreduktion",
                appetiteStatement: "Riskaptitförklaring",
                noAppetiteStatement: "Ingen riskaptitförklaring har definierats.",
                rank: "#",
                grossScore: "Brutto",
                residualScore: "Residual",
                level: "Nivå",
                noRisks: "Inga aktiva risker registrerade.",
                totalControls: "Totalt kontroller",
                effectiveControls: "Effektiva kontroller (≥3)",
                controlCoverage: "Täckningsgrad",
                avgEffectiveness: "Medel effektivitet",
                risksWithoutControls: "Risker utan kontroller",
                totalGaps: "Totala gap",
                noControlsCount: "Utan kontroller",
                ineffectiveOnlyCount: "Ineffektiva kontroller",
                gapRate: "Gapandel",
                allControlsAdequate: "Alla aktiva risker har adekvata kontroller.",
                reviewDate: "Granskningsdatum",
                reviewerName: "Granskarens namn",
                reviewerTitle: "Titel / Funktion",
                reviewerNamePlaceholder: "Ange namn",
                reviewerTitlePlaceholder: "Ange titel",
                signatureLabel: "Underskrift",
                approvalLabel: "Styrelsebeslut",
                printHint: "Använd webbläsarens utskriftsfunktion (Ctrl+P / Cmd+P) för att spara som PDF.",
                fffsNote: "Upprättad i enlighet med FFFS 2017:11 och AMLR:s krav.",
                preparedBy: "Upprättad av",
                confidential: "KONFIDENTIELLT — Endast för internt bruk",
            },
            trendAnalysis: {
                tab: "Trender",
                title: "Risktrendanalys",
                subtitle: "Följ hur risknivåer utvecklas över årsgranskningsperioder. Identifiera risker som förbättras, försämras eller är stabila över tid.",
                improving: "Förbättras",
                worsening: "Försämras",
                stable: "Stabil",
                noData: "Ingen data",
                noHistory: "Ingen riskhistorik tillgänglig",
                noHistoryDesc: "Riskpoängshistorik visas här när risker har bedömts över tid.",
                residualScore: "Residualrisk",
                grossScore: "Bruttorisk",
                selectHint: "Välj risker att visa i diagrammet",
                selectRisksHint: "Välj en eller flera risker ovan för att visa trenden.",
                chartAriaLabel: "Diagram över riskpoängsutveckling",
                summaryTitle: "Risktrendsammanfattning",
                reviewsTitle: "Årsgranskningsreferens",
                trend: {
                    improving: "Förbättras",
                    worsening: "Försämras",
                    stable: "Stabil",
                },
                col: {
                    risk: "Risk",
                    category: "Kategori",
                    grossScore: "Brutto",
                    residualScore: "Residual",
                    delta: "Förändring",
                    trend: "Trend",
                },
            },
            calendar: {
                tab: "Kalender",
                title: "Compliance-kalender",
                subtitle: "Översikt över alla deadlines i åtgärdsplanen. Klicka på en deadline för att navigera till relaterad risk.",
                prevMonth: "Föregående månad",
                nextMonth: "Nästa månad",
                today: "Idag",
                thisMonth: "Denna månads deadlines",
                noDeadlines: "Inga deadlines denna månad.",
                overdueAlert: "{{count}} försenad(e) åtgärd(er) kräver uppmärksamhet.",
                months: {
                    jan: "Januari",
                    feb: "Februari",
                    mar: "Mars",
                    apr: "April",
                    may: "Maj",
                    jun: "Juni",
                    jul: "Juli",
                    aug: "Augusti",
                    sep: "September",
                    oct: "Oktober",
                    nov: "November",
                    dec: "December",
                },
                weekdays: {
                    mon: "Mån",
                    tue: "Tis",
                    wed: "Ons",
                    thu: "Tor",
                    fri: "Fre",
                    sat: "Lör",
                    sun: "Sön",
                },
            },
            fieldMapping: {
                tab: "Mappning",
                title: "Fältmappning",
                subtitle: "Mappa BWRA-riskfält (bruttoriskpoäng, kategori, status) till kolumner i en extern datakälla.",
                dataSource: "Datakälla",
                selectDataSource: "— Välj datakälla —",
                table: "Tabell",
                selectTable: "— Välj tabell —",
                tableNamePlaceholder: "Ange tabellnamn",
                rowIdColumn: "Riskens ID-kolumn",
                selectRowIdColumn: "— Välj kolumn —",
                rowIdColumnPlaceholder: "Ange kolumnnamn (t.ex. risk_id)",
                riskMappings: "Riskfältsmappningar",
                riskFieldCol: "Riskfält",
                columnCol: "Källkolumn",
                transformCol: "Transform",
                selectColumn: "— Välj kolumn —",
                columnPlaceholder: "Ange kolumnnamn",
                transform_none: "Ingen",
                transform_uppercase: "Versaler",
                transform_lowercase: "Gemener",
                transform_trim: "Trimma",
                transform_number: "Till nummer",
                transform_boolean: "Till boolean",
                transform_date: "Till datum",
                previewData: "Förhandsgranska data (5 rader)",
                saveMapping: "Spara mappning",
                saving: "Sparar…",
                statusComplete: "Mappning klar — alla fält mappade",
                statusIncomplete: "{{count}} fält saknar kolumnmappning",
                savedSuccess: "Mappning sparad",
                saveError: "Kunde inte spara mappning: {{error}}",
                errorNoDataSource: "Välj en datakälla",
                errorNoTable: "Ange ett tabellnamn",
                errorNoRowId: "Ange riskens ID-kolumn",
                previewTitle: "Dataförhandsvisning",
                previewSubtitle: "Exempelrader från {{table}}",
                previewNoData: "Ingen förhandsvisning tillgänglig — anslut till en live-datakälla för att hämta rader.",
                noDataSources: "Inga datakällor konfigurerade. Lägg till en i Admin → Datakällor.",
                noRisks: "Inga risker registrerade. Lägg till risker i riskregistret innan du konfigurerar fältmappning.",
            },
            backToSection: "Tillbaka till {{section}}",
            sections: {
                overview: {
                    label: "Översikt",
                    description: "Modulöversikt, verksamhetsomfång och riskaptitutlåtande.",
                },
                riskanalysis: {
                    label: "Riskanalys",
                    description: "Riskregister, riskmatris, trendanalys och gapanalys.",
                },
                controlsActions: {
                    label: "Kontroller & Åtgärder",
                    description: "Kontroller, åtgärdsplan, årlig genomgång och efterlevnadskalender.",
                },
                compliancePolicies: {
                    label: "Compliance & Policyer",
                    description: "AMLR-efterlevnadskarta, policyer, modellkoppling och fältmappning.",
                },
                reports: {
                    label: "Rapporter",
                    description: "Risk- och styrelserapporter på organisationsnivå.",
                },
            },
            toolLabels: {
                overview:      "Översikt",
                scope:         "Verksamhetsomfång",
                appetite:      "Riskaptit",
                register:      "Riskregister",
                heatmap:       "Riskmatris",
                trendanalysis: "Trendanalys",
                gapanalysis:   "Gapanalys",
                controls:      "Kontroller",
                actionplan:    "Åtgärdsplan",
                annualreview:  "Årlig utvärdering",
                calendar:      "Kalender",
                compliance:    "Compliance-karta",
                policies:      "Policyer",
                mapping:       "Modellkoppling",
                fieldmapping:  "Fältmappning",
                orgreport:     "Org-rapport",
                boardreport:   "Styrelserapport",
                compliancechain: "Helhetsvalidering",
            },
            toolDescriptions: {
                overview:      "Sammanfattning av risker, kontroller och riskexponering.",
                scope:         "Definiera organisationens verksamhetsomfång och kontext.",
                appetite:      "Fastställ och dokumentera riskaptitutlåtandet.",
                register:      "Hantera och bedöm alla identifierade AML/CFT-risker.",
                heatmap:       "Visualisera risker efter sannolikhet och konsekvens.",
                trendanalysis: "Följ riskpoängtrender över tid.",
                gapanalysis:   "Identifiera gap mellan krävda och implementerade kontroller.",
                controls:      "Dokumentera och utvärdera kontrollåtgärder.",
                actionplan:    "Följ upp åtgärder och deadlines.",
                annualreview:  "Genomför den årliga AML/CFT-kontrollgenomgången (ÅRKU).",
                calendar:      "Se efterlevnadsdeadlines och kommande genomgångar.",
                compliance:    "Koppla risker till AMLR-artiklar och krav.",
                policies:      "Länka policyer och rutiner till riskkategorier.",
                mapping:       "Koppla risker till modeller och analysverktyg.",
                fieldmapping:  "Konfigurera fältmappning mellan BWRA och datakällor.",
                orgreport:     "Aggregerad riskrapport på organisationsnivå.",
                boardreport:   "Risksammanfattning och attestering för styrelsen.",
                compliancechain: "Helhetsvalidering — verifierar att BWRA-risker hanteras av modeller och att modeller är validerade mot data.",
            },
            complianceChain: {
                title: "Helhetsvalidering",
                subtitle: "End-to-end-validering att BWRA-risker hanteras av modeller/kontroller OCH att dessa modeller är validerade mot aktuell data.",
                runNow: "Kör validering nu",
                running: "Kör…",
                lastRun: "Senast körd: {{time}}",
                gapsHeading: "Gap som kräver åtgärd",
                coverageHeading: "Risktäckning",
                modelsHeading: "Modellvalideringsstatus",
                scheduleHeading: "Automatiskt schema",
                historyHeading: "Körningshistorik",
                scheduleEnable: "Kör automatiskt enligt schema",
                scheduleFrequency: "Frekvens",
                scheduleHour: "Timme (0–23)",
                scheduleNote: "Schemalagda körningar utförs av systemet när en användare öppnar appen vid eller efter nästa schemalagda tidpunkt. För produktionsmiljö bör detta kopplas till serversidig jobb-runner.",
                coveredYes: "Täckt",
                coveredNo: "Ej täckt",
                status: {
                    green: "Kedjan är OK",
                    yellow: "Uppmärksamhet krävs",
                    red: "Kritiska gap",
                    grey: "Inga data",
                },
                statusDesc: {
                    green: "Alla BWRA-risker hanteras och alla refererade modeller är validerade mot aktuell data.",
                    yellow: "Några modeller har föråldrad validering eller saknar validering helt. Åtgärda innan nästa styrelsemöte.",
                    red: "BWRA-risker är otäckta eller modeller har failat validering. Omedelbar åtgärd krävs.",
                    grey: "Inga data tillgängliga. Definiera BWRA-risker och kör en validering.",
                },
                counts: {
                    risksCovered: "Risker täckta",
                    modelsOk: "Modeller OK",
                    failedModels: "Failade valideringar",
                    staleModels: "Föråldrade valideringar",
                },
                modelStatus: {
                    green: "OK",
                    yellow: "Föråldrad",
                    red: "Failad",
                    grey: "Ingen validering",
                },
                issueTypes: {
                    uncovered_risk: "Otäckt BWRA-risk",
                    failed_model: "Modell failade validering",
                    stale_validation: "Föråldrad modellvalidering",
                    missing_validation: "Modell aldrig validerad",
                },
                issueMeta: {
                    accuracy: "Träffsäkerhet: {{value}}%",
                    ageDays: "Ålder: {{days}} dagar",
                },
                frequency: {
                    daily: "Daglig",
                    weekly: "Veckovis",
                    monthly: "Månadsvis",
                },
                table: {
                    risk: "Risk",
                    category: "Kategori",
                    coverage: "Täckning",
                    models: "Modeller/Kontroller",
                    module: "Modul",
                    model: "Modell",
                    accuracy: "Träffsäkerhet",
                    validationDate: "Valideringsdatum",
                    status: "Status",
                    timestamp: "Tidpunkt",
                    triggeredBy: "Utlöst av",
                    gaps: "Gap",
                },
            },
        },
        transactionMonitoring: {
            title: "Transaktionsövervakning",
            subtitle: "Dokumentera övervakningsregler, koppla dem till BWRA-risker och registrera larmprestandadata.",
            tabOverview: "Översikt",
            tabRules: "Regler",
            tabMapping: "Regelkoppling",
            tabPerformance: "Larmprestation",
            tabModelOverview: "Modellöversikt",
            tabValidation: "Validering",
            tabThresholds: "Trösklar",
            tabAlerts: "Larmärenden",
            tabTemplates: "Regelmallar",
            tabScenarioTest: "Scenariotest",
            overview: {
                title: "Översikt transaktionsövervakning",
                description: "Dokumentera och bedöm transaktionsövervakningsfunktionen. Koppla övervakningsregler till BWRA-risker och registrera larmprestandadata från det operativa systemet.",
                totalRules: "Dokumenterade regler",
                activeRules: "Aktiva regler",
                mappedRules: "Regler kopplade till risker",
                rulesWithPerformance: "Regler med prestandadata",
                totalThresholds: "Definierade trösklar",
                totalModels: "Dokumenterade modeller",
                noData: "Inga regler dokumenterade ännu. Gå till fliken Regler för att lägga till övervakningsregler.",
            },
            rules: {
                title: "Övervakningsregler",
                subtitle: "Dokumentera övervakningsreglerna i det operativa systemet. Registrera vilka regler som finns, deras typ, tröskel och aktuell status.",
                addRule: "Lägg till regel",
                newRuleFromTemplate: "Ny regel från mall",
                editRule: "Redigera regel",
                saveRule: "Spara regel",
                cancel: "Avbryt",
                deleteRule: "Ta bort",
                confirmDelete: "Är du säker på att du vill ta bort regeln {{name}}?",
                noRules: "Inga regler dokumenterade. Lägg till en regel för att börja.",
                ruleId: "Regel-ID",
                ruleName: "Regelnamn",
                ruleNamePlaceholder: "t.ex. Högt transaktionsvärde",
                systemName: "Operativt system",
                systemNamePlaceholder: "t.ex. Actimize, NICE Actimize, Temenos",
                description: "Beskrivning",
                descriptionPlaceholder: "Beskriv vad denna regel övervakar och dess syfte...",
                ruleType: "Regeltyp",
                threshold: "Tröskel (belopp)",
                thresholdPlaceholder: "t.ex. 10000",
                currency: "Valuta",
                enabled: "Aktiv i system",
                actions: "Åtgärder",
                ruleType_AMOUNT: "Belopp",
                ruleType_FREQUENCY: "Frekvens",
                ruleType_PATTERN: "Mönster",
                nameRequired: "Regelnamn krävs.",
                thresholdInvalid: "Tröskel måste vara ett positivt tal.",
                patternRegex: "Sökmönster (regex)",
                patternRegexPlaceholder: "t.ex. kontant|uttag|ATM",
                patternRegexFallback: "(nyckelordsmatch från regelnamn — inget mönster angivet)",
                patternInvalidRegex: "Ogiltigt reguljärt uttryck. Kontrollera mönstrets syntax.",
                patternExplanation: "Mönster-regler matchar transaktionsfält (description, type, reference) med regex. Om inget mönster anges används nyckelord från regelnamnet som reserv.",
                ruleAction: "Operativ åtgärd",
                ruleAction_ALERT: "Larm",
                ruleAction_BLOCK: "Blockera",
                ruleAction_REVIEW: "Granska",
                ruleAction_SAR: "STR",
                ruleAction_LOG: "Logga endast",
                simulate: "Simulera",
                simulatorTitle: "Regelsimulatorn",
                simulatorDescription: "Testa hur denna regel skulle bete sig utan att aktivera den i produktion.",
                simulatorRule: "Simulerar regel",
                simulatorThresholdOverride: "Testtröskel",
                simulatorThresholdHint: "Tillfällig överskrivning — sparas ej. Ändra för att se hur olika trösklar påverkar träffandelen.",
                simulatorAddScenario: "Lägg till testscenario",
                simulatorInputAmount: "Testbelopp",
                simulatorInputCount: "Testantal",
                simulatorInputPattern: "Testbeskrivning / text",
                simulatorInputPlaceholder_AMOUNT: "t.ex. 15000",
                simulatorInputPlaceholder_FREQUENCY: "t.ex. 5",
                simulatorInputPlaceholder_PATTERN: "t.ex. kontantuttag internationellt",
                simulatorAdd: "Lägg till",
                simulatorNoScenarios: "Inga testscenarier tillagda ännu. Ange ett värde ovan och klicka Lägg till.",
                simulatorHit: "TRÄFF",
                simulatorMiss: "MISS",
                simulatorSummary: "{{hits}} av {{total}} scenarier skulle utlösa denna regel.",
                simulatorClear: "Rensa scenarier",
                simulatorClose: "Stäng simulator",
                simulatorRemove: "Ta bort",
                simulatorNoThreshold: "Ingen tröskel definierad — alla scenarier missar.",
                triggerCount30Days: "Träffar (30 dagar)",
                triggerCountZeroBadge: "Inga träffar",
                triggerCountZeroTooltip: "Möjlig ineffektiv regel — inga träffar registrerade de senaste 30 dagarna",
                thresholdMismatchBadge: "Överskrider regulatorisk tröskel",
                thresholdMismatchDetail: "Regelbelopp överskrider {{name}}: {{amount}} {{currency}}",
                searchPlaceholder: "Sök regler på namn, beskrivning eller typ…",
                filterStatus: "Status",
                filterStatusAll: "Alla statusar",
                filterStatusActive: "Aktiv",
                filterStatusInactive: "Inaktiv",
                filterType: "Typ",
                filterTypeAll: "Alla typer",
                noFilterResults: "Inga regler matchar aktuella filter.",
                filterCount: "Visar {{shown}} av {{total}} regler",
                batchSelectAll: "Välj alla synliga",
                batchDeselectAll: "Avmarkera alla",
                batchSelected: "{{count}} markerade",
                batchActivate: "Aktivera valda",
                batchDeactivate: "Inaktivera valda",
                batchConfirmActivate: "Aktivera {{count}} regler?",
                batchConfirmDeactivate: "Inaktivera {{count}} regler?",
                batchActivated: "{{count}} regler aktiverade",
                batchDeactivated: "{{count}} regler inaktiverade",
                history: "Historik",
                historyButtonTitle: "Visa ändringshistorik för denna regel",
                historyTitle: "Ändringshistorik",
                historyVersions: "Versioner",
                historyNoVersions: "Inga tidigare versioner registrerade ännu. Historik skapas när en regel redigeras.",
                historyChangedBy: "Ändrad av",
                historySelectHint: "Välj en version i listan för att se vad som ändrades.",
                historyVersion: "Version",
                historyNoDiff: "Inga fältändringar jämfört med aktuell version.",
                historyFieldLabel: "Fält",
                historyOldValue: "Tidigare värde",
                historyNewValue: "Nuvarande värde",
                historySnapshotValue: "Värde i denna version",
                historyAllFields: "Visa alla fält",
                historyRestore: "Återställ till",
                historyRestored: "Regeln återställd till tidigare version.",
                historyConfirmRestore: "Återställ regeln till {{version}}? De nuvarande värdena blir en ny version i historiken.",
                historyBatchOperation: "Batchoperation",
                exportRules: "Exportera regler",
                exportSelected: "Exportera valda ({{count}})",
                exportAll: "Exportera alla ({{count}})",
                exportSuccess: "{{count}} regler exporterade",
                importRules: "Importera regler",
                importTitle: "Importera regler från JSON",
                importDescription: "Ladda upp en tidigare exporterad regelfil (.json). Granska konflikter innan import.",
                importDrop: "Välj fil eller dra hit",
                importFileError: "Kunde inte läsa filen. Kontrollera att det är en giltig JSON-export.",
                importFormatError: "Ogiltigt filformat. En RMS-regelexport förväntades.",
                importPreviewTitle: "Importförhandsvisning",
                importPreviewNew: "{{count}} nya regler",
                importPreviewConflicts: "{{count}} konflikter",
                importConflictId: "ID-konflikt",
                importConflictName: "Namnkonflikt",
                importStrategyLabel: "Vid konflikter:",
                importStrategySkip: "Hoppa över konflikter (importera bara nya regler)",
                importStrategyOverwrite: "Skriv över befintliga regler",
                importStrategyNew: "Importera som nya regler (nya ID tilldelas)",
                importConfirm: "Importera",
                importCancel: "Avbryt",
                importSuccess: "Import klar: {{imported}} importerade, {{overwritten}} överskrivna, {{skipped}} hoppades över",
                importNoFile: "Ingen fil vald",
                importClose: "Stäng",
                depViewBtn: "Beroendevy",
                depTitle: "Regelberoende och överlappningsanalys",
                depSubtitle: "Visar vilka regler som överlappar och kan triggas av samma transaktioner. Redundanta regler bör granskas och eventuellt konsolideras.",
                depClose: "Stäng beroendevy",
                depNoOverlaps: "Inga överlappande regler hittades. Varje regel täcker ett unikt scenario.",
                depMatrixTitle: "Överlappningsmatris",
                depPairsTitle: "{{count}} överlappande regelpar",
                depFilterAll: "Alla överlappningstyper",
                depNoFilteredPairs: "Inga par matchar valt filter.",
                depNoOverlap: "Ingen överlappning",
                depRedundantAlert: "{{count}} potentiellt redundant(a) regelpar hittades — samma typ, tröskel och valuta.",
                depRedundantDetail: "Identisk tröskel: {{threshold}}",
                depAmountDetail: "Båda triggar ovan {{threshold}}",
                depPatternDetail: "Gemensamma nyckelord: {{keywords}}",
                depRiskDetail: "{{count}} gemensam(ma) BWRA-risk(er)",
                depOverlap_REDUNDANT: "Potentiellt redundant",
                depOverlap_AMOUNT_OVERLAP: "Beloppöverlappning",
                depOverlap_FREQUENCY_OVERLAP: "Frekvensöverlappning",
                depOverlap_PATTERN_OVERLAP: "Mönsteröverlappning",
                depOverlap_RISK_OVERLAP: "Risköverlappning",
            },
            mapping: {
                title: "Regelkoppling till risker",
                description: "Dokumentera vilka BWRA-risker varje övervakningsregel adresserar och vilka produkter/tjänster den täcker.",
                noRules: "Inga regler dokumenterade ännu. Gå till fliken Regler för att lägga till övervakningsregler.",
                editMapping: "Redigera koppling",
                saveMapping: "Spara koppling",
                cancel: "Avbryt",
                confirmTitle: "Bekräfta ändringar",
                confirmMessage: "Du håller på att ändra riskkopplingar för regel {{ruleName}}. Vill du fortsätta?",
                saveSuccess: "Kopplingar sparade",
                saveError: "Det gick inte att spara kopplingar",
                bwraRisks: "BWRA-risker som adresseras",
                noRisksInBWRA: "Inga risker i BWRA. Lägg till risker i BWRA-modulen först.",
                linkedProducts: "Täckta produkter / tjänster",
                noProductsInBWRA: "Inga produkter i BWRA Scope. Lägg till produkter i BWRA-modulen.",
                risksLinked: "risker",
                productsLinked: "produkter",
                noMapping: "Ej kopplad",
                unmappedActiveWarning: "Aktiv regel utan riskkoppling — granskas av revisor",
            },
            performance: {
                title: "Larmprestandadata",
                description: "Dokumentera övervakningsreglernas prestanda i det operativa systemet per period. Registrera hur många larm som skapats, utretts och rapporterats.",
                noRules: "Inga regler dokumenterade ännu. Gå till fliken Regler för att lägga till övervakningsregler.",
                noData: "Ingen prestandadata registrerad för denna regel.",
                entries: "poster",
                expand: "Visa / Lägg till",
                collapse: "Stäng",
                addEntry: "Lägg till perioddata",
                editEntry: "Redigera",
                deleteEntry: "Ta bort",
                saveEntry: "Spara",
                confirmDelete: "Ta bort denna prestandapost?",
                period: "Period",
                periodPlaceholder: "t.ex. 2025-Q1 eller 2025-01",
                periodRequired: "Period krävs",
                alertsCreated: "Skapade larm",
                alertsInvestigated: "Utredda",
                alertsReported: "Rapporterade (STR/SAR)",
                sarFiled: "Inlämnade STR",
                notes: "Anteckningar",
            },
            modelOverview: {
                title: "Modellöversikt transaktionsövervakning",
                subtitle: "Definiera transaktionsövervakningsmodeller. Varje modell grupperar övervakningsregler och deklarerar vilka BWRA-risker den adresserar. Dessa deklarationer används av BWRA för automatisk härleding av kontroller.",
                addModel: "Lägg till modell",
                editModel: "Redigera modell",
                saveModel: "Spara modell",
                cancel: "Avbryt",
                deleteModel: "Ta bort",
                confirmDelete: "Är du säker på att du vill ta bort modellen \"{{name}}\"?",
                noModels: "Inga modeller definierade. Lägg till en modell för att börja.",
                modelId: "Modell-ID",
                modelName: "Modellnamn",
                modelNamePlaceholder: "t.ex. Detektion av höga transaktionsvärden",
                description: "Beskrivning",
                descriptionPlaceholder: "Beskriv syfte och räckvidd för denna övervakningsmodell...",
                status: "Status",
                status_active: "Aktiv",
                status_draft: "Utkast",
                status_deprecated: "Avvecklad",
                linkedRules: "Kopplade regler",
                linkedRulesCount: "{{count}} regler",
                declaredRisks: "Deklarerade BWRA-risker",
                declaredRisksCount: "{{count}} risker",
                lastModified: "Senast ändrad",
                nameRequired: "Modellnamn krävs.",
                selectRules: "Koppla övervakningsregler",
                noRulesAvailable: "Inga regler dokumenterade. Gå till fliken Regler för att lägga till övervakningsregler.",
                selectRisks: "Deklarera adresserade BWRA-risker",
                noRisksAvailable: "Inga risker i BWRA. Lägg till risker i BWRA-modulen först.",
                actions: "Åtgärder",
            },
            thresholds: {
                title: "Regulatoriska tröskelvärden",
                subtitle: "Dokumentera regulatoriska tröskelvärden som styr obligatorisk rapportering och eskalering. Exempel: STR/SAR-gränser för kontanter, gränsvärden för rapportering av överföringar.",
                addThreshold: "Lägg till tröskel",
                editThreshold: "Redigera tröskel",
                saveThreshold: "Spara tröskel",
                cancel: "Avbryt",
                deleteThreshold: "Ta bort",
                confirmDelete: "Är du säker på att du vill ta bort tröskeln \"{{name}}\"?",
                noThresholds: "Inga tröskelvärden definierade. Lägg till en tröskel för att börja.",
                thresholdId: "ID",
                name: "Namn",
                namePlaceholder: "t.ex. STR Kontantgräns",
                regulation: "Reglering / Grund",
                regulationPlaceholder: "t.ex. AMLD5 Art. 11(b)",
                amount: "Belopp",
                amountPlaceholder: "t.ex. 10000",
                currency: "Valuta",
                transactionType: "Transaktionstyp",
                description: "Beskrivning",
                descriptionPlaceholder: "Beskriv syftet med detta tröskelvärde...",
                enabled: "Aktiv",
                actions: "Åtgärder",
                nameRequired: "Namn på tröskel krävs.",
                transactionType_CASH: "Kontant",
                transactionType_TRANSFER: "Överföring",
                transactionType_ALL: "Alla typer",
                transactionType_WIRE: "Banköverföring",
                transactionType_CRYPTO: "Krypto",
            },
            validation: {
                title: "Modellvalidering",
                description: "Testa övervakningsregler mot exempeltransaktioner för att verifiera att de fungerar som förväntat. Kör regler mot testdata och se vilka regler som träffade eller missade.",
                noRules: "Inga regler dokumenterade ännu. Gå till fliken Regler för att lägga till övervakningsregler innan du kör validering.",
                selectRules: "Välj regler att testa",
                testData: "Testtransaktioner (JSON)",
                testDataHint: "Ange transaktioner som en JSON-array. Använd 'amount' för beloppsregler, 'count'/'frequency' för frekvensregler, 'description'/'type' för mönsterregler.",
                runValidation: "Kör validering",
                loadExample: "Läs in exempel",
                jsonInvalid: "Ogiltig JSON. Ange en giltig JSON-array med transaktioner.",
                noTransactions: "Inga transaktioner angivna.",
                results: "Valideringsresultat",
                saveResult: "Spara resultat",
                totalTransactions: "Testtransaktioner",
                totalHits: "Totalt träffar",
                totalMisses: "Totalt missar",
                rulesWithHits: "Regler med träffar",
                hits: "Träffar",
                misses: "Missar",
                hitRate: "Träffandel",
                skipped: "Hoppades över (inaktiv)",
                disabled: "inaktiv",
                hit: "träff",
                miss: "miss",
                hitsDetail: "Transaktioner som utlöste denna regel",
                missesDetail: "Transaktioner som inte utlöste denna regel",
                expand: "Detaljer",
                collapse: "Dölj",
                history: "Valideringshistorik",
                noHistory: "Inga valideringsresultat sparade ännu.",
                showHistory: "Visa historik",
                hideHistory: "Dölj historik",
                transactions: "transaktioner",
                delete: "Ta bort",
                confirmDelete: "Är du säker på att du vill ta bort detta valideringsresultat? Åtgärden kan inte ångras.",
                noThresholdDefined: "Ingen tröskel definierad",
                patternMatchFound: "Mönstermatch hittad",
                noPatternMatch: "Ingen mönstermatch",
                unknownRuleType: "Okänd regeltyp",
                historyLimitWarning: "Historiken närmar sig lagringsgränsen ({{count}}/{{limit}} körningar). Äldre körningar tas bort automatiskt när gränsen nås. Konfigurera gränsen under Admin → Inställningar → Granskning.",
                historyLimitReached: "Lagringsgränsen för historik nådd ({{limit}} körningar). Äldsta körningar tas bort automatiskt när nya resultat sparas. Konfigurera gränsen under Admin → Inställningar → Granskning.",
                exportCsv: "Exportera CSV",
                exportCsvTitle: "Exportera valideringsresultat som CSV",
            },
            alerts: {
                title: "Larmärenden",
                subtitle: "Ärendehantering för utlösta larm. Följ varje larm från initial detektion via utredning till SAR-rapport eller avslut.",
                newAlert: "Nytt larm",
                filterAll: "Alla",
                colId: "ID",
                colSubject: "Ämne",
                colRule: "Regel",
                colStatus: "Status",
                colCreated: "Skapad",
                statusNew: "Ny",
                statusInvestigating: "Under utredning",
                statusSar: "SAR-rapport",
                statusClosed: "Avslutad",
                createAlert: "Skapa larm",
                subject: "Ämne",
                subjectPlaceholder: "Kort beskrivning av larmet",
                subjectRequired: "Ämne är obligatoriskt",
                description: "Beskrivning",
                ruleLabel: "Kopplad regel",
                noRule: "— Ingen regel —",
                amount: "Belopp",
                currency: "Valuta",
                updateStatus: "Uppdatera status",
                comment: "Kommentar",
                commentRequired: "Kommentar krävs vid statusändringar",
                commentPlaceholder: "Lägg till en anteckning som förklarar statusändringen",
                statusRequired: "Välj en målstatus",
                update: "Uppdatera",
                cancel: "Avbryt",
                auditTrail: "Granskningslogg",
                noActivity: "Ingen aktivitet registrerad",
                deleteAlert: "Ta bort larm",
                confirmDelete: "Är du säker på att du vill ta bort larm {{id}}?",
                noAlerts: "Inga larm hittade.",
                alertCreated: "Larm skapat",
                statusUpdated: "Status uppdaterad",
                alertDeleted: "Larm borttaget",
                actionStartInvestigation: "Starta utredning",
                actionFileSar: "Inlämna SAR-rapport",
                actionClose: "Avsluta (falskt positiv)",
            },
            ruleTemplates: {
                title: "Regelmallar",
                description: "Förbyggda övervakningsregler för vanliga AML-scenarier. Använd en mall som utgångspunkt och anpassa efter ditt operativa system.",
                useTemplate: "Använd mall",
                cancelTemplate: "Avbryt",
                customizeRule: "Anpassa regel",
                saveAsRule: "Spara som ny regel",
                ruleSaved: "Regel skapad från mall.",
                category_ctr: "CTR",
                category_structuring: "Strukturering",
                category_highrisk: "Högriskjurisdiktion",
                category_pep: "PEP",
                category_largeamount: "Stor transaktion",
                tpl_ctr_name: "CTR — Kontanttransaktioner >15 000 EUR",
                tpl_ctr_description: "Identifierar kontanttransaktioner som överstiger den regulatoriska rapporteringsgränsen på 15 000 EUR (CTR-krav enligt 6AMLD).",
                tpl_structuring_name: "Strukturering — Uppdelade insättningar",
                tpl_structuring_description: "Detekterar potentiell strukturering (smurfing) genom att identifiera hög frekvens av transaktioner under rapporteringsgränsen.",
                tpl_highrisk_name: "Högriskjurisdiktioner — FATF-länder",
                tpl_highrisk_description: "Flaggar transaktioner med koppling till FATF:s grålistade eller svartlistade länder.",
                tpl_pep_name: "PEP — Politiskt exponerade personer",
                tpl_pep_description: "Identifierar transaktioner med potentiell koppling till politiskt exponerade personer (PEP).",
                tpl_large_name: "Ovanligt stora transaktioner >50 000 EUR",
                tpl_large_description: "Flaggar transaktioner som väsentligt överstiger normalt transaktionsmönster för privatpersoner.",
            },
            rulePerformance: {
                title: "Regelprestandaanalys",
                buttonTitle: "Prestandaanalys",
                close: "Stäng",
                fpRate: "Falskt positiv-andel",
                detectionRate: "Detektionsgrad",
                triggersPerDay: "Utlösningar / dag (30d)",
                triggersPerWeek: "Utlösningar / vecka",
                totalCases: "Totalt antal larmärenden",
                sarCases: "STR inlämnade",
                closedCases: "Avslutade (falskt positiv)",
                triggersLast30Days: "Utlösningar (senaste 30 dagarna)",
                documentedAlerts: "Dokumenterade larm (alla perioder)",
                documentedSar: "STR",
                highFpWarning: "Falskt positiv-andelen överstiger 80% — överväg tröskeloptimering",
                sourceDocumented: "Mätvärden baserade på dokumenterad prestandadata (fliken Larmprestation).",
                sourceCases: "Mätvärden baserade på larmärenden i systemet. Ingen dokumenterad prestandadata tillgänglig.",
                sourceNone: "Ingen data tillgänglig. Lägg till larmärenden eller dokumentera prestandadata för att se mätvärden.",
            },
            thresholdTuning: {
                title: "Tröskeltunigassistent",
                buttonTitle: "Optimera trösklar",
                close: "Stäng",
                currentThreshold: "Nuvarande tröskel",
                suggestedThreshold: "Föreslagen tröskel",
                expectedImpact: "Förväntad effekt",
                fpChange: "Falskt positiv-andel",
                detectionChange: "Detektionsgrad",
                applyThreshold: "Tillämpa föreslagen tröskel",
                keepCurrent: "Behåll nuvarande tröskel",
                disclaimer: "Förslagen är uppskattningar baserade på historisk prestandadata. Granska med ditt complianceteam innan du tillämpar.",
                reason_noRule: "Regel hittades inte.",
                reason_patternRule: "Tröskeloptimering är inte tillämplig på mönsterbaserade regler.",
                reason_noThreshold: "Den här regeln har inget numeriskt tröskelvärde konfigurerat.",
                reason_noData: "Inte tillräckligt med prestandadata för att generera ett förslag. Lägg till larmärenden eller dokumentera prestandadata först.",
                reason_highFpCritical: "Falskt positiv-andelen överstiger 80 %. En höjning av tröskeln rekommenderas starkt för att minska analytikernas arbetsbelastning.",
                reason_highFpModerate: "Falskt positiv-andelen överstiger 60 %. En måttlig höjning av tröskeln bör minska bruset utan att påverka detektionen.",
                reason_highTriggerRate: "Utlösningsvolymen överstiger 20 larm per dag. En höjning av tröskeln minskar analytikernas arbetsbelastning.",
                reason_lowDetection: "Detektionsgraden är under 30 % trots rimlig utlösningsvolym. En sänkning av tröskeln kan fånga fler sanna positiva.",
                reason_lowTriggerRate: "Färre än 0,5 utlösningar per dag — tröskeln kan vara för hög. En sänkning kan synliggöra relevanta transaktioner.",
                reason_performanceGood: "Nuvarande tröskelns prestanda är inom acceptabelt intervall. Ingen justering rekommenderas för tillfället.",
            },
            scenarioTest: {
                title: "Scenariobaserad testning",
                description: "Testa dina regler mot realistiska fördefinierade scenarier för att verifiera att de fångar kända penningtvättsstrategier som strukturering, smurfing och handelsbaserad tvättning.",
                noRules: "Inga övervakningsregler definierade. Lägg till regler i fliken Regler innan du kör scenariotester.",
                selectScenario: "Välj ett scenario",
                selectScenarioHint: "Klicka på ett scenario för att köra det mot alla dina övervakningsregler. Resultaten visar direkt vilka regler som utlöses.",
                resultsFor: "Resultat för",
                totalTransactions: "Transaktioner",
                rulesTriggered: "Utlösta regler",
                rulesNotTriggered: "Ej utlösta regler",
                totalHits: "Totalt antal träffar",
                triggeredRules: "Utlösta regler",
                notTriggeredRules: "Ej utlösta",
                hitsOf: "träffar",
                transactions: "transaktioner",
                skippedRules: "{{count}} inaktiverad(e) regel(er) hoppades över.",
                showTransactions: "Visa scenariotransaktioner",
                txAmount: "Belopp",
                txType: "Typ",
                txCount: "Antal",
                txDescription: "Beskrivning",
                risk_high: "Hög risk",
                risk_medium: "Medelhög risk",
                scenarios: {
                    structuring: {
                        name: "Strukturering",
                        description: "5 kontantinsättningar strax under gränsen 10 000 EUR — ett klassiskt struktureringsmönster (uppdelning/layering).",
                        expectedTriggers: "Förväntas utlösa: AMOUNT-regler med tröskel ≤ 9 750 EUR och FREQUENCY-regler med tröskel ≤ 5.",
                    },
                    smurfing: {
                        name: "Smurfing",
                        description: "8 små överföringar från olika konton plus en aggregerad transaktion på totalt 25 500 EUR. Typiskt smurfing-nätverksbeteende.",
                        expectedTriggers: "Förväntas utlösa: FREQUENCY-regler med tröskel ≤ 8, AMOUNT-regler med tröskel ≤ 3 000 EUR (individuell) och AMOUNT-regler ≤ 25 500 EUR (aggregat).",
                    },
                    tbml: {
                        name: "Handelsbaserad tvättning",
                        description: "3 import-/exporttransaktioner med högriskjurisdiktioner (Iran, Myanmar, Nordkorea) och förhöjda belopp.",
                        expectedTriggers: "Förväntas utlösa: PATTERN-regler för högriskjurisdiktioner (iran, myanmar, nordkorea) och AMOUNT-regler med tröskel ≤ 12 000 EUR.",
                    },
                    pep: {
                        name: "PEP-transaktioner",
                        description: "3 transaktioner med beskrivningar som tyder på politiskt exponerade personer — politikerbetalningar, ministerarvoden och diplomathonorar.",
                        expectedTriggers: "Förväntas utlösa: PATTERN-regler för PEP-nyckelord (pep, politiker, minister, diplomat) och AMOUNT-regler med tröskel ≤ 30 000 EUR.",
                    },
                    large_cash: {
                        name: "Stora kontanttransaktioner",
                        description: "2 ovanligt stora kontantinsättningar på 75 000 EUR och 50 000 EUR, som väsentligt överstiger normala transaktionsmönster.",
                        expectedTriggers: "Förväntas utlösa: AMOUNT-regler med tröskel ≤ 50 000 EUR.",
                    },
                },
            },
        },
        periodicReporting: {
            title: "Periodisk rapportering",
            subtitle: "Generera, hantera och exportera periodiska AML-efterlevnadsrapporter.",
            tabAnnual: "Årsrapporter",
            tabComparison: "Jämförelsevy",
            tabExport: "Exportera",
            tabSchedule: "Schemaläggning",
            annual: {
                title: "Årsrapporter",
                description: "Skapa och hantera årliga AML-riskbedömningsrapporter. Följ utkast, slutförda och inlämnade rapporter.",
                draftReports: "Utkast",
                finalizedReports: "Slutförda rapporter",
                submittedReports: "Inlämnade rapporter",
                noData: "Inga rapporter tillgängliga. Skapa en årsrapport för att komma igång.",
                createReport: "Skapa årsrapport",
                year: "År",
                selectYear: "Välj år...",
                invalidYear: "Ange ett giltigt år (2000–2100)",
                status: "Status",
                status_draft: "Utkast",
                status_review: "Under granskning",
                status_approved: "Godkänd",
                filterByStatus: "Filtrera på status",
                allStatuses: "Alla",
                actions: "Åtgärder",
                completion: "Ifyllnadsgrad",
                completionEmpty: "Tom",
                completionFull: "Komplett",
                view: "Visa",
                deleteReport: "Ta bort",
                confirmDelete: "Är du säker på att du vill ta bort rapporten för {{year}}?",
                reportYear: "Rapportår",
                yearAlreadyExists: "En rapport för {{year}} finns redan.",
                form: {
                    title: "Rapport {{year}}",
                    backToList: "Tillbaka till listan",
                    save: "Spara",
                    saved: "Sparat",
                    unsavedChanges: "Osparade ändringar",
                    submitForReview: "Skicka för granskning",
                    approve: "Godkänn",
                    rejectWithComment: "Avvisa (återgå till utkast)",
                    rejectionComment: "Kommentar vid avvisning",
                    rejectionCommentPlaceholder: "Förklara varför rapporten återgår till utkast...",
                    rejectConfirm: "Avvisa rapport",
                    rejectionCommentRequired: "En kommentar krävs vid avvisning av en rapport.",
                    approvalHistory: "Godkännandehistorik",
                    noHistory: "Inga statusövergångar ännu.",
                    lockedMessage: "Denna rapport är godkänd och låst. Inga ytterligare ändringar kan göras.",
                    lockedBadge: "Låst",
                    historyFrom: "från",
                    historyTo: "till",
                    historyBy: "av",
                    historyDate: "Datum",
                    historyComment: "Kommentar",
                    allApprovalStepsCompleted: "Alla {{count}} godkännandesteg klara",
                    approvalFlowTitle: "Godkännandeflöde",
                    multiStepFlowTitle: "Flerstegs-godkännandeflöde",
                    toastSubmitted: "Rapport skickad för granskning.",
                    toastApproved: "Rapport godkänd.",
                    toastRejected: "Rapport återgick till utkast.",
                    toastStepApproved: "Steg godkänt. Går vidare till nästa steg.",
                    toastAllStepsApproved: "Alla steg godkända. Rapporten är nu låst.",
                    distributionLogTitle: "Distribution",
                    distributionLogSentAt: "Distribuerad",
                    distributionLogSentBy: "Skickad av",
                    distributionLogRecipients: "Mottagare",
                    distributionLogNoRecipients: "Ingen distributionslista konfigurerad. Konfigurera mottagare i Admin → Distributionslista.",
                    distributionLogStatusSent: "Skickad",
                    distributionLogRole: "Roll",
                    stepLabel: "Steg",
                    stepSubmitted: "Inskickad",
                    stepStatus_pending: "Väntar",
                    stepStatus_approved: "Godkänt",
                    stepStatus_rejected: "Avvisat",
                    currentStep: "Aktuellt steg",
                    approveStep: "Godkänn detta steg",
                    approveComment: "Kommentar (valfritt)",
                    approveCommentPlaceholder: "Lägg till en notering för detta godkännandesteg...",
                    stepApprovedBy: "Godkänt av",
                    sectionGeneral: "Allmän information",
                    responsiblePerson: "Ansvarig person",
                    responsiblePersonPlaceholder: "Fullständigt namn",
                    reportingDate: "Rapportdatum",
                    notes: "Anteckningar",
                    notesPlaceholder: "Allmänna anteckningar för denna rapporteringsperiod...",
                    sectionCustomers: "Kundstatistik",
                    totalCustomers: "Totalt antal kunder",
                    highRiskCustomers: "Högrisk-kunder",
                    pepCustomers: "PEP-kunder",
                    newCustomers: "Nya kunder",
                    terminatedCustomers: "Avslutade kundrelationer",
                    sectionTransactions: "Transaktionsövervakning",
                    reviewedTransactions: "Granskade transaktioner",
                    flaggedTransactions: "Flaggade transaktioner",
                    flaggedTransactionsValue: "Värde på flaggade transaktioner (SEK)",
                    sectionReporting: "Rapportering",
                    strFiled: "Inlämnade STR (misstänkta transaktionsrapporter)",
                    largeAmountReports: "Inlämnade kontantrapporter",
                    sectionTraining: "Utbildning",
                    trainedEmployees: "Utbildade medarbetare",
                    avgTrainingHours: "Genomsnittliga utbildningstimmar per anställd",
                    sectionRiskAssessment: "Riskbedömning",
                    overallRiskLevel: "Övergripande risknivå",
                    deficienciesFound: "Identifierade brister",
                    deficienciesFoundPlaceholder: "Beskriv identifierade brister...",
                    actionsTaken: "Vidtagna åtgärder",
                    actionsTakenPlaceholder: "Beskriv vidtagna åtgärder för att åtgärda brister...",
                    plannedActions: "Planerade åtgärder",
                    plannedActionsPlaceholder: "Beskriv planerade framtida åtgärder...",
                    sectionAttachments: "Internt stödmaterial",
                    attachmentsInternalNote: "Internt referensmaterial — inkluderas inte i rapportexporten.",
                    addAttachment: "Lägg till bilaga",
                    attachmentName: "Filnamn",
                    attachmentSize: "Storlek",
                    attachmentDate: "Uppladdad",
                    attachmentUploader: "Uppladdad av",
                    attachmentUploadedBy: "Av: {{user}}",
                    attachmentVersionCount: "{{count}} versioner",
                    attachmentShowHistory: "Versionshistorik",
                    attachmentHideHistory: "Dölj historik",
                    attachmentAddVersion: "Ladda upp ny version",
                    attachmentVersionHistory: "Versionshistorik",
                    attachmentVersionNum: "Version",
                    attachmentLatest: "Senaste",
                    noAttachments: "Inga bilagor",
                    deleteAttachment: "Ta bort",
                    sectionMeta: "Rapportmetadata",
                    financialEntityId: "FI Entitets-ID",
                    consolidationScope: "Konsolideringsomfattning",
                    consolidationConsolidated: "Konsoliderat",
                    consolidationIndividual: "Enskilt",
                    expandAll: "Expandera alla",
                    collapseAll: "Komprimera alla",
                    sectionCount: "{{count}} avsnitt",
                    autoFillBtn: "Fyll i automatiskt",
                    autoFillTitle: "Data från andra moduler",
                    autoFillClose: "Stäng",
                    autoFillApply: "Applicera",
                    autoFillApplyAll: "Applicera alla",
                    autoFillApplied: "Applicerad",
                    autoFillAllApplied: "Alla förslag applicerade.",
                    autoFillSource: "Källa: {{source}} • {{date}}",
                    autoFillGeoRiskTitle: "Geografisk risk (GeoRisk)",
                    autoFillBwraTitle: "Verksamhetsövergripande riskbedömning (BWRA)",
                    autoFillCustomerRiskTitle: "Kundrisker",
                    autoFillHighRiskCountries: "{{count}} högriskländer identifierade",
                    autoFillNoHighRiskCountries: "Inga högriskländer identifierade",
                    autoFillRiskSummary: "{{total}} aktiva risker, {{high}} bedömda som hög/mycket hög",
                    autoFillNoRisks: "Inga risker registrerade i BWRA",
                    autoFillModelStatus: "{{count}} sparade modeller, {{params}} parametrar",
                    autoFillNoModels: "Inga kundrisksmodeller sparade",
                    autoFillFieldB13: "B13: Länder bedömda som hög risk",
                    autoFillFieldB14: "B14: Beaktas högrisktredjeländer → Ja",
                    autoFillFieldB1: "B1: Allmän riskbedömning genomförd → Ja",
                    autoFillFieldB2: "B2: Senaste uppdatering av riskbedömning",
                    autoFillSourceBadge: "Autofylld från {{source}}",
                    autoFillActiveIndexes: "{{count}} aktiva index",
                    autoFillLastUpdated: "Senast uppdaterad: {{date}}",
                    autoFillNoFieldMappings: "Inga direkta fältförslag tillgängliga",
                    autoFillOverrideNote: "Värden kan redigeras efter att ha applicerats.",
                },
            },
            comparison: {
                title: "Jämförelsevy",
                description: "Jämför riskmätvärden över rapporteringsperioder för att identifiera trender och förändringar.",
                comingSoon: "Periodjämförelse kommer att vara tillgänglig i en framtida version.",
                needMoreReports: "Skapa minst 2 årsrapporter för att aktivera flerårig jämförelse.",
                selectYears: "Välj år att jämföra:",
                filterSection: "Filtrera per avsnitt:",
                allSections: "Alla avsnitt",
                field: "Fält",
                selectAtLeastTwo: "Välj minst två år för att jämföra.",
                legendUp: "Ökning jämfört med föregående år",
                legendDown: "Minskning jämfört med föregående år",
                legendSame: "Ingen förändring",
                legendChanged: "Värde ändrat",
                deltaNote: "Numeriska fält visar absolut och procentuell förändring jämfört med föregående år",
                dynRowsCount: "Antal poster",
                dynRowsBadge: "Dynamiska rader",
                exportCsv: "Exportera CSV",
                csvSection: "Avsnitt",
                exportCsvFilename: "jamforelse_{{years}}.csv",
                showChangedOnly: "Visa bara ändrade rader",
                showAll: "Visa alla rader",
                changedRowsSummary: "{{changed}} av {{total}} fält ändrade",
                noChangedRows: "Inga skillnader hittades mellan de valda åren.",
                unchangedIndicator: "= Ingen ändring",
            },
            export: {
                title: "Exportera rapporter",
                description: "Exportera godkända rapporter till JSON för regulatorisk inlämning och intern granskning.",
                noReports: "Inga rapporter tillgängliga för export. Skapa en årsrapport först.",
                tableYear: "År",
                tableStatus: "Status",
                tableUpdated: "Senast uppdaterad",
                tableActions: "Åtgärder",
                exportReport: "Exportera JSON",
                exportAll: "Exportera alla godkända",
                noApproved: "Inga godkända rapporter att exportera.",
                exportedAt: "Exporterad",
                successMessage: "Rapport {{year}} exporterades.",
                exportAllSuccess: "{{count}} godkänd(a) rapport(er) exporterades.",
                fiExportBtn: "Exportera till FI JSON (v4.0)",
                fiExportSuccess: "FI JSON-export för {{year}} slutfördes.",
                fiValidationTitle: "Valideringsvarning",
                fiValidationWarning: "Följande obligatoriska fält är tomma. Filen genereras ändå men kan avvisas av Finansinspektionen.",
                fiMissingFieldsLabel: "Saknade fält:",
                fiExportAnyway: "Exportera ändå",
                fiSchemaFieldsMissing: "{{count}} obligatoriska datafält i rapportschemat är tomma.",
                fiSchemaFieldsLabel: "Saknade schemafält:",
                fiAndMore: "...och {{count}} till",
                fiFieldLabel_reportingDate: "Rapporteringsdatum",
                fiFieldLabel_financialEntityId: "Finansiellt entitets-ID",
                fiFieldLabel_consolidationScope: "Konsolideringsomfång",
            },
            schema: {
                sectionA0101: "A. Allmän information om företaget",
                sectionA0102: "A. Verksamhet per land",
                sectionA0201: "B. Riskbedömning och rutiner",
                sectionA0301: "C. Kundstock",
                sectionA0401: "D. Transaktionsövervakning",
                sectionA0501: "E. Sanktioner",
                sectionA0601: "F. Utbildning",
                sectionA0701: "G. Gränsöverskridande transaktioner",
                sectionA0702: "G. Gränsöverskridande transaktioner per land",
                sectionA0801: "H. Frysta tillgångar",
                groupHeadingA0301_010: "Antal kunder med skatterättslig hemvist i Sverige",
                groupHeadingA0301_030: "Antal kunder med skatterättslig hemvist i EU/EES (ej Sverige)",
                groupHeadingA0301_050: "Antal kunder med skatterättslig hemvist utanför EU/EES",
                groupHeadingA0301_070: "Hur många kunder bedöms som hög risk?",
                groupHeadingA0301_090: "Antal kunder som är identifierade som PEP, familjemedlem till PEP eller känd medarbetare till PEP",
                groupHeadingA0301_150: "Etablerade affärsförbindelser",
                groupHeadingA0301_160: "För hur stort antal av företagets etablerade affärsförbindelser saknas aktuella och tillräckliga uppgifter för kundkännedom?",
                groupHeadingA0301_180: "I förekommande fall för banker: korrespondentförbindelser ni tillhandahåller, med skatterättslig hemvist i:",
                groupHeadingA0301_210: "I förekommande fall för banker: korrespondentförbindelser ni innehar, med skatterättslig hemvist i:",
                addRow: "Lägg till rad",
                deleteRow: "Ta bort",
                noRows: "Inga rader ännu. Klicka 'Lägg till rad' för att lägga till.",
                schemaVersion: "Schema: AML v{{version}}",
                searchPlaceholder: "Sök...",
                selected: "valda",
                showMore: "... och {{count}} till. Sök för att filtrera.",
                selectedItems: "Valda:",
                removeItem: "Ta bort",
            },
            validation: {
                required: "Obligatoriskt fält",
                invalidNumber: "Måste vara ett tal",
                minValue: "Minsta värde: {{min}}",
                maxValue: "Högsta värde: {{max}}",
                crossFieldLte: "{{fiId}} får inte överstiga {{refFiId}} (angivet: {{value}}, gräns: {{max}})",
                crossFieldRequiredIf: "{{fiId}} är obligatoriskt när {{condFiId}} har ett värde",
            },
            comments: {
                toggleTitle: "Kommentarer",
                noComments: "Inga kommentarer ännu.",
                placeholder: "Skriv en kommentar... (Ctrl+Enter för att spara)",
                save: "Spara",
                close: "Stäng",
                delete: "Ta bort kommentar",
            },
            interpretationComment: {
                label: "Tolkningskommentar",
                toggle: "Tolkningskommentar",
                placeholder: "Dokumentera hur er organisation tolkar denna fråga (för internt bruk)...",
                hint: "Sparas tillsammans med rapporten. Ärvs av nya rapporter.",
            },
            regulatory: {
                toggleTitle: "Visa regulatoriska krav",
                showReqs: "Regulatorisk grund",
                coverageTitle: "Regulatorisk täckningskontroll",
                coverageSuffix: "regelverk fullt täckta",
                coverageDesc: "Visar om obligatoriska fält för varje regulatoriskt krav är ifyllda.",
                fieldsFilledSuffix: "obligatoriska fält ifyllda",
                reqs: {
                    FFFS_kap2: "Kundidentifiering och riskbedömning",
                    FFFS_kap3: "Kundkännedom (KYC)",
                    FFFS_kap4: "Skärpt kundkännedom",
                    FFFS_kap5: "Sanktionsscreening",
                    FFFS_kap6: "Geografisk risk och gränsöverskridande verksamhet",
                    FFFS_kap7: "Transaktionsövervakning och STR-rapportering",
                    FFFS_kap8: "Personalutbildning och kompetens",
                    AMLD4_Art6: "Nationell riskbedömning (NRA)",
                    AMLD4_Art13: "Kundkännedomsåtgärder",
                    AMLD4_Art17: "Skärpta åtgärder för högriskstater utanför EU",
                    AMLD4_Art30: "Information om verkliga huvudmän (juridiska personer)",
                    AMLD4_Art33: "Rapportering av misstänkta transaktioner",
                    AMLD4_Art46: "Personalutbildning",
                    AMLD4_Art47: "Efterlevnad av sanktioner",
                },
            },
            schedule: {
                title: "Rapportschema",
                description: "Konfigurera automatisk skapande av årsrapportutkast. När det är aktiverat skapas ett nytt utkast automatiskt på det schemalagda datumet varje år.",
                enabledLabel: "Aktivera automatisk rapportering",
                enabledDescription: "Skapar automatiskt ett nytt rapportutkast på det schemalagda datumet varje år.",
                monthLabel: "Skapandemånad",
                dayLabel: "Dag i månaden",
                targetYearLabel: "Rapporten avser",
                targetYearCurrentYear: "Innevarande år",
                targetYearPreviousYear: "Föregående år",
                assigneesLabel: "Notifiera användare (kommaseparerat)",
                assigneesPlaceholder: "t.ex. användare1, användare2",
                save: "Spara schema",
                saved: "Schema sparat",
                nextRunLabel: "Nästa schemalagda körning",
                lastAutoCreatedLabel: "Senast automatiskt skapad rapport",
                lastAutoCreatedValue: "Rapport {{year}} skapad {{date}}",
                noLastAutoCreated: "Inga rapporter har skapats automatiskt ännu.",
                autoCreatedBanner: "Ett rapportutkast för {{year}} skapades automatiskt.",
                months: {
                    "1": "Januari", "2": "Februari", "3": "Mars", "4": "April",
                    "5": "Maj", "6": "Juni", "7": "Juli", "8": "Augusti",
                    "9": "September", "10": "Oktober", "11": "November", "12": "December",
                },
            },
        },
        administration: {
            welcome: "Administrationsmodul",
            chooseModule: "Välj en modul i sidomenyn för att börja.",
            tabs: {
                overview: "Översikt",
                audit: "Granskningslogg",
                users: "Användare",
                entities: "Entiteter",
                settings: "Inställningar",
                licenses: "Licenser",
                validation: "Valideringsschema",
                activity: "Aktivitetsöversikt",
                export: "Regulatorisk export",
                approval: "Godkännandeflöde",
                distribution: "Distributionslista",
                manuals: "Användarmanualer",
                backup: "Säkerhetskopiering",
                systemHealth: "Systemhälsa",
                reportTemplates: "Rapportmallar",
                retention: "Datalagring",
                dataIsolation: "Dataisolering",
                userImport: "Användarimport",
                apiKeys: "API-nycklar",
                dataSources: "Datakällor",
                dataMigration: "Datamigrering",
                configTemplates: "Konfigurationsmallar",
                notificationSettings: "Notifieringar",
                entityMigration: "Entitetsmigrering",
                ssoConfig: "SSO / Identitet"
            },
            sections: {
                overview: { label: "Översikt", description: "Modulstatus, granskningsloggar och systemaktivitet" },
                usersAccess: { label: "Användare & Åtkomst", description: "Användarhantering, roller, import och arbetsflöden" },
                entities: { label: "Entiteter", description: "Entitetshantering, hierarki och licenser" },
                dataIntegration: { label: "Data & Integration", description: "Datakällor, migrering, isolering och lagring" },
                configuration: { label: "Konfiguration", description: "Inställningar, mallar, validering och notifieringar" },
                help: { label: "Hjälp", description: "Användarmanualer och dokumentation" },
            },
            toolDescriptions: {
                overview: "Modulstatistik och systemstatus i korthet",
                audit: "Bläddra och filtrera i systemets granskningslogg",
                activity: "Användartrender och inloggningsstatistik",
                systemHealth: "Övervaka tjänster, lagring och prestanda",
                users: "Skapa, redigera och hantera användarkonton och roller",
                userImport: "Massimportera användare från CSV eller katalog",
                approval: "Konfigurera flerstegs godkännandeflöden för rapporter",
                distribution: "Hantera mottagarlistor för rapportdistribution",
                entities: "Lägg till och redigera juridiska entiteter och hierarki",
                entityMigration: "Flytta data mellan entiteter på ett säkert sätt",
                licenses: "Tilldela och spåra modullicenser per entitet",
                dataSources: "Registrera externa databas- och API-anslutningar",
                dataMigration: "Migrera och transformera data mellan källor",
                dataIsolation: "Verifiera att entitetens datagränser är korrekta",
                backup: "Skapa, schemalägg och återställ systemsäkerhetskopior",
                retention: "Konfigurera hur länge data lagras per modul",
                settings: "Systemövergripande konfiguration och modulväxlar",
                configTemplates: "Återanvändbara konfigurationsmallar",
                notificationSettings: "Konfigurera aviseringar och notifieringskanaler",
                reportTemplates: "Redigera rapportlayout och varumärkesmallar",
                validation: "Schemalägg automatiska modellvalideringskörningar",
                export: "Exportera data för regulatoriska inlämningar",
                apiKeys: "Hantera API-nycklar för externa integrationer",
                manuals: "Ladda ner användarmanualer och snabbreferensguider",
                ssoConfig: "Konfigurera SSO med Azure AD, Okta, ADFS eller annan identitetsleverantör",
            },
            backToSection: "Tillbaka till {{section}}",
            ssoConfig: {
                title: "SSO & Identitetsleverantör",
                subtitle: "Konfigurera Single Sign-On (SSO) för din organisation via OIDC eller SAML 2.0",
                mockNotice: "Arkitekturläge: Konfigurationen sparas lokalt. En riktig IdP-integration kräver en backend-SSO-proxy. Kontakta ditt driftteam för att koppla upp mot Azure AD, Okta eller ADFS.",
                providerSelection: "Autentiseringsleverantör",
                active: "Aktiv",
                testConnection: "Testa anslutning",
                testing: "Testar...",
                reset: "Återställ",
                save: "Spara",
                saving: "Sparar...",
                savedSuccess: "SSO-konfiguration sparad",
                testSuccess: "Anslutningstest lyckades",
                testFailed: "Anslutningstest misslyckades",
                testResultOk: "Anslutningen lyckades — IdP svarade som förväntat",
                testResultFail: "Anslutningen misslyckades — kontrollera din konfiguration",
                providers: {
                    local: {
                        label: "Lokala konton",
                        desc: "Användare autentiseras med användarnamn och lösenord som lagras i systemet",
                        activeTitle: "Lokal autentisering är aktiv",
                        activeDesc: "Användare loggar in med sina lokala inloggningsuppgifter. För att aktivera SSO, välj OIDC eller SAML 2.0 ovan och spara konfigurationen."
                    },
                    oidc: {
                        label: "OIDC / OAuth 2.0",
                        desc: "OpenID Connect — Azure AD, Okta, Google, Auth0"
                    },
                    saml: {
                        label: "SAML 2.0",
                        desc: "SAML-federation — ADFS, Shibboleth, PingFederate"
                    }
                },
                oidc: {
                    title: "OpenID Connect-konfiguration",
                    desc: "Ange discovery-URL och klientuppgifter från din identitetsleverantör.",
                    discoveryUrl: "Discovery-URL",
                    discoveryUrlHint: "Slutpunkten /.well-known/openid-configuration hos din IdP",
                    clientId: "Klient-ID",
                    clientIdPlaceholder: "t.ex. a1b2c3d4-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
                    clientSecret: "Klienthemlighet",
                    redirectUri: "Omdirigerings-URI",
                    scope: "Scope",
                    scopeHint: "Blankstegsavgränsad lista med OAuth-scope"
                },
                saml: {
                    title: "SAML 2.0-konfiguration",
                    desc: "Ange entitets-ID och IdP-metadata-URL från din identitetsleverantör.",
                    entityId: "Tjänsteleverantörens entitets-ID",
                    entityIdHint: "Unik identifierare för den här applikationen (vanligtvis en URL)",
                    idpMetadataUrl: "IdP Metadata-URL",
                    idpMetadataUrlHint: "URL:en där din IdP publicerar sin SAML-metadata",
                    acsUrl: "Assertion Consumer Service URL",
                    nameIdFormat: "NameID-format",
                    nameIdEmail: "E-postadress",
                    nameIdPersistent: "Beständig",
                    nameIdTransient: "Tillfällig",
                    nameIdUnspecified: "Ospecificerad"
                },
                errors: {
                    missing_required_fields: "Obligatoriska fält saknas",
                    invalid_discovery_url: "Discovery-URL måste börja med https://",
                    invalid_metadata_url: "Metadata-URL måste börja med https://",
                    unsupported_provider: "Providertypen stöds inte"
                },
                docs: {
                    title: "Identitetsleverantörer som stöds",
                    azureAD: "Azure Active Directory (Entra ID) — OIDC och SAML 2.0",
                    okta: "Okta — OIDC och SAML 2.0",
                    adfs: "Active Directory Federation Services (ADFS) — SAML 2.0"
                }
            },
            dataSources: {
                title: "Datakällor",
                subtitle: "Registrera och hantera externa datakällor. Testa anslutning och konfigurera inloggningsuppgifter för varje källa.",
                addBtn: "Lägg till datakälla",
                addTitle: "Lägg till datakälla",
                editTitle: "Redigera datakälla",
                labelName: "Namn",
                namePlaceholder: "t.ex. Produktions-Supabase, Kärnbankens REST-API...",
                labelType: "Typ",
                type_supabase: "Supabase",
                type_postgres: "PostgreSQL",
                type_mssql: "SQL Server (MSSQL)",
                type_rest: "REST API",
                type_csv: "CSV-fil",
                type_mock: "Mock (Demo)",
                field_url: "Projekt-URL",
                field_url_hint: "https://xxxx.supabase.co",
                field_anonKey: "Anon- / tjänstenyckel",
                field_anonKey_hint: "Supabase anon eller service role-nyckel",
                field_host: "Värd",
                field_host_hint: "t.ex. db.example.com eller 10.0.0.1",
                field_port: "Port",
                field_port_hint: "t.ex. 5432",
                field_database: "Databas",
                field_database_hint: "Databasnamn",
                field_user: "Användarnamn",
                field_user_hint: "Databasanvändare",
                field_password: "Lösenord",
                field_password_hint: "Databaslösenord",
                field_baseUrl: "Bas-URL",
                field_baseUrl_hint: "https://api.example.com",
                field_token: "Bearer-token",
                field_token_hint: "Valfri auktoriseringstoken",
                colName: "Namn",
                colType: "Typ",
                colStatus: "Status",
                colLastTested: "Senast testad",
                colActions: "Åtgärder",
                statusOk: "OK",
                statusError: "Fel",
                statusUnknown: "Ej testad",
                testBtn: "Testa anslutning",
                testing: "Testar…",
                editBtn: "Redigera",
                deleteBtn: "Ta bort",
                cancel: "Avbryt",
                saveBtn: "Spara",
                emptyTitle: "Inga datakällor ännu",
                emptyDesc: "Lägg till din första datakälla för att ansluta Prime RMS till externa databaser eller API:er.",
                loading: "Läser in…",
                createdToast: "Datakällan \"{{name}}\" skapades.",
                updatedToast: "Datakällan \"{{name}}\" uppdaterades.",
                deletedToast: "Datakällan \"{{name}}\" togs bort.",
                testOk: "Anslutning lyckades ({{ms}} ms)",
                testFail: "Anslutning misslyckades: {{error}}",
                testOkToast: "\"{{name}}\" — ansluten ({{ms}} ms)",
                testFailToast: "\"{{name}}\" — anslutning misslyckades: {{error}}",
                confirmDeleteMsg: "Ta bort datakällan \"{{name}}\"? Åtgärden kan inte ångras.",
                errorNameRequired: "Namn är obligatoriskt.",
                testSaveFirst: "Spara datakällan först, testa sedan anslutningen.",
                saveError: "Det gick inte att spara datakällan."
            },
            distribution: {
                title: "Rapportdistributionslista",
                subtitle: "Konfigurera vilka som tar emot godkända rapporter. När en rapport är fullt godkänd notifieras alla aktiva mottagare och leveransen spåras.",
                addRecipient: "Lägg till mottagare",
                nameLabel: "Namn",
                namePlaceholder: "t.ex. Compliance-ansvarig",
                emailLabel: "E-post",
                emailPlaceholder: "t.ex. cco@example.com",
                roleLabel: "Roll",
                role_board: "Styrelseledamot",
                role_auditor: "Extern revisor",
                role_compliance: "Compliance-ansvarig",
                role_regulator: "Tillsynsmyndighet",
                role_other: "Övrigt",
                removeRecipient: "Ta bort",
                noRecipients: "Inga mottagare konfigurerade. Lägg till mottagare för att aktivera distribution när rapporter godkänns.",
                savedToast: "Distributionslista sparad.",
                errorNameRequired: "Mottagarens namn är obligatoriskt.",
                errorEmailRequired: "En giltig e-postadress krävs.",
                tableHeaderName: "Namn",
                tableHeaderEmail: "E-post",
                tableHeaderRole: "Roll",
                tableHeaderActions: "Åtgärder",
                addSectionHeading: "Lägg till mottagare",
                recipientsSectionHeading: "Mottagare ({{count}})",
            },
            approval: {
                title: "Konfiguration av godkännandeflöde",
                subtitle: "Konfigurera flerstegs-godkännandekrav för periodiska rapporter.",
                enabled: "Flerstegs-godkännande aktiverat",
                disabled: "Flerstegs-godkännande inaktiverat",
                toggleHint: "När aktiverat måste varje rapport passera alla konfigurerade steg innan den godkänns.",
                disabledInfo: "Flerstegs-godkännande är för närvarande inaktiverat. Rapporter använder den vanliga enstegs-granskningsprocessen.",
                stepsHeading: "Godkännandesteg",
                stepsHint: "Definiera de steg en rapport måste genomgå. Ordningen spelar roll — stegen behandlas uppifrån och ned.",
                noSteps: "Inga steg konfigurerade ännu. Klicka \"Lägg till steg\" för att definiera det första steget.",
                stepNamePlaceholder: "Stegnamn (t.ex. Granskning av Compliance-ansvarig)",
                addStep: "Lägg till steg",
                removeStep: "Ta bort steg",
                moveUp: "Flytta upp",
                moveDown: "Flytta ned",
                savedToast: "Konfiguration av godkännandeflöde sparad.",
                errorNoSteps: "Aktivering kräver minst ett steg med namn.",
            },
            entities: {
                title: "Entitetshantering",
                addEntity: "Lägg till entitet",
                editEntity: "Redigera entitet",
                deleteEntity: "Ta bort entitet",
                createEntity: "Skapa entitet",
                saveChanges: "Spara ändringar",
                cancel: "Avbryt",
                totalEntities: "Totalt antal",
                parentCompanies: "Moderbolag",
                subsidiaries: "Dotterbolag & filialer",
                companyStructure: "Bolagsstruktur",
                noEntities: "Inga entiteter konfigurerade. Lägg till ett moderbolag för att komma igång.",
                entityName: "Entitetsnamn",
                namePlaceholder: "Ange bolagsnamn",
                orgNumber: "Organisationsnummer",
                orgNumberPlaceholder: "t.ex. 556000-0001",
                entityType: "Entitetstyp",
                country: "Land",
                parentEntity: "Moderentitet",
                noParent: "Ingen (rotentitet)",
                description: "Beskrivning",
                descriptionPlaceholder: "Kort beskrivning av entiteten...",
                addChild: "Lägg till dotterbolag",
                confirmDelete: "Är du säker på att du vill ta bort \"{{name}}\"?",
                confirmDeleteWithChildren: "\"{{name}}\" har {{count}} direkta dotterbolag. De flyttas upp en nivå. Fortsätta?",
                type_parent: "Moderbolag",
                type_subsidiary: "Dotterbolag",
                type_branch: "Filial",
                type_joint_venture: "Joint Venture",
                countryCodePlaceholder: "SE",
                countrySelectPlaceholder: "Sök land...",
                entityLabel: "Entitet",
                reportColorsHeading: "Rapportfärger (override)",
                reportColorsDesc: "Valfria entitetsspecifika färger. Lämna tomt för att använda globala varumärkesfärger.",
                reportColorPrimary: "Primärfärg",
                reportColorSecondary: "Sekundärfärg",
                reportColorAccent: "Accentfärg",
                reportColorsUseGlobal: "Använd globala varumärkesfärger",
                errorCircularParent: "Kan inte sätta en ättling som förälder — det skulle skapa en cirkulär referens.",
                errorInvalidCountryCode: "Ange ISO 3166-1 landskod, t.ex. SE, DE, US",
                activeEntity: "Aktiv entitet",
                setAsActive: "Sätt som aktiv entitet",
                searchPlaceholder: "Sök på namn eller org.nummer…",
                noSearchResults: "Inga entiteter matchar sökningen.",
                viewList: "Lista",
                viewHierarchy: "Hierarki",
                hierarchy: {
                    expandAll: "Expandera alla",
                    collapseAll: "Minimera alla",
                    expand: "Expandera",
                    collapse: "Minimera",
                    usersTitle: "Användare med åtkomst till denna entitet",
                    usersHint: "användare med åtkomst",
                    licensesTitle: "{{active}} av {{total}} moduler licensierade",
                    licensesHint: "aktiva / inga licenser"
                },
                wizard: {
                    title: "Guide för ny entitet",
                    stepInfo: "Steg {{current}} av {{total}}",
                    step_grunddata: "Grunddata",
                    step_moduler: "Moduler",
                    step_access: "Åtkomst",
                    step_colors: "Rapportfärger",
                    step_summary: "Sammanfattning",
                    back: "Tillbaka",
                    next: "Nästa",
                    finish: "Skapa entitet",
                    successToast: "Entiteten \"{{name}}\" skapades",
                    modulesDesc: "Välj vilka moduler som ska licensieras för denna entitet.",
                    accessDesc: "Konfigurera vilka användare som har åtkomst till denna entitet.",
                    accessAutoGroup: "Global åtkomst",
                    accessSpecificGroup: "Begränsad åtkomst",
                    accessAutomatic: "Alla entiteter",
                    colorsDesc: "Konfigurera valfria entitetsspecifika rapportfärger.",
                    summaryDesc: "Granska konfigurationen innan entiteten skapas.",
                    summaryModules: "Moduler",
                    summaryAccess: "Användaråtkomst",
                    summaryNoModules: "Inga moduler valda",
                    summaryNoSpecificUsers: "Inga specifika användare konfigurerade",
                    summaryUsersAutomatic: "{{count}} användare med global åtkomst (automatisk)",
                    summaryColors: "Rapportfärger",
                    summaryColorsGlobal: "Globala varumärkesfärger används",
                    summaryColorsCustom: "Anpassade färger konfigurerade",
                    noUsers: "Inga användare hittades"
                }
            },
            overview: {
                title: "Administrationsöversikt",
                subtitle: "Systemstatus, användarstatistik och senaste aktivitet.",
                systemStatus: "Systemstatus",
                allSystemsOperational: "Alla system fungerar",
                activeUsers: "Aktiva användare",
                activeUsersCount: "{{count}} aktiva av {{total}} totalt",
                totalUsers: "Totalt antal användare",
                activeUsersLabel: "Aktiva",
                inactiveUsersLabel: "Inaktiva",
                modules: "Moduler",
                modulesOperational: "{{count}} moduler aktiva",
                userStats: "Användarstatistik",
                roleAdmins: "Administratörer",
                roleAnalysts: "Analytiker",
                roleViewers: "Läsare",
                moduleSummary: "Modulsammanfattning",
                indexCount: "{{count}} index",
                countryCount: "{{count}} länder",
                modelCount: "{{count}} modeller",
                parameterCount: "{{count}} parametrar",
                riskCount: "{{count}} risker",
                controlCount: "{{count}} kontroller",
                reportCount: "{{count}} rapporter",
                ruleCount: "{{count}} regler",
                prApprovedCount: "{{count}} godkända",
                txActiveCount: "{{count}} aktiva",
                noModulesEnabled: "Inga riskmoduler är aktiverade.",
                recentActivity: "Senaste aktivitet",
                noRecentActivity: "Ingen aktivitet registrerad.",
                showingXofY: "Visar {{shown}} av {{total}} händelser",
                viewAll: "Visa alla",
                colTime: "Tid",
                colUser: "Användare",
                colModule: "Modul",
                colAction: "Åtgärd",
                action_CREATE: "Skapade",
                action_UPDATE: "Uppdaterade",
                action_DELETE: "Tog bort",
                action_LOGIN: "Inloggning",
                action_LOGOUT: "Utloggning",
                action_DATA_IMPORT: "Dataimport",
                action_DATA_EXPORT: "Dataexport",
                action_STATUS_TRANSITION: "Statusändring",
                action_COMMENT: "Kommentar",
                action_EXPORT: "Export",
                module_Auth: "Autentisering",
                module_GeoRisk: "Geografisk risk",
                module_CustomerRisk: "Kundrisk",
                module_BWRA: "BWRA",
                module_Admin: "Administration",
                module_Settings: "Inställningar"
            },
            audit: {
                title: "Granskningslogg",
                filters: "Filter",
                export: "Exportera CSV",
                clearAll: "Rensa alla",
                showingResults: "Visar {{filtered}} av {{total}} loggposter",
                noLogs: "Inga loggposter matchar aktuella filter.",
                allModules: "Alla moduler",
                allActions: "Alla åtgärder",
                allLevels: "Alla nivåer",
                fromDate: "Från datum",
                toDate: "Till datum",
                filterByUser: "Filtrera på användare...",
                searchPlaceholder: "Sök modul, åtgärd, användare, detaljer…",
                searchLabel: "Fritextsökning",
                clearSearch: "Rensa sökning",
                clearFilters: "Rensa filter",
                viewDetails: "Visa detaljer",
                confirmClearAll: "Är du säker på att du vill rensa alla granskningsloggar? Denna åtgärd kan inte ångras.",
                clearAllSuccess: "Rensade {{count}} granskningsloggposter.",
                clearAllAdminOnly: "Endast administratörer kan rensa granskningsloggen.",
                logDetails: "Loggdetaljer",
                timestamp: "Tidsstämpel",
                user: "Användare",
                module: "Modul",
                action: "Åtgärd",
                level: "Nivå",
                details: "Detaljer",
                sessionInfo: "Sessionsinformation",
                time: "Tid",
                actions: "Åtgärder",
                levelDefault: "INFO",
                chainIntegrityBroken: "VARNING: Granskningsloggens integritet är bruten. Möjlig manipulering detekterad. Kontakta din administratör.",
                chainIntegrityOk: "Granskningsloggens integritet verifierad.",
                paginationPrevious: "Föregående",
                paginationNext: "Nästa",
                paginationPageInfo: "Sida {{current}} av {{total}}",
                paginationEntries: "{{from}}–{{to}} av {{filtered}} poster",
                exportSuccess: "Exporterade {{count}} loggposter till CSV",
                exportMenu: "Exportera",
                exportCsv: "Exportera CSV",
                exportExcel: "Exportera Excel",
                exportPdf: "Exportera PDF",
                exportExcelSuccess: "Exporterade {{count}} loggposter till Excel",
                exportPdfSuccess: "Exporterade {{count}} loggposter till PDF"
            },
            users: {
                title: "Användarhantering",
                addUser: "Lägg till användare",
                editUser: "Redigera användare",
                deleteUser: "Ta bort användare",
                createUser: "Skapa användare",
                createRole: "Skapa roll",
                saveChanges: "Spara ändringar",
                cancel: "Avbryt",
                totalUsers: "Totalt antal",
                activeUsers: "Aktiva",
                admins: "Administratörer",
                searchPlaceholder: "Sök användare...",
                allRoles: "Alla roller",
                allStatuses: "Alla statusar",
                role_admin: "Admin",
                role_analyst: "Analytiker",
                role_viewer: "Läsare",
                statusActive: "Aktiv",
                statusInactive: "Inaktiv",
                toggleStatus: "Växla status",
                confirmToggleStatus: "Ändra status för \"{{name}}\" till {{status}}?",
                colUser: "Användare",
                colRole: "Roll",
                colStatus: "Status",
                colModules: "Modulåtkomst",
                colLastLogin: "Senaste inloggning",
                colActions: "Åtgärder",
                noUsersFound: "Inga användare matchar aktuella filter.",
                showingCount: "Visar {{filtered}} av {{total}} användare",
                confirmDelete: "Är du säker på att du vill ta bort",
                cannotDeleteSelf: "Du kan inte ta bort ditt eget konto.",
                cannotDeleteLastAdmin: "Det går inte att ta bort den sista administratören.",
                firstName: "Förnamn",
                firstNamePlaceholder: "Ange förnamn",
                lastName: "Efternamn",
                lastNamePlaceholder: "Ange efternamn",
                email: "E-post (inloggning)",
                emailPlaceholder: "Ange e-postadress",
                password: "Lösenord",
                passwordPlaceholder: "Ange lösenord",
                confirmPassword: "Bekräfta lösenord",
                confirmPasswordPlaceholder: "Ange lösenordet igen",
                emailInvalid: "Ange en giltig e-postadress",
                passwordMismatch: "Lösenorden matchar inte",
                passwordRequired: "Lösenord krävs",
                passwordHint: "Den nya användaren loggar in med detta lösenord",
                changePassword: "Ändra lösenord",
                changePasswordHint: "Lämna tomt för att behålla befintligt lösenord",
                newPassword: "Nytt lösenord",
                newPasswordPlaceholder: "Ange nytt lösenord",
                confirmNewPasswordPlaceholder: "Ange nytt lösenord igen",
                passwordTooWeak: "Lösenordet uppfyller inte säkerhetskraven",
                passwordReqLength: "Minst 8 tecken",
                passwordReqUppercase: "Minst 1 versal (A–Ö)",
                passwordReqLowercase: "Minst 1 gemen (a–ö)",
                passwordReqNumber: "Minst 1 siffra (0–9)",
                passwordReqSpecial: "Minst 1 specialtecken (!@#$...)",
                module_Dashboard: "Dashboard",
                module_GeoRisk: "Geografisk risk",
                module_CustomerRisk: "Kundrisk",
                module_BWRA: "BWRA",
                module_Admin: "Administration",
                module_TransactionMonitoring: "Transaktionsövervakning",
                module_PeriodicReporting: "Periodisk rapportering",
                moduleShort_Dashboard: "Dash",
                moduleShort_GeoRisk: "Geo",
                moduleShort_CustomerRisk: "Kund",
                moduleShort_BWRA: "BWRA",
                moduleShort_Admin: "Adm",
                moduleShort_TransactionMonitoring: "TxÖv",
                moduleShort_PeriodicReporting: "Per",
                permissions: "Behörigheter",
                permissionLevel: "Behörighetsnivå",
                accessEnabled: "Åtkomst",
                noAccess: "Ingen åtkomst",
                rolePresetHint: "Vid rollbyte tilldelas standardbehörigheter för den rollen",
                perm_view: "Visa",
                perm_edit: "Redigera",
                perm_admin: "Administrera",
                permShort_view: "V",
                permShort_edit: "R",
                permShort_admin: "A",
                entityAccess: "Entitetstillgång",
                entityAccessHint: "Välj vilka entiteter användaren har åtkomst till",
                allEntities: "Alla entiteter",
                allEntitiesHint: "Användaren kan komma åt alla nuvarande och framtida entiteter",
                specificEntities: "Specifika entiteter",
                noEntitiesSelected: "Inga entiteter valda",
                noEntityAccessWarning: "Inga entiteter valda — användaren kan inte se någon data",
                entityCount: "{{count}} entitet(er)",
                perEntityRoles: "Roller per entitet",
                perEntityRolesHint: "Tilldela olika roller per entitet. Lämna tomt för att använda den globala rollen.",
                sameAsGlobal: "Samma som global roll",
                entityRoleOverrides: "{{count}} rollöverskrivning(ar)",
                manageRoles: "Hantera roller",
                hideRoles: "Dölj roller",
                rolesSection: "Rolldefinitioner",
                addRole: "Lägg till roll",
                editRole: "Redigera roll",
                deleteRole: "Ta bort roll",
                roleName: "Rollnamn",
                roleNamePlaceholder: "Ange rollnamn",
                builtIn: "Inbyggd",
                customRoleLabel: "Anpassad",
                confirmDeleteRole: "Är du säker på att du vill ta bort rollen",
                roleInUse: "Kan inte ta bort: rollen är tilldelad en eller flera användare",
                roleBuiltInNoDelete: "Inbyggda roller kan inte tas bort",
                defaultPermissions: "Standardbehörigheter",
                roleNameRequired: "Rollnamn krävs",
                noCustomRoles: "Inga anpassade roller definierade ännu",
                roleUsersCount: "{{count}} användare",
                createCustomRole: "Skapa anpassad roll",
                roleBuilderTitle: "Rollbyggare",
                roleBuilderSubtitle: "Definiera granulära behörigheter per modul",
                roleBuilderStep1: "Rolldetaljer",
                roleBuilderStep2: "Behörigheter",
                roleBuilderStep3: "Tilldela användare",
                roleBuilderPreset: "Utgå från förinställning",
                roleBuilderPresetHint: "Välj valfritt en basroll att utgå från",
                action_read: "Läsa",
                action_create: "Skapa",
                action_edit: "Redigera",
                action_delete: "Radera",
                action_approve: "Godkänna",
                actionsMatrixHint: "Välj vilka åtgärder som är tillåtna per modul",
                actionsSelectedLabel: "åtgärder",
                assignUsersStep: "Tilldela till användare",
                assignUsersHint: "Tilldela valfritt den nya rollen till befintliga användare",
                roleCreatedSuccess: "Roll skapad och sparad",
                roleBuilderBack: "Tillbaka",
                roleBuilderNext: "Nästa",
                selectAll: "Välj alla",
                selectNone: "Avmarkera alla"
            },
            settings: {
                title: "Systeminställningar",
                sessionManagement: "Sessionshantering",
                sessionTimeout: "Sessionstimeout",
                hours: "timmar",
                minutes: "minuter",
                sessionTimeoutDesc: "Automatisk utloggning efter denna tid oavsett aktivitet",
                inactivityTimeout: "Inaktivitetstimeout",
                inactivityTimeoutDesc: "Utloggning efter denna tid av inaktivitet",
                warningTime: "Varningstid",
                warningTimeDesc: "Visa varning detta antal minuter innan sessionen löper ut",
                resetSessionSettings: "Återställ sessionsinställningar",
                auditLogSettings: "Granskningslogginställningar",
                retentionDays: "Lagringstid (dagar)",
                retentionDaysDesc: "Hur många dagar granskningsloggar ska sparas",
                maximumLogs: "Maximalt antal loggar",
                maximumLogsDesc: "Maximalt antal loggar att spara",
                resetAuditSettings: "Återställ granskningsinställningar",
                validationHistorySettings: "Lagring av valideringshistorik",
                validationHistoryLimit: "Maximalt antal sparade valideringskörningar",
                validationHistoryLimitDesc: "Maximalt antal valideringskörningar att spara. Äldre körningar tas bort automatiskt. Rekommendation: minst 100 för 7 års regulatorisk lagring.",
                tabSession: "Session",
                tabAudit: "Granskning",
                import: "Importera",
                export: "Exportera",
                saveChanges: "Spara ändringar",
                resetAllSettings: "Återställ alla inställningar",
                currentRole: "Nuvarande roll",
                confirmReset: "Återställ {{category}}-inställningar till standardvärden?",
                confirmResetAll: "Återställ alla inställningar till standardvärden?",
                savedSuccess: "Inställningar sparade - Ändringar aktiva omedelbart",
                savedError: "Fel vid sparande av inställningar",
                resetSuccess: "Inställningar återställda - Ändringar aktiva omedelbart",
                importedSuccess: "Inställningar importerade - Ändringar aktiva omedelbart",
                importError: "Fel vid import av inställningar",
                importPreviewTitle: "Förhandsgranska import",
                importPreviewDesc: "Granska ändringarna nedan innan du bekräftar importen.",
                importPreviewNoChanges: "Inga ändringar hittades — de importerade inställningarna är identiska med nuvarande.",
                importPreviewChanged: "{{count}} ändring(ar) hittades",
                importPreviewCurrent: "Nuvarande",
                importPreviewNew: "Nytt",
                importPreviewConfirm: "Bekräfta import",
                importPreviewCategorySession: "Session",
                importPreviewCategoryAudit: "Granskningslogg",
                importPreviewCategoryUi: "Gränssnitt",
                importPreviewCategoryBranding: "Varumärke",
                importPreviewCategoryModules: "Moduler",
                importPreviewInvalidFile: "Ogiltig inställningsfil — saknar egenskapen 'settings'.",
                tabModules: "Moduler",
                modulesSettings: "Modulkonfiguration",
                modulesDesc: "Välj vilka moduler som är aktiva och deras ordning i sidomenyn.",
                alwaysActive: "Alltid aktiv",
                moduleEnabled: "Aktiv",
                moduleDisabled: "Inaktiv",
                moveUp: "Flytta upp",
                moveDown: "Flytta ner",
                resetModulesSettings: "Återställ modulinställningar",
                tabBranding: "Varumärke",
                tabThresholds: "KPI-trösklar",
                thresholds: {
                    title: "KPI-tröskelvarningar",
                    subtitle: "Konfigurera trösklar för Dashboard KPI-kort. Korten visar en röd varningsram och ikon när ett KPI-värde uppnår eller överskrider tröskeln.",
                    geo_highRisk: "Högriskländer",
                    geo_highRiskDesc: "Varna när antalet länder med hög risk (eller högre) når detta värde",
                    bwra_unmitigated: "Omitigerade BWRA-risker",
                    bwra_unmitigatedDesc: "Varna när antalet omitigerade BWRA-risker når detta värde",
                    prpt_review: "Rapporter under granskning",
                    prpt_reviewDesc: "Varna när antalet periodiska rapporter under granskning når detta värde",
                    threshold: "Tröskel",
                    enabled: "Aktiv",
                    reset: "Återställ KPI-trösklar till standardvärden",
                },
                brandingSettings: "Rapportvarumärke",
                brandingDesc: "Konfigurera företagsidentitet som visas i PDF-rapporter.",
                companyName: "Företagsnamn",
                companyNameDesc: "Visas i PDF-rapportens sidhuvud. Ersätter standardsystemnamnet.",
                companySubtitle: "Rapportundertitel",
                companySubtitleDesc: "Valfri sekundär rad i PDF-rapportens sidhuvud (t.ex. avdelning eller dokumenttyp).",
                companyOrgNumber: "Organisationsnummer",
                companyOrgNumberDesc: "Valfritt registreringsnummer som visas på PDF-omslaget.",
                companyLogo: "Företagslogotyp",
                companyLogoDesc: "Ladda upp en PNG- eller JPEG-logotyp. Den visas längst upp till vänster i PDF-rapporter.",
                logoUpload: "Ladda upp logotyp",
                logoRemove: "Ta bort logotyp",
                logoPreview: "Förhandsvisning av logotyp",
                resetBrandingSettings: "Återställ varumärkesinställningar",
                reportColorsHeading: "Rapportfärger",
                reportColorsDesc: "Konfigurera färgschemat som används i PDF-rapporter. Varje entitet kan ha egna färger.",
                reportColorPrimary: "Primärfärg",
                reportColorPrimaryDesc: "Huvudvarumärkesfärg. Används för rubriker, sektionslinjer och omslaget.",
                reportColorSecondary: "Sekundärfärg",
                reportColorSecondaryDesc: "Accentfältets färg på omslaget.",
                reportColorAccent: "Accentfärg",
                reportColorAccentDesc: "Dekorativt fält och visuella markeringar i rapporten.",
                reportColorsReset: "Återställ till standardfärger"
            },
            licenses: {
                title: "Licenshantering",
                subtitle: "Hantera vilka moduler som är licensierade per entitet.",
                entityLabel: "Entitet",
                selectEntity: "Välj entitet",
                noEntities: "Inga entiteter konfigurerade. Lägg till entiteter under fliken Entiteter.",
                moduleColumn: "Modul",
                statusColumn: "Status",
                expiryColumn: "Utgångsdatum",
                actionsColumn: "Åtgärder",
                licensed: "Licensierad",
                unlicensed: "Ej licensierad",
                expired: "Utgången",
                noExpiry: "Inget utgångsdatum",
                expiresOn: "Utgår {{date}}",
                activate: "Aktivera",
                deactivate: "Inaktivera",
                editExpiry: "Ändra utgångsdatum",
                saveExpiry: "Spara",
                cancelExpiry: "Avbryt",
                expiryLabel: "Utgångsdatum (valfritt)",
                activateSuccess: "Licens aktiverad för {{module}}",
                deactivateSuccess: "Licens inaktiverad för {{module}}",
                expiryUpdated: "Utgångsdatum uppdaterat",
                noLicenseHeading: "Ingen licens",
                noLicenseMessage: "Den här modulen är inte licensierad för din organisation. Kontakta din administratör.",
                module_customer_risk: "Kundrisk",
                module_geographical_risk: "Geografisk risk",
                module_bwra: "BWRA",
                module_transaction_monitoring: "Transaktionsövervakning",
                module_periodic_reporting: "Periodisk rapportering",
                expiryWarningTitle: "Licenser som snart går ut",
                expiryWarningItem: "{{module}} — {{days}} dag(ar) kvar",
                expiryWarningItemToday: "{{module}} — går ut idag",
                showMatrix: "Matrisvy",
                showList: "Listvy"
            },
            validationScheduler: {
                title: "Valideringsschema",
                subtitle: "Konfigurera automatiserade valideringsscheman, kör validering manuellt och se historik.",
                schedulesTitle: "Modellscheman",
                noModels: "Inga modeller tillgängliga för schemaläggning. Ladda upp dataset i GeoRisk- eller Kundrisk-modulen först.",
                colModel: "Modell",
                colEnabled: "Aktiverat",
                colFrequency: "Frekvens",
                colDataset: "Dataset",
                colLastRun: "Senast körd",
                colNextRun: "Nästa körning",
                colActions: "Åtgärder",
                colTimestamp: "Tidsstämpel",
                colTriggeredBy: "Utlöst av",
                colAccuracy: "Noggrannhet",
                colStatus: "Status",
                enable: "Aktivera schema",
                disable: "Inaktivera schema",
                daily: "Dagligen",
                weekly: "Veckovis",
                frequency: "Frekvens",
                dataset: "Dataset",
                noDataset: "— välj dataset —",
                runNow: "Kör nu",
                running: "Kör…",
                neverRun: "Aldrig körd",
                statusSuccess: "Lyckad",
                statusError: "Fel",
                statusPending: "Väntande",
                triggeredScheduled: "Schemalagd",
                triggeredManual: "Manuell",
                historyTitle: "Valideringshistorik",
                noHistory: "Inga valideringskörningar registrerade ännu.",
                runSuccess: "Validering klar — noggrannhet: {{accuracy}}%",
                runError: "Validering misslyckades: {{error}}",
                errorNoDataset: "Välj ett dataset innan du kör validering.",
                clearOlderThan: "Rensa historik äldre än",
                days: "dagar",
                clearBtn: "Rensa historik",
                clearConfirmMessage: "Detta tar permanent bort alla valideringshistorikposter som är äldre än {{days}} dagar. Åtgärden kan inte ångras.",
                clearSuccess: "Raderade {{count}} historikposter äldre än {{days}} dagar.",
                clearNone: "Inga historikposter hittades som är äldre än {{days}} dagar.",
                weekdayLabel: "Veckodag",
                mon: "Måndag",
                tue: "Tisdag",
                wed: "Onsdag",
                thu: "Torsdag",
                fri: "Fredag",
                sat: "Lördag",
                sun: "Söndag"
            },
            activity: {
                title: "Användaraktivitets-dashboard",
                period7d: "7 dagar",
                period30d: "30 dagar",
                period90d: "90 dagar",
                totalEvents: "Totalt antal händelser",
                uniqueUsers: "Unika användare",
                activeModules: "Aktiva moduler",
                noData: "Ingen aktivitetsdata för de senaste {{days}} dagarna.",
                moduleActivity: "Aktivitet per modul",
                mostActiveUsers: "Mest aktiva användare",
                topActions: "Vanligaste åtgärder",
                rank: "#",
                user: "Användare",
                events: "Händelser",
                share: "Andel",
                action: "Åtgärd"
            },
            regulatoryExport: {
                title: "Regulatorisk export",
                subtitle: "Generera och ladda ned AML-riskdata i format som krävs av tillsynsmyndigheter (EBA, Finansinspektionen).",
                formatTitle: "Exportformat",
                formatHelp: "Välj det format som din tillsynsmyndighet kräver. EBA XML används för rapportering till European Banking Authority. Finansinspektionens CSV används för inlämning till FI.",
                formatEbaDesc: "European Banking Authority — XML-schema för AML-riskbedömningar",
                formatFiDesc: "Finansinspektionen — CSV-format för inlämning till FI",
                periodTitle: "Rapporteringsperiod",
                periodHelp: "Ange den period exporten avser, t.ex. 2025-Q4 för fjärde kvartalet 2025.",
                periodLabel: "Period",
                periodPlaceholder: "t.ex. 2025-Q4",
                periodHint: "Format: YYYY-Q1/Q2/Q3/Q4 eller YYYY-MM",
                scopeTitle: "Dataomfång",
                scopeHelp: "Välj vilka moduler som ska inkluderas i exporten. Minst en modul måste väljas.",
                scopeBwra: "BWRA — Verksamhetsövergripande riskbedömning",
                scopeGeoRisk: "Geografisk risk",
                scopeCustomerRisk: "Kundrisk",
                validationTitle: "Förhandskontroll",
                validationDesc: "Validera att all nödvändig data finns och är komplett innan exportfilen genereras.",
                validateBtn: "Validera data",
                validationPassed: "Validering godkänd — data är klar för export",
                validationFailed: "Validering misslyckades — korrigera fel innan export",
                exportBtn: "Generera och ladda ned",
                exportHint: "Validering körs automatiskt innan filen genereras.",
                exportSuccess: "Export genererad: {{filename}}",
                exportError: "Export misslyckades: {{error}}",
                historyTitle: "Exporthistorik",
                clearHistory: "Rensa historik",
                clearHistoryConfirm: "Detta tar permanent bort alla exporthistorikposter. Fortsätta?",
                historyClearedSuccess: "Exporthistorik rensad.",
                colTimestamp: "Tidpunkt",
                colFormat: "Format",
                colPeriod: "Period",
                colFile: "Filnamn",
                validation_no_entity: "Ingen aktiv entitet konfigurerad",
                validation_entity_name_missing: "Den aktiva entiteten saknar namn",
                validation_entity_country_missing: "Entitetens land är inte angivet",
                validation_entity_orgnumber_missing: "Entitetens organisationsnummer är inte angivet",
                validation_bwra_no_risks: "BWRA-modulen har inga registrerade risker",
                validation_georisk_no_indexes: "GeoRisk-modulen har inga konfigurerade index",
                validation_georisk_no_active_indexes: "GeoRisk-modulen har inga aktiva index",
                validation_customerrisk_no_models: "Kundrisk-modulen har inga modeller",
                validation_no_scope_selected: "Välj minst en modul att inkludera",
                validationBwraMissingResidual: "{{count}} risker saknar residualriskbedömning",
                validationBwraMissingCategory: "{{count}} risker har ingen kategori tilldelad"
            },
            manuals: {
                title: "Användarmanualer",
                subtitle: "Ladda ned och visa dokumentation för alla moduler och systemfunktioner.",
                searchPlaceholder: "Sök manualer…",
                filterCategory: "Filtrera på kategori",
                allCategories: "Alla kategorier",
                download: "Ladda ned",
                downloadTitle: "Ladda ned PDF",
                downloadStarted: "Laddar ned: {{name}}",
                version: "v",
                docCount: "{{count}} dokument",
                noResults: "Inga manualer matchar din sökning.",
                cat: {
                    user: "Användarmanual",
                    technical: "Teknisk manual",
                    admin: "Administratörsmanual",
                    compliance: "Compliancemanual"
                },
                doc: {
                    "ug-overview": {
                        title: "Systemöversikt",
                        desc: "Introduktion till Prime RMS — moduler, navigering och nyckelbegrepp."
                    },
                    "ug-georisk": {
                        title: "Georiskmodulen",
                        desc: "Konfigurera landsriskindex, poängsättningsmodeller och risknivåer."
                    },
                    "ug-custrisk": {
                        title: "Kundriskmodulen",
                        desc: "Bygg kundriskmodeller, poängsättningsparametrar och CDD/EDD-klassificering."
                    },
                    "ug-bwra": {
                        title: "BWRA-modulen",
                        desc: "Verksamhetsövergripande riskbedömning: risker, kontroller, residualrisk och spårbarhet."
                    },
                    "ug-txmon": {
                        title: "Transaktionsövervakning",
                        desc: "Konfigurera övervakningsregler, larmhantering och ärendehantering."
                    },
                    "ug-reporting": {
                        title: "Periodisk rapportering",
                        desc: "Generera, godkänna och exportera compliancerapporter."
                    },
                    "tg-install": {
                        title: "Installationsguide",
                        desc: "Systemkrav, installationssteg och lokal driftsättning."
                    },
                    "tg-config": {
                        title: "Konfigurationsreferens",
                        desc: "Miljövariabler, lagringsbackends och avancerad konfiguration."
                    },
                    "tg-api": {
                        title: "API-referens",
                        desc: "REST API-endpoints, autentisering och integrationsexempel."
                    },
                    "tg-storage": {
                        title: "Databas- och lagringsguide",
                        desc: "StorageAdapter, migreringsvägar från localStorage till produktionsdatabaser."
                    },
                    "ag-users": {
                        title: "Användar- och rollhantering",
                        desc: "Skapa användare, tilldela roller, konfigurera behörigheter och entitetsåtkomst."
                    },
                    "ag-licenses": {
                        title: "Licenshantering",
                        desc: "Aktivera moduler, sätt utgångsdatum och hantera licenser per entitet."
                    },
                    "ag-settings": {
                        title: "Systeminställningar",
                        desc: "Sessionstimeouter, granskningsloggsbehållning, varumärke och modulkonfiguration."
                    },
                    "cg-aml": {
                        title: "AML-complianceramverk",
                        desc: "Regulatorisk bakgrund, FATF-riktlinjer och hur Prime RMS stöder AML-compliance."
                    },
                    "cg-validation": {
                        title: "Modellvalideringsguide",
                        desc: "Valideringsmetodik, schemaläggning, noggrannhetsmått och godkännandeflöden."
                    },
                    "cg-audit": {
                        title: "Granskningslogg och bevis",
                        desc: "Använda granskningsloggen för regulatoriska bevis, exportformat och bevaranderegler."
                    }
                }
            },
            dataMigration: {
                title: "Datamigrering",
                subtitle: "Migrera befintlig localStorage-data till den konfigurerade Supabase-molndatabasen.",
                migrateHeading: "Migrera lokal data till molnet",
                migrateDesc: "{{count}} dataposter hittades i lokal lagring. Klicka på knappen nedan för att kopiera dem till Supabase-databasen. Åtgärden är idempotent — säker att köra flera gånger.",
                startBtn: "Starta migrering",
                runAgainBtn: "Kör igen",
                notAvailable: "Datamigrering är endast tillgänglig när en Supabase-backend är konfigurerad. Ange REACT_APP_SUPABASE_URL och REACT_APP_SUPABASE_ANON_KEY för att aktivera.",
                noLocalData: "Ingen lokal data hittades. Inget att migrera.",
                progressLabel: "Migrerar nyckel {{current}} av {{total}} ({{pct}}%)…",
                status_migrated: "Migrerad",
                status_skipped: "Hoppades över",
                status_error: "Fel",
                doneBanner: "Migrering klar — {{migrated}} poster migrerade, {{errors}} fel.",
                errorBanner: "Migrering stötte på ett fel. Kontrollera resultaten och försök igen.",
                doneToast: "Migrering klar — {{count}} poster migrerade till Supabase.",
                errorToast: "Migrering misslyckades. Kontrollera anslutningen och försök igen.",
                historyHeading: "Migreringshistorik",
                historyStats: "{{migrated}} / {{total}} migrerade",
                historyErrors: "({{errors}} fel)"
            },
            entityMigration: {
                title: "Entitetsdatamigrering",
                subtitle: "Flytta eller kopiera riskbedömningsdata mellan entiteter vid organisatorisk omstrukturering. En fullständig revisionslogg skapas för varje åtgärd.",
                stepEntitiesHeading: "Välj käll- och målentitet",
                labelSource: "Källentitet (från)",
                labelTarget: "Målentitet (till)",
                selectEntity: "— Välj entitet —",
                labelMode: "Åtgärd:",
                modeCopy: "Kopiera",
                modeMove: "Flytta",
                modeCopyDesc: "Data kopieras — originalet bevaras.",
                modeMoveDesc: "Data flyttas — originalet raderas efter överföring.",
                sameEntityWarning: "Käll- och målentitet måste vara olika.",
                previewBtn: "Förhandsgranska data",
                stepPreviewHeading: "Välj data att migrera",
                previewDesc: "Välj vilka moduler som ska inkluderas i migreringen. Avmarkera moduler som ska kvarstå i källentiteten.",
                noDataFound: "Ingen entitetsspecifik data hittades för den valda källentiteten.",
                module_bwra: "BWRA (Business-Wide Risk Assessment)",
                module_georisk: "GeoRisk",
                module_customerrisk: "Kundrisk",
                module_txmon: "Transaktionsövervakning",
                module_other: "Övrigt / Okategoriserat",
                keyCount: "{{count}} lagringsnycklar",
                itemCount: "{{count}} poster",
                moveWarning: "Flytt-läge: källdata raderas permanent efter överföring. Åtgärden kan inte ångras.",
                migrateBtn: "Migrera {{count}} nyckel/nycklar",
                runningBtn: "Migrerar…",
                resultsHeading: "Migrering slutförd",
                resultMigrated: "{{count}} nyckel/nycklar migrerade",
                resultSkipped: "{{count}} hoppades över (tomma)",
                statusMigrated: "Migrerad",
                statusSkipped: "Hoppades över",
                doneToast: "Migrering klar — {{count}} lagringsnyckel/nycklar överförda.",
                errorToast: "Migrering misslyckades. Försök igen."
            },
            backup: {
                title: "Säkerhetskopiering och återställning",
                subtitle: "Exportera all systemdata till en fil och återställ från en tidigare säkerhetskopia.",
                createHeading: "Skapa säkerhetskopia",
                createDesc: "Ladda ned en fullständig ögonblicksbild av all systemdata. Filen innehåller konfigurationer, riskbedömningar, modeller, rapporter och användarinställningar.",
                createBtn: "Ladda ned säkerhetskopia",
                downloadedToast: "Säkerhetskopia skapad — {{count}} dataposter exporterade.",
                downloadError: "Det gick inte att skapa säkerhetskopia. Försök igen.",
                restoreHeading: "Återställ från säkerhetskopia",
                restoreDesc: "Ladda upp en säkerhetskopiefil för att återställa systemdata. Endast filer skapade av detta system accepteras.",
                restoreWarning: "Varning: återställning av en säkerhetskopia skriver över all aktuell data. Åtgärden kan inte ångras.",
                restoreBtn: "Ladda upp säkerhetskopiefil",
                confirmRestore: "Återställ säkerhetskopia från {{date}} ({{count}} poster)? All aktuell data skrivs över.",
                restoredToast: "Säkerhetskopia återställd — {{count}} dataposter inlästa.",
                restoreError: "Det gick inte att återställa säkerhetskopian. Kontrollera filen och försök igen.",
                validationFailed: "Ogiltig säkerhetskopiefil",
                error_parseError: "Filen kunde inte tolkas som JSON",
                error_invalidFormat: "Okänt filformat",
                error_unknownVersion: "Versionen av säkerhetskopian stöds inte",
                error_missingData: "Säkerhetskopian innehåller ingen data"
            },
            systemHealth: {
                title: "Systemhälsa",
                subtitle: "Övervaka lagringsanvändning och datamängder per modul. Hjälper till att identifiera överdimensionerade datamängder och planera kapacitet.",
                totalUsed: "Lagring använd",
                ofLimit: "av {{limit}} gräns",
                totalKeys: "Dataposter",
                storageEntries: "nycklar i lagringen",
                usagePercent: "Användning",
                moduleBreakdown: "Data per modul",
                refresh: "Uppdatera",
                lastRefreshed: "Senast uppdaterad kl. {{time}}",
                colModule: "Modul",
                colKeys: "Poster",
                colSize: "Storlek",
                colShare: "Andel",
                alertWarning: "Lagringen är vid {{pct}}% — överväg att rensa oanvänd data.",
                alertCritical: "Lagringen är vid {{pct}}% — kritiskt! Export eller sparning kan misslyckas.",
                module_core: "Kärna / Infrastruktur",
                module_admin: "Admin",
                module_bwra: "BWRA",
                module_georisk: "GeoRisk",
                module_customerrisk: "Kundrisk",
                module_validation: "Datavalidering",
                module_periodicreporting: "Periodisk rapportering",
                module_txmon: "Transaktionsövervakning",
                module_scheduledvalidation: "Schemalagd validering",
                module_regulatoryexport: "Regulatorisk export",
                module_other: "Övrigt"
            },
            reportTemplates: {
                title: "Rapportmallsredigerare",
                subtitle: "Definiera de sektioner och fält som ingår i genererade rapporter. Varje mall kan tilldelas specifika entiteter.",
                templatesHeading: "Mallar",
                newTemplate: "Ny mall",
                templateNamePlaceholder: "Mallnamn",
                templateDescPlaceholder: "Kort beskrivning (valfri)",
                create: "Skapa",
                selectTemplate: "Välj en mall från listan för att börja redigera.",
                templateName: "Mallnamn",
                templateDescription: "Beskrivning",
                sectionsHeading: "Sektioner",
                addSection: "Lägg till sektion",
                sectionTitlePlaceholder: "Sektionstitel",
                noSections: "Inga sektioner ännu. Klicka på \"Lägg till sektion\" för att börja.",
                fieldCount: "{{count}} fält",
                sectionCount: "{{count}} sektioner",
                addField: "Lägg till fält",
                noFields: "Inga fält. Klicka på \"Lägg till fält\" för att lägga till det första fältet.",
                fieldLabelPlaceholder: "Fältetikett",
                fieldDescription: "Hjälptext",
                fieldDescriptionPlaceholder: "Valfri beskrivning som visas för rapportförfattaren.",
                required: "Obligatoriskt",
                moreOptions: "Fler alternativ",
                moveUp: "Flytta upp",
                moveDown: "Flytta ned",
                removeField: "Ta bort fält",
                removeSection: "Ta bort sektion",
                expand: "Expandera",
                collapse: "Komprimera",
                selectOptions: "Alternativ",
                selectOptionsPlaceholder: "Alternativ 1, Alternativ 2, Alternativ 3",
                selectOptionsHint: "Kommaseparerad lista med alternativ.",
                deleteTemplate: "Ta bort mall",
                confirmDelete: "Är du säker på att du vill ta bort mallen \"{{name}}\"?",
                cannotDeleteDefault: "Standardmallen kan inte tas bort.",
                savedToast: "Mallen sparades.",
                deletedToast: "Mallen togs bort.",
                errorNameRequired: "Mallnamn är obligatoriskt.",
                fieldType_text: "Text",
                fieldType_textarea: "Textyta",
                fieldType_number: "Nummer",
                fieldType_date: "Datum",
                fieldType_boolean: "Ja / Nej",
                fieldType_select: "Dropdown"
            },
            retention: {
                title: "Datalagring",
                subtitle: "Konfigurera hur länge varje datatyp lagras innan arkivering. AML-regelverket kräver en minimiperiod på 5 år.",
                policiesHeading: "Retentionspolicyer",
                policiesDesc: "Definiera lagringtid per datatyp. När du kör arkiveringen flyttas poster äldre än konfigurerad period till arkivet.",
                runArchiveBtn: "Kör arkivering nu",
                colDataType: "Datatyp",
                colRetentionPeriod: "Lagringstid",
                colEnabled: "Aktiv",
                yearSingular: "år",
                yearPlural: "år",
                enabled: "Aktiv",
                disabled: "Inaktiv",
                enablePolicy: "Aktivera denna retentionspolicy",
                disablePolicy: "Inaktivera denna retentionspolicy",
                policyEnabled: "Retentionspolicy aktiverad.",
                policyDisabled: "Retentionspolicy inaktiverad.",
                archiveRunToast: "Arkivering slutförd — {{count}} post(er) flyttades till arkivet.",
                lastRunTitle: "Senaste arkivkörning:",
                summaryArchived: "{{archived}} arkiverades, {{remaining}} kvar",
                archiveHeading: "Arkiverade poster",
                archiveDesc: "Bläddra bland och återställ poster som har arkiverats. Återställning flyttar posten tillbaka till det aktiva datalagret.",
                allDataTypes: "Alla datatyper",
                searchPlaceholder: "Sök i arkiverade poster...",
                colArchivedType: "Datatyp",
                colArchivedAt: "Arkiverades",
                colOriginalDate: "Originaldatum",
                colActions: "Åtgärder",
                restoreBtn: "Återställ",
                purgeBtn: "Rensa arkiv",
                emptyArchiveTitle: "Inga arkiverade poster",
                emptyArchiveDesc: "Kör arkiveringen för att flytta gamla poster hit baserat på konfigurerade retentionspolicyer.",
                confirmRestore: "Återställa denna post till det aktiva datalagret?",
                confirmPurge: "Ta bort alla arkiverade poster för \"{{dataType}}\" permanent? Det går inte att ångra.",
                restoreSuccess: "Posten återställdes.",
                restoreError: "Det gick inte att återställa posten.",
                purgeSuccess: "{{count}} arkiverad(e) post(er) borttagna permanent.",
                dataType_audit_logs: "Granskningsloggar",
                dataType_validation_history: "Valideringshistorik",
                exportBtn: "Exportera CSV",
                exportToast: "{{count}} arkiverad(e) post(er) exporterades till CSV.",
                exportColRecord: "Post",
                pagePrev: "Föregående",
                pageNext: "Nästa",
                pageLabel: "Sida {{current}} av {{total}}",
                pageRecordCount: "{{count}} totalt"
            },
            dataIsolation: {
                title: "Verifiering av dataisolering",
                subtitle: "Kontrollera att varje entitets data är korrekt avgränsad och separerad i lagringen.",
                description: "Det här verktyget skannar alla lagringsnycklar och bekräftar att entitetsspecifik data är korrekt isolerad per entitet. Kör kontrollen inför revisioner eller när nya dotterbolag läggs till.",
                runBtn: "Verifiera dataisolering",
                running: "Kör...",
                verifySuccess: "Dataisolering verifierad — inga problem hittades.",
                verifyIssues: "Verifiering klar — {{count}} potentiellt problem hittades.",
                verifyError: "Ett fel uppstod under verifieringen.",
                statusOk: "All data korrekt isolerad",
                statusIssues: "{{count}} potentiellt problem detekterades",
                verifiedAt: "Verifierad {{time}}",
                cardEntities: "Entiteter",
                cardTotalKeys: "Totalt antal nycklar",
                cardSystemKeys: "Globala nycklar",
                cardIssues: "Övergivna nycklar",
                entityBreakdownTitle: "Dataöversikt per entitet",
                colEntity: "Entitet",
                colType: "Typ",
                colScopedKeys: "Avgränsade lagringsnycklar",
                colStatus: "Isoleringsstatus",
                statusKeyOk: "Isolerad",
                noData: "Ingen entitetsscopead data hittades",
                globalKeysTitle: "Globala (system) nycklar",
                globalKeysDesc: "Dessa nycklar delas av alla entiteter avsiktligt — användare, inställningar, granskningslogg, licenser och annan systemgemensam data.",
                orphanKeysTitle: "Övergivna nycklar",
                orphanKeysDesc: "Dessa lagringsnycklar verkar tillhöra en borttagen entitet. De är ofarliga men kan rensas via en fullständig dataåterställning eller manuell borttagning.",
                issuesTitle: "Detekterade problem",
                noOrphans: "Inga övergivna nycklar detekterades.",
                type_parent: "Moderbolag",
                type_subsidiary: "Dotterbolag",
                type_branch: "Filial"
            },
            userImport: {
                title: "Importera användare",
                subtitle: "Lägg till många användare på en gång genom att ladda upp en CSV-fil. Förhandsgranska och validera innan import.",
                instructions: "Ladda upp en CSV-fil med en användare per rad. Obligatoriska kolumner: firstName, lastName, email, role. Valfritt: entityAccess (semikolonavgränsade entitets-ID:n eller 'all').",
                requiredColumns: "Obligatoriska kolumner:",
                columnList: "firstName, lastName, email, role",
                downloadTemplate: "Ladda ned mall",
                uploadLabel: "Klicka för att välja en CSV-fil",
                uploadHint: "Format som stöds: .csv (UTF-8)",
                validRows: "{{count}} giltiga",
                invalidRows: "{{count}} ogiltiga",
                colFirstName: "Förnamn",
                colLastName: "Efternamn",
                colEmail: "E-post",
                colRole: "Roll",
                colEntityAccess: "Entitetsåtkomst",
                colStatus: "Status",
                statusOk: "OK",
                changeFile: "Byt fil",
                cancel: "Avbryt",
                importBtn: "Importera {{count}} användare",
                noValidRows: "Inga giltiga rader att importera.",
                importSuccess: "{{count}} användare importerades.",
                importDone: "{{count}} användare importerades.",
                skippedRows: "{{count}} rad(er) hoppades över på grund av fel.",
                importAnother: "Importera fler användare",
                error_missingEmail: "E-post saknas",
                error_invalidEmail: "Ogiltigt e-postformat",
                error_duplicateEmail: "E-postadressen finns redan",
                error_missingRole: "Roll saknas",
                error_invalidRole: "Ogiltig roll (använd: admin, analyst eller viewer)"
            },
            apiKeys: {
                title: "API-nyckelhantering",
                subtitle: "Skapa och hantera API-nycklar för externa integrationer. Varje nyckel tilldelas specifika behörighetsomfång.",
                statTotal: "Totalt antal nycklar",
                statActive: "Aktiva nycklar",
                statRequests: "Totalt antal anrop",
                keysHeading: "API-nycklar",
                createKeyBtn: "Skapa API-nyckel",
                createTitle: "Skapa ny API-nyckel",
                labelName: "Nyckelnamn",
                namePlaceholder: "t.ex. Rapportintegration, BI-koppling...",
                labelScopes: "Behörighetsomfång",
                scopesHint: "Välj vilka behörigheter denna nyckel ska ha.",
                labelExpiry: "Utgångsdatum",
                expiryHint: "Lämna tomt för en nyckel utan utgångsdatum.",
                scopeGroup_read: "Läsåtkomst",
                scopeGroup_export: "Export",
                scopeGroup_admin: "Admin",
                scope_read_georisk: "Läs GeoRisk-data",
                scope_read_customers: "Läs kundriskdata",
                scope_read_bwra: "Läs BWRA-data",
                scope_read_compliance: "Läs compliance-data",
                scope_export_data: "Exportera data",
                scope_admin_reports: "Hantera rapporter",
                cancel: "Avbryt",
                createBtn: "Generera nyckel",
                colName: "Namn",
                colKey: "Nyckel (maskerad)",
                colScopes: "Omfång",
                colCreated: "Skapad",
                colExpiry: "Utgår",
                colUsage: "Anrop",
                colLastUsed: "Senast använd",
                colStatus: "Status",
                colActions: "Åtgärder",
                statusActive: "Aktiv",
                statusRevoked: "Återkallad",
                statusExpired: "Utgången",
                revokeBtn: "Återkalla",
                activateBtn: "Aktivera",
                deleteBtn: "Ta bort",
                confirmDelete: "Ta bort denna nyckel?",
                deleteConfirmYes: "Ja, ta bort",
                noExpiry: "Inget utgångsdatum",
                expired: "(utgången)",
                emptyTitle: "Inga API-nycklar ännu",
                emptyDesc: "Skapa din första API-nyckel för att tillåta externa system att integrera med Prime RMS.",
                keyCreated: "API-nyckeln \"{{name}}\" skapades.",
                keyOnceWarning: "Kopiera nyckeln nu — den visas inte igen.",
                copy: "Kopiera",
                copied: "Kopierad!",
                understood: "Jag har sparat nyckeln",
                infoNote: "API-nycklar ger programmatisk åtkomst till Prime RMS. Håll dem hemliga — behandla dem som lösenord. Återkalla varje nyckel som kan ha komprometterats.",
                keyCreatedToast: "API-nyckeln \"{{name}}\" skapades.",
                keyRevokedToast: "API-nyckeln \"{{name}}\" återkallades.",
                keyActivatedToast: "API-nyckeln \"{{name}}\" aktiverades.",
                keyDeletedToast: "API-nyckeln \"{{name}}\" togs bort.",
                errorNameRequired: "Nyckelnamn är obligatoriskt.",
                errorScopeRequired: "Välj minst ett behörighetsomfång.",
                close: "Stäng"
            },
            configTemplates: {
                title: "Konfigurationsmallar",
                subtitle: "Spara aktuella inställningar som en namngiven mall och applicera den på andra entiteter.",
                saveCurrentBtn: "Spara som mall",
                saveFormTitle: "Spara aktuella inställningar som mall",
                templateName: "Mallnamn",
                templateNamePlaceholder: "t.ex. Standard bankfilial, Högriskentitet...",
                templateDescription: "Beskrivning (valfritt)",
                templateDescriptionPlaceholder: "Kort beskrivning av denna konfiguration",
                saveBtn: "Spara mall",
                saving: "Sparar...",
                cancelBtn: "Avbryt",
                saveNote: "En ögonblicksbild av alla aktuella inställningar (session, granskning, moduler, varumärke) sparas.",
                savedSuccess: "Mallen sparades.",
                applyBtn: "Applicera",
                deleteBtn: "Ta bort",
                confirmApply: "Applicera mallen \"{{name}}\" på aktuella inställningar? Detta skriver över din nuvarande konfiguration.",
                confirmDelete: "Ta bort mallen \"{{name}}\"? Det går inte att ångra.",
                appliedSuccess: "Mallen \"{{name}}\" applicerades.",
                applyError: "Kunde inte applicera mallen.",
                deletedSuccess: "Mallen togs bort.",
                createdAt: "Skapad",
                emptyTitle: "Inga mallar sparade",
                emptyDesc: "Spara aktuella inställningar som en mall för att återanvända dem för andra entiteter."
            },
            notificationSettings: {
                title: "Notifieringsinställningar",
                subtitle: "Konfigurera vilka notifieringstyper som är aktiva per användarroll och hur ofta de levereras.",
                selectRole: "Roll:",
                builtIn: "Inbyggd",
                configFor: "Konfiguration för: {{role}}",
                colType: "Notifieringstyp",
                colEnabled: "Aktiv",
                colFrequency: "Frekvens",
                enableBtn: "Aktivera",
                disableBtn: "Inaktivera",
                savedToast: "Notifieringsinställningar sparade.",
                resetToast: "Inställningar återställda till standard.",
                resetDefaults: "Återställ standard",
                type_deadlines: "Deadlines och årsgenomgångar",
                type_deadlines_desc: "Aviseringar för kommande rapportdeadlines och BWRA-årsgenomgångsdatum.",
                type_staleData: "Föråldrad data",
                type_staleData_desc: "Notifieringar när moduldata inte uppdaterats inom krävd tidsram.",
                type_scheduledValidations: "Schemalagda modellvalideringar",
                type_scheduledValidations_desc: "Påminnelser för kommande eller försenade schemalagda modellvalideringar.",
                type_assigneeNotifications: "Uppgiftstilldelningar",
                type_assigneeNotifications_desc: "Notifieringar för rapporter eller uppgifter som tilldelats direkt till användaren.",
                type_kpiThresholds: "KPI-tröskelvarningar",
                type_kpiThresholds_desc: "Notifieringar när Dashboard KPI-värden överskrider konfigurerade trösklar.",
                freq_immediate: "Omedelbart",
                freq_daily: "Daglig sammanfattning",
                freq_weekly: "Veckovis sammanfattning",
                webhookTitle: "Webhook-integration",
                webhookDesc: "Skicka händelsedata till en extern URL när viktiga händelser inträffar i systemet. Payloaden är ett JSON-objekt med fälten event, payload, timestamp och source.",
                webhookUrl: "Endpoint-URL",
                webhookTest: "Skicka test",
                webhookTesting: "Skickar…",
                webhookTestOk: "Test-payload levererades.",
                webhookTestFail: "Kunde inte nå webhook-endpointen. Kontrollera URL:en och försök igen.",
                webhookSavedToast: "Webhook-inställningar sparade.",
                webhookEvents: "Händelser som triggar webhooken",
                webhookEvent_approvalGranted: "Godkännande beviljat",
                webhookEvent_approvalGranted_desc: "Utlöses när en modell eller rapport godkänns.",
                webhookEvent_ruleChanged: "Regel ändrad",
                webhookEvent_ruleChanged_desc: "Utlöses när en transaktionsövervakningsregel skapas, redigeras eller tas bort.",
                webhookEvent_newRiskAdded: "Ny risk tillagd",
                webhookEvent_newRiskAdded_desc: "Utlöses när en ny BWRA-riskpost skapas.",
                webhookEvent_reportCreated: "Rapport skapad",
                webhookEvent_reportCreated_desc: "Utlöses när en periodisk rapport skapas automatiskt eller publiceras.",
                webhookEvent_validationCompleted: "Validering klar",
                webhookEvent_validationCompleted_desc: "Utlöses när en schemalagd modellvalidering är klar."
            }
        },
        geoRisk: {
            tabs: {
                overview: "Översikt",
                riskManagement: "Riskhantering",
                countryManager: "Landshantering",
                modelConfiguration: "Modellkonfiguration",
                validation: "Validering",
                mapView: "Kartvy",
                abTest: "A/B-test",
                countryHistory: "Landshistorik",
                mapping: "Mappning",
                correlationAnalysis: "Indexkorrelation"
            },
            correlationAnalysis: {
                title: "Indexkorrelationsanalys",
                subtitle: "Pearsonkorrelation mellan indexpoäng över alla länder. Index med korrelation > 0,9 är potentiellt redundanta.",
                computeBtn: "Beräkna korrelationer",
                computing: "Beräknar...",
                noScoringIndexes: "Inga scoringindex med data tillgängliga. Lägg till scoringindex och ladda upp landsdata för att använda analysen.",
                matrixTitle: "Korrelationsmatris",
                warningThreshold: "Varning: korrelation > 0,9",
                warningHigh: "Hög korrelation",
                warningLow: "Låg dataöverlappning",
                countriesOverlap: "länder",
                legendTitle: "Korrelationsskala",
                legendNeg: "Stark negativ",
                legendNone: "Ingen korrelation",
                legendPos: "Stark positiv",
                warningList: "Högt korrelerade indexpar",
                warningListEmpty: "Inga par med korrelation över 0,9 hittades.",
                warningPairLabel: "Korrelation",
                warningPairCountries: "gemensamma länder",
                indexA: "Index A",
                indexB: "Index B",
                indexType: "Typ",
                indexTypeBoolean: "Boolesk",
                indexTypeScoring: "Poängsättning",
                insufficientData: "Otillräcklig data (< 3 gemensamma länder)"
            },
            countryHistory: {
                pageTitle: "Landsriskhistorik",
                pageSubtitle: "Spåra hur ett lands riskpoäng har utvecklats över tid och vilka indexändringar som drivit det",
                selectCountry: "Välj ett land för att visa dess riskhistorik",
                searchPlaceholder: "Sök land...",
                noData: "Ingen historisk data ännu. Ögonblicksbilder sparas automatiskt när du besöker denna sida.",
                singleSnapshot: "Bara en ögonblicksbild tillgänglig. Återkom efter att mer data har samlats in.",
                currentLevel: "Aktuell risknivå",
                chartTitle: "Risknivå över tid",
                snapshotsCount: "ögonblicksbilder registrerade",
                captureSnapshot: "Ta ögonblicksbild nu",
                snapshotCaptured: "Ögonblicksbild sparad",
                indexChanges: "Senaste indexhändelser",
                noIndexChanges: "Inga indexhändelser registrerade.",
                eventLabel: "uppdaterades",
                affectedCountry: "Detta land påverkades"
            },
            abTest: {
                title: "Modell A/B-test",
                subtitle: "Jämför två modellkonfigurationer sida vid sida för alla länder",
                modelA: "Modell A",
                modelB: "Modell B",
                currentModel: "Aktuell modell",
                vs: "VS",
                selectByType: "Annan modelltyp",
                selectByVersion: "Sparad version",
                noVersions: "inga sparade versioner",
                modelType: "Modelltyp",
                savedVersion: "Version",
                runComparison: "Kör jämförelse",
                running: "Kör...",
                summaryTitle: "Jämförelsesammanfattning",
                totalCountries: "Utvärderade länder",
                changed: "Annan klassificering",
                higherRisk: "Högre risk i B",
                lowerRisk: "Lägre risk i B",
                distributionComparison: "Fördelning av risknivåer",
                riskLevel: "Risknivå",
                delta: "Förändring",
                resultsTitle: "Land-för-land-resultat",
                showOnlyDiff: "Visa bara skillnader",
                noDifferences: "Inga skillnader — båda modellerna ger identiska resultat.",
                country: "Land",
                change: "Förändring",
                increased: "Ökad",
                decreased: "Minskad"
            },
            overview: {
                pageSubtitle: "Landsriskpoäng och fördelning för alla aktiva index",
                country: "Land",
                riskLevel: "Risknivå",
                searchCountry: "Sök land...",
                all: "Alla",
                veryHigh: "Mycket hög",
                high: "Hög",
                medium: "Medel",
                low: "Låg",
                unacceptable: "Oacceptabel",
                fatfStatus: "FATF-status",
                listed: "Listad",
                nonListed: "Ej listad",
                euStatus: "EU-status",
                highRisk: "Högrisk",
                normal: "Normal",
                exportData: "Exportera data",
                noResults: "Inga länder matchar aktuella filter.",
                summaryLabel: "Riskfördelning",
                summaryTotal: "Totalt",
                summaryHighPlus: "HÖG eller högre",
                clearFilters: "Rensa filter",
                notCalculated: "Ej beräknad",
                attributionTitle: "Riskdrivare",
                attributionTopContributors: "Viktigaste bidragande index",
                attributionWeight: "Vikt",
                attributionValue: "Värde",
                attributionContribution: "Bidrag",
                attributionNoData: "Ingen attributionsdata tillgänglig",
                attributionDeterministicLabel: "Utlöst av",
                attributionGroupLabel: "Regelgrupp",
                attributionDeductionLabel: "Poängavdrag",
                attributionShowWhy: "Varför?",
                attributionHideWhy: "Dölj"
            },
            override: {
                modalTitle: "Sätt manuell risknivå",
                modalTitleEdit: "Redigera manuell risknivå",
                info: "En manuell override ersätter modellens beräknade risknivå för detta land. En reviderbar motivering krävs.",
                riskLevelLabel: "Manuell risknivå",
                reasonLabel: "Motivering (obligatorisk)",
                reasonPlaceholder: "Beskriv varför detta lands risknivå sätts manuellt...",
                setBy: "Satt av",
                date: "Överskrivningsdatum",
                overrideBadge: "Manuell",
                overrideTooltip: "Denna risknivå har manuellt satts av en användare",
                removeButton: "Ta bort override",
                saveButton: "Spara override",
                cancelButton: "Avbryt",
                confirmRemoveTitle: "Ta bort override",
                confirmRemoveMessage: "Är du säker på att du vill ta bort den manuella overriden för detta land? Modellens beräknade nivå återställs.",
                reasonRequired: "En motivering krävs för att spara overriden.",
                currentOverride: "Aktuell override",
                modelLevel: "Modellnivå",
                renewButton: "Förnya override",
                renewedConfirm: "Override förnyad.",
                staleWarning: "Denna override sattes för {days} dagar sedan och har inte granskats. Bekräfta att den fortfarande är giltig.",
                staleBadge: "Granskning krävs",
                staleAlertTitle: "{count} manuell(a) override(s) behöver granskas",
                staleAlertItem: "{country} — {days} dagar sedan senaste granskning",
                staleAlertDismiss: "Stäng",
                lastReviewed: "Senast granskad",
                reviewedBy: "Granskad av",
                reviewHistory: "Granskningshistorik",
                reviewPeriodLabel: "Granskningsperiod (dagar)",
                reviewPeriodSaved: "Granskningsperiod sparad"
            },
            comparison: {
                title: "Landsjämförelse",
                back: "← Tillbaka till översikt",
                attribute: "Attribut",
                legendHighest: "Högsta värde",
                legendLowest: "Lägsta värde",
                legendDiff: "Värden skiljer sig mellan länder",
                compareButton: "Jämför",
                selectHint: "Välj 2–4 länder att jämföra",
                clearSelection: "Rensa urval"
            },
            riskExplanation: {
                title: "Riskf\u00f6rklaring i klarspr\u00e5k",
                generateBtn: "F\u00f6rklara risk",
                copyBtn: "Kopiera text",
                copiedFeedback: "Kopierat!",
                exportBtn: "Exportera (.txt)",
                close: "St\u00e4ng",
                noActiveIndexes: "Inga aktiva riskindex \u2014 risken kan inte bed\u00f6mas.",
                generatedNote: "Automatiskt genererad fr\u00e5n aktuell modellkonfiguration",
                assessedToHave: " bed\u00f6ms ha ",
                riskSuffix: " risk.",
                basisPrefix: "Bed\u00f6mningen baseras p\u00e5 ",
                basisIndexCount: " aktivt riskindex.",
                basisIndexCountPlural: " aktiva riskindex.",
                noTriggersNote: "Inga riskfaktorer har triggats \u00f6ver sina tr\u00f6skelv\u00e4rden. Landet uppfyller baslinjens riskniv\u00e5.",
                detDriversLabel: "Risken drivs av f\u00f6ljande faktorer:",
                weightedScoreIntro: "Baserat p\u00e5 viktad analys \u00e4r den \u00f6vergripande riskpo\u00e4ngen",
                weightedTopLabel: "Viktigaste bidragande faktorer:",
                weightedRowWeight: "vikt",
                weightedRowContrib: "bidrag",
                invertedScoreIntro: "Med en baspo\u00e4ng p\u00e5",
                invertedScoreEnd: " \u00e4r den slutliga riskpo\u00e4ngen",
                invertedDeductionsLabel: "Po\u00e4ngreducerande faktorer:",
                invertedDeductionOf: "avdrag:",
                ruleGroupsLabel: "Triggade riskregel-grupper:",
                ruleGroupTriggeredBy: "triggad av:",
                footerNote: "Denna f\u00f6rklaring \u00e4r avsedd f\u00f6r styrelseprotokoll och compliance-dokumentation."
            },
            export: {
                country: "Land",
                riskClass: "Riskklass",
                fatfStatus: "FATF-status",
                euStatus: "EU-status",
                cpiScore: "CPI-poäng",
                lastUpdated: "Senast uppdaterad",
                dataSources: "Datakällor"
            },
            mapView: {
                subtitle: "Geografisk fördelning av landrisk. Hovra för detaljer, klicka för att filtrera landstabellen.",
                legendLabel: "Risknivåfilter",
                filterHint: "Klicka för att filtrera på denna risknivå",
                clearFilter: "Visa alla",
                mapAriaLabel: "Världskarta med landrisk visualiserad som färgade bubblor",
                equator: "Ekvator",
                fatfListed: "FATF-listad",
                euHighRisk: "EU högrisk",
                clickHint: "Klicka för att filtrera landstabellen",
                showing: "Visar {{count}} av {{total}} länder"
            },
            stalenessAlerts: {
                sectionLabel: "Datafärskhetslarm",
                title: "{{count}} index behöver uppdateras",
                lastUpdated: "Senast uppdaterad",
                daysStale: "{{days}} dagar försenat",
                affectedCountries: "{{count}} länder",
                dismiss: "Avfärda larm",
                dashboardBadge: "{{count}} inaktuella index",
                dashboardHint: "Visa larm i GeoRisk-översikten"
            },
            riskMgmt: {
                pageSubtitle: "Hantera riskindex, konfigurera poängregler och uppdatera landsdata",
                addNewIndex: "Lägg till nytt index",
                riskIndex: "Riskindex",
                indexName: "Indexnamn",
                type: "Typ",
                lastUpdated: "Senast uppdaterad",
                status: "Status",
                actions: "Åtgärder",
                edit: "Redigera",
                name: "Namn",
                description: "Beskrivning",
                source: "Källa",
                updateFrequency: "Uppdateringsfrekvens",
                cancel: "Avbryt",
                createIndex: "Skapa index",
                editIndex: "Redigera index",
                save: "Spara",
                delete: "Ta bort",
                confirmDeleteTitle: "Ta bort index",
                confirmDeleteMessage: "Är du säker på att du vill ta bort indexet \"{{name}}\"? Denna åtgärd kan inte ångras.",
                confirm: "Ta bort",
                indexDeleted: "Index borttaget",
                // IndexDetailView
                countryScores: "Landspoäng",
                countriesWithValues: "Länder med värden",
                countriesWithoutValues: "Länder utan värden",
                allCountries: "Alla länder",
                uploadNewData: "Ladda upp ny data",
                countryCode: "Landskod",
                country: "Land",
                sourceValue: "Källvärde",
                calculatedValue: "Beräknat värde",
                statusCol: "Status",
                lastUpdatedCol: "Senast uppdaterad",
                updateSourceCol: "Uppdateringskälla",
                manualChange: "Manuell ändring",
                sourceRef: "Källreferens",
                sourceRefPlaceholder: "t.ex. FATF MER 2025, Transparency International CPI 2024",
                yes: "Ja",
                no: "Nej",
                invalidType: "Ogiltig typ",
                close: "Stäng",
                indexType: "Indextyp",
                associatedRisks: "Associerade risker",
                scaleMaximum: "Skalmaximum",
                invertedScale: "Inverterad skala",
                normalizeValues: "Normalisera värden",
                displayValueMissing: "Visningsvärde för saknad data",
                newCountryDefault: "Standardvärde nytt land",
                changesSaved: "Ändringarna har sparats",
                // ScoreEditor
                editScoreFor: "Redigera poäng för {{name}}",
                enterScore: "Ange poäng",
                invalidScore: "Ange ett giltigt poäng mellan {{min}} och {{max}}.",
                // ChangesSummary
                reviewChanges: "Granska ändringar",
                scoreChanges: "Poängändringar",
                configChanges: "Konfigurationsändringar",
                previousValue: "Tidigare värde",
                newValue: "Nytt värde",
                setting: "Inställning",
                statusActive: "Aktiv",
                statusInactive: "Inaktiv",
                commentRequired: "Kommentar krävs",
                commentLabel: "Kommentar (obligatorisk)",
                commentPlaceholder: "Beskriv ändringarna...",
                confirmBtn: "Bekräfta",
                // DataUpload
                uploadTitle: "Ladda upp ny data",
                validationErrors: "Valideringsfel",
                preview: "Förhandsgranskning",
                value: "Värde",
                importData: "Importera data",
                errorParsingFile: "Fel vid filanalys: {{message}}",
                invalidCountry: "Ogiltigt land: {{name}}",
                invalidValue: "Ogiltigt värde för {{country}}: {{value}}",
                valueOutOfRange: "Värde för {{country}} måste vara mellan {{min}} och {{max}}",
                formatInstructionsTitle: "Förväntat filformat",
                formatInstructionsIntro: "Format som stöds:",
                formatCsvTitle: "CSV (komma- eller semikolonseparerad)",
                formatCsvExample: "Kolumn 1: landskod (alpha-2), Kolumn 2: numeriskt värde",
                formatJsonTitle: "JSON-objekt",
                formatXlsxTitle: "Excel (XLSX / XLS)",
                formatXlsxExample: "Kolumn A: landskod (alpha-2), Kolumn B: numeriskt värde",
                formatNote: "Rubriker är valfria och identifieras automatiskt.",
                // DataUpload diff preview
                importDiffExistingValue: "Befintligt",
                importDiffNewValue: "Nytt",
                importDiffChange: "Förändring",
                importDiffStatusNew: "Ny",
                importDiffStatusChanged: "Ändrad",
                importDiffStatusUnchanged: "Oförändrad",
                importDiffSummary: "{{changed}} ändrade, {{added}} nya, {{unchanged}} oförändrade",
                importDiffPreviewTitle: "Förhandsgranskning — ändringar mot befintlig data",
                importDiffNoChanges: "Inga ändringar — alla importerade värden matchar befintlig data.",
                // VersionHistory
                versionHistory: "Versionshistorik",
                version: "Version {{number}}",
                updatedBy: "Uppdaterad av: {{name}}",
                versionComment: "Kommentar: {{comment}}",
                noVersions: "Inga versioner sparade ännu. Spara ändringar för att skapa den första versionen.",
                file: "Fil: {{name}}",
                changes: "Ändringar:",
                // IndexDetails tabs
                indexInfo: "Indexinfo",
                configuration: "Konfiguration",
                updatedByLabel: "Uppdaterad av",
                // Status translations
                status_active: "Aktiv",
                status_pendingUpdate: "Väntande uppdatering",
                status_outdated: "Föråldrad",
                // Type translations
                type_booleanType: "Boolesk lista",
                type_scoringType: "Poängindex",
                type_dominantType: "Dominant",
                type_weightedType: "Viktad",
                // Update frequency translations
                freq_quarterly: "Kvartalsvis",
                freq_yearly: "Årligen",
                freq_semiAnnual: "Halvårsvis",
                freq_monthly: "Månadsvis",
                freq_daily: "Dagligen",
                freq_weekly: "Veckovis",
                stalenessThresholdInfo: "Stalhetsgräns: {{days}} dagar",
                // IndexList — declared risks column
                coversRisks: "Täcker risker",
                coversRisksNone: "Ingen",
                coversRisksCount: "{{count}} risk",
                coversRisksCountPlural: "{{count}} risker",
                coversRisksModalTitle: "Täckta BWRA-risker",
                coversRisksModalDesc: "Följande BWRA-risker adresseras av detta index:",
                // TypeConfig
                indexTypeConfig: "Indextypskonfiguration",
                selectIndexType: "Välj indextyp:",
                scoringIndex: "Poängindex",
                booleanList: "Boolesk lista",
                associatedRisksLabel: "Associerade risker:",
                bwraCategoriesLabel: "BWRA-risktäckning:",
                bwraCategoriesHelp: "Vilka BWRA-riskkategorier detta index bidrar till att täcka",
                declaredRisksLabel: "Adresserade risker:",
                declaredRisksHelp: "Vilka specifika BWRA-risker detta index bidrar till att adressera",
                noRisksRegistered: "Inga risker registrerade i BWRA",
                impactType: "Effekttyp:",
                impactScore: "Poäng",
                impactRiskLevel: "Risknivå",
                riskScore: "Riskpoäng:",
                riskLevelLabel: "Risknivå:",
                veryHigh: "Mycket hög",
                high: "Hög",
                mediumLevel: "Medel",
                lowLevel: "Låg",
                allowNullValues: "Tillåt null-värden:",
                // ScaleConfig
                scaleConfiguration: "Skalakonfiguration",
                scaleRange: "Skalomfång:",
                to: "till",
                invertedScaleLabel: "Inverterad skala:",
                invertedScaleHelp: "Markera om högre värden innebär lägre risk",
                normalizeValuesLabel: "Normalisera värden:",
                normalizeValuesHelp: "Normalisera värden automatiskt till 0-100 skala",
                // DefaultValues
                defaultValues: "Standardvärden",
                displayValueLabel: "Visningsvärde för saknad data:",
                newCountryDefaultLabel: "Standardvärde nytt land:",
                noDefaultPlaceholder: "Lämna tomt för inget standardvärde",
                // Risks
                moneyLaundering: "Penningtvättsrisk",
                terroristFinancing: "Terrorfinansieringsrisk",
                sanctions: "Sanktionsrisk",
                corruption: "Korruptionsrisk",
                taxEvasion: "Skatteflykttsrisk",
                proliferationFinancing: "Proliferingsfinansieringsrisk",
                regulatory: "Regulatorisk risk",
                minValuePlaceholder: "Minvärde",
                maxValuePlaceholder: "Maxvärde",
                // Empty state
                emptyStateTitle: "Inga index definierade",
                emptyStateDesc: "Kom igång genom att lägga till ditt första riskindex.",
                emptyStateAddBtn: "Lägg till index"
            },
            dataPanel: {
                currentValues: "Aktuella värden",
                uploadNewData: "Ladda upp ny data",
                noData: "Ingen data tillgänglig för detta index. Ladda upp data för att komma igång.",
                countryCode: "Landskod",
                country: "Land",
                value: "Värde"
            },
            countryManager: {
                title: "Hantera länder",
                addCountry: "Lägg till land",
                addNewCountry: "Lägg till nytt land",
                alpha2: "Alpha2",
                alpha3: "Alpha3",
                nameEn: "Namn (EN)",
                nameSv: "Namn (SV)",
                options: "Alternativ",
                edit: "Redigera",
                editCountry: "Redigera land",
                delete: "Ta bort",
                deleteConfirm: "Är du säker på att du vill ta bort detta land? Åtgärden kan inte ångras.",
                allFieldsRequired: "Alla fält är obligatoriska.",
                cancel: "Avbryt",
                save: "Spara",
                search: "Sök länder (namn, alpha2, alpha3)...",
                countries: "länder",
                noResults: "Inga länder matchar din sökning.",
                previous: "Föregående",
                next: "Nästa",
                page: "Sida",
                alpha2Format: "Alpha-2-kod måste vara exakt 2 stora bokstäver (t.ex. SE).",
                alpha3Format: "Alpha-3-kod måste vara exakt 3 stora bokstäver (t.ex. SWE).",
                duplicateAlpha2: "Ett land med denna Alpha-2-kod finns redan.",
                riskLevel: "Risknivå"
            },
            modelConfig: {
                title: "Konfigurera riskmodell",
                generatePreview: "Förhandsgranska",
                exportConfig: "Exportera konfiguration",
                previewResults: "Förhandsgranskning",
                noRiskIndicators: "Inga riskindikatorer",
                score: "Poäng",
                base: "Bas",
                boosts: "Boost",
                weightedAvg: "Viktat medel",
                topContributors: "Största bidrag",
                started: "Start",
                deductions: "Avdrag",
                mainDeductions: "Största avdrag",
                previewNoData: "Ingen landsdata tillgänglig. Lägg till data i index via Riskhantering för att generera en förhandsgranskning.",
                previewNoActiveIndexes: "Inga aktiva index konfigurerade — lägg till index i Riskhantering.",
                unsavedChanges: "osparade ändringar",
                saveChanges: "Spara ändringar",
                discardChanges: "Ångra ändringar",
                failedToSave: "Kunde inte spara ändringar: ",
                draftFoundTitle: "Osparat utkast hittades",
                draftFoundMessage: "Osparade ändringar från {timestamp} hittades. Vill du återställa utkastet eller ignorera det?",
                draftRestore: "Återställ utkast",
                draftIgnore: "Ignorera",
                generalSettings: "Allmänna modellinställningar",
                modelType: "Modelltyp:",
                dominantModel: "Dominant modell",
                weightedModel: "Viktad modell",
                ruleGroupsModel: "Regelgruppsmodell",
                invertedModel: "Inverterad modell",
                dominantHelp: "Högsta risken från valfritt index vinner (traditionell dominant modell)",
                weightedHelp: "Alla index viktas och kombineras till en slutpoäng (0-100)",
                ruleGroupsHelp: "Index grupperas med maxboost per grupp",
                invertedHelp: "Länder startar vid hög risk (100) och får avdrag för bra poäng",
                lowestRiskLevel: "Lägsta risknivå:",
                low: "Låg",
                weightedConfig: "Viktad modellkonfiguration",
                weightedConfigHelp: "Varje index viktas och kombineras till en riskpoäng (0-100). Högre vikt gör ett index viktigare i beräkningen.",
                weight: "Vikt:",
                ruleGroupsConfig: "Regelgruppskonfiguration",
                ruleGroupsConfigHelp: "Konfigurera hur olika riskfaktorer kombineras. Varje grupp har en maxboost som tillämpas även om flera villkor i gruppen uppfylls.",
                groupName: "Gruppnamn",
                maxBoost: "Maxboost:",
                remove: "Ta bort",
                assignIndexes: "Tilldela index till denna grupp:",
                alreadyAssigned: "Redan tilldelad",
                addNewRuleGroup: "Lägg till ny regelgrupp",
                newRuleGroup: "Ny regelgrupp",
                baseRiskLabel: "Basrisk:",
                baseRiskLow: "Länder startar vid 0 (Låg risk).",
                baseRiskMedium: "Länder startar vid 1 (Medel risk).",
                invertedConfigTitle: "Inverterad modellkonfiguration",
                invertedConfigHelp: "Länder startar vid 100 (Hög risk) och får avdrag för bra indexvärden. Saknade data behåller hög risk – länder måste bevisa låg risk.",
                deductionWeight: "Avdragsvikt:",
                riskLevelsTitle: "Risknivåer och tröskelvärden (0-4 skala)",
                riskLevelsHelp: "Konfigurera mappning mellan poäng och risknivå. Risknivåer filtreras baserat på inställningen Lägsta risknivå.",
                invertedSettings: "Inverterade modellinställningar",
                startingScore: "Startpoäng:",
                booleanDeduction: "Booleskt avdrag:",
                maxScoringDeduction: "Max poängavdrag:",
                minimumScore: "Minimipoäng:",
                howItWorks: "Så fungerar det:",
                viewVersionHistory: "Visa versionshistorik",
                riskConfiguration: "Riskkonfiguration",
                booleanScale: "Boolesk (Finns/Finns inte)",
                unknownScale: "Okänd skala",
                whenCountryPresent: "När land finns på denna lista:",
                presentEqualsRisk: "Finns = Risk",
                absentEqualsRisk: "Finns inte = Risk",
                riskBoostValue: "Riskhöjningsvärde:",
                riskTriggerConfig: "Risktrigger-konfiguration",
                triggerThresholdAbove: "Tröskelvärde (poäng ÖVER detta utlöser boost):",
                triggerThresholdBelow: "Tröskelvärde (poäng UNDER detta utlöser boost):",
                invertedWarning: "Detta index är inverterat – lägre poäng innebär högre risk",
                weightConfig: "Viktkonfiguration",
                weightImportance: "Vikt (viktighetsfaktor):",
                weightHelp: "Högre vikt gör detta index viktigare i viktade beräkningar",
                closeSettings: "Stäng inställningar",
                indexesPanel: "Riskindex",
                indexesPanelHint: "Klicka på en rad för att konfigurera per-index-inställningar",
                settingsPanel: "Modellkonfiguration",
                step1: "Konfigurera index",
                step2: "Välj modelltyp",
                step3: "Förhandsgranska & spara",
                typeLabel: "Typ:",
                sourceLabel: "Källa:",
                scaleLabel: "Skala:",
                lastUpdatedLabel: "Senast uppdaterad:",
                // ModelSnapshot
                noModelData: "Ingen modelldata tillgänglig",
                yes: "Ja",
                no: "Nej",
                indexConfiguration: "Indexkonfiguration",
                noIndexConfigDeterministic: "Ingen indexkonfiguration för deterministisk modell",
                activationThreshold: "Aktiveringströskelvärde:",
                notSet: "Ej satt",
                activation: "Aktivering:",
                presenceTriggers: "Närvaro utlöser",
                listBasedActivation: "Listbaserad aktivering",
                riskLevelsLabel: "Risknivåer",
                ruleGroupsLabel: "Regelgrupper",
                maxBoostLabel: "Maxboost:",
                noIndexesAssigned: "Inga index tilldelade",
                invertedModelConfig: "Inverterad modellkonfiguration",
                modelTypeLabel: "Modelltyp",
                lowestRiskLevelLabel: "Lägsta risknivå",
                // ModelChangesSummary
                reviewModelChanges: "Granska modelländringar",
                reviewChangesDescription: "Granska ändringarna innan du sparar. En kommentar och ditt namn krävs.",
                changeTypeModelType: "Modelltyp",
                changeTypeIndexWeights: "Indexvikter",
                changeTypeDeductionWeights: "Avdragsvikter",
                changeTypeRiskLevel: "Risknivåkonfiguration",
                changeTypeRuleGroups: "Regelgrupper",
                changeTypeGeneralSetting: "Allmänna inställningar",
                changeTypeHybridSetting: "Hybridinställningar",
                changeTypeInvertedConfig: "Inverterad modellkonfiguration",
                changes: "ändringar",
                fieldColumn: "Fält",
                previousValue: "Föregående värde",
                newValue: "Nytt värde",
                descriptionColumn: "Beskrivning",
                changesSavedBy: "Ändringar sparas av:",
                currentUser: "Aktuell användare",
                commentRequired: "Kommentar (obligatorisk)",
                commentRequiredAlert: "Kommentar krävs",
                userRequiredAlert: "Kan inte fastställa aktuell användare. Logga in igen.",
                commentPlaceholder: "Beskriv ändringarna...",
                saveChangesCount: "Spara ändringar ({{count}} totalt)",
                cancel: "Avbryt",
                // VersionHistoryModal
                versionHistory: "Versionshistorik",
                versionLabel: "Version",
                noVersionsYet: "Inga sparade versioner ännu. Spara en modellkonfiguration för att börja spåra historik.",
                restoreVersion: "Återställ",
                restoreVersionConfirm: "Återställ version {{version}} från {{date}}? Konfigurationen laddas som osparade ändringar — du kan granska och spara den.",
                restoreVersionSuccess: "Version {{version}} återställd — granska och spara för att tillämpa.",
                // VersionDetailModal
                byUser: "av",
                commentLabel: "Kommentar:",
                modelConfiguration: "Modellkonfiguration",
                changesMade: "Ändringar ({{count}})",
                // IndexSettings help-text
                scaleRange: "Skala: 1-{{max}}",
                normalizedSuffix: " (normaliserad till 1-100)",
                invertedSuffix: " (inverterad – lägre värden = högre risk)",
                booleanHelpText: "+{{boost}} läggs till basrisken när villkoret uppfylls",
                scoringHelpText: "När landets poäng är {{direction}} {{threshold}} utlöses +{{boost}} riskhöjning",
                scoringHelpAbove: "över",
                scoringHelpBelow: "under",
                scoringBoostHelpText: "+{{boost}} läggs till basrisken när tröskelvärdet utlöses",
                // GeneralSettings inverted explanation
                baseRiskDeterminedBy: "Bestäms av inställningen \"Lägsta risknivå\" ovan.",
                invertedExplCountries: "Länder startar med {{score}} poäng (hög risk)",
                invertedExplBoolean: "Booleska index: -{{deduction}} poäng vid falskt/0",
                invertedExplScoring: "Poängindex: upp till -{{deduction}} poäng baserat på poäng",
                invertedExplMinimum: "Slutpoängen kan inte gå under {{min}}",
                minPlaceholder: "Min",
                maxPlaceholder: "Max",
                chooseColor: "Välj färg",
                // Ändringsbeskrivningar (useModelChangeTracker)
                changeDesc: {
                    changedModelType: 'Ändrade modelltyp från "{{from}}" till "{{to}}"',
                    setWeight: 'Satte vikt för {{name}} till {{value}} (ny parameter)',
                    changedWeight: 'Ändrade vikt för {{name}} från {{from}} till {{to}}',
                    setDeductionWeight: 'Satte avdragsvikt för {{name}} till {{value}} (ny parameter)',
                    changedDeductionWeight: 'Ändrade avdragsvikt för {{name}} från {{from}} till {{to}}',
                    setMinThreshold: 'Satte minimumtröskel för risknivån {{level}} till {{value}} (ny parameter)',
                    changedMinThreshold: 'Ändrade minimumtröskel för risknivån {{level}} från {{from}} till {{to}}',
                    setMaxThreshold: 'Satte maximumtröskel för risknivån {{level}} till {{value}} (ny parameter)',
                    changedMaxThreshold: 'Ändrade maximumtröskel för risknivån {{level}} från {{from}} till {{to}}',
                    setColor: 'Satte färg för risknivån {{level}} till {{value}} (ny parameter)',
                    changedColor: 'Ändrade färg för risknivån {{level}} från {{from}} till {{to}}',
                    setRiskLevelSetting: 'Satte risknivåinställning: {{field}}',
                    changedRiskLevelSetting: 'Ändrade risknivåinställning: {{field}}',
                    addedIndexesToGroup: 'Lade till index i regelgruppen "{{group}}": {{indexes}}',
                    createdEmptyGroup: 'Skapade regelgruppen "{{group}}" utan index',
                    modifiedGroupAddedRemoved: 'Ändrade regelgruppen "{{group}}": lade till {{added}}, tog bort {{removed}}',
                    addedToGroup: 'Lade till i regelgruppen "{{group}}": {{indexes}}',
                    removedFromGroup: 'Tog bort från regelgruppen "{{group}}": {{indexes}}',
                    modifiedGroupIndexes: 'Ändrade indextilldelningar för regelgruppen "{{group}}"',
                    setGroupName: 'Satte namn för regelgruppen "{{group}}" till "{{value}}" (ny parameter)',
                    changedGroupName: 'Ändrade regelgruppsnamn från "{{from}}" till "{{to}}"',
                    setGroupMaxBoost: 'Satte maxboost för regelgruppen "{{group}}" till {{value}} (ny parameter)',
                    changedGroupMaxBoost: 'Ändrade maxboost för regelgruppen "{{group}}" från {{from}} till {{to}}',
                    createdRuleGroups: 'Skapade regelgrupper: {{groups}} (ny parameter)',
                    createdEmptyRuleGroups: 'Skapade tom regelgruppskonfiguration (ny parameter)',
                    addedRuleGroup: 'Lade till ny regelgrupp: "{{name}}"',
                    removedRuleGroup: 'Tog bort regelgrupp',
                    modifiedRuleGroupsConfig: 'Ändrade regelgruppskonfiguration',
                    setRuleGroupSetting: 'Satte regelgruppsinställning: {{field}}',
                    changedRuleGroupSetting: 'Ändrade regelgruppsinställning: {{field}}',
                    setLowestRiskLevel: 'Satte lägsta risknivå till "{{value}}" (ny parameter)',
                    changedLowestRiskLevel: 'Ändrade lägsta risknivå från "{{from}}" till "{{to}}"',
                    setGeneralSetting: 'Satte allmän inställning: {{field}}',
                    changedGeneralSetting: 'Ändrade allmän inställning: {{field}}',
                    setHybridStrategy: 'Satte hybridstrategi till "{{value}}" (ny parameter)',
                    changedHybridStrategy: 'Ändrade hybridstrategi från "{{from}}" till "{{to}}"',
                    setAdditiveThreshold: 'Satte additivt tröskelvärde till {{value}} (ny parameter)',
                    changedAdditiveThreshold: 'Ändrade additivt tröskelvärde från {{from}} till {{to}}',
                    setWeightedThreshold: 'Satte viktat tröskelvärde till {{value}} (ny parameter)',
                    changedWeightedThreshold: 'Ändrade viktat tröskelvärde från {{from}} till {{to}}',
                    setHybridSetting: 'Satte hybridinställning: {{field}}',
                    changedHybridSetting: 'Ändrade hybridinställning: {{field}}',
                    setStartingScore: 'Satte startpoäng till {{value}} (ny parameter)',
                    changedStartingScore: 'Ändrade startpoäng från {{from}} till {{to}}',
                    setBooleanDeduction: 'Satte booleskt avdrag till {{value}} (ny parameter)',
                    changedBooleanDeduction: 'Ändrade booleskt avdrag från {{from}} till {{to}}',
                    setMaxScoringDeduction: 'Satte max poängavdrag till {{value}} (ny parameter)',
                    changedMaxScoringDeduction: 'Ändrade max poängavdrag från {{from}} till {{to}}',
                    setMinimumScore: 'Satte minimipoäng till {{value}} (ny parameter)',
                    changedMinimumScore: 'Ändrade minimipoäng från {{from}} till {{to}}',
                    setInvertedConfig: 'Satte inverterad konfiguration: {{field}}',
                    changedInvertedConfig: 'Ändrade inverterad konfiguration: {{field}}',
                    setField: 'Satte {{field}} till {{value}} (ny parameter)',
                    changedField: 'Ändrade {{field}} från {{from}} till {{to}}'
                },
                // Modellgovernance-godkännande (COMP-038)
                approval: {
                    panelTitle: "Modellgovernance",
                    statusDraft: "Utkast",
                    statusSubmitted: "Under granskning",
                    statusApproved: "Godkänd",
                    submitForReview: "Skicka för godkännande",
                    approveModel: "Godkänn konfiguration",
                    rejectModel: "Avvisa",
                    approveComment: "Godkännandekommentar (valfri)",
                    rejectComment: "Skäl för avvisning (obligatorisk)",
                    rejectCommentRequired: "Ange ett skäl för avvisningen.",
                    approvedBy: "Godkänd av",
                    approvedAt: "Godkänd datum",
                    approvalHistory: "Godkännandehistorik",
                    noHistory: "Inga godkännandeåtgärder ännu.",
                    historyFrom: "Från",
                    historyTo: "Till",
                    historyBy: "Av",
                    historyAt: "Datum",
                    historyComment: "Kommentar",
                    lockedMessage: "Konfigurationen är låst under granskning. Avvisa för att låsa upp för redigering.",
                    approvedLockedMessage: "Konfigurationen är godkänd och aktiv. Spara nya ändringar för att starta en ny granskningscykel.",
                    toastSubmitted: "Konfigurationen har skickats för godkännande.",
                    toastApproved: "Konfigurationen är godkänd och aktiv.",
                    toastRejected: "Konfigurationen har återsänts till utkast.",
                    lockedChangesHint: "Konfigurationen är låst — avvisa granskningen för att göra ändringar.",
                }
            },
            preview: {
                title: "Förhandsgranskning av konfiguration",
                country: "Land",
                originalValue: "Originalvärde",
                processedValue: "Bearbetat värde",
                displayValue: "Visningsvärde"
            },
            validation: {
                title: "Modell- och datavalidering",
                lastRun: "Senast körd",
                rerunValidation: "Kör validering igen",
                rerunComplete: "Validering slutförd",
                expandAll: "Expandera alla",
                collapseAll: "Kollapsa alla",
                all: "Alla",
                errors: "Fel",
                warnings: "Varningar",
                passed: "Godkänd",
                totalChecks: "Totalt antal kontroller",
                noChecksMatch: "Inga kontroller matchar aktuellt filter.",
                clearFilter: "Visa alla",
                navigateToIndex: "Öppna {{name}} i Riskhantering",
                modelConfiguration: "Modellkonfiguration",
                dataFreshness: "Dataaktualitet",
                dataCompleteness: "Datakomplettering",
                countryData: "Landsdata",
                manualOverrides: "Manuella risknivåer",
                checksCount: "{{count}} kontroll(er)",
                checks: {
                    indexAvailability: "Indextillgänglighet",
                    indexTypeValidity: "Indextypvaliditet",
                    indexStatus: "Indexstatus",
                    scoringScale: "Poängskala-konfiguration",
                    riskModelInstantiation: "RiskModel-initiering",
                    updateStatus: "{{name}} – uppdateringsstatus",
                    requiredFields: "{{name}} – obligatoriska fält",
                    riskCategorization: "{{name}} – riskkategorisering",
                    countryList: "Landslista",
                    countryDataIntegrity: "Landsdata-integritet",
                    uniqueCountryCodes: "Unika landskoder",
                    overrideCount: "Aktiva manuella risknivåer",
                    staleOverrides: "Overrides som behöver granskning"
                },
                messages: {
                    indexesConfigured: "{{count}} index konfigurerade",
                    noIndexesConfigured: "Inga index konfigurerade – modellen kan inte beräkna risk",
                    allIndexesValidType: "Alla index har giltig typ (BOOLEAN eller SCORING)",
                    invalidTypeCount: "{{count}} index har ogiltig typ",
                    allIndexesActive: "Alla index är AKTIVA",
                    inactiveCount: "{{count}} index är inte AKTIVA",
                    allScoringScaleConfigured: "Alla {{count}} poängindex har skala konfigurerad",
                    missingScoringScale: "{{count}} poängindex saknar scale.maxValue",
                    riskModelOk: "RiskModel initieras och valideras korrekt",
                    riskModelFailed: "RiskModel kunde inte initieras eller valideras",
                    noUpdateDate: "Inget uppdateringsdatum registrerat",
                    overdue: "Försenad: senast uppdaterad för {{days}} dagar sedan (gräns: {{threshold}} dagar för {{frequency}})",
                    approachingDeadline: "Närmar sig deadline: senast uppdaterad för {{days}} dagar sedan (gräns: {{threshold}} dagar)",
                    upToDate: "Aktuell: senast uppdaterad för {{days}} dagar sedan",
                    allRequiredFieldsPresent: "Alla obligatoriska fält finns",
                    missingFields: "Saknade fält: {{fields}}",
                    coversRiskCategories: "Täcker {{count}} riskkategori(er): {{categories}}",
                    noRiskCategoriesDefined: "Inga riskkategorier definierade",
                    countriesInSystem: "{{count}} länder i systemet",
                    expectedCountryCoverage: "Förväntar minst 100 länder för heltäckande coverage",
                    allCountriesHaveFields: "Alla länder har alpha2-kod och engelskt namn",
                    countriesMissingFields: "{{count}} länder saknar obligatoriska fält",
                    allCountryCodesUnique: "Alla landskoder är unika",
                    duplicateCodesFound: "{{count}} dubblettkod(er) hittade",
                    noOverrides: "Inga aktiva manuella risknivåer",
                    activeOverrides: "{{count}} aktiv(a) manuell(a) risknivå(er) — modellresultat ersatt för dessa länder",
                    noStaleOverrides: "Alla overrides är inom granskningsperioden",
                    staleOverrideCount: "{{count}} override(s) behöver granskas — granskningsperioden har gått ut",
                    daysAgo: "dagar sedan senaste granskning"
                },
                details: {
                    lastUpdated: "Senast uppdaterad: {{date}}",
                    updatedBy: "Uppdaterad av: {{user}}",
                    frequency: "Frekvens: {{frequency}}",
                    source: "Källa: {{source}}",
                    countriesInSystem: "Länder i systemet: {{count}}"
                },
                modelChecks: "Modellkontroller",
                dataValidation: "Datavalidering",
                exportReport: "Exportera rapport",
                export: {
                    sheetChecks: "Modellkontroller",
                    sheetSummary: "Sammanfattning",
                    colCategory: "Kategori",
                    colCheck: "Kontroll",
                    colStatus: "Status",
                    colMessage: "Meddelande",
                    colDetails: "Detaljer",
                    statusPass: "GODKÄND",
                    statusWarning: "VARNING",
                    statusError: "FEL",
                    summaryTitle: "GeoRisk Modellvalideringsrapport",
                    totalChecks: "Totalt antal kontroller",
                    errors: "Fel",
                    warnings: "Varningar",
                    passed: "Godkända",
                    generatedBy: "Genererad av Prime RMS",
                    entity: "Entitet",
                    runAt: "Körd"
                }
            },
            dataValidation: {
                title: "Datavalidering",
                description: "Ladda upp anonymiserad produktionsdata för att validera hur väl din modell fångar verkligheten.",
                uploadCSV: "Ladda upp CSV",
                uploadHint: "CSV-, TSV- eller TXT-fil med landskoder och risknivåer",
                noDatasets: "Inga dataset ännu",
                noDatasetsDesc: "Ladda upp en CSV-fil med landskoder och förväntade risknivåer för att validera din modell mot verklig data.",
                datasets: "Uppladdade dataset",
                rowCount: "{{count}} rader",
                colCount: "{{count}} kolumner",
                deleteDataset: "Ta bort dataset",
                confirmDeleteDataset: "Är du säker på att du vill ta bort datasetet \"{{name}}\"? Åtgärden kan inte ångras.",
                columnMapping: "Kolumnmappning",
                columnMappingHint: "Mappa dina CSV-kolumner till modellens datafält.",
                countryCodeColumn: "Landskod (ISO Alpha-2)",
                expectedRiskColumn: "Förväntad risknivå",
                indexDataColumns: "Indexdatakolumner (valfritt)",
                useSystemData: "— Använd systemdata —",
                selectColumn: "— Välj kolumn —",
                runValidation: "Kör validering",
                running: "Kör...",
                results: "Valideringsresultat",
                ranAt: "Validering kördes {{time}}",
                accuracy: "Träffsäkerhet",
                totalRecords: "Poster",
                falsePositives: "Falskt positiva",
                falseNegatives: "Falskt negativa",
                recommendationGood: "Modellen fångar {{pct}}% av fallen korrekt. God överensstämmelse med produktionsdata.",
                recommendationModerate: "Modellen fångar {{pct}}% av fallen korrekt. Överväg att granska felklassificerade fall och justera tröskelvärden.",
                recommendationPoor: "Modellen fångar bara {{pct}}% av fallen korrekt. Betydande kalibrering behövs — granska risktrösklar och indexkonfiguration.",
                distributionComparison: "Risknivåfördelning",
                riskLevel: "Risknivå",
                expected: "Förväntat (data)",
                predicted: "Predikterat (modell)",
                difference: "Skillnad",
                confusionMatrix: "Förväxlingsmatris",
                confusionLegend: "Rader = faktisk risknivå från data. Kolumner = predikterad av modellen. Diagonalen = korrekta prediktioner.",
                showDetails: "Visa detaljer ({{count}} poster)",
                countryCode: "Kod",
                countryName: "Land",
                score: "Poäng",
                match: "Träff",
                showingFirst: "Visar {{count}} av {{total}} poster",
                showMoreRows: "Visa fler ({{count}} kvar)",
                showingXofY: "Visar {{shown}} av {{total}} poster",
                exportPDF: "Exportera PDF",
                exportExcel: "Exportera Excel",
                exportingReport: "Genererar rapport...",
                exportError: "Exporten misslyckades. Försök igen."
            },
            // Fältmappning-tab (GEO-069)
            fieldMapping: {
                tab: "Mappning",
                title: "Fältmappning",
                subtitle: "Koppla aktiva GeoRisk-index till kolumner i en extern datakälla.",
                dataSource: "Datakälla",
                selectDataSource: "— Välj datakälla —",
                table: "Tabell",
                selectTable: "— Välj tabell —",
                tableNamePlaceholder: "Ange tabellnamn",
                countryCodeColumn: "Landskodskolumn",
                selectCountryCodeColumn: "— Välj kolumn —",
                countryCodeColumnPlaceholder: "Ange kolumnnamn (t.ex. country_code)",
                indexMappings: "Indexmappningar",
                indexCol: "Index",
                columnCol: "Källkolumn",
                transformCol: "Transform",
                selectColumn: "— Välj kolumn —",
                columnPlaceholder: "Ange kolumnnamn",
                transform_none: "Ingen",
                transform_uppercase: "Versaler",
                transform_lowercase: "Gemener",
                transform_trim: "Trimma",
                transform_number: "Till tal",
                transform_boolean: "Till bool",
                transform_date: "Till datum",
                previewData: "Förhandsgranska data (5 rader)",
                saveMapping: "Spara mappning",
                saving: "Sparar…",
                statusComplete: "Mappning fullständig — alla index mappade",
                statusIncomplete: "{{count}} index saknar kolumnmappning",
                savedSuccess: "Mappning sparad",
                saveError: "Kunde inte spara mappning: {{error}}",
                errorNoDataSource: "Välj en datakälla",
                errorNoTable: "Ange ett tabellnamn",
                errorNoCountryColumn: "Ange landskodskolumn",
                previewTitle: "Dataförhandsgranskning",
                previewSubtitle: "Exempelrader från {{table}}",
                previewNoData: "Förhandsgranskning ej tillgänglig — anslut till en aktiv datakälla för att hämta rader.",
                noDataSources: "Inga datakällor konfigurerade. Lägg till en i Admin → Datakällor.",
                noIndexes: "Inga aktiva index konfigurerade. Lägg till index under fliken Riskhantering.",
            }
        },
      },
      validationReport: {
        title: "Valideringsrapport",
        accuracy: "Total noggrannhet",
        highRiskCapture: "Högriskfångst",
        falseAlarmRate: "Falsklarmfrekvens",
        underClassified: "Underklassificerade",
        overClassified: "Överklassificerade",
        perLevelAnalysis: "Analys per risknivå",
        performanceMetrics: "Prestandamått",
        riskLevel: "Risknivå",
        actual: "Faktisk",
        correct: "Korrekt",
        sensitivity: "Känslighet (återkallning)",
        precision: "Precision",
        recall: "Återkallning",
        f1Score: "F1-poäng",
        fpr: "Falskt positiv-andel",
        macroAverage: "Makrosnitt",
        under: "Under",
        over: "Över",
        recommendationCapture: "Modellen fångar {{capture}}% av högriskfallen med en total noggrannhet på {{pct}}%. Resultatet kan kopplas till BWRA som valideringsdokumentation.",
        recommendationGeneric: "Modellen uppnår en total noggrannhet på {{pct}}%. Kör validering med produktionsdata för att bedöma högriskfångst.",
        export: {
          reportTitle: "GeoRisk-valideringsrapport",
          systemName: "RMS",
          exportDate: "Exportdatum",
          generatedBy: "Genererad av",
          dataset: "Datamängd",
          totalRecords: "Totalt antal poster",
          correctPredictions: "Korrekta prediktioner",
          falsePositives: "Falskt positiva",
          falseNegatives: "Falskt negativa",
          sheetSummary: "Sammanfattning",
          sheetPerLevel: "Nivåanalys",
          sheetDistribution: "Distribution",
          sheetConfusion: "Förväxlingsmatris",
          sheetDetails: "Detaljer",
          confusionActual: "Faktisk / Predikterad",
          noData: "Ingen valideringsdata tillgänglig för export",
          pdfPageOf: "Sida {{page}} av {{total}}",
        },
      },
      login: {
        title: "Logga in",
        email: "E-postadress",
        emailLabel: "E-postadress",
        usernameLabel: "Användarnamn",
        password: "Lösenord",
        passwordLabel: "Lösenord",
        submit: "Logga in",
        loading: "Loggar in...",
        error: "Fel användarnamn eller lösenord!",
        jwtNotConfigured: "JWT-autentisering är inte konfigurerad ännu. Kontakta din administratör.",
        tagline: "AML-riskhanteringsplattform",
        secureAccess: "Betrodd av complianceteam inom finansiella institutioner",
        poweredBy: "Drivs av Mertil",
        riskManagementSystem: "Riskhanteringssystem",
        feature1: "GeoRisk — Geografisk riskmodellering",
        feature2: "CustomerRisk — Kundbaserad riskbedömning",
        feature3: "BWRA — Verksamhetsövergripande riskanalys",
        emailPlaceholder: "namn@foretag.se",
        usernamePlaceholder: "Ange ditt användarnamn",
        version: "RMS v2.0",
      },
      glossary: {
        title: "AML-ordlista",
        subtitle: "Definitioner av nyckelbegrepp inom AML-riskhantering.",
        searchPlaceholder: "Sök begrepp...",
        noResults: "Inga matchande begrepp hittades.",
        aml: {
          title: "AML — Penningtvättsbekämpning",
          definition: "En uppsättning lagar, regler och processer för att förhindra att kriminellt genererade medel maskeras som legitima intäkter.",
        },
        bwra: {
          title: "BWRA — Verksamhetsövergripande riskbedömning",
          definition: "En heltäckande bedömning av alla risker för penningtvätt och terrorismfinansiering i hela organisationen. Krävs enligt AMLR.",
        },
        cdd: {
          title: "CDD — Kundkännedom",
          definition: "Processen att verifiera en kunds identitet och bedöma deras risknivå innan och under en affärsrelation.",
        },
        edd: {
          title: "EDD — Förstärkt kundkännedom",
          definition: "Extra kontroller för högriskpersoner, t.ex. PEP:ar eller kunder från högriskjurisdiktioner, utöver standard-CDD.",
        },
        fatf: {
          title: "FATF — Financial Action Task Force",
          definition: "Den globala standardsättaren för bekämpning av penningtvätt och terrorismfinansiering. Publicerar listan över högrisk- och övervakade jurisdiktioner.",
        },
        grossRisk: {
          title: "Bruttorisk",
          definition: "Den inneboende risknivån innan några kontroller eller åtgärder tillämpas. Beräknas som sannolikhet × konsekvens.",
        },
        kri: {
          title: "KRI — Nyckelriskindikator",
          definition: "Ett mått som signalerar ökad riskexponering. KRI:er ger tidiga varningar om potentiella överträdelser av riskaptitsgränser.",
        },
        kyc: {
          title: "KYC — Känn din kund",
          definition: "Processen att identifiera och verifiera kunders identitet. En kärndel av CDD och AML-efterlevnad.",
        },
        pep: {
          title: "PEP — Politiskt utsatt person",
          definition: "En person som innehar eller har innehaft en framträdande offentlig funktion. PEP:ar anses vara högrisk och kräver EDD-åtgärder.",
        },
        periodicReporting: {
          title: "Periodisk rapportering",
          definition: "Regelbunden rapportering till tillsynsmyndigheter om AML/CFT-aktiviteter, modellprestanda och efterlevnadsstatus, typiskt på årsbasis.",
        },
        residualRisk: {
          title: "Residualrisk",
          definition: "Den kvarvarande risken efter att kontroller och åtgärder har tillämpats. Målet är att sänka residualrisken under organisationens riskaptit.",
        },
        riskAppetite: {
          title: "Riskaptit",
          definition: "Den risknivå en organisation är villig att acceptera för att nå sina mål. Definierar acceptabla trösklar för varje riskkategori.",
        },
        riskIndex: {
          title: "Riskindex",
          definition: "En poängsatt indikator för att bedöma risk i en specifik dimension (t.ex. ett korruptionsindex för ett land). Flera index kan kombineras i en riskmodell.",
        },
        riskModel: {
          title: "Riskmodell",
          definition: "En strukturerad metodik för att beräkna en riskpoäng från flera indata. RMS stödjer deterministiska, viktade och regelbaserade modeller.",
        },
        riskScore: {
          title: "Riskpoäng",
          definition: "Ett numeriskt värde som representerar risknivån för en specifik enhet (t.ex. land eller kund). Används för kategorisering och rapportering.",
        },
        str: {
          title: "STR — Misstänkt transaktionsrapport",
          definition: "En rapport som skickas till finansunderrättelseenheten (FIU) när en transaktion misstänks involvera penningtvätt eller terrorismfinansiering.",
        },
        transactionMonitoring: {
          title: "Transaktionsövervakning",
          definition: "Automatiserad eller manuell granskning av kundtransaktioner för att upptäcka ovanliga mönster som kan tyda på penningtvätt, bedrägeri eller annan ekonomisk brottslighet.",
        },
        auditLog: {
          title: "Granskningslogg",
          definition: "En kronologisk förteckning över alla användaråtgärder i systemet, inklusive vem som gjorde vad och när. Krävs för regulatorisk ansvarsskyldighet och forensiska utredningar.",
        },
        rbac: {
          title: "RBAC — Rollbaserad åtkomstkontroll",
          definition: "En metod för att begränsa systemåtkomst baserat på de roller som tilldelats enskilda användare. I RMS definierar roller vilka moduler en användare kan visa, redigera eller administrera.",
        },
        userRole: {
          title: "Användarroll",
          definition: "En fördefinierad uppsättning behörigheter tilldelad en användare. Vanliga roller är Admin (fullständig åtkomst), Analytiker (läs/skriv) och Visare (enbart läs).",
        },
        entityAccess: {
          title: "Enhetstillgång",
          definition: "Styr vilka juridiska enheter eller organisatoriska enheter en användare kan komma åt och hantera. Användbart i flerenhetsinstallationer för att upprätthålla datasegregering.",
        },
        licenseManagement: {
          title: "Licenshantering",
          definition: "Processen att aktivera, inaktivera och hantera modullicenser per enhet. En modul utan aktiv licens är otillgänglig för användare av den enheten.",
        },
        reportingCycle: {
          title: "Rapporteringscykel",
          definition: "Den definierade perioden som täcks av en compliancerapport — vanligtvis ett år. Varje rapporteringscykel måste dokumenteras, slutföras och lämnas till tillsynsmyndigheten.",
        },
        approvalFlow: {
          title: "Godkännandeflöde",
          definition: "Den strukturerade processen genom vilken en rapport går från Utkast → Under granskning → Godkänd. Säkerställer tillräcklig översyn innan inlämning till tillsynsmyndigheten.",
        },
        reportCompletion: {
          title: "Rapportfullständighet",
          definition: "Andelen obligatoriska avsnitt som är ifyllda i en periodisk rapport. En fullständigt ifylld rapport har alla obligatoriska fält ifyllda och är klar för godkännande.",
        },
        complianceReport: {
          title: "Compliancerapport",
          definition: "Ett formellt dokument som sammanfattar AML/CFT-aktiviteter, modellprestanda och riskresultat för en specifik period. Lämnas till tillsynsmyndigheter som en del av löpande efterlevnadsskyldigheter.",
        },
        riskControl: {
          title: "Riskkontroll",
          definition: "En åtgärd för att minska en risk. Kontroller kan vara modellbaserade (kopplade till en riskpoängmodell), systembaserade (automatiserade) eller manuella. Kontrollernas effektivitet avgör hur mycket residualrisken minskar.",
        },
        unmitigatedRisk: {
          title: "Omitigerad risk",
          definition: "En risk utan kopplade kontroller — varken manuella kontroller eller modellbaserade autokontroller. Omitigerade risker har hela bruttoriskpoängen som residualrisk. De bör åtgärdas genom att lägga till en kontroll eller dokumentera ett aktivt riskaptitbeslut.",
        },
        traceabilityMatrix: {
          title: "Spårbarhetsmatrix",
          definition: "En tabell som visar vilka riskkategorier som täcks av vilka modeller eller kontroller. Säkerställer att alla identifierade risker har motsvarande åtgärder och att modellernas täckning är fullständig.",
        },
        modelValidation: {
          title: "Modellvalidering",
          definition: "Processen att verifiera att en riskmodell fungerar som avsett. Inkluderar kontroll av konfiguration, datakvalitet och jämförelse av modellens förutsägelser med kända utfall. Krävs för regulatorisk efterlevnad.",
        },
        sdd: {
          title: "SDD — Förenklad kundkännedom",
          definition: "En förenklad nivå av kundkännedom för utpräglat lågriskkunder. Färre identitetskontroller krävs jämfört med standard-CDD. Tillämpas endast när alla riskfaktorer konsekvent är låga.",
        },
        cddEddClassification: {
          title: "CDD/EDD-klassificering",
          definition: "Trestegsramverk för kundkännedom som avgör vilken kontrollnivå som krävs:\n• SDD (Förenklad) — lågrisk, grundläggande verifiering\n• CDD (Standard) — standardnivå för de flesta kunder\n• EDD (Förstärkt) — triggas av hög riskpoäng, PEP-status eller högriskland",
        },
      },
      onboarding: {
        title: "Välkommen till RMS",
        step: "Steg",
        skip: "Hoppa över",
        back: "Tillbaka",
        next: "Nästa",
        getStarted: "Kom igång",
        steps: {
          welcome: {
            title: "Välkommen till Risk Management Studio",
            body: "RMS är en AML-plattform för modelldokumentation och -validering. Den hjälper complianceteam att hantera risker, modeller och regulatoriska krav på ett ställe.",
          },
          bwra: {
            title: "Verksamhetsövergripande riskbedömning",
            body: "Börja här. Identifiera alla affärsövergripande risker och sätt riskaptiten. BWRA är startpunkten — GeoRisk, Kundrisk och Transaktionsövervakning är modeller som finns för att täcka de risker du definierar här.",
          },
          geoRisk: {
            title: "Geografisk risk",
            body: "En modell som täcker geografiska risker identifierade i BWRA. Bedöm AML-risk på landnivå med konfigurerbara index (FATF, korruption, sanktioner) och mata resultaten tillbaka i residualrisk.",
          },
          customerRisk: {
            title: "Kundrisk",
            body: "En modell som täcker kundrelaterade risker identifierade i BWRA. Bygg poängsättningsmodeller med parametrar som KYC-status, PEP-flagga och transaktionsvolym.",
          },
          txMon: {
            title: "Transaktionsövervakning",
            body: "En modell som täcker transaktionsriskscenarier identifierade i BWRA. Definiera regler för att upptäcka misstänkta mönster och hantera larm i hela portföljen.",
          },
          complete: {
            title: "Allt klart!",
            body: "Utforska varje modul via sidomenyn. Använd ordlistaikonen i toppmenyn för att slå upp AML-begrepp när som helst.",
          },
        },
      },
    },
};
export default translations;
